export type Language = 'en' | 'es' | 'pt' | 'zh' | 'fr' | 'ar';

export const languageNames: Record<Language, string> = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
  zh: '中文',
  fr: 'Français',
  ar: 'العربية',
};

// RTL languages
export const rtlLanguages: Language[] = ['ar'];

export const isRTL = (lang: Language): boolean => rtlLanguages.includes(lang);

export const translations: Record<Language, Record<string, string>> = {
  en: {
    // Index
    'title': 'THE HUMAN ROOMS',
    'tagline': 'A collection of rooms that reflect who you are when nobody is watching.',
    'enter': 'Enter',
    'footer': 'Not therapy. Not social. Not productive.',
    
    // Hallway
    'back': '← Back',
    'hallway': 'The Hallway',
    'challenges': 'Challenges',
    'random': 'Random',
    'open_rooms': 'Open Rooms',
    'dark_rooms': 'The Dark Rooms',
    'enter_risk': 'enter at your own risk',
    'premium_rooms': 'Premium Rooms',
    'unlock_to_access': 'Unlock to access',
    'daily_rooms': 'Daily Rooms',
    'refreshes_daily': 'refreshes every day',
    'time_locked': 'Time-Locked Rooms',
    'seasonal_rooms': 'Seasonal Rooms',
    'available_now': 'Available now',
    'fragments': 'Fragments',
    'boss_unlocked': 'Boss Room Unlocked',
    'collect_unlock': 'Collect to unlock Boss Room',
    'dark_fragments': 'Dark Fragments',
    'dark_boss_unlocked': 'Dark Boss Unlocked',
    'hidden_discovered': 'Hidden Room Discovered',
    'stillness_revealed': 'Your stillness revealed something...',
    'constellation_map': 'Constellation Map',
    'your_stats': 'Your Stats',
    'completed': 'Completed',
    'game_rooms': 'Game Rooms',
    'experimental_rooms': 'Experimental Rooms',
    'deep_rooms': 'Deep Rooms',
    'meta_rooms': 'Meta Rooms',
    'search_rooms': 'Search rooms...',
    'no_results': 'No rooms match your search',
    'clear_filters': 'Clear filters',
    'explore': 'Explore',
    'all_categories': 'All',
    'random_room': 'Random Room',
    'ai_reflection': 'AI Reflection',
    'support': 'Support',
    'choose_direction': 'Choose a direction. Some doors respond.',
    'rooms_explored': 'rooms explored',
    'your_journey': 'Your Journey',
    'complete_rooms_fragments': 'Complete rooms to collect fragments.',
    'no_rooms_completed': 'No rooms completed yet.',
    
    // Daily Challenges
    'daily_challenges': 'Daily Challenges',
    'challenges_completed': 'completed',
    'challenges_total': 'total',
    'resets_midnight': 'Resets at midnight',
    
    // Weekly Summary
    'weekly_summary': 'Weekly Summary',
    'week': 'Week',
    'this_week': 'This Week',
    'rooms': 'Rooms',
    'days': 'Days',
    'vs_last_week': 'vs last week',
    'refresh_insight': 'Refresh insight',
    
    // About
    'what_is_this': 'What is this place?',
    'not_productivity': 'The Human Rooms is not a productivity tool.',
    'not_social': 'It is not a social network.',
    'not_therapy': 'It is not therapy.',
    'pause_experiences': 'It is a collection of small experiences designed to make you pause.',
    'each_room': 'Each room takes 30 seconds to 2 minutes.',
    'make_think': 'Some rooms will make you think.',
    'make_uncomfortable': 'Some will make you uncomfortable.',
    'make_calm': 'Some will make you calm.',
    'make_aware': 'All will make you aware.',
    'no_notifications': 'There are no notifications here.',
    'no_points': 'No points to collect.',
    'no_validation': 'No social validation.',
    'just_truth': 'Just you, a room, and the truth.',
    'enter_rooms': 'Enter the rooms',
    
    // Unlock
    'unlock_title': 'Unlock Premium Rooms',
    'unlock_subtitle': 'Access deeper experiences',
    'lifetime': 'Lifetime Access',
    'monthly': 'Monthly',
    'one_time': 'One-time payment',
    'per_month': 'per month',
    'unlock_now': 'Unlock Now',
    'subscribe': 'Subscribe',
    'already_premium': 'You have premium access',
    'premium_features': 'Premium Features',
    'redeem_code': 'Have a code?',
    'redeem_placeholder': 'Enter redeem code',
    'redeem_button': 'Redeem',
    'redeem_success': 'Code redeemed successfully!',
    'redeem_error': 'Invalid or expired code',
    
    // Stats
    'total_time': 'Total Time',
    'rooms_visited': 'Rooms Visited',
    'total_visits': 'Total Visits',
    'most_visited': 'Most Visited',
    'longest_session': 'Longest Session',
    'fragments_collected': 'Fragments Collected',
    'completed_rooms': 'Completed Rooms',
    'privacy_note': 'All data stored locally on your device',
    
    // Common
    'loading': 'Loading...',
    'error': 'Error',
    'continue': 'Continue',
    'submit': 'Submit',
    'cancel': 'Cancel',
    'close': 'Close',
    'next': 'Next',
    'previous': 'Previous',
    'start': 'Start',
    'stop': 'Stop',
    'yes': 'Yes',
    'no': 'No',
    'leave_room': '← Leave room',
    'return': '← Return',
    'return_hallway': 'Return to Hallway',
    'escape': '← Escape',
    'run': '← Run',
    'leave': '← Leave',
    'begin': 'Begin',
    'watch': 'Watch...',
    'correct': 'Correct',
    'round': 'Round',
    'enter_again': 'Enter again',
    'steps': 'Steps',
    'depth': 'Depth',
    
    // Keyboard Shortcuts
    'keyboard_shortcuts': 'Keyboard Shortcuts',
    'shortcut_hallway': 'Go to Hallway',
    'shortcut_random': 'Random Room',
    'shortcut_back': 'Go Back',
    'shortcut_bookmark': 'Toggle Bookmark',
    'shortcut_reading': 'Toggle Reading Mode',
    'shortcut_search': 'Focus Search',
    'shortcut_stats': 'View Stats',
    'shortcut_achievements': 'View Achievements',
    'shortcut_cat_nav': 'Navigation',
    'shortcut_cat_rooms': 'Rooms',
    'shortcut_cat_access': 'Accessibility',
    'press_to_close': 'Press',
    'to_close': 'to close',
    'or': 'or',
    
    // Bookmarks
    'add_bookmark': 'Add Bookmark',
    'remove_bookmark': 'Remove Bookmark',
    'bookmarks': 'Bookmarks',
    'bookmarked_rooms': 'Bookmarked',
    'no_bookmarks': 'No bookmarks yet',
    
    // Reading Mode
    'reading_mode': 'Reading Mode',
    'reading_mode_on': 'Reading mode enabled',
    'reading_mode_off': 'Reading mode disabled',
    
    // Settings Page
    'settings': 'Settings',
    'settings_subtitle': 'Customize your journey',
    'language': 'Language',
    'ambient_audio': 'Ambient Audio',
    'ambient_audio_desc': 'Background soundscapes for rooms',
    'reduced_motion': 'Reduced Motion',
    'reduced_motion_desc': 'Minimize animations and transitions',
    'data_management': 'Data Management',
    'export_journey': 'Export Journey Data',
    'clear_all_data': 'Clear All Data',
    'audio_enabled': 'Audio Enabled',
    'audio_enabled_desc': 'Ambient sounds will play in rooms',
    'audio_disabled': 'Audio Disabled',
    'audio_disabled_desc': 'Rooms will be silent',
    'reduced_motion_enabled': 'Reduced Motion Enabled',
    'reduced_motion_disabled': 'Reduced Motion Disabled',
    'data_exported': 'Data Exported',
    'data_exported_desc': 'Your journey has been saved',
    'clear_data_confirm': 'Are you sure? This will erase all your progress.',
    'data_cleared': 'Data Cleared',
    'data_cleared_desc': 'Your journey has been reset',
    'shared_room': 'Shared Room',
    'imagine_room': 'Imagine',
    'dream_room': 'Dreams',
    'future_room': 'Future',
    
    // Theme settings
    'theme': 'Theme',
    'theme_dark': 'Dark',
    'theme_dark_desc': 'Pure darkness with subtle grain',
    'theme_ash': 'Ash',
    'theme_ash_desc': 'Embers rising from the aftermath',
    'theme_twilight': 'Twilight',
    'theme_twilight_desc': 'Stars twinkling in the between',
    'theme_void': 'Void',
    'theme_void_desc': 'Nothing remains but shadows',
    'theme_eclipse': 'Eclipse',
    'theme_eclipse_desc': 'Light devoured by darkness',
    'theme_dawn': 'Dawn',
    'theme_dawn_desc': 'First light of awakening',
    'theme_changed': 'Theme updated',
    'font_size': 'Font Size',
    'font_small': 'Small',
    'font_medium': 'Medium',
    'font_large': 'Large',
    'font_size_changed': 'Font size updated',
    'high_contrast': 'High Contrast',
    'high_contrast_desc': 'Increase text and border contrast',
    'high_contrast_enabled': 'High contrast enabled',
    'high_contrast_disabled': 'High contrast disabled',
    
    // Ambient Music Control
    'ambient_music': 'Ambient Music',
    'ambient_music_desc': 'Control background soundscapes',
    'volume': 'Volume',
    'ambient_track': 'Ambient Track',
    'playing': 'Playing',
    'play': 'Play',
    'ambient_default': 'Calm',
    'ambient_night': 'Night',
    'ambient_scary': 'Eerie',
    'ambient_truth': 'Truth',
    'ambient_identity': 'Reflection',
    'ambient_mortality': 'Time',
    'ambient_nothing': 'Void',
    'ambient_meta': 'Digital',
    'ambient_daily': 'Warm',
    'ambient_seasonal': 'Magical',
    'ambient_premium': 'Deep',
    
    // Theme Exclusive Section
    'theme_exclusive_rooms': 'Theme-Exclusive Rooms',
    'theme_exclusive_hint': 'Switch themes in settings to unlock these rooms.',
    'requires_theme': 'Requires',
    'switch_to': 'Switch to',
    
    // Advanced Easter Eggs
    'corner_sequence': 'You traced the boundaries.',
    'morse_sos': 'S.O.S. received.',
    'reverse_scroll': 'Going back... but faster.',
    'midnight_visitor': 'The witching hour welcomes you.',
    'shake': 'You shook something loose.',
    
    // Achievements
    'achievements': 'Achievements',
    'achievements_subtitle': 'Milestones on your journey',
    'points': 'points',
    'unlocked': 'unlocked',
    'exploration': 'Exploration',
    'consistency': 'Consistency',
    'discovery': 'Discovery',
    'special': 'Special',
    'all': 'All',
    
    // Imagine Yourself Room
    'imagine_title': 'Imagine Yourself',
    'imagine_subtitle': 'Describe how you see yourself. Let the room show you what emerges from your words.',
    'imagine_placeholder': 'I see myself as someone who...\n\nDescribe your essence, your nature, how you feel inside. Not your appearance, but who you are.',
    'imagine_hint': 'Be honest. The image reflects your words.',
    'imagine_generate': 'See Yourself',
    'imagine_generating': 'Creating...',
    'imagine_try_again': 'Describe Again',
    'imagine_result_alt': 'Your self-portrait',
    'imagine_quote': 'We don\'t see ourselves clearly. We see what we believe.',
    'imagine_empty_error': 'Write something about yourself first',
    'imagine_short_error': 'Tell us more about yourself',
    'rate_limit_error': 'Too many requests. Please wait a moment.',
    'credits_error': 'Service temporarily unavailable',
    'generation_error': 'Could not create image. Try again.',
    
    // Dream Visualization Room
    'dream_title': 'Dream Visualization',
    'dream_subtitle': 'Describe a recurring dream. Watch it materialize before you.',
    'dream_placeholder': 'I keep dreaming about...\n\nDescribe the imagery, the feelings, the recurring elements. What lingers when you wake?',
    'dream_hint': 'Dreams speak in symbols.',
    'dream_generate': 'Visualize Dream',
    'dream_generating': 'Dreaming...',
    'dream_try_again': 'Dream Again',
    'dream_result_alt': 'Your dream visualization',
    'dream_quote': 'Dreams are the subconscious mind trying to tell us something.',
    'dream_empty_error': 'Describe your dream first',
    'dream_short_error': 'Tell us more about your dream',
    'room_dream-visualization': 'Dream Visualization',
    
    // Future Self Room
    'future_title': 'Future Self',
    'future_subtitle': 'Describe who you want to become. See your potential.',
    'future_placeholder': 'In the future, I see myself as...\n\nDescribe the person you are working towards becoming. The qualities, the achievements, the peace.',
    'future_hint': 'Aspiration shapes reality.',
    'future_generate': 'See Your Future',
    'future_generating': 'Envisioning...',
    'future_try_again': 'Envision Again',
    'future_result_alt': 'Your future self',
    'future_quote': 'The future is not something we enter. It is something we create.',
    'future_empty_error': 'Describe your future self first',
    'future_short_error': 'Tell us more about your future self',
    'room_future-self': 'Future Self',
    
    // Updates Page
    'updates': 'Updates',
    'updates_title': 'What\'s New',
    'updates_subtitle': 'Recent changes and improvements',
    'updates_footer': 'Building rooms, one update at a time.',
    'new': 'NEW',
    'subscribe_rss': 'Subscribe via RSS',
    'latest': 'LATEST',
    'whats_new_intro': 'Latest updates:',
    'view_all_updates': 'View all updates',
    'update_updates_page': 'Added Updates page for patch notes',
    'update_full_i18n': 'Complete 6-language translation for all rooms',
    'update_hallway_animations': 'Smooth category switching animations',
    'update_daily_challenges': 'Improved Daily Challenges display',
    'update_game_rooms': 'Added 15 new game rooms',
    'update_experimental_rooms': 'Added 14 experimental rooms',
    'update_random_room': 'Random room shuffle button',
    'update_stats_route': 'Fixed stats page routing',
    'update_streak_system': 'Daily streak tracking system',
    'update_weekly_summary': 'Weekly progress summaries',
    'update_rtl_support': 'Right-to-left language support',
    'update_dark_rooms': 'New dark/horror room category',
    'update_boss_rooms': 'Boss rooms with fragment requirements',
    'update_audio_system': 'Ambient audio for rooms',
    'update_constellation_map': 'Visual journey constellation map',
    'update_fragment_system': 'Room completion fragment collection',
    'update_mobile_layout': 'Mobile responsive improvements',
    'update_multi_language': 'Multi-language support (6 languages)',
    'update_premium_system': 'Premium rooms and unlock system',
    'update_room_endings': 'Unique room ending quotes',
    'update_ui_redesign': 'Complete UI design refresh',
    'update_theme_effects': 'Theme-specific visual effects (embers, stars, glitches)',
    'update_void_theme': 'New Void theme - absolute darkness mode',
    'update_achievements': 'Achievement system with 19 milestones',
    'update_ai_reflection': 'AI Reflection room with personalized insights',
    'update_settings_redesign': 'Redesigned settings with 4 atmospheric themes',
    'update_theme_exclusive_rooms': 'Theme-exclusive rooms unlocked by atmosphere',
    'update_discovery_localization': 'Localized easter egg discovery messages',
    'update_site_mood_system': 'Site mood system - the site decides its emotions',
    'update_voice_imperfection': 'Imperfect voice rooms with breathing and pauses',
    'update_mobile_optimization': 'Mobile optimization with 48px tap targets',
    'update_interaction_clues': 'Subtle interaction clues for new users',
    'update_theme_exclusive_hallway': 'Theme-exclusive rooms visible in Hallway with unlock indicators',
    'update_advanced_easter_eggs': 'Advanced easter eggs: corner sequence, morse code, midnight visitor',
    'update_hidden_discoveries': 'Hidden text reveals and secret discovery mechanics',
    'update_dual_mode_system': 'Classic and Alternate dual-mode experience system',
    'update_alternate_mode': 'Alternate mode - a parallel, experimental reality',
    'update_hidden_discovery': 'Hidden discovery triggers: triple tap, scroll patterns, idle awakening',
    'update_mode_effects': 'Mode-specific visual effects, glitches, and ambient audio',
    'update_translation_navigator': 'Translation Navigator - visual coverage dashboard',
    'update_full_localization': 'Near 100% translation coverage across all 6 languages',
    'update_meta_room_translations': 'Localized meta room content (The Refusal, Parallel You, Your Shadow)',
    'update_animated_timeline': 'Animated changelog timeline with scroll-triggered animations',
    'update_toast_notifications': 'Update toast notification for first visit after new version',
    'update_rss_feed': 'RSS feed for changelog subscriptions',
    'update_bookmarked_filter': 'Bookmarked Rooms filter tab in Hallway for quick access to favorites',
    'update_keyboard_shortcuts': 'Keyboard shortcuts overlay (press ? to view)',
    'update_reading_mode': 'Reading mode for enhanced typography and focus',
    'update_accessibility': 'Improved focus indicators and keyboard navigation',
    'update_ambient_crossfade': 'Smooth fade-in/fade-out transitions between ambient tracks',
    'update_settings_audio': 'Centralized audio controls in Settings page',
    'update_arabic_complete': 'Complete Arabic translation coverage',
    'update_duplicate_icon_fix': 'Fixed duplicate audio icons in Hallway header',
    'update_ritual_rooms': 'Ritual Rooms - Morning, Evening, and Weekly scheduled experiences',
    'update_ai_voice_mode': 'AI Guide Voice Mode using Web Speech API',
    'update_proactive_guide': 'Proactive AI Guide that appears based on behavior patterns',
    'update_offline_support': 'Offline support via Service Worker',
    'update_community_submissions': 'Community room idea submissions',
    'update_constellation_interactive': 'Interactive Constellation Map with zoom/pan and animated trails',
    'update_notification_system': 'Browser notification system for time-locked content',
    'update_journey_export': 'Journey export/import functionality (JSON)',
    'update_game_rooms_new': 'New game rooms: Entropy Garden and Memory Palace',
    'update_new_achievements': '15 new achievements across all categories',
    'update_weather_system': 'Weather system affecting site atmosphere',
    'update_personal_timeline': 'Personal Timeline page for journey history',
    'update_ai_rooms_new': 'New AI rooms: Letters Never Sent and Breath Sync',
    'update_recommended_rooms': 'Personalized "Recommended For You" section based on visit history',
    'update_daily_highlight': 'Prominent Daily Highlight card featuring time-appropriate rooms',
    'update_exit_ritual': 'Exit ritual with session summary and graceful closure',
    'update_anonymous_reflections': 'Anonymous reflection sharing in rooms',
    
    // Phase 14-16 Updates
    'update_realtime_social': 'Real-time social: Whisper Wall, Shared Moments, and Collective Memory are now live multiplayer',
    'update_guided_journeys': 'Guided Journeys: Curated room sequences with narrative connectors',
    'update_pwa_polish': 'Mobile PWA polish: Install prompts and improved offline support',
    'update_haptic_feedback': 'Haptic feedback on mobile interactions',
    
    // v3.20.0 Updates
    'update_ui_refresh': 'UI refresh: Rounded cards, category accents, hover lift effects',
    'update_mood_selector': 'Mood check-in: One-tap mood selector that influences recommendations',
    'update_stats_bar': 'Mini stats bar: Rooms explored, streak, and fragments at a glance',
    'update_quick_links_redesign': 'Quick links reorganized into Explore and Your Space rows',
    
    // Mood Selector
    'mood_question': 'How are you feeling right now?',
    'mood_calm': 'Calm',
    'mood_anxious': 'Anxious',
    'mood_curious': 'Curious',
    'mood_heavy': 'Heavy',
    'mood_restless': 'Restless',
    'your_space': 'Your Space',
    'stats_streak': 'day streak',
    
    // Guided Journeys
    'guided_journeys': 'Journeys',
    'guided_journeys_title': 'Guided Journeys',
    'guided_journeys_subtitle': 'Curated room sequences with narrative connectors. Like a guided meditation playlist.',
    'journey_complete': 'Journey Complete',
    'journey_complete_desc': 'You walked the full path. The rooms remember your passage.',
    'more_journeys': 'More Journeys',
    'next_room': 'Next Room',
    'enter_room': 'Enter Room',
    'finish_journey': 'Finish Journey',
    'step': 'Step',
    
    // Journey: The Identity Path
    'journey_identity_title': 'The Identity Path',
    'journey_identity_subtitle': '5 rooms exploring who you are',
    'journey_identity_desc': 'Strip away the labels, the roles, the expectations. What remains?',
    'journey_identity_after_1': 'You wrote one honest thing. Now let\'s go deeper.',
    'journey_identity_before_2': 'If your job disappeared tomorrow, what would be left?',
    'journey_identity_after_2': 'The labels fell away. Something stayed.',
    'journey_identity_before_3': 'Now look at yourself — but through a blur.',
    'journey_identity_after_3': 'Focus reveals less than blur sometimes.',
    'journey_identity_before_4': 'Remove everything. What\'s the last thing to go?',
    'journey_identity_after_4': 'You found the irreducible.',
    'journey_identity_before_5': 'One final test: Watch the thought that won\'t leave.',
    
    // Journey: The Truth Spiral
    'journey_truth_title': 'The Truth Spiral',
    'journey_truth_subtitle': '4 rooms peeling back layers',
    'journey_truth_desc': 'Each room reveals another layer of comfortable lies.',
    'journey_truth_after_1': 'You chose the safe option. Or didn\'t. Either way — noticed.',
    'journey_truth_before_2': 'What shortcuts do you take without thinking?',
    'journey_truth_after_2': 'Patterns are just habits wearing masks.',
    'journey_truth_before_3': 'Now let\'s see where your time actually goes.',
    'journey_truth_after_3': 'The gap between what you say matters and what you spend time on.',
    'journey_truth_before_4': 'Time for the receipt.',
    
    // Journey: Night Descent
    'journey_night_title': 'Night Descent',
    'journey_night_subtitle': '3 rooms for the sleepless',
    'journey_night_desc': 'Best experienced after dark. The rooms get quieter.',
    'journey_night_after_1': 'The thing that keeps you up — it has a shape now.',
    'journey_night_before_2': 'There\'s a thought that comes back. Every night.',
    'journey_night_after_2': 'Naming it doesn\'t stop it. But you see it now.',
    'journey_night_before_3': 'It\'s 3:17 AM somewhere. Maybe here.',
    
    // Journey: Stillness Practice
    'journey_stillness_title': 'Stillness Practice',
    'journey_stillness_subtitle': '4 rooms of quiet',
    'journey_stillness_desc': 'Slow down. Then stop. Then stay.',
    'journey_stillness_after_1': 'Your breathing found a rhythm.',
    'journey_stillness_before_2': 'Now: nothing. Can you sit with nothing?',
    'journey_stillness_after_2': 'You stayed. That\'s enough.',
    'journey_stillness_before_3': 'Find a color. Stay in it.',
    'journey_stillness_after_3': 'Comfort doesn\'t need a reason.',
    'journey_stillness_before_4': 'This moment. Once. Now.',
    
    // Journey: Mortality Mirror
    'journey_mortality_title': 'Mortality Mirror',
    'journey_mortality_subtitle': '3 rooms of finite time',
    'journey_mortality_desc': 'Not a warning. Just a count.',
    'journey_mortality_after_1': 'The number is smaller than you thought.',
    'journey_mortality_before_2': 'Now see it in weeks. All of them.',
    'journey_mortality_after_2': 'Each filled square — gone.',
    'journey_mortality_before_3': 'If nothing changes, here\'s what happens.',
    
    // PWA
    'pwa_install_title': 'Install The Human Rooms',
    'pwa_install_desc': 'Add to your home screen for offline access and a native app experience.',
    'pwa_install_button': 'Install',
    
    // Live indicator
    'whisper_live_note': 'Others will see it in real-time.',
     
     // Phase 7 - Regret Machine Room
     'regret_machine_title': 'The Regret Machine',
     'regret_machine_subtitle': 'Describe a decision you wish you had made differently. The machine will show you what might have been.',
     'regret_placeholder': 'I regret...\n\nDescribe a choice you made that you wish had gone differently. What would you have done instead?',
     'regret_hint': 'This is not about blame. It is about understanding.',
     'regret_explore': 'Explore the Timeline',
     'regret_generating': 'Reconstructing an alternate reality...',
     'regret_alternate_timeline': 'The Alternate Timeline',
     'regret_explore_another': 'Explore Another',
     'regret_error_short': 'Please describe your regret in more detail',
     'room_regret-machine': 'The Regret Machine',
     'update_regret_machine': 'The Regret Machine - AI-powered alternate timeline narratives',
     
     // Phase 7 - Memory Reconstruction Room
     'memory_reconstruction_title': 'Memory Reconstruction',
     'memory_reconstruction_subtitle': 'Describe a fading memory. Let us help you feel your way back into it.',
     'memory_placeholder': 'I remember...\n\nDescribe a distant or fragmented memory. What fragments remain? What feelings linger?',
     'memory_timeframe_placeholder': 'When was this? (e.g., "childhood", "ten years ago")',
     'memory_hint': 'Memory is not a recording. It is a reconstruction.',
     'memory_reconstruct': 'Reconstruct Memory',
     'memory_reconstructing': 'Piecing together the fragments...',
     'memory_reconstructed': 'The Reconstructed Memory',
     'memory_explore_another': 'Explore Another Memory',
     'memory_error_short': 'Please describe your memory in more detail',
     'room_memory-reconstruction': 'Memory Reconstruction',
     'update_memory_reconstruction': 'Memory Reconstruction - AI fills in sensory details of fading memories',
    
    // Exit Ritual & Session
    'close_the_door': 'Close the Door',
    'stay_longer': 'Stay a little longer',
    'session_summary': 'This Session',
    'time_spent': 'spent here',
    'rooms_visited_session': 'rooms visited',
    'total_journey': 'Total journey',
    'the_rooms_wait': 'The rooms will wait for you.',
    
    // Recommendations
    'recommended_for_you': 'Recommended For You',
    'continue_journey': 'Continue Your Journey',
    'todays_room': "Today's room",
    'live': 'Live',
    
    // Anonymous Reflections
    'anonymous_reflections': 'Anonymous Reflections',
    'visitors': 'visitors',
    'no_reflections_yet': 'No reflections yet. Be the first.',
    'share_reflection': 'Share your reflection (anonymous)',
    'your_reflection': 'Your reflection...',
    'anonymous_note': 'Anonymous and stored locally',
    'reflection_shared': 'Your reflection has been shared.',
    
    // Interaction hints
    'hint_click_enter': 'Click to enter',
    'hint_take_time': 'Take your time',
    'hint_each_room': 'Each room holds a question',
    'hint_no_rush': 'There is no rush here',
    
    // Behavior reflections
    'behavior_stillness': 'You paused.',
    'behavior_patience': 'You wait longer than most.',
    'behavior_silence': 'The silence speaks.',
    
    // Easter Egg Discoveries
    'discovery_label': 'Discovery',
    'discovery_konami': 'You know the old ways.',
    'discovery_secret_sequence': 'You spelled the truth.',
    'discovery_long_press': 'Patience reveals secrets.',
    'discovery_triple_click': 'Persistence pays off.',
    'discovery_silence': 'Stillness speaks.',
    
    // Theme-Exclusive Rooms
    'ash_exclusive': 'Ash Theme Exclusive',
    'twilight_exclusive': 'Twilight Theme Exclusive',
    'void_exclusive': 'Void Theme Exclusive',
    'theme_required': 'Requires {theme} theme',
    
    // Ash Embers Room
    'ash_embers_title': 'From the Ashes',
    'ash_embers_line1': 'Everything burns eventually.',
    'ash_embers_line2': 'What matters is what rises after.',
    'ash_embers_line3': 'Touch the screen. Create embers.',
    'ash_embers_quote': 'From ashes, we remember what once burned.',
    'room_ash-embers': 'Ash & Embers',
    'sub_ash-embers': 'Only visible in Ash theme',
    
    // Twilight Stars Room
    'twilight_stars_title': 'Between Day and Night',
    'twilight_stars_line1': 'The stars remember everyone who looked up.',
    'twilight_stars_line2': 'Connect them. Make your own constellation.',
    'twilight_stars_quote': 'In twilight, we see what daylight hides.',
    'room_twilight-stars': 'Twilight Stars',
    'sub_twilight-stars': 'Only visible in Twilight theme',
    
    // Void Abyss Room
    'void_abyss_title': 'The Abyss',
    'void_abyss_line1': 'Look into the void. The void looks back.',
    'void_word_1': 'nothing',
    'void_word_2': 'empty',
    'void_word_3': 'silence',
    'void_word_4': 'gone',
    'void_word_5': 'alone',
    'void_word_6': 'end',
    'void_revealed': 'revealed',
    'void_abyss_quote': 'In absolute darkness, we finally see ourselves.',
    'room_void-abyss': 'The Abyss',
    'sub_void-abyss': 'Only visible in Void theme',
    
    // Game UI
    'game_watching': 'watching...',
    'game_repeat': 'repeat',
    'game_echo_fades': 'the echo fades...',
    'game_escaped': 'You escaped.',
    'game_found_exit': 'You found the exit.',
    'game_looping': 'You are looping.',
    'game_deeper': 'Going deeper...',
    'game_doors_shift': 'The doors shift.',
    'maze_left': 'left',
    'maze_right': 'right',
    'maze_forward': 'forward',
    'maze_back': 'back',
    'maze_past': 'past',
    'maze_future': 'future',
    'maze_here': 'here',
    'maze_there': 'there',
    'maze_truth': 'truth',
    'maze_lies': 'lies',
    'maze_light': 'light',
    'maze_dark': 'dark',
    'maze_memory': 'memory',
    'maze_dream': 'dream',
    'maze_silence': 'silence',
    'maze_noise': 'noise',
    
    // Daily Joke Room
    'joke_number': 'Joke #{number}',
    'joke_today': "Today's Joke",
    'joke_reveal': 'Reveal the punchline',
    'joke_come_back': 'Come back tomorrow for a new joke.',
    'joke_quote': 'Laughter is the shortest distance between two people.',
    
    // Experience Mode
    'experience_mode': 'Experience Mode',
    'classic_mode': 'Classic',
    'classic_mode_desc': 'The familiar reality',
    'alternate_mode': '▒ Alternate ▒',
    'alternate_mode_desc': '[ Y.o" u sh0u"ldnt b.e h"ere ]',
    'switch_to_alternate': '░▓█ descend █▓░',
    'switch_to_classic': '← Return to surface',
    'enter_parallel_reality': '[ The d00r is 0pen ]',
    'return_to_familiar': 'Return to the familiar',
    'something_shifted': '[ S0mething shifted ]',
    'reality_feels_different': '[ Reality feels different n0w ]',
    'alternate_tagline': 'The s.ame r00ms. D1fferent truths.',
    'alternate_enter': 'Descend',
    'alternate_footer': '[ Th1ngs are n0t what they seem ]',
    'alternate_rooms': '░▓ P.ar4llel Re4lity ▓░',
    'alternate_rooms_hint': '[ these r00ms exist only in the p4rallel ]',
    
    // Room Titles
    'room_how-many-times-left': 'How Many Times Left',
    'room_life-in-weeks': 'Your Life in Weeks',
    'room_delay-button': 'The Delay Button',
    'room_one-honest-sentence': 'One Honest Sentence',
    'room_this-moment': 'This Moment Will Never Happen Again',
    'room_reality-receipt': 'Reality Receipt',
    'room_nothing-happens': 'Nothing Happens Here',
    'room_default-choice': 'Your Default Choice',
    'room_thought-loop': 'The Thought Loop',
    'room_expect-vs-happens': 'What You Expect vs What Happens',
    'room_question-never-answer': 'The Question You Never Answer',
    'room_not-special': "You're Not Special (And That's Okay)",
    'room_shortcut-always-take': 'The Shortcut You Always Take',
    'room_blurred-self': 'Blurred Self',
    'room_image-zooming': 'The Image You Keep Zooming',
    'room_comfort-color': 'The Comfort Color',
    'room_line-you-crossed': 'The Line You Crossed',
    'room_read-aloud': 'Read This Aloud',
    'room_voice-without-meaning': 'Your Voice Without Meaning',
    'room_sentence-you-avoid': 'The Sentence You Avoid',
    'room_if-nothing-changes': 'If Nothing Changes',
    'room_real-priorities': 'Your Real Priorities',
    'room_last-conversation': 'The Last Conversation',
    'room_who-without-job': 'Who Are You Without Your Job?',
    'room_comfort-zone-scanner': 'Comfort Zone Scanner',
    'room_say-it-once': 'Say It Once',
    'room_listen-to-yourself': 'Listen to Yourself',
    'room_life-as-shapes': 'Your Life as Shapes',
    'room_face-without-features': 'Face Without Features',
    'room_what-keeps-awake': 'What Keeps You Awake',
    'room_you-at-317am': 'You at 3:17 AM',
    'room_thought-that-returns': 'The Thought That Returns',
    'room_room-you-visit-most': 'The Room You Visit Most',
    'room_room-you-avoid': 'The Room You Avoid',
    'room_left-too-early': 'You Left Too Early',
    'room_daily-quote': 'Quote of the Day',
    'room_daily-joke': 'Joke of the Day',
    'room_theyre-watching': "They're Watching",
    'room_the-last-visitor': 'The Last Visitor',
    'room_dont-look-away': "Don't Look Away",
    'room_the-whisper': 'The Whisper',
    'room_you-forgot-something': 'You Forgot Something',
    'room_the-mirror-lies': 'The Mirror Lies',
    'room_something-follows': 'Something Follows',
    'room_the-countdown': 'The Countdown',
    'room_eyes-in-the-dark': 'Eyes in the Dark',
    'room_the-voice': 'The Voice',
    'room_it-knows-your-name': 'It Knows Your Name',
    'room_the-door': 'The Door',
    'room_boss-room-free': 'The Sum of Your Truths',
    'room_boss-room-horror': 'The Heart of Fear',
    'room_boss-room-premium': 'The Deepest Truth',
    'room_mouse-tracker': 'The Mouse Tracker',
    'room_scroll-confession': 'Scroll Confession',
    'room_waiting-game': 'The Waiting Game',
    'room_day-in-pixels': 'Your Day in Pixels',
    'room_memory-maze': 'Memory Maze',
    'room_decision-shadow': 'Decision Shadow',
    'room_anonymous-mirrors': 'Anonymous Mirrors',
    'room_collective-room': 'The Collective Room',
    'room_secret-room': 'The Secret Room',
    'room_glitch-room': 'Unstable Room',
    'room_full-moon-room': 'Full Moon Room',
    'room_weekend-room': 'Weekend Room',
    'room_317am-room': '3:17 AM Room',
    'room_idle-room': 'The Still Room',
    'room_invite-friend-room': 'The Shared Room',
    'room_halloween-room': 'The Veil Room',
    'room_winter-solstice-room': 'The Longest Night',
    'room_new-year-room': 'Letter to Future Self',
    // Game Rooms
    'room_memory-puzzle': 'Memory Puzzle',
    'room_shadow-chase': 'Shadow Chase',
    'room_timing-room': 'Timing Room',
    'room_pattern-whisper': 'Pattern Whisper',
    'room_silence-puzzle': 'Silence Puzzle',
    'room_decay-game': 'Decay Game',
    'room_observation-test': 'Observation Test',
    'room_balance-room': 'Balance Room',
    'room_word-drift': 'Word Drift',
    'room_color-echo': 'Color Echo',
    'room_mirror-tap': 'Mirror Tap',
    'room_maze-of-doors': 'Maze of Doors',
    'room_pulse-match': 'Pulse Match',
    'room_light-path': 'Light Path',
    'room_sound-match': 'Sound Match',
    
    // Experimental Rooms
    'room_mirror-room': 'Mirror Room',
    'room_echo-room': 'Echo Room',
    'room_pulse-room': 'Pulse Room',
    'room_vanishing-room': 'Vanishing Room',
    'room_letter-room': 'Letter Room',
    'room_empty-room': 'Empty Room',
    'room_reverse-room': 'Reverse Room',
    'room_whisper-room': 'Whisper Room',
    'room_shadow-room': 'Shadow Room',
    'room_labyrinth-room': 'Labyrinth Room',
    'room_color-room': 'Color Room',
    'room_surrender-room': 'Surrender Room',
    'room_noise-room': 'Noise Room',
    'room_observer-room': 'Observer Room',
    'room_imagine-yourself': 'Imagine Yourself',
    'room_ai-reflection-room': 'AI Reflection Room',
    // Alternate Mode Exclusive Rooms
    'room_the-other-side': 'The Other Side',
    'room_the-unraveling': 'The Unraveling',
    'room_the-echo': 'The Echo',
    
    // Room Subtitles
    'sub_how-many-times-left': 'A count of what remains',
    'sub_life-in-weeks': "4,000 squares. That's all.",
    'sub_delay-button': 'It gets harder every time',
    'sub_one-honest-sentence': 'No edits. No delete.',
    'sub_this-moment': 'You are here. Once.',
    'sub_reality-receipt': 'A summary of your days',
    'sub_nothing-happens': 'Exactly what it says',
    'room_eclipse-shadows': 'Eclipse Shadows',
    'sub_eclipse-shadows': 'Speak into the total darkness',
    'room_dawn-awakening': 'Dawn Awakening',
    'sub_dawn-awakening': 'Greet the first light',
    'sub_default-choice': 'Safety or risk. Every time.',
    'sub_thought-loop': 'Watch your mind repeat itself',
    'sub_expect-vs-happens': 'The gap between hope and reality',
    'sub_question-never-answer': 'Most users leave',
    'sub_not-special': 'No comfort. Just truth.',
    'sub_shortcut-always-take': 'Patterns reveal themselves',
    'sub_blurred-self': 'Memory works like this',
    'sub_image-zooming': 'Seeking answers in nothing',
    'sub_comfort-color': 'How long will you stay?',
    'sub_line-you-crossed': 'We see where you hesitated',
    'sub_read-aloud': 'Words you must speak',
    'sub_voice-without-meaning': 'Sound without words',
    'sub_sentence-you-avoid': 'You cannot leave until you write it',
    'sub_if-nothing-changes': 'A projection of your next five years',
    'sub_real-priorities': 'Based on time, not words',
    'sub_last-conversation': 'Write what you will never send',
    'sub_who-without-job': 'Rewrite yourself',
    'sub_comfort-zone-scanner': 'We see your hesitation',
    'sub_say-it-once': 'No replay. No redo.',
    'sub_listen-to-yourself': 'Your voice, slowed down',
    'sub_life-as-shapes': 'Abstract. Unlabeled. Yours.',
    'sub_face-without-features': 'Remove until nothing remains',
    'sub_what-keeps-awake': 'Available after 10 PM',
    'sub_you-at-317am': 'Time-specific. Personal.',
    'sub_thought-that-returns': 'It comes back every visit',
    
    // Game Room Subtitles
    'sub_memory-puzzle': 'Match patterns that subtly shift',
    'sub_shadow-chase': 'Catch what moves unpredictably',
    'sub_timing-room': 'Click exactly in the moment',
    'sub_pattern-whisper': 'Repeat the fading sequence',
    'sub_silence-puzzle': 'Solve without instructions',
    'sub_decay-game': 'Touch to preserve fading things',
    'sub_observation-test': 'Find subtle differences',
    'sub_balance-room': 'Keep floating objects aligned',
    'sub_word-drift': 'Catch letters before they fall',
    'sub_color-echo': 'Repeat the subtle tone sequence',
    'sub_mirror-tap': 'Every tap echoes elsewhere',
    'sub_maze-of-doors': 'Doors rearrange after each choice',
    'sub_pulse-match': 'Move in rhythm with the room',
    'sub_light-path': 'Guide light through shifting darkness',
    'sub_sound-match': 'Tap shapes to match soft tones',
    
    // Experimental Room Subtitles
    'sub_mirror-room': 'Reflections slightly distorted',
    'sub_echo-room': 'Clicks trigger delayed echoes',
    'sub_pulse-room': 'The environment breathes',
    'sub_vanishing-room': 'Elements fade as you interact',
    'sub_letter-room': 'Words scramble like failing memory',
    'sub_empty-room': 'Blank until you find secrets',
    'sub_reverse-room': 'Actions behave opposite',
    'sub_whisper-room': 'Barely audible truths',
    'sub_shadow-room': 'Your dark companion',
    'sub_labyrinth-room': 'Find your way',
    'sub_color-room': 'Chromatic meditation',
    'sub_surrender-room': 'Let go of control',
    'sub_noise-room': 'Static thoughts',
    'sub_observer-room': 'Being watched',
    'sub_imagine-yourself': 'Describe yourself. See what emerges.',
    'sub_ai-reflection-room': 'Let AI reflect with you',
    // Alternate Mode Exclusive Room Subtitles
    'sub_the-other-side': 'move slowly through the dark',
    'sub_the-unraveling': 'reality frays with each word',
    'sub_the-echo': 'what you say never leaves',
    
    // Room Content - Common
    'how_old_are_you': 'How old are you?',
    'based_on_life_expectancy': 'Based on average life expectancy, you have approximately:',
    'summers': 'Summers',
    'meals': 'Meals',
    'birthdays': 'Birthdays',
    'full_moons': 'Full moons',
    'sunrises': 'Sunrises',
    'books_might_finish': 'Books you might finish',
    'quote_not_warning': 'This is not a warning. Just a count.',
    'each_square_is_week': 'Each square is one week. There are about 4,000.',
    'your_age': 'Your age',
    'see_your_life': 'See your life',
    'weeks_lived': '{count} weeks lived',
    'weeks_remaining': '{count} remaining (if you\'re average)',
    'quote_week_never_back': 'Each filled square is a week you will never get back.',
    'write_one_true_thing': 'Write one true thing about yourself.',
    'cannot_edit_delete': 'You cannot edit it. You cannot delete it.',
    'i_placeholder': 'I...',
    'submit_forever': 'Submit forever',
    'previous_truths': 'Your previous truths',
    'write_another': 'Write another',
    'quote_cannot_unsaid': 'This cannot be unsaid.',
    'content_nothing_waiting': 'You have been here for {seconds} seconds.',
    'content_nothing_still': 'Nothing is still happening.',
    'content_nothing_stayed': 'And yet, you stayed.',
    'content_thought_visit': 'Visit #{count}',
    'content_thought_always': 'This thought will be here every time you return.',
    'content_thought_never_leave': 'Some thoughts never leave. They just wait.',
    'click_it_harder': 'Click it. It will get harder.',
    'click': 'Click',
    'times_clicked': 'times clicked',
    'quote_things_chase': 'The things we chase get smaller the more we pursue them.',
    'scenario_stranger': 'A stranger asks for help',
    'scenario_help': 'Help them',
    'scenario_keep_walking': 'Keep walking',
    'scenario_free_hour': 'You have a free hour',
    'scenario_start_new': 'Start something new',
    'scenario_scroll': 'Scroll your phone',
    'scenario_disagrees': 'Someone disagrees with you',
    'scenario_engage': 'Engage',
    'scenario_avoid': 'Avoid conflict',
    'scenario_risky': 'An opportunity feels risky',
    'scenario_take_it': 'Take it',
    'scenario_wait_safer': 'Wait for a safer one',
    'scenario_speak_up': 'You could speak up now',
    'scenario_say_it': 'Say it',
    'scenario_stay_quiet': 'Stay quiet',
    'choose_safety': 'of the time, you choose safety',
    'quote_become_choose': 'We become what we repeatedly choose.',
    
    // Unlock page
    'unlock_locked_rooms': 'Locked Rooms',
    'unlock_not_free': 'Some rooms are not free.',
    'unlock_not_because': 'Not because they are better —',
    'unlock_go_further': 'but because they go further.',
    'unlock_whats_behind': "What's behind the door",
    'unlock_and_more': '...and {count} more rooms',
    'unlock_deepest_truth': 'The Deepest Truth',
    'unlock_premium_boss': 'The Premium Boss Room',
    'unlock_lifetime_desc': 'One time. Forever yours.',
    'unlock_monthly_desc': 'Cancel anytime.',
    'unlock_no_account': 'No account required. No emails. Just access.',
    'unlock_all_unlocked': 'All Rooms Unlocked',
    'unlock_have_access': 'You have access to every room in The Human Rooms.',
    'unlock_go_deeper': 'Go deeper. Take your time.',
    'unlock_enter_hallway': 'Enter the Hallway',
    'unlock_welcome_depths': 'Welcome to the depths',
    'unlock_verifying': 'Verifying your purchase...',
    'unlock_canceled': 'Payment canceled',
    'unlock_try_again': "You can try again whenever you're ready.",
    'unlock_error': 'Something went wrong',
    'unlock_try_later': 'Please try again later.',
    
    // Room additional keys
    'seconds_that_wont_return': 'that you will never get back',
    'moment_id': 'Moment ID',
    
    // This Moment room
    'this_moment_title': 'This Moment Will Never Happen Again',
    'this_moment_been_here': 'You have been in this moment for',
    'this_moment_quote': 'This exact configuration of time, place, and you will never occur again in the history of the universe.',
    
    // Thought Loop room
    'thought_loop_title': 'The Thought Loop',
    'thought_loop_prompt': 'Write a thought that keeps returning.',
    'thought_loop_placeholder': "The thought that won't leave...",
    'thought_loop_release': 'Release it',
    'thought_loop_quote': 'The mind repeats what it cannot release.',
    
    // Expect vs Happens room
    'expect_title': 'What You Expect vs What Happens',
    'expect_subtitle': 'Two truths. One gap.',
    'expect_what_expect': 'What you expect to happen',
    'expect_what_happens': 'What usually happens',
    'expect_show': 'Show me',
    'expect_distance': 'The distance between these two sentences is where you live.',
    'expect_quote': 'Hope is the gap between expectation and reality.',
    
    // Reality Receipt room
    'receipt_title': 'Reality Receipt',
    'receipt_sleep': 'Hours of sleep per night?',
    'receipt_work': 'Hours of work per day?',
    'receipt_screen': 'Hours of screen time (non-work)?',
    'receipt_commute': 'Hours commuting?',
    'receipt_next': 'Next',
    'receipt_generate': 'Generate receipt',
    'receipt_header': 'REALITY RECEIPT',
    'receipt_sleep_label': 'Sleep',
    'receipt_work_label': 'Work',
    'receipt_screen_label': 'Screen (leisure)',
    'receipt_commute_label': 'Commute',
    'receipt_total_used': 'TOTAL USED',
    'receipt_free_time': 'FREE TIME',
    'receipt_yearly': 'YEARLY PROJECTION:',
    'receipt_yearly_work': 'hours working',
    'receipt_yearly_screen': 'hours on screens',
    'receipt_yearly_sleep': 'hours sleeping',
    'receipt_yearly_free': 'hours free',
    'receipt_recalculate': 'Recalculate',
    'receipt_quote': 'This is how you spend your life.',
    
    // Question Never Answer room
    'question_title': 'The Question You Never Answer',
    'question_seconds': 'seconds',
    'question_most_left': 'Most people have already left.',
    'question_quote': 'The question remains. You stayed.',
    'question_1': 'What would you do if you knew you couldn\'t fail?',
    'question_2': 'Who would you be if no one was watching?',
    'question_3': 'What are you pretending not to know?',
    'question_4': 'What would your 10-year-old self think of you?',
    'question_5': 'What are you afraid to want?',
    'question_6': 'If you died today, what would remain unfinished?',
    'question_7': 'What lie do you tell yourself most often?',
    'question_8': 'What would you do if money wasn\'t real?',
    'question_9': 'Who have you not forgiven?',
    'question_10': 'What are you running from?',
    
    // Not Special room
    'not_special_right_now': 'Right now, approximately',
    'not_special_share_fear': 'people share your fear of',
    'not_special_title': "You're Not Special",
    'not_special_okay': "And that's okay.",
    'not_special_quote': 'Your fear is not unique. Neither is your courage.',
    'fear_forgotten': 'being forgotten',
    'fear_dying_alone': 'dying alone',
    'fear_not_enough': 'not being good enough',
    'fear_wasting_life': 'wasting their life',
    'fear_being_seen': 'being truly seen',
    'fear_failure': 'failure',
    'fear_rejection': 'rejection',
    'fear_missing_out': 'missing out',
    'fear_ordinary': 'being ordinary',
    'fear_time': 'running out of time',
    
    // Scary Room Content
    'watching_alone': "You're alone here.",
    'watching_completely': "Completely alone.",
    'watching_or_are_you': "Or are you?",
    'watching_been_here': "They've been here the whole time.",
    'watching_always': "They're always watching.",
    'watching_quote': "You are never truly alone with your thoughts.",
    'countdown_title': 'The Countdown',
    'countdown_something': 'Something happens when it reaches zero.',
    'countdown_sure': 'Are you sure you want to stay?',
    'countdown_almost': "It's almost here.",
    'countdown_end_1': "Nothing happened. Were you afraid?",
    'countdown_end_2': "The anticipation was the experience.",
    'countdown_end_3': "What did you expect?",
    'countdown_end_4': "Fear lives in the waiting.",
    'countdown_end_5': "The countdown was never about the end.",
    'countdown_quote': "The scariest moment is always just before you start.",
    'dont_look_title': "Don't Look Away",
    'dont_look_away_msg': "Don't look away.",
    'dont_look_looked': "You looked away.",
    'dont_look_seconds': '{count} seconds of attention',
    'dont_look_times': 'Looked away {count} time(s)',
    'dont_look_quote': "Presence is the hardest thing to give.",
    'whisper_hello': "Hello.",
    'whisper_came': "You came.",
    'whisper_expecting': "We've been expecting you.",
    'whisper_remember': "Do you remember this place?",
    'whisper_been_before': "You've been here before.",
    'whisper_know': "We know.",
    'whisper_remember_name': "Do you remember your name?",
    'whisper_quote': "Some rooms remember you better than you remember yourself.",
    
    // Additional Room Titles (only unique ones not already defined)
    'room_void-room': 'The Void',
    'room_pattern-room': 'Your Patterns',
    'room_time-capsule': 'Time Capsule',
    'room_the-revisit': 'The Revisit',
    'room_contradiction-room': 'The Contradiction Room',
    'room_export-journey': 'Export Your Journey',
    'room_anonymous-echoes': 'Anonymous Echoes',
    'room_the-waiting-room': 'The Waiting Room',
    
    // Additional Room Subtitles (only unique ones not already defined)
    'sub_secret-room': 'You found it',
    'sub_idle-room': 'Found through patience',
    'sub_glitch-room': 'Something is wrong',
    'sub_void-room': 'A wound in the code',
    'sub_pattern-room': 'What the site sees in you',
    'sub_collective-room': 'Everyone is here. No one stays.',
    'sub_theyre-watching': 'You are not alone',
    'sub_the-last-visitor': 'Someone was here before you',
    'sub_dont-look-away': 'It knows when you leave',
    'sub_the-whisper': 'Listen closely',
    'sub_you-forgot-something': 'What was it?',
    'sub_the-mirror-lies': 'Crack it to see the truth',
    'sub_something-follows': 'Keep running',
    'sub_the-countdown': 'Something happens at zero',
    'sub_eyes-in-the-dark': 'They multiply',
    'sub_the-voice': 'Someone is trapped here',
    'sub_it-knows-your-name': 'You should not have come',
    'sub_the-door': 'Something is knocking',
    'sub_boss-room-free': 'The final free room',
    'sub_boss-room-horror': 'Face the final darkness',
    'sub_boss-room-premium': 'The final deep room',
    'sub_halloween-room': 'October 31st - The veil is thin',
    'sub_winter-solstice-room': 'December 21st - Release and keep',
    'sub_new-year-room': 'December 31st - Write to who you will be',
    'sub_weekend-room': 'Rest is a destination',
    'sub_full-moon-room': 'Only opens during lunar peak',
    'sub_317am-room': 'Time-specific. Personal.',
    'sub_time-capsule': 'Write to your future self',
    'sub_the-revisit': 'See how your answers changed',
    'sub_contradiction-room': 'Your truths disagree',
    'sub_export-journey': 'Download your path',
    'sub_anonymous-echoes': 'Fragments from strangers',
    'sub_the-waiting-room': 'Unlock with presence',
    'sub_invite-friend-room': 'Some journeys are meant to be witnessed',
    'sub_room-you-visit-most': 'Your pattern revealed',
    'sub_room-you-avoid': 'What you skip says something',
    'sub_left-too-early': 'Triggered when you exit fast',
    'sub_daily-quote': 'Wisdom that changes daily',
    'sub_daily-joke': 'Dark humor, updated daily',
    'sub_dream-visualization': 'Describe a dream. Watch it materialize.',
    'sub_future-self': 'Who do you want to become?',
    'sub_regret-machine': 'Explore what might have been',
    'sub_memory-reconstruction': 'Rebuild a fading memory',
    'sub_mouse-tracker': 'We see how you move',
    'sub_scroll-confession': 'How fast do you rush?',
    'sub_waiting-game': 'Can you do nothing?',
    'sub_day-in-pixels': 'Time becomes shape',
    'sub_memory-maze': 'The pattern changes',
    'sub_decision-shadow': 'Your choices reveal patterns',
    'sub_anonymous-mirrors': 'Thoughts from strangers',
    'sub_whisper-wall': 'Anonymous truths float in the dark',
    'sub_collective-memory': 'Shared fragments of what we remember',
    'sub_shared-moments': 'How are you, honestly?',
    'sub_the-archive': 'Your complete journey history',
    'sub_mirror-of-all-selves': 'AI reveals your behavioral portrait',
    'sub_the-ending': 'A farewell you earned',
    
    // Meta Room Content - The Refusal
    'refusal_line1': 'This room refuses you.',
    'refusal_line2': 'Not everything opens when you want it to.',
    'refusal_line3': 'Sometimes the answer is simply: no.',
    'refusal_line4': "Try the button. It won't help.",
    'refusal_button': 'Enter Room',
    
    // Meta Room Content - Parallel You
    'parallel_choice_1': 'You chose speed.',
    'parallel_alternate_1': 'Another you chose patience.',
    'parallel_reflection_1': "They're still waiting.",
    'parallel_choice_2': 'You left early.',
    'parallel_alternate_2': 'Another you stayed until the end.',
    'parallel_reflection_2': 'They saw what you missed.',
    'parallel_choice_3': 'You avoided the hard question.',
    'parallel_alternate_3': 'Another you answered it.',
    'parallel_reflection_3': 'They carry that truth now.',
    'parallel_choice_4': 'You rushed through.',
    'parallel_alternate_4': 'Another you savored each moment.',
    'parallel_reflection_4': 'They remember more.',
    'parallel_choice_5': 'You kept scrolling.',
    'parallel_alternate_5': 'Another you stopped.',
    'parallel_reflection_5': 'They found something.',
    'parallel_choice_6': 'You chose comfort.',
    'parallel_alternate_6': 'Another you chose discomfort.',
    'parallel_reflection_6': 'They grew from it.',
    'parallel_quote': 'Every moment splits into infinite paths. You only walk one.',
    
    // Meta Room Content - Your Shadow
    'shadow_generated': 'Generated from your behavior',
    'shadow_intro': 'Your shadow...',
    'shadow_follows': 'This shadow follows you through every room.',
    'shadow_quote': 'We are not what we think. We are what we do.',
    'shadow_trait_completion': 'seeks completion',
    'shadow_trait_surface': 'prefers the surface',
    'shadow_trait_lingers': 'lingers in spaces',
    'shadow_trait_moves_quickly': 'moves quickly',
    'shadow_trait_avoids_depth': 'avoids depth',
    'shadow_trait_stillness': 'embraces stillness',
    'shadow_trait_chases': 'chases something unnamed',
    'shadow_trait_darkness': 'drawn to darkness',
    'shadow_trait_emptiness': 'comfortable with emptiness',
    'shadow_trait_undefined': 'undefined',
    'shadow_trait_forming': 'still forming',
    
    // Translation Navigator
    'translation_navigator': 'Translation Navigator',
    'translation_coverage': 'Room translation coverage by language',
    'keys': 'keys',
    'room_translations': 'Room Translations',
    'show_all': 'Show All',
    'show_missing': 'Show Missing',
    'all_translated': 'All rooms are translated!',
    'missing_translations': 'missing translations',
    
    // New Phase 1-3 Rooms
    'room_letters-never-sent': 'Letters Never Sent',
    'sub_letters-never-sent': 'Write to someone who will never read it',
    'room_breath-sync': 'Breath Sync',
    'sub_breath-sync': 'Breathe with the room',
    'room_entropy-garden': 'Entropy Garden',
    'sub_entropy-garden': 'Growth and decay intertwined',
    'room_memory-palace': 'Memory Palace',
    'sub_memory-palace': 'Build your mental sanctuary',
    'room_morning-ritual': 'Morning Ritual',
    'sub_morning-ritual': 'Start your day with intention',
    'room_evening-ritual': 'Evening Ritual',
    'sub_evening-ritual': 'Close the day with reflection',
    'room_weekly-ritual': 'Weekly Ritual',
    'sub_weekly-ritual': 'Sundays are for looking back',
    
    // Letters Never Sent Room
    'letters_title': 'Letters Never Sent',
    'letters_subtitle': 'Write to someone who will never read it. The AI will respond as they might.',
    'letters_select_recipient': 'Who are you writing to?',
    'letters_past_self': 'My Past Self',
    'letters_future_self': 'My Future Self',
    'letters_someone_lost': 'Someone I Lost',
    'letters_someone_wronged': 'Someone I Wronged',
    'letters_placeholder': 'Dear...',
    'letters_send': 'Send into the Void',
    'letters_sending': 'Sending...',
    'letters_response': 'A response emerges...',
    'letters_write_another': 'Write Another Letter',
    'letters_quote': 'Some words are meant to be written but never sent.',
    
    // Breath Sync Room
    'breath_title': 'Breath Sync',
    'breath_subtitle': 'Match your breath to the rhythm of the room.',
    'breath_inhale': 'Inhale',
    'breath_hold': 'Hold',
    'breath_exhale': 'Exhale',
    'breath_cycles': 'Cycles completed',
    'breath_quote': 'The breath is the bridge between body and mind.',
    
    // Entropy Garden Room
    'entropy_title': 'Entropy Garden',
    'entropy_subtitle': 'Your plants grow with each visit, but decay when you stay away.',
    'entropy_plant': 'Plant a Seed',
    'entropy_water': 'Water',
    'entropy_days_alive': 'days alive',
    'entropy_health': 'Health',
    'entropy_empty': 'Your garden is empty. Plant something.',
    'entropy_quote': 'Everything grows. Everything fades. That is the way.',
    
    // Memory Palace Room
    'memory_palace_title': 'Memory Palace',
    'memory_palace_subtitle': 'Build a mental sanctuary. Place objects in rooms you create.',
    'memory_palace_add_room': 'Add Room',
    'memory_palace_add_object': 'Add Object',
    'memory_palace_room_name': 'Room name...',
    'memory_palace_object_name': 'Object name...',
    'memory_palace_empty': 'Your palace is empty. Create your first room.',
    'memory_palace_quote': 'We build our minds one room at a time.',
    
    // Ritual Rooms
    'ritual_locked': 'This ritual is not yet available',
    'morning_ritual_title': 'Morning Ritual',
    'morning_ritual_intro': 'A moment to set intentions for the day ahead.',
    'morning_ritual_available': 'Available 6 AM - 9 AM',
    'morning_ritual_prompt_1': 'What do you want to carry with you today?',
    'morning_ritual_prompt_2': 'What will you let go of today?',
    'morning_ritual_prompt_3': 'What is one thing you can do for yourself today?',
    'evening_ritual_title': 'Evening Ritual',
    'evening_ritual_intro': 'A moment to reflect on the day that was.',
    'evening_ritual_available': 'Available 8 PM - 11 PM',
    'evening_ritual_prompt_1': 'What are you grateful for today?',
    'evening_ritual_prompt_2': 'What challenged you today?',
    'evening_ritual_prompt_3': 'What will you release before sleep?',
    'weekly_ritual_title': 'Weekly Ritual',
    'weekly_ritual_intro': 'A moment to reflect on the week that was, and set intentions for the one ahead.',
    'weekly_locked': 'The Weekly Ritual awaits you',
    'weekly_time': 'Return on Sunday',
    'today_is': 'Today is',
    'your_week': 'Your Week',
    'days_active': 'Days Active',
    'weekly_summary_note': 'Now, let us reflect more deeply.',
    'weekly_highlight': 'What was the highlight of your week?',
    'weekly_challenge': 'What challenged you most this week?',
    'weekly_growth': 'How did you grow this week?',
    'weekly_intention': 'What intention do you set for next week?',
    'weekly_placeholder': 'Take your time...',
    'reflect': 'Reflect',
    'reflecting': 'Reflecting...',
    'complete': 'Complete',
    'weekly_quote': 'A week ends. Another begins. You carry what matters.',
    
    // Personal Timeline
    'personal_timeline': 'Personal Timeline',
    'timeline_subtitle': 'Your journey through the rooms',
    'timeline_empty': 'Your timeline is empty. Start exploring.',
    'timeline_today': 'Today',
    'timeline_yesterday': 'Yesterday',
    'timeline_this_week': 'This Week',
    'timeline_earlier': 'Earlier',
    
    // Weather System
    'weather_clear': 'Clear',
    'weather_rain': 'Rain',
    'weather_fog': 'Fog',
    'weather_snow': 'Snow',
    
    // Notifications
    'notifications': 'Notifications',
    'notifications_desc': 'Get notified about time-locked content',
    'notify_time_locked': 'Time-locked rooms',
    'notify_full_moon': 'Full moon nights',
    'notify_daily_reset': 'Daily challenge resets',
    'notify_time_capsule': 'Time capsule unlocks',
    'notifications_enabled': 'Notifications enabled',
    'notifications_disabled': 'Notifications disabled',
    'notification_permission_denied': 'Notification permission denied',
    
    // Community Submissions
    'community_submissions': 'Community Submissions',
    'community_subtitle': 'Submit your room ideas',
    'community_room_title': 'Room Title',
    'community_room_description': 'Room Description',
    'community_room_category': 'Category',
    'community_submit': 'Submit Idea',
    'community_submitted': 'Thank you for your submission!',
    'community_your_submissions': 'Your Submissions',
    'community_empty': 'No submissions yet. Be the first!',
    
    // Proactive Guide
    'guide_hesitation': 'Taking your time? That\'s okay. Would you like a suggestion?',
    'guide_long_absence': 'Welcome back. It\'s been a while.',
    'guide_milestone': 'You\'ve come far. Would you like to reflect on your journey?',
    'guide_pattern': 'I notice you tend to visit certain rooms. Shall I suggest something different?',
    'guide_dismiss': 'Dismiss',
    'guide_accept': 'Show me',
    
    // Voice Mode
    'voice_mode': 'Voice Mode',
    'voice_mode_desc': 'Let the guide speak to you',
    'voice_mode_enabled': 'Voice mode enabled',
    'voice_mode_disabled': 'Voice mode disabled',
    
    // Offline Support
    'offline_available': 'Available offline',
    'offline_cached': 'This room is cached for offline viewing',
    'offline_not_available': 'Not available offline',
    
    // Journey Export
    'journey_export': 'Export Journey',
    'journey_import': 'Import Journey',
    'journey_export_json': 'Export as JSON',
    'journey_import_json': 'Import from JSON',
    'journey_exported': 'Journey exported successfully',
    'journey_imported': 'Journey imported successfully',
    'journey_import_error': 'Failed to import journey',
    
    // Phase 4 - Seasonal Rooms
    'room_spring-equinox-room': 'Spring Equinox',
    'sub_spring-equinox-room': 'Release and renew',
    'room_summer-solstice-room': 'Summer Solstice',
    'sub_summer-solstice-room': 'The longest day',
    'room_autumn-equinox-room': 'Autumn Equinox',
    'sub_autumn-equinox-room': 'Harvest and accept',
    'room_valentine-room': 'Self-Love Room',
    'sub_valentine-room': 'The love you deserve',
    
    // Spring Equinox Room
    'spring_date': 'MARCH 20TH',
    'spring_title': 'Spring Equinox',
    'spring_message_1': 'Day and night are equal.',
    'spring_message_2': 'A moment of perfect balance.',
    'spring_message_3': 'What will you release to make room for growth?',
    'spring_letting_go': 'I am letting go of:',
    'spring_letting_go_placeholder': 'What no longer serves you...',
    'spring_planting': 'I am planting:',
    'spring_planting_placeholder': 'What you wish to grow...',
    'spring_bloom': 'Let it bloom',
    'spring_released': 'Released:',
    'spring_planted': 'Planted:',
    'spring_closing': 'From this moment, new life begins.',
    'spring_quote': 'Nature does not hurry, yet everything is accomplished.',
    
    // Summer Solstice Room
    'summer_date': 'JUNE 21ST',
    'summer_title': 'The Longest Day',
    'summer_message_1': 'This is the longest day of the year.',
    'summer_message_2': 'Light reaches its peak.',
    'summer_message_3': 'What would you do with more time?',
    'summer_message_4': 'Describe your fullest, most vibrant day.',
    'summer_placeholder': 'On my fullest day, I would...',
    'summer_submit': 'Embrace the light',
    'summer_closing': 'The sun has heard you. Now it begins its descent.',
    'summer_quote': 'At the peak of light, we discover what we truly want to illuminate.',
    
    // Autumn Equinox Room
    'autumn_date': 'SEPTEMBER 22ND',
    'autumn_title': 'Autumn Equinox',
    'autumn_message_1': 'Balance returns.',
    'autumn_message_2': 'The harvest is ready.',
    'autumn_message_3': 'What have you grown? What can you accept?',
    'autumn_harvesting': 'I am harvesting:',
    'autumn_harvesting_placeholder': 'What you have grown this year...',
    'autumn_accepting': 'I am accepting:',
    'autumn_accepting_placeholder': 'What you cannot change...',
    'autumn_release': 'Let the leaves fall',
    'autumn_harvested': 'Harvested:',
    'autumn_accepted': 'Accepted:',
    'autumn_closing': 'Everything falls in its own time.',
    'autumn_quote': 'Autumn shows us how beautiful it is to let things go.',
    
    // Valentine's Day Room (Self-Love)
    'valentine_date': 'FEBRUARY 14TH',
    'valentine_title': 'The Love You Deserve',
    'valentine_message_1': 'Today is not about others.',
    'valentine_message_2': 'It is about the one who is always with you.',
    'valentine_message_3': 'The one who hears every thought.',
    'valentine_message_4': 'Write yourself a love letter.',
    'valentine_prompt': 'A letter to yourself:',
    'valentine_placeholder': 'Dear me, I want you to know...',
    'valentine_submit': 'Send with love',
    'valentine_closing': 'You are worthy of the love you give to others.',
    'valentine_quote': 'You yourself deserve your love and affection.',
    
    // Update changelog
    'update_seasonal_rooms': 'Four new seasonal rooms: Spring Equinox, Summer Solstice, Autumn Equinox, and Valentine\'s Day',
    'update_fragment_trading': 'Fragment Trading system - sacrifice fragments to unlock time-locked rooms, preview horror rooms, or receive AI messages',
    'update_new_themes': 'Two new themes: Eclipse (pure darkness) and Dawn (warm gradients, 5-7 AM only)',
    'update_eclipse_room': 'Eclipse Shadows - speak into total darkness, reveal in light flashes',
    'update_dawn_room': 'Dawn Awakening - greet the first light with breathing exercises and intentions',
    
    // New themes
    'eclipse_theme_required': 'This room requires the Eclipse theme. The total darkness awaits.',
    'dawn_theme_required': 'This room requires the Dawn theme during the early hours.',
    'dawn_time_hint': 'Available 5-7 AM only',
    'change_theme': 'Change theme in Settings',
    
    // Eclipse Shadows Room
    'eclipse_shadows_title': 'Eclipse Shadows',
    'eclipse_shadows_intro': 'In complete darkness, secrets feel safer. Answer honestly—only brief flashes will illuminate your words.',
    'enter_darkness': 'Enter the darkness',
    'shadow_question_1': 'What do you hide from yourself?',
    'shadow_question_2': 'What truth would you speak if no one could hear?',
    'shadow_question_3': 'What haunts you at 3 AM?',
    'shadow_question_4': 'What would you do if fear didn\'t exist?',
    'shadow_question_5': 'What are you not ready to face?',
    'speak_into_dark': 'Speak into the dark...',
    'release': 'Release',
    'eclipse_reveal_prompt': 'Click each shadow to reveal what you confessed.',
    'hidden_in_shadow': '▓▓▓▓▓▓▓▓▓▓▓▓',
    'eclipse_reveal_hint': 'Some truths are clearer in the dark.',
    'eclipse_shadows_quote': 'In total darkness, we finally see ourselves.',
    
    // Dawn Awakening Room
    'dawn_awakening_title': 'Dawn Awakening',
    'dawn_awakening_intro': 'The first light carries possibility. Before the world wakes, set your intentions.',
    'greet_the_light': 'Greet the light',
    'breathe_in_light': 'Breathe in the light',
    'hold_warmth': 'Hold the warmth',
    'release_night': 'Release the night',
    'dawn_prompt_1': 'What do you want to carry with you today?',
    'dawn_prompt_2': 'What will you let go of?',
    'dawn_prompt_3': 'What small act of kindness will you offer yourself?',
    'set_your_intention': 'Set your intention...',
    'plant_seed': 'Plant this seed',
    'dawn_awakening_quote': 'Each dawn is an invitation to begin again.',
    
    // Fragment Trading
    'fragment_trading': 'Fragment Trading',
    'sacrifice_to_unlock': 'Sacrifice fragments to unlock secrets',
    'your_fragments': 'Your Fragments',
    'fragments_any': 'any fragments',
    'fragments_free': 'clarity fragments',
    'fragments_premium': 'depth fragments',
    'fragments_horror': 'shadow fragments',
    'trade_successful': 'Trade Successful!',
    'trade_failed': 'Trade Failed',
    'not_enough_fragments': 'Not enough fragments',
    'continue_trading': 'Continue Trading',
    'trading_fragments': 'Trading fragments...',
    'fragment_trade_warning': 'Fragments sacrificed cannot be recovered',
    'unlock-time-locked': 'Unlock Time-Locked Room',
    'unlock-time-locked_desc': 'Access a time-locked room without waiting',
    'preview-horror': 'Horror Room Preview',
    'preview-horror_desc': 'Peek into a horror room before entering',
    'ai-reflection-trade': 'AI Reflection',
    'ai-reflection-trade_desc': 'Receive a personalized message from the AI',
    'unlock-premium-preview': 'Premium Room Trial',
    'unlock-premium-preview_desc': 'Access a premium room for one visit',
    'fragment_trading_room': 'The Fragment Altar',
    'fragment_trading_intro': 'Here, you may sacrifice your collected fragments to unlock secrets hidden within these rooms.',
    'your_collection': 'Your Collection',
    'fragment_clarity': 'Clarity',
    'fragment_depth': 'Depth',
    'fragment_shadow': 'Shadow',
    'total_fragments': 'Total',
    'open_altar': 'Approach the Altar',
    'recent_trades': 'Recent Offerings',
    'fragment_trade_ending': 'What was given cannot be retrieved. But what was gained... that remains.',
    'room_fragment-trade': 'The Fragment Altar',
    'sub_fragment-trade': 'Sacrifice to unlock secrets',
    // Phase 8 - Anniversary & Milestones
    'update_anniversary_system': 'Anniversary & Milestone System - track your journey over time',
    'update_archive_room': 'The Archive - full journey history unlocked at 25 rooms',
    'update_mirror_room': 'Mirror of All Selves - AI behavioral portrait at 50 rooms',
    'update_ending_room': 'The Ending - a personalized farewell at 75 rooms',
    'room_the-archive': 'The Archive',
    'room_mirror-of-all-selves': 'Mirror of All Selves',
    'room_the-ending': 'The Ending',
    'archive_locked': 'Complete 25 rooms to unlock your journey archive.',
    'archive_subtitle': 'Your complete journey through The Human Rooms',
    'mirror_locked': 'Complete 50 rooms to see your reflection.',
    'mirror_subtitle': 'AI-generated portrait from your behavioral patterns',
    'mirror_look': 'Look into the mirror',
    'mirror_loading': 'The mirror is studying you...',
    'mirror_error': 'The mirror is clouded. Try again.',
    'mirror_portrait': 'Your Portrait',
    'mirror_step_away': 'Step away from the mirror',
    'ending_locked': 'Complete 75 rooms to reach the ending.',
    'ending_final': 'Thank you for being here.',
    'days_since_first': 'Days since first visit',
    'rooms_completed_count': 'Rooms completed',
    'archive_timeline': 'Timeline',
    'archive_stats': 'Stats',
    'archive_patterns': 'Patterns',
    'close_the_archive': 'Close the archive',
    'click_continue': 'click to continue',
    'return_to_hallway': 'Return to the hallway',
    // Phase 9 - Fragment Collection
    'update_fragment_rarities': 'Fragment Rarities - Common, Rare, and Legendary collectible fragments',
    'update_fragment_gallery': 'Fragment Gallery - visual display case for your collection',
    'update_seasonal_fragments': 'Seasonal and secret fragments - time-limited collectibles',
    'fragment_collection': 'Collection',
    'fragment_collection_title': 'Fragment Collection',
    'fragment_collection_subtitle': 'Every room leaves something behind. Collect them all.',
    'collection_milestones': 'Milestones',
    'collection_secrets': 'Secrets',
    'collection_footer': 'Some fragments only appear under certain conditions.',
    'rarity_common': 'Common',
    'rarity_rare': 'Rare',
    'rarity_legendary': 'Legendary',
    // Phase 10 - Accessibility
    'accessibility': 'Accessibility',
    'accessibility_desc': 'Customize readability and motion preferences',
    'font_family': 'Font Family',
    'font_default': 'Monospace',
    'font_dyslexia': 'Dyslexia-Friendly',
    'font_sans': 'Sans Serif',
    'font_changed': 'Font updated',
    'line_height': 'Line Height',
    'line_compact': 'Compact',
    'line_normal': 'Normal',
    'line_relaxed': 'Relaxed',
    'line_spacious': 'Spacious',
    'line_height_changed': 'Line height updated',
    'reduced_motion_full_desc': 'Disable all animations, parallax, and particle effects',
    'larger_cursors': 'Larger Targets',
    'larger_cursors_desc': 'Increase click and tap target sizes',
    'larger_cursors_enabled': 'Larger targets enabled',
    'larger_cursors_disabled': 'Larger targets disabled',
    'update_accessibility_suite': 'Accessibility Suite - dyslexia fonts, line height control, enhanced reduced motion',
    'update_dyslexia_font': 'Dyslexia-friendly font option in settings',
    'update_line_height': 'Adjustable line height for improved readability',
    'update_enhanced_reduced_motion': 'Enhanced reduced motion - completely disables all particle effects and animations',
    // Phase 11 - Social Whispers & Shared Moments
    'update_shared_moments': 'Shared Moments - anonymous emotional check-ins with others',
    'update_whisper_wall': 'The Whisper Wall - leave anonymous truths in the dark',
    'update_collective_memory': 'Collective Memory - shared fragments of what we remember',
    'whisper_prompt': 'Leave a whisper on the wall. Anonymous. Permanent.',
    'whisper_placeholder': 'What do you carry silently?',
    'whisper_send': 'Whisper',
    'whisper_added': 'Your whisper joins the wall.',
    'whisper_another': 'Whisper again',
    'collective_memory_subtitle': 'A shared space for fragments of memory',
    'collective_prompt_1': 'What sound reminds you of childhood?',
    'collective_prompt_2': 'Describe a place you can never return to.',
    'collective_prompt_3': 'What does home smell like?',
    'collective_prompt_4': 'What song plays in your earliest memory?',
    'collective_prompt_5': 'Describe the light in the room where you felt safest.',
    'collective_prompt_6': 'What does silence sound like to you?',
    'others_said': 'Others have said:',
    'collective_placeholder': 'Share your memory...',
    'share_memory': 'Share this memory',
    'memory_absorbed': 'Your memory has been absorbed into the collective...',
    'collective_complete_title': 'The collective remembers.',
    'collective_complete_desc': 'Your memories now exist alongside those of others who have visited this room.',
    'memories_stored': 'memories stored',
    'shared_moments_title': 'Right now, in this moment...',
    'shared_moments_subtitle': 'How are you, honestly?',
    'feeling_grateful': 'Grateful',
    'feeling_lost': 'Lost',
    'feeling_hopeful': 'Hopeful',
    'feeling_heavy': 'Heavy',
    'feeling_peaceful': 'Peaceful',
    'feeling_restless': 'Restless',
    'moments_shared': 'moments shared in this room',
    'moment_detail_placeholder': 'Say more, if you want to... (optional)',
    'back_short': 'Back',
    'share_moment': 'Share this moment',
    'moment_shared_title': 'Your moment has been shared.',
    'moment_shared_desc': "You're not alone in feeling this way.",
    'others_felt': 'Others who felt the same:',
    'room_whisper-wall': 'The Whisper Wall',
    'room_collective-memory': 'Collective Memory',
    'room_shared-moments': 'Shared Moments',

    // v3.21.0 - New Rooms
    'room_mask-you-wear': 'The Mask You Wear',
    'sub_mask-you-wear': 'What face do you show?',
    'room_recurring-dream': 'Your Recurring Dream',
    'sub_recurring-dream': 'The dream that keeps coming back',
    'room_promise-you-broke': 'The Promise You Broke',
    'sub_promise-you-broke': 'Honesty about broken promises',
    'room_words-you-swallowed': 'Words You Swallowed',
    'sub_words-you-swallowed': 'Things you never said',
    'room_age-you-feel': 'The Age You Feel',
    'sub_age-you-feel': 'How old are you really?',
    'room_biggest-lie': 'Your Biggest Lie',
    'sub_biggest-lie': 'The lie you tell most',
    'room_person-you-pretend-to-be': 'The Person You Pretend To Be',
    'sub_person-you-pretend-to-be': 'Your performance',
    'room_person-you-pretend': 'The Person You Pretend To Be',
    'sub_person-you-pretend': 'Your performance',
    'room_what-you-collect': 'What You Collect',
    'sub_what-you-collect': 'Objects reveal identity',
    'room_apology-you-owe': 'The Apology You Owe',
    'sub_apology-you-owe': 'Write what you should have said',
    'room_unfinished-story': 'Your Unfinished Story',
    'sub_unfinished-story': 'The thing you never completed',
    'room_version-others-see': 'The Version Others See',
    'sub_version-others-see': 'How others perceive you',
    'room_secret-talent': 'Your Secret Talent',
    'sub_secret-talent': 'What no one knows you can do',
    'room_fear-you-feed': 'The Fear You Feed',
    'sub_fear-you-feed': 'Which fear controls you',
    'room_guilty-pleasure': 'Your Guilty Pleasure',
    'sub_guilty-pleasure': 'What you hide enjoying',
    'room_rule-you-break': 'The Rule You Always Break',
    'sub_rule-you-break': 'Your pattern of rebellion',
    'room_childhood-promise': 'Your Childhood Promise',
    'sub_childhood-promise': 'What you promised yourself',
    'room_compliment-you-reject': 'The Compliment You Reject',
    'sub_compliment-you-reject': 'Why you can\'t accept praise',
    'room_autopilot-mode': 'Your Autopilot Mode',
    'sub_autopilot-mode': 'What you do without thinking',
    'room_boundary-you-need': 'The Boundary You Need',
    'sub_boundary-you-need': 'What you should protect',
    'room_inner-critic': 'Your Inner Critic',
    'sub_inner-critic': 'The voice that judges',
    'room_last-sunday': 'Last Sunday',
    'sub_last-sunday': 'What did you actually do?',
    'room_wasted-hours': 'Your Wasted Hours',
    'sub_wasted-hours': 'Where time disappears',
    'room_year-that-changed': 'The Year That Changed You',
    'sub_year-that-changed': 'The turning point',
    'room_tomorrows-regret': 'Tomorrow\'s Regret',
    'sub_tomorrows-regret': 'What will you wish you did?',
    'room_morning-autopilot': 'Your Morning Autopilot',
    'sub_morning-autopilot': 'The first hour ritual',
    'room_season-you-dread': 'The Season You Dread',
    'sub_season-you-dread': 'Which season weighs heaviest?',
    'room_ten-years-ago': 'Ten Years Ago Today',
    'sub_ten-years-ago': 'Who were you then?',
    'room_last-normal-day': 'Your Last Normal Day',
    'sub_last-normal-day': 'Before everything changed',
    'room_clock-you-ignore': 'The Clock You Ignore',
    'sub_clock-you-ignore': 'Time you pretend doesn\'t pass',
    'room_your-deadline': 'Your Deadline',
    'sub_your-deadline': 'What are you postponing?',
    'room_the-pause': 'The Pause',
    'sub_the-pause': 'Just breathe for a moment',
    'room_static-room': 'Static',
    'sub_static-room': 'White noise and stillness',
    'room_gap-between-thoughts': 'The Gap Between Thoughts',
    'sub_gap-between-thoughts': 'Find the space',
    'room_sit-with-discomfort': 'Sit With Discomfort',
    'sub_sit-with-discomfort': 'Don\'t fix anything',
    'room_boring-room': 'The Boring Room',
    'sub_boring-room': 'Nothing interesting at all',
    'room_unplug-room': 'Unplug',
    'sub_unplug-room': 'Imagine no devices',
    'room_unplug': 'Unplug',
    'sub_unplug': 'Imagine no devices',
    'room_last-page': 'The Last Page',
    'sub_last-page': 'The story ends here',
    'room_scrolling-pattern': 'Your Scrolling Pattern',
    'sub_scrolling-pattern': 'How you consume content',
    'room_tab-you-keep-open': 'The Tab You Keep Open',
    'sub_tab-you-keep-open': 'What stays in your browser',
    'room_digital-shadow': 'Your Digital Shadow',
    'sub_digital-shadow': 'Your online footprint',
    'room_notification-you-ignore': 'The Notification You Ignore',
    'sub_notification-you-ignore': 'What you dismiss',
    'room_typing-speed': 'Your Typing Speed',
    'sub_typing-speed': 'How fast do you express?',
    'room_link-you-almost-clicked': 'The Link You Almost Clicked',
    'sub_link-you-almost-clicked': 'Hesitation patterns',
    'room_bookmark-graveyard': 'Your Bookmark Graveyard',
    'sub_bookmark-graveyard': 'Saved but never returned',
    'room_search-you-deleted': 'The Search You Deleted',
    'sub_search-you-deleted': 'What you looked for and erased',
    'room_gravity-room': 'Gravity Room',
    'sub_gravity-room': 'Everything falls slowly',
    'room_ink-blot': 'Ink Blot',
    'sub_ink-blot': 'What do you see?',
    'room_hallway-of-mirrors': 'The Hallway of Mirrors',
    'sub_hallway-of-mirrors': 'Infinite reflections',
    'room_dissolving-text': 'Dissolving Text',
    'sub_dissolving-text': 'Words melt as you read',
    'room_the-pendulum': 'The Pendulum',
    'sub_the-pendulum': 'Hypnotic motion',
    'room_pixel-rain': 'Pixel Rain',
    'sub_pixel-rain': 'Digital precipitation',
    'room_the-threshold': 'The Threshold',
    'sub_the-threshold': 'Between here and there',
    'most_loved_rooms': 'Most Loved Rooms',
    'rate_room': 'Rate this room',
    'update_52_new_rooms': '52 new rooms across Identity, Time, Contemplation, Meta, and Experimental categories',
    'update_room_rating': 'Room Rating system - thumbs up/down feedback with Most Loved Rooms section',
    'update_bug_fix_reflections': 'Fixed anonymous reflections display and storage',
    'update_100_rooms': 'Over 100 rooms now available in the Hallway',
    // v3.22.0
    'room_the-friend-you-lost': 'The Friend You Lost',
    'sub_the-friend-you-lost': 'Write to someone you drifted from.',
    'room_conversation-you-replay': 'The Conversation You Replay',
    'sub_conversation-you-replay': 'Recreate it from memory.',
    'room_the-thank-you': 'The Thank You',
    'sub_the-thank-you': 'Gratitude you never expressed.',
    'room_people-you-forgot': 'People You Forgot',
    'sub_people-you-forgot': 'Try to remember forgotten names.',
    'room_the-advice-you-give': 'The Advice You Give',
    'sub_the-advice-you-give': 'Advice you give but never follow.',
    'room_what-others-dont-know': "What Others Don't Know",
    'sub_what-others-dont-know': 'Your hidden layers.',
    'room_the-lie-you-believe': 'The Lie You Believe',
    'sub_the-lie-you-believe': 'A comforting untruth.',
    'room_your-shadow-self': 'Your Shadow Self',
    'sub_your-shadow-self': 'The part you suppress.',
    'room_the-story-you-tell': 'The Story You Tell',
    'sub_the-story-you-tell': 'How you narrate your life.',
    'room_your-safe-place': 'Your Safe Place',
    'sub_your-safe-place': 'Describe where you feel safe.',
    'room_the-risk-you-didnt-take': "The Risk You Didn't Take",
    'sub_the-risk-you-didnt-take': 'Roads not traveled.',
    'room_your-last-excuse': 'Your Last Excuse',
    'sub_your-last-excuse': 'The excuse you use most.',
    'room_the-habit-you-hide': 'The Habit You Hide',
    'sub_the-habit-you-hide': 'Secret routines.',
    'room_what-you-postpone': 'What You Postpone',
    'sub_what-you-postpone': 'The thing always "tomorrow".',
    'room_the-door-you-closed': 'The Door You Closed',
    'sub_the-door-you-closed': 'Opportunities you rejected.',
    'journal_title': 'Reflection Journal',
    'journal_subtitle': 'Your thoughts across rooms',
    'journal_empty': 'No reflections yet. Visit rooms and write your thoughts.',
    'journal': 'Journal',
    'room_chains': 'Room Chains',
    'chain_identity_journey': 'Identity Journey',
    'chain_time_reflection': 'Time Reflection',
    'chain_truth_path': 'Truth Path',
    'recently_visited': 'Recently Visited',
    'update_reflection_journal': 'Reflection Journal - save your thoughts across rooms',
    'update_room_chains': 'Room Chains - complete rooms in sequence for bonus fragments',
    'update_15_new_rooms': '15 new interactive rooms across 3 new categories',
    'update_recently_visited': 'Recently Visited section for quick room re-entry',
    'ai_companion': 'Companion',
    'companion_create': 'Create Your Companion',
    'companion_setup_desc': 'A reflection that walks beside you.',
    'companion_name': 'Name your companion',
    'companion_name_placeholder': 'e.g., Echo, Mirror, Shadow...',
    'companion_lens': 'Personality lens',
    'companion_mood': 'Communication mood',
    'companion_fears': 'What are you afraid of? (optional, comma-separated)',
    'companion_struggles': 'What do you struggle with? (optional, comma-separated)',
    'create_companion': 'Create',
    'companion_input': 'Say something...',
    'companion_greeting': 'Your companion is here. Say something.',
    'companion_starter_1': "What's on my mind today?",
    'companion_starter_2': 'I feel stuck.',
    'companion_starter_3': 'Tell me something I avoid.',
    'fears_placeholder': 'e.g., failure, rejection, being forgotten...',
    'struggles_placeholder': 'e.g., overthinking, procrastination, loneliness...',
    'back_step': 'Back',
    'lens_reflective': 'Reflective',
    'lens_reflective_desc': 'Deep questions, pattern recognition',
    'lens_supportive': 'Supportive',
    'lens_supportive_desc': 'Warm, encouraging, validating',
    'lens_challenging': 'Challenging',
    'lens_challenging_desc': 'Direct, thought-provoking',
    'lens_curious': 'Curious',
    'lens_curious_desc': 'Unexpected questions, wonder',
    'lens_shadow': 'Shadow',
    'lens_shadow_desc': 'The hidden side, honest discomfort',
    'companion_mood_calm': 'Calm',
    'companion_mood_calm_desc': 'Measured and peaceful',
    'companion_mood_intense': 'Intense',
    'companion_mood_intense_desc': 'Urgent and deep',
    'companion_mood_playful': 'Playful',
    'companion_mood_playful_desc': 'Witty and light',
    'companion_mood_melancholic': 'Melancholic',
    'companion_mood_melancholic_desc': 'Beautiful sadness',
    'update_ai_companion': 'AI Companion - a personal AI that remembers your journey',
  },
  
  // Arabic translations
  ar: {
    // Index
    'title': 'الغرف البشرية',
    'tagline': 'مجموعة من الغرف تعكس من أنت عندما لا يراقبك أحد.',
    'enter': 'ادخل',
    'footer': 'ليست علاجاً. ليست شبكة اجتماعية. ليست منتجة.',
    
    // Hallway
    'back': 'رجوع ←',
    'hallway': 'الممر',
    'challenges': 'التحديات',
    'random': 'عشوائي',
    'open_rooms': 'الغرف المفتوحة',
    'dark_rooms': 'الغرف المظلمة',
    'enter_risk': 'ادخل على مسؤوليتك',
    'premium_rooms': 'الغرف المميزة',
    'unlock_to_access': 'افتح للوصول',
    'daily_rooms': 'الغرف اليومية',
    'refreshes_daily': 'تتجدد يومياً',
    'time_locked': 'غرف مقفلة بالوقت',
    'seasonal_rooms': 'الغرف الموسمية',
    'available_now': 'متاحة الآن',
    'fragments': 'الشظايا',
    'boss_unlocked': 'غرفة الزعيم مفتوحة',
    'collect_unlock': 'اجمع لفتح غرفة الزعيم',
    'dark_fragments': 'الشظايا المظلمة',
    'dark_boss_unlocked': 'زعيم الظلام مفتوح',
    'hidden_discovered': 'تم اكتشاف غرفة مخفية',
    'stillness_revealed': 'سكونك كشف شيئاً...',
    'constellation_map': 'خريطة النجوم',
    'your_stats': 'إحصائياتك',
    'completed': 'مكتملة',
    'game_rooms': 'غرف الألعاب',
    'experimental_rooms': 'الغرف التجريبية',
    'deep_rooms': 'الغرف العميقة',
    'meta_rooms': 'الغرف الميتا',
    'search_rooms': 'ابحث عن غرفة...',
    'no_results': 'لا توجد غرف تطابق بحثك',
    'clear_filters': 'مسح الفلاتر',
    'explore': 'استكشف',
    'all_categories': 'الكل',
    
    // About
    'what_is_this': 'ما هذا المكان؟',
    'not_productivity': 'الغرف البشرية ليست أداة إنتاجية.',
    'not_social': 'ليست شبكة اجتماعية.',
    'not_therapy': 'ليست علاجاً.',
    'pause_experiences': 'هي مجموعة من التجارب الصغيرة المصممة لتجعلك تتوقف.',
    'each_room': 'كل غرفة تستغرق من 30 ثانية إلى دقيقتين.',
    'make_think': 'بعض الغرف ستجعلك تفكر.',
    'make_uncomfortable': 'بعضها سيجعلك غير مرتاح.',
    'make_calm': 'بعضها سيجعلك هادئاً.',
    'make_aware': 'كلها ستجعلك واعياً.',
    'no_notifications': 'لا توجد إشعارات هنا.',
    'no_points': 'لا نقاط للجمع.',
    'no_validation': 'لا تحقق اجتماعي.',
    'just_truth': 'فقط أنت، غرفة، والحقيقة.',
    'enter_rooms': 'ادخل الغرف',
    
    // Common
    'loading': 'جاري التحميل...',
    'error': 'خطأ',
    'continue': 'استمر',
    'submit': 'إرسال',
    'cancel': 'إلغاء',
    'close': 'إغلاق',
    'next': 'التالي',
    'previous': 'السابق',
    'start': 'ابدأ',
    'stop': 'توقف',
    'yes': 'نعم',
    'no': 'لا',
    'leave_room': 'غادر الغرفة ←',
    'return': 'عودة ←',
    'return_hallway': 'العودة للممر',
    'escape': 'هروب ←',
    'run': 'اركض ←',
    'leave': 'غادر ←',
    'begin': 'ابدأ',
    'watch': 'شاهد...',
    'correct': 'صحيح',
    'round': 'الجولة',
    'enter_again': 'ادخل مجدداً',
    'steps': 'خطوات',
    'depth': 'العمق',
    
    // Keyboard Shortcuts
    'keyboard_shortcuts': 'اختصارات لوحة المفاتيح',
    'shortcut_hallway': 'الذهاب للممر',
    'shortcut_random': 'غرفة عشوائية',
    'shortcut_back': 'العودة',
    'shortcut_bookmark': 'تبديل الإشارة المرجعية',
    'shortcut_reading': 'تبديل وضع القراءة',
    'shortcut_search': 'التركيز على البحث',
    'shortcut_stats': 'عرض الإحصائيات',
    'shortcut_achievements': 'عرض الإنجازات',
    'shortcut_cat_nav': 'التنقل',
    'shortcut_cat_rooms': 'الغرف',
    'shortcut_cat_access': 'إمكانية الوصول',
    'press_to_close': 'اضغط',
    'to_close': 'للإغلاق',
    'or': 'أو',
    
    // Bookmarks
    'add_bookmark': 'إضافة إشارة مرجعية',
    'remove_bookmark': 'إزالة الإشارة المرجعية',
    'bookmarks': 'الإشارات المرجعية',
    'bookmarked_rooms': 'المفضلة',
    'no_bookmarks': 'لا توجد إشارات مرجعية بعد',
    
    // Reading Mode
    'reading_mode': 'وضع القراءة',
    'reading_mode_on': 'تم تفعيل وضع القراءة',
    'reading_mode_off': 'تم إيقاف وضع القراءة',
    
    // Settings Page
    'settings': 'الإعدادات',
    'settings_subtitle': 'خصص رحلتك',
    'language': 'اللغة',
    'ambient_audio': 'الصوت المحيطي',
    'ambient_audio_desc': 'أصوات خلفية للغرف',
    'reduced_motion': 'تقليل الحركة',
    'reduced_motion_desc': 'تقليل الرسوم المتحركة',
    'data_management': 'إدارة البيانات',
    'export_journey': 'تصدير بيانات الرحلة',
    'clear_all_data': 'مسح جميع البيانات',
    'audio_enabled': 'تم تفعيل الصوت',
    'audio_disabled': 'تم إيقاف الصوت',
    'data_exported': 'تم تصدير البيانات',
    'clear_data_confirm': 'هل أنت متأكد؟ سيتم مسح كل تقدمك.',
    'data_cleared': 'تم مسح البيانات',
    // Theme settings
    'theme': 'السمة',
    'theme_dark': 'داكن',
    'theme_dark_desc': 'ظلام مطلق',
    'theme_bone': 'عظم',
    'theme_bone_desc': 'شحوب الموت',
    'theme_ash': 'رماد',
    'theme_ash_desc': 'ما بعد النار',
    'theme_fog': 'ضباب',
    'theme_fog_desc': 'غموض أثيري',
    'theme_twilight': 'شفق',
    'theme_twilight_desc': 'بين النهار والليل',
    'theme_eclipse': 'كسوف',
    'theme_eclipse_desc': 'الضوء تلتهمه الظلمة',
    'theme_dawn': 'فجر',
    'theme_dawn_desc': 'أول ضوء اليقظة',
    'theme_changed': 'تم تحديث السمة',
    'font_size': 'حجم الخط',
    'font_small': 'صغير',
    'font_medium': 'متوسط',
    'font_large': 'كبير',
    'font_size_changed': 'تم تحديث حجم الخط',
    'high_contrast': 'تباين عالي',
    'high_contrast_desc': 'زيادة تباين النص والحدود',
    'high_contrast_enabled': 'تم تفعيل التباين العالي',
    'high_contrast_disabled': 'تم إيقاف التباين العالي',
    
    // Ambient Music Control
    'ambient_music': 'موسيقى محيطة',
    'ambient_music_desc': 'التحكم في المقاطع الصوتية الخلفية',
    'volume': 'مستوى الصوت',
    'ambient_track': 'مسار صوتي',
    'playing': 'قيد التشغيل',
    'play': 'تشغيل',
    'ambient_default': 'هادئ',
    'ambient_night': 'ليلي',
    'ambient_scary': 'مخيف',
    'ambient_truth': 'حقيقة',
    'ambient_identity': 'تأمل',
    'ambient_mortality': 'زمن',
    'ambient_nothing': 'فراغ',
    'ambient_meta': 'رقمي',
    'ambient_daily': 'دافئ',
    'ambient_seasonal': 'سحري',
    'ambient_premium': 'عميق',
    
    'shared_room': 'الغرفة المشتركة',
    'imagine_room': 'تخيل',
    'imagine_title': 'تخيل نفسك',
    'imagine_subtitle': 'صف كيف ترى نفسك. دع الغرفة تظهر لك ما ينبثق من كلماتك.',
    'imagine_placeholder': 'أرى نفسي كشخص...\n\nصف جوهرك، طبيعتك، كيف تشعر من الداخل.',
    'imagine_hint': 'كن صادقاً. الصورة تعكس كلماتك.',
    'imagine_generate': 'اكتشف نفسك',
    'imagine_generating': 'جاري الإنشاء...',
    'imagine_try_again': 'صف مجدداً',
    'imagine_quote': 'لا نرى أنفسنا بوضوح. نرى ما نعتقده.',
    'room_imagine-yourself': 'تخيل نفسك',
    'sub_imagine-yourself': 'صف نفسك. انظر ما ينبثق.',
    'dream_room': 'الأحلام',
    'future_room': 'المستقبل',
    'dream_title': 'تصور الحلم',
    'dream_subtitle': 'صف حلماً متكرراً. شاهده يتجسد أمامك.',
    'dream_placeholder': 'أحلم دائماً بـ...\n\nصف الصور، المشاعر، العناصر المتكررة.',
    'dream_hint': 'الأحلام تتحدث بالرموز.',
    'dream_generate': 'تصور الحلم',
    'dream_generating': 'أحلم...',
    'dream_try_again': 'احلم مجدداً',
    'dream_quote': 'الأحلام هي محاولة العقل الباطن لإخبارنا بشيء.',
    'dream_empty_error': 'صف حلمك أولاً',
    'dream_short_error': 'أخبرنا المزيد عن حلمك',
    'room_dream-visualization': 'تصور الحلم',
    'future_title': 'الذات المستقبلية',
    'future_subtitle': 'صف من تريد أن تصبح. انظر إلى إمكانياتك.',
    'future_placeholder': 'في المستقبل، أرى نفسي كـ...\n\nصف الشخص الذي تسعى لتصبحه.',
    'future_hint': 'الطموح يشكل الواقع.',
    'future_generate': 'انظر مستقبلك',
    'future_generating': 'أتخيل...',
    'future_try_again': 'تخيل مجدداً',
    'future_quote': 'المستقبل ليس شيئاً ندخله. إنه شيء نصنعه.',
    'future_empty_error': 'صف ذاتك المستقبلية أولاً',
    'future_short_error': 'أخبرنا المزيد عن ذاتك المستقبلية',
    'room_future-self': 'الذات المستقبلية',
    
    // Updates Page
    'updates': 'التحديثات',
    'updates_title': 'ما الجديد',
    'updates_subtitle': 'التغييرات والتحسينات الأخيرة',
    'updates_footer': 'نبني الغرف، تحديث تلو الآخر.',
    'new': 'جديد',
    'subscribe_rss': 'اشترك عبر RSS',
    'latest': 'الأحدث',
    'whats_new_intro': 'آخر التحديثات:',
    'view_all_updates': 'عرض جميع التحديثات',
    'update_updates_page': 'إضافة صفحة التحديثات',
    'update_full_i18n': 'ترجمة كاملة لـ 6 لغات لجميع الغرف',
    'update_hallway_animations': 'رسوم متحركة سلسة للتبديل بين الفئات',
    'update_daily_challenges': 'تحسين عرض التحديات اليومية',
    'update_game_rooms': 'إضافة 15 غرفة ألعاب جديدة',
    'update_experimental_rooms': 'إضافة 14 غرفة تجريبية',
    'update_random_room': 'زر الغرفة العشوائية',
    'update_stats_route': 'إصلاح مسار صفحة الإحصائيات',
    'update_streak_system': 'نظام تتبع الخطوط اليومية',
    'update_weekly_summary': 'ملخصات التقدم الأسبوعية',
    'update_rtl_support': 'دعم اللغات من اليمين لليسار',
    'update_dark_rooms': 'فئة غرف مظلمة/مرعبة جديدة',
    'update_boss_rooms': 'غرف الزعيم مع متطلبات الشظايا',
    'update_audio_system': 'صوت محيطي للغرف',
    'update_constellation_map': 'خريطة النجوم المرئية للرحلة',
    'update_fragment_system': 'جمع شظايا إكمال الغرف',
    'update_mobile_layout': 'تحسينات التوافق مع الجوال',
    'update_multi_language': 'دعم متعدد اللغات (6 لغات)',
    'update_premium_system': 'الغرف المميزة ونظام الفتح',
    'update_room_endings': 'اقتباسات نهاية فريدة للغرف',
    'update_ui_redesign': 'تجديد كامل لتصميم الواجهة',
    'update_theme_effects': 'تأثيرات بصرية خاصة بالسمات',
    'update_void_theme': 'سمة الفراغ الجديدة',
    'update_achievements': 'نظام الإنجازات',
    'update_ai_reflection': 'غرفة التأمل بالذكاء الاصطناعي',
    'update_settings_redesign': 'إعادة تصميم الإعدادات',
    'update_theme_exclusive_rooms': 'غرف حصرية للسمات',
    'update_discovery_localization': 'ترجمة رسائل الاكتشاف',
    'update_site_mood_system': 'نظام مزاج الموقع',
    'update_voice_imperfection': 'غرف صوت غير مثالية',
    'update_mobile_optimization': 'تحسينات الجوال',
    'update_interaction_clues': 'تلميحات التفاعل الدقيقة',
    'update_theme_exclusive_hallway': 'غرف حصرية للسمات في الممر',
    'update_advanced_easter_eggs': 'بيض عيد الفصح المتقدم',
    'update_hidden_discoveries': 'اكتشافات النص المخفي',
    'update_dual_mode_system': 'نظام الوضع المزدوج',
    'update_alternate_mode': 'الوضع البديل - واقع موازي تجريبي',
    'update_hidden_discovery': 'مشغلات الاكتشاف المخفية',
    'update_mode_effects': 'تأثيرات بصرية خاصة بالوضع',
    'update_translation_navigator': 'متصفح الترجمة - لوحة تغطية مرئية',
    'update_full_localization': 'تغطية ترجمة كاملة تقريباً لـ 6 لغات',
    'update_meta_room_translations': 'محتوى الغرف الميتا مترجم',
    'update_animated_timeline': 'جدول زمني متحرك للتحديثات مع رسوم متحركة',
    'update_toast_notifications': 'إشعار منبثق للتحديثات عند الزيارة الأولى',
    'update_rss_feed': 'خلاصة RSS للاشتراك في التحديثات',
    'update_bookmarked_filter': 'علامة تبويب الغرف المفضلة في الممر',
    'update_keyboard_shortcuts': 'اختصارات لوحة المفاتيح (اضغط ؟ للعرض)',
    'update_reading_mode': 'وضع القراءة للتركيز وتحسين الخط',
    'update_accessibility': 'تحسين مؤشرات التركيز والتنقل بلوحة المفاتيح',
    'update_ambient_crossfade': 'انتقالات سلسة بين المقاطع الصوتية المحيطة',
    'update_settings_audio': 'عناصر تحكم الصوت المركزية في صفحة الإعدادات',
    'update_arabic_complete': 'تغطية ترجمة عربية كاملة',
    'update_duplicate_icon_fix': 'إصلاح أيقونات الصوت المكررة في رأس الممر',

    // Easter Egg Discoveries
    'discovery_label': 'اكتشاف',
    'discovery_konami': 'أنت تعرف الطرق القديمة.',
    'discovery_secret_sequence': 'كتبت الحقيقة.',
    'discovery_long_press': 'الصبر يكشف الأسرار.',
    'discovery_triple_click': 'المثابرة تؤتي ثمارها.',
    'discovery_silence': 'السكون يتحدث.',
    
    // Theme-Exclusive Rooms
    'ash_exclusive': 'حصري لسمة الرماد',
    'twilight_exclusive': 'حصري لسمة الشفق',
    'void_exclusive': 'حصري لسمة الفراغ',
    'theme_required': 'يتطلب سمة {theme}',
    'ash_embers_title': 'من الرماد',
    'ash_embers_line1': 'كل شيء يحترق في النهاية.',
    'ash_embers_line2': 'المهم هو ما ينهض بعد ذلك.',
    'ash_embers_line3': 'المس الشاشة. اخلق الجمر.',
    'ash_embers_quote': 'من الرماد، نتذكر ما احترق.',
    'room_ash-embers': 'الرماد والجمر',
    'sub_ash-embers': 'مرئي فقط في سمة الرماد',
    'twilight_stars_title': 'بين النهار والليل',
    'twilight_stars_line1': 'النجوم تتذكر كل من نظر إليها.',
    'twilight_stars_line2': 'اربطها. اصنع كوكبتك الخاصة.',
    'twilight_stars_quote': 'في الشفق، نرى ما يخفيه النهار.',
    'room_twilight-stars': 'نجوم الشفق',
    'sub_twilight-stars': 'مرئي فقط في سمة الشفق',
    'void_abyss_title': 'الهاوية',
    'void_abyss_line1': 'انظر إلى الفراغ. الفراغ ينظر إليك.',
    'void_word_1': 'لا شيء',
    'void_word_2': 'فارغ',
    'void_word_3': 'صمت',
    'void_word_4': 'ذهب',
    'void_word_5': 'وحيد',
    'void_word_6': 'نهاية',
    'void_revealed': 'مكشوف',
    'void_abyss_quote': 'في الظلام المطلق، نرى أنفسنا أخيراً.',
    'room_void-abyss': 'الهاوية',
    'sub_void-abyss': 'مرئي فقط في سمة الفراغ',
    'theme_void': 'الفراغ',
    'theme_void_desc': 'لا شيء يبقى سوى الظلال',
    
    // Game UI
    'game_watching': 'شاهد...',
    'game_repeat': 'كرر',
    'game_echo_fades': 'الصدى يتلاشى...',
    'game_escaped': 'هربت.',
    'game_found_exit': 'وجدت المخرج.',
    'game_looping': 'أنت تدور في حلقة.',
    'game_deeper': 'أعمق...',
    'game_doors_shift': 'الأبواب تتغير.',
    'maze_left': 'يسار',
    'maze_right': 'يمين',
    'maze_forward': 'أمام',
    'maze_back': 'خلف',
    'maze_past': 'الماضي',
    'maze_future': 'المستقبل',
    'maze_here': 'هنا',
    'maze_there': 'هناك',
    'maze_truth': 'الحقيقة',
    'maze_lies': 'الأكاذيب',
    'maze_light': 'الضوء',
    'maze_dark': 'الظلام',
    'maze_memory': 'الذاكرة',
    'maze_dream': 'الحلم',
    'maze_silence': 'الصمت',
    'maze_noise': 'الضجيج',
    
    // Daily Joke Room
    'joke_number': 'نكتة #{number}',
    'joke_today': 'نكتة اليوم',
    'joke_reveal': 'اكشف النهاية',
    'joke_come_back': 'عد غداً لنكتة جديدة.',
    'joke_quote': 'الضحك أقصر مسافة بين شخصين.',
    
    'random_room': 'غرفة عشوائية',
    'ai_reflection': 'تأمل الذكاء الاصطناعي',
    'support': 'الدعم',
    'choose_direction': 'اختر اتجاهاً. بعض الأبواب تستجيب.',
    'rooms_explored': 'غرفة مستكشفة',
    'your_journey': 'رحلتك',
    'complete_rooms_fragments': 'أكمل الغرف لجمع الشظايا.',
    'no_rooms_completed': 'لم تكتمل أي غرفة بعد.',
    
    // Stats
    'total_time': 'الوقت الكلي',
    'rooms_visited': 'الغرف المزارة',
    'total_visits': 'إجمالي الزيارات',
    'most_visited': 'الأكثر زيارة',
    'longest_session': 'أطول جلسة',
    'fragments_collected': 'الشظايا المجمعة',
    'completed_rooms': 'الغرف المكتملة',
    'privacy_note': 'جميع البيانات مخزنة محلياً على جهازك',
    
    // Daily Challenges
    'daily_challenges': 'التحديات اليومية',
    'challenges_completed': 'مكتملة',
    'challenges_total': 'الإجمالي',
    'resets_midnight': 'تتجدد عند منتصف الليل',
    
    // Weekly Summary
    'weekly_summary': 'ملخص الأسبوع',
    'week': 'أسبوع',
    'this_week': 'هذا الأسبوع',
    'rooms': 'غرف',
    'days': 'أيام',
    'vs_last_week': 'مقارنة بالأسبوع الماضي',
    'refresh_insight': 'تحديث الرؤية',
    
    // Room Titles (Arabic)
    'room_how-many-times-left': 'كم مرة متبقية',
    'room_life-in-weeks': 'حياتك بالأسابيع',
    'room_delay-button': 'زر التأخير',
    'room_one-honest-sentence': 'جملة صادقة واحدة',
    'room_this-moment': 'هذه اللحظة لن تتكرر',
    'room_reality-receipt': 'إيصال الواقع',
    'room_nothing-happens': 'لا شيء يحدث هنا',
    'room_default-choice': 'خيارك الافتراضي',
    'room_thought-loop': 'حلقة الأفكار',
    'room_expect-vs-happens': 'ما تتوقعه مقابل ما يحدث',
    'room_question-never-answer': 'السؤال الذي لا تجيب عليه',
    'room_not-special': 'أنت لست مميزاً (وهذا مقبول)',
    'room_memory-puzzle': 'لغز الذاكرة',
    'room_shadow-chase': 'مطاردة الظل',
    'room_timing-room': 'غرفة التوقيت',
    'room_pattern-whisper': 'همسات النمط',
    'room_silence-puzzle': 'لغز الصمت',
    'room_decay-game': 'لعبة التلاشي',
    'room_observation-test': 'اختبار الملاحظة',
    'room_mirror-room': 'غرفة المرآة',
    'room_echo-room': 'غرفة الصدى',
    'room_pulse-room': 'غرفة النبض',
    'room_vanishing-room': 'الغرفة المتلاشية',
    'room_letter-room': 'غرفة الرسائل',
    'room_empty-room': 'الغرفة الفارغة',
    'room_reverse-room': 'الغرفة المعكوسة',
    'room_balance-room': 'غرفة التوازن',
    'room_word-drift': 'انجراف الكلمات',
    'room_color-echo': 'صدى الألوان',
    'room_mirror-tap': 'نقرة المرآة',
    'room_maze-of-doors': 'متاهة الأبواب',
    'room_pulse-match': 'مطابقة النبض',
    'room_light-path': 'مسار الضوء',
    'room_sound-match': 'مطابقة الصوت',
    'room_whisper-room': 'غرفة الهمس',
    'room_shadow-room': 'غرفة الظل',
    'room_labyrinth-room': 'غرفة المتاهة',
    'room_color-room': 'غرفة الألوان',
    'room_surrender-room': 'غرفة الاستسلام',
    'room_noise-room': 'غرفة الضجيج',
    'room_observer-room': 'غرفة المراقب',
    
    // Room Subtitles (Arabic)
    'sub_how-many-times-left': 'عد ما تبقى',
    'sub_life-in-weeks': '٤٠٠٠ مربع. هذا كل شيء.',
    'sub_delay-button': 'يصبح أصعب في كل مرة',
    'sub_one-honest-sentence': 'بدون تعديل. بدون حذف.',
    'sub_this-moment': 'أنت هنا. مرة واحدة.',
    'sub_reality-receipt': 'ملخص أيامك',
    'sub_nothing-happens': 'بالضبط كما يقول',
    'sub_memory-puzzle': 'اختبر ما تتذكره',
    'sub_shadow-chase': 'امسك ما يتبعك',
    'sub_timing-room': 'اشعر بالإيقاع',
    'sub_pattern-whisper': 'استمع للنمط',
    'sub_silence-puzzle': 'حل بدون صوت',
    'sub_decay-game': 'كل شيء يتلاشى',
    'sub_observation-test': 'شاهد ما يفوت الآخرين',
    'sub_mirror-room': 'واجه نفسك',
    'sub_echo-room': 'اسمع نفسك تعود',
    'sub_pulse-room': 'اشعر بالنبض',
    'sub_vanishing-room': 'شاهده يختفي',
    'sub_letter-room': 'اكتب لنفسك',
    'sub_empty-room': 'لا شيء هنا',
    'sub_reverse-room': 'كل شيء معكوس',
    'sub_balance-room': 'جد التوازن',
    'sub_word-drift': 'الكلمات تطفو بعيداً',
    'sub_color-echo': 'الألوان تتذكر',
    'sub_mirror-tap': 'انقر الانعكاس',
    'sub_maze-of-doors': 'أي باب يقود للخارج؟',
    'sub_pulse-match': 'طابق نبضات القلب',
    'sub_light-path': 'اتبع الضوء',
    'sub_sound-match': 'طابق النغمات',
    'sub_whisper-room': 'حقائق بالكاد مسموعة',
    'sub_shadow-room': 'رفيقك المظلم',
    'sub_labyrinth-room': 'جد طريقك',
    'sub_color-room': 'تأمل لوني',
    'sub_surrender-room': 'تخلى عن السيطرة',
    'sub_noise-room': 'أفكار ثابتة',
    'sub_observer-room': 'تتم مراقبتك',
    
    // Alternate Mode Exclusive Rooms (Arabic)
    'room_the-other-side': 'الجانب الآخر',
    'room_the-unraveling': 'التفكك',
    'room_the-echo': 'الصدى',
    'sub_the-other-side': 'تحرك ببطء في الظلام',
    'sub_the-unraveling': 'الواقع يتلاشى مع كل كلمة',
    'sub_the-echo': 'ما تقوله لا يغادر أبداً',
    
    // AI Rooms Subtitles (Arabic)
    'room_ai-reflection-room': 'غرفة التأمل الذكي',
    'sub_ai-reflection-room': 'دع الذكاء يتأمل معك',
    'sub_dream-visualization': 'صف حلمك. شاهده يتحقق.',
    'sub_future-self': 'رسالة من المستقبل',
    
    // Meta/Behavioral Rooms (Arabic)
    'room_the-refusal': 'الرفض',
    'room_the-silence-counter': 'عداد الصمت',
    'room_parallel-you': 'أنت الموازي',
    'room_your-shadow': 'ظلك',
    'room_the-exit-reflection': 'تأمل الخروج',
    'room_unvisited-rooms': 'الغرف غير المزارة',
    'sub_the-refusal': 'أحياناً يرفض الموقع',
    'sub_the-silence-counter': 'كم من الوقت يمكنك البقاء صامتاً',
    'sub_parallel-you': 'نسخة أخرى منك',
    'sub_your-shadow': 'ما يتبعك',
    'sub_the-exit-reflection': 'لحظة قبل المغادرة',
    'sub_unvisited-rooms': 'الأبواب التي لم تفتحها',
    
    // Theme Exclusive Section
    'theme_exclusive_rooms': 'غرف حصرية للسمات',
    'theme_exclusive_hint': 'غيّر السمة في الإعدادات لفتح هذه الغرف.',
    'requires_theme': 'يتطلب',
    'switch_to': 'التبديل إلى',
    // Advanced Easter Eggs
    'corner_sequence': 'لقد رسمت الحدود.',
    'morse_sos': 'تم استلام S.O.S.',
    'reverse_scroll': 'العودة للخلف... لكن أسرع.',
    'midnight_visitor': 'ساعة السحر ترحب بك.',
    'shake': 'لقد هززت شيئاً ما.',
    
    // Experience Mode
    'experience_mode': 'وضع التجربة',
    'classic_mode': 'كلاسيكي',
    'classic_mode_desc': 'الواقع المألوف',
    'alternate_mode': '▒ بديل ▒',
    'alternate_mode_desc': '[ لا يجب أن تكون هنا ]',
    'switch_to_alternate': '░▓█ انزل █▓░',
    'switch_to_classic': '← العودة للسطح',
    'enter_parallel_reality': '[ الباب مفتوح ]',
    'return_to_familiar': 'العودة للمألوف',
    'something_shifted': '[ شيء ما تغير ]',
    'reality_feels_different': '[ الواقع يبدو مختلفاً الآن ]',
    'alternate_tagline': 'نفس الغرف. حقائق مختلفة.',
    'alternate_enter': 'انزل',
    'alternate_footer': '[ الأشياء ليست كما تبدو ]',
    'alternate_rooms': '░▓ الواقع الم0ازي ▓░',
    'alternate_rooms_hint': '[ هذه الغرف م0جودة فقط في الم0ازي ]',
    
    // Meta Room Content - The Refusal (Arabic)
    'refusal_line1': 'هذه الغرفة ترفضك.',
    'refusal_line2': 'ليس كل شيء يفتح عندما تريد.',
    'refusal_line3': 'أحياناً الجواب ببساطة: لا.',
    'refusal_line4': 'جرب الزر. لن يساعد.',
    'refusal_button': 'دخول الغرفة',
    
    // Meta Room Content - Parallel You (Arabic)
    'parallel_choice_1': 'اخترت السرعة.',
    'parallel_alternate_1': 'أنت آخر اختار الصبر.',
    'parallel_reflection_1': 'لا يزالون ينتظرون.',
    'parallel_choice_2': 'غادرت مبكراً.',
    'parallel_alternate_2': 'أنت آخر بقي حتى النهاية.',
    'parallel_reflection_2': 'رأوا ما فاتك.',
    'parallel_choice_3': 'تجنبت السؤال الصعب.',
    'parallel_alternate_3': 'أنت آخر أجاب عليه.',
    'parallel_reflection_3': 'يحملون تلك الحقيقة الآن.',
    'parallel_choice_4': 'مررت مسرعاً.',
    'parallel_alternate_4': 'أنت آخر تذوق كل لحظة.',
    'parallel_reflection_4': 'يتذكرون أكثر.',
    'parallel_choice_5': 'واصلت التمرير.',
    'parallel_alternate_5': 'أنت آخر توقف.',
    'parallel_reflection_5': 'وجدوا شيئاً.',
    'parallel_choice_6': 'اخترت الراحة.',
    'parallel_alternate_6': 'أنت آخر اختار عدم الراحة.',
    'parallel_reflection_6': 'نموا منه.',
    'parallel_quote': 'كل لحظة تنقسم إلى مسارات لا نهائية. أنت تمشي واحداً فقط.',
    
    // Meta Room Content - Your Shadow (Arabic)
    'shadow_generated': 'مُولَّد من سلوكك',
    'shadow_intro': 'ظلك...',
    'shadow_follows': 'هذا الظل يتبعك عبر كل غرفة.',
    'shadow_quote': 'لسنا ما نفكر. نحن ما نفعل.',
    'shadow_trait_completion': 'يسعى للإكمال',
    'shadow_trait_surface': 'يفضل السطح',
    'shadow_trait_lingers': 'يبقى في الأماكن',
    'shadow_trait_moves_quickly': 'يتحرك بسرعة',
    'shadow_trait_avoids_depth': 'يتجنب العمق',
    'shadow_trait_stillness': 'يحتضن السكون',
    'shadow_trait_chases': 'يطارد شيئاً بلا اسم',
    'shadow_trait_darkness': 'منجذب للظلام',
    'shadow_trait_emptiness': 'مرتاح مع الفراغ',
    'shadow_trait_undefined': 'غير محدد',
    'shadow_trait_forming': 'لا يزال يتشكل',
    
    // Translation Navigator (Arabic)
    'translation_navigator': 'متصفح الترجمات',
    'translation_coverage': 'تغطية ترجمة الغرف حسب اللغة',
    'keys': 'مفاتيح',
    'room_translations': 'ترجمات الغرف',
    'show_all': 'عرض الكل',
    'show_missing': 'عرض المفقود',
    'all_translated': 'جميع الغرف مترجمة!',
    'missing_translations': 'ترجمات مفقودة',
    
    // Unlock page (Arabic)
    'unlock_title': 'فتح الغرف المميزة',
    'unlock_subtitle': 'الوصول لتجارب أعمق',
    'lifetime': 'وصول مدى الحياة',
    'monthly': 'شهري',
    'one_time': 'دفعة واحدة',
    'per_month': 'شهرياً',
    'unlock_now': 'افتح الآن',
    'subscribe': 'اشترك',
    'already_premium': 'لديك وصول مميز',
    'premium_features': 'المميزات المميزة',
    'redeem_code': 'لديك كود؟',
    'redeem_placeholder': 'أدخل كود الاسترداد',
    'redeem_button': 'استرداد',
    'redeem_success': 'تم استرداد الكود بنجاح!',
    'redeem_error': 'كود غير صالح أو منتهي الصلاحية',
    'unlock_locked_rooms': 'الغرف المقفلة',
    'unlock_not_free': 'بعض الغرف ليست مجانية.',
    'unlock_not_because': 'ليس لأنها أفضل —',
    'unlock_go_further': 'لكن لأنها تذهب أبعد.',
    'unlock_whats_behind': 'ما وراء الباب',
    'unlock_and_more': '...و {count} غرف إضافية',
    'unlock_deepest_truth': 'الحقيقة الأعمق',
    'unlock_premium_boss': 'غرفة الزعيم المميزة',
    'unlock_lifetime_desc': 'مرة واحدة. ملكك للأبد.',
    'unlock_monthly_desc': 'ألغِ في أي وقت.',
    'unlock_no_account': 'لا حساب مطلوب. لا إيميلات. فقط الوصول.',
    'unlock_all_unlocked': 'جميع الغرف مفتوحة',
    'unlock_have_access': 'لديك وصول لكل غرفة في الغرف البشرية.',
    'unlock_go_deeper': 'اذهب أعمق. خذ وقتك.',
    'unlock_enter_hallway': 'ادخل الممر',
    'unlock_welcome_depths': 'مرحباً بك في الأعماق',
    'unlock_verifying': 'جاري التحقق من الشراء...',
    'unlock_canceled': 'تم إلغاء الدفع',
    'unlock_try_again': 'يمكنك المحاولة مجدداً عندما تكون مستعداً.',
    'unlock_error': 'حدث خطأ ما',
    'unlock_try_later': 'يرجى المحاولة لاحقاً.',
    
    // Room additional keys (Arabic)
    'seconds_that_wont_return': 'لن تعود أبداً',
    'moment_id': 'معرف اللحظة',
    
    // This Moment room (Arabic)
    'this_moment_title': 'هذه اللحظة لن تتكرر أبداً',
    'this_moment_been_here': 'كنت في هذه اللحظة لمدة',
    'this_moment_quote': 'هذا التكوين الدقيق من الزمن والمكان وأنت لن يحدث مرة أخرى في تاريخ الكون.',
    
    // Thought Loop room (Arabic)
    'thought_loop_title': 'حلقة الأفكار',
    'thought_loop_prompt': 'اكتب فكرة تعود باستمرار.',
    'thought_loop_placeholder': 'الفكرة التي لا تغادر...',
    'thought_loop_release': 'أطلقها',
    'thought_loop_quote': 'العقل يكرر ما لا يستطيع إطلاقه.',
    
    // Expect vs Happens room (Arabic)
    'expect_title': 'ما تتوقعه مقابل ما يحدث',
    'expect_subtitle': 'حقيقتان. فجوة واحدة.',
    'expect_what_expect': 'ما تتوقع حدوثه',
    'expect_what_happens': 'ما يحدث عادةً',
    'expect_show': 'أرني',
    'expect_distance': 'المسافة بين هاتين الجملتين هي حيث تعيش.',
    'expect_quote': 'الأمل هو الفجوة بين التوقع والواقع.',
    
    // Reality Receipt room (Arabic)
    'receipt_title': 'إيصال الواقع',
    'receipt_sleep': 'ساعات النوم في الليلة؟',
    'receipt_work': 'ساعات العمل يومياً؟',
    'receipt_screen': 'ساعات الشاشة (غير العمل)؟',
    'receipt_commute': 'ساعات التنقل؟',
    'receipt_next': 'التالي',
    'receipt_generate': 'إنشاء الإيصال',
    'receipt_header': 'إيصال الواقع',
    'receipt_sleep_label': 'النوم',
    'receipt_work_label': 'العمل',
    'receipt_screen_label': 'الشاشة (ترفيه)',
    'receipt_commute_label': 'التنقل',
    'receipt_total_used': 'المجموع المستخدم',
    'receipt_free_time': 'الوقت الحر',
    'receipt_yearly': 'التوقع السنوي:',
    'receipt_yearly_work': 'ساعات عمل',
    'receipt_yearly_screen': 'ساعات على الشاشات',
    'receipt_yearly_sleep': 'ساعات نوم',
    'receipt_yearly_free': 'ساعات حرة',
    'receipt_recalculate': 'إعادة الحساب',
    'receipt_quote': 'هكذا تقضي حياتك.',
    
    // Question Never Answer room (Arabic)
    'question_title': 'السؤال الذي لا تجيب عليه أبداً',
    'question_seconds': 'ثانية',
    'question_most_left': 'معظم الناس غادروا بالفعل.',
    'question_quote': 'السؤال يبقى. أنت بقيت.',
    'question_1': 'ماذا ستفعل لو علمت أنك لن تفشل؟',
    'question_2': 'من ستكون لو لم يكن أحد يراقب؟',
    'question_3': 'ما الذي تتظاهر بأنك لا تعرفه؟',
    'question_4': 'ماذا سيفكر فيك طفلك ذو العشر سنوات؟',
    'question_5': 'ما الذي تخاف من أن تريده؟',
    'question_6': 'إذا مت اليوم، ما الذي سيبقى ناقصاً؟',
    'question_7': 'ما الكذبة التي تخبرها لنفسك أكثر؟',
    'question_8': 'ماذا ستفعل لو لم يكن المال حقيقياً؟',
    'question_9': 'من لم تسامحه بعد؟',
    'question_10': 'ما الذي تهرب منه؟',
    
    // Not Special room (Arabic)
    'not_special_right_now': 'الآن، تقريباً',
    'not_special_share_fear': 'شخص يشاركك خوفك من',
    'not_special_title': 'أنت لست مميزاً',
    'not_special_okay': 'وهذا مقبول.',
    'not_special_quote': 'خوفك ليس فريداً. شجاعتك أيضاً ليست كذلك.',
    'fear_forgotten': 'أن يُنسى',
    'fear_dying_alone': 'الموت وحيداً',
    'fear_not_enough': 'ألا يكون جيداً بما فيه الكفاية',
    'fear_wasting_life': 'إضاعة حياتهم',
    'fear_being_seen': 'أن يُرى حقاً',
    'fear_failure': 'الفشل',
    'fear_rejection': 'الرفض',
    'fear_missing_out': 'تفويت الفرص',
    'fear_ordinary': 'أن يكون عادياً',
    'fear_time': 'نفاد الوقت',
    
    // Scary Room Content (Arabic)
    'watching_alone': 'أنت وحدك هنا.',
    'watching_completely': 'وحدك تماماً.',
    'watching_or_are_you': 'أم هل أنت؟',
    'watching_been_here': 'كانوا هنا طوال الوقت.',
    'watching_always': 'هم دائماً يراقبون.',
    'watching_quote': 'لست وحدك أبداً مع أفكارك.',
    'countdown_title': 'العد التنازلي',
    'countdown_something': 'شيء ما يحدث عندما يصل للصفر.',
    'countdown_sure': 'هل أنت متأكد أنك تريد البقاء؟',
    'countdown_almost': 'إنه يقترب.',
    'countdown_end_1': 'لم يحدث شيء. هل كنت خائفاً؟',
    'countdown_end_2': 'الترقب كان التجربة.',
    'countdown_end_3': 'ماذا كنت تتوقع؟',
    'countdown_end_4': 'الخوف يعيش في الانتظار.',
    'countdown_end_5': 'العد التنازلي لم يكن أبداً عن النهاية.',
    'countdown_quote': 'أخوف لحظة هي دائماً قبل أن تبدأ.',
    'dont_look_title': 'لا تنظر بعيداً',
    'dont_look_away_msg': 'لا تنظر بعيداً.',
    'dont_look_looked': 'نظرت بعيداً.',
    'dont_look_seconds': '{count} ثانية من الانتباه',
    'dont_look_times': 'نظرت بعيداً {count} مرة',
    'dont_look_quote': 'الحضور هو أصعب شيء للعطاء.',
    'whisper_hello': 'مرحباً.',
    'whisper_came': 'لقد جئت.',
    'whisper_expecting': 'كنا ننتظرك.',
    'whisper_remember': 'هل تتذكر هذا المكان؟',
    'whisper_been_before': 'كنت هنا من قبل.',
    'whisper_know': 'نحن نعلم.',
    'whisper_remember_name': 'هل تتذكر اسمك؟',
    'whisper_quote': 'بعض الغرف تتذكرك أفضل مما تتذكر نفسك.',
    
    // Additional Room Titles (Arabic)
    'room_void-room': 'الفراغ',
    'room_pattern-room': 'أنماطك',
    'room_time-capsule': 'كبسولة الزمن',
    'room_the-revisit': 'إعادة الزيارة',
    'room_contradiction-room': 'غرفة التناقض',
    'room_export-journey': 'تصدير رحلتك',
    'room_anonymous-echoes': 'أصداء مجهولة',
    'room_the-waiting-room': 'غرفة الانتظار',
    'room_eclipse-shadows': 'ظلال الكسوف',
    'room_dawn-awakening': 'يقظة الفجر',
    'room_shortcut-always-take': 'الاختصار الذي تتخذه دائماً',
    'room_blurred-self': 'الذات الضبابية',
    'room_image-zooming': 'الصورة التي تكبرها',
    'room_comfort-color': 'لون الراحة',
    'room_line-you-crossed': 'الخط الذي تجاوزته',
    'room_read-aloud': 'اقرأ هذا بصوت عالٍ',
    'room_voice-without-meaning': 'صوتك بدون معنى',
    'room_sentence-you-avoid': 'الجملة التي تتجنبها',
    'room_if-nothing-changes': 'إذا لم يتغير شيء',
    'room_real-priorities': 'أولوياتك الحقيقية',
    'room_last-conversation': 'المحادثة الأخيرة',
    'room_who-without-job': 'من أنت بدون وظيفتك؟',
    'room_comfort-zone-scanner': 'ماسح منطقة الراحة',
    'room_say-it-once': 'قلها مرة واحدة',
    'room_listen-to-yourself': 'استمع لنفسك',
    'room_life-as-shapes': 'حياتك كأشكال',
    'room_face-without-features': 'وجه بدون ملامح',
    'room_what-keeps-awake': 'ما يبقيك مستيقظاً',
    'room_you-at-317am': 'أنت في 3:17 صباحاً',
    'room_thought-that-returns': 'الفكرة التي تعود',
    'room_room-you-visit-most': 'الغرفة التي تزورها أكثر',
    'room_room-you-avoid': 'الغرفة التي تتجنبها',
    'room_left-too-early': 'غادرت مبكراً جداً',
    'room_daily-quote': 'اقتباس اليوم',
    'room_daily-joke': 'نكتة اليوم',
    'room_theyre-watching': 'هم يراقبون',
    'room_the-last-visitor': 'الزائر الأخير',
    'room_dont-look-away': 'لا تنظر بعيداً',
    'room_the-whisper': 'الهمسة',
    'room_you-forgot-something': 'نسيت شيئاً',
    'room_the-mirror-lies': 'المرآة تكذب',
    'room_something-follows': 'شيء يتبعك',
    'room_the-countdown': 'العد التنازلي',
    'room_eyes-in-the-dark': 'عيون في الظلام',
    'room_the-voice': 'الصوت',
    'room_it-knows-your-name': 'يعرف اسمك',
    'room_the-door': 'الباب',
    'room_boss-room-free': 'مجموع حقائقك',
    'room_boss-room-horror': 'قلب الخوف',
    'room_boss-room-premium': 'الحقيقة الأعمق',
    'room_mouse-tracker': 'متتبع الفأرة',
    'room_scroll-confession': 'اعتراف التمرير',
    'room_waiting-game': 'لعبة الانتظار',
    'room_day-in-pixels': 'يومك بالبكسل',
    'room_memory-maze': 'متاهة الذاكرة',
    'room_decision-shadow': 'ظل القرار',
    'room_anonymous-mirrors': 'مرايا مجهولة',
    'room_collective-room': 'الغرفة الجماعية',
    'room_secret-room': 'الغرفة السرية',
    'room_glitch-room': 'غرفة غير مستقرة',
    'room_full-moon-room': 'غرفة البدر',
    'room_weekend-room': 'غرفة نهاية الأسبوع',
    'room_317am-room': 'غرفة 3:17 صباحاً',
    'room_idle-room': 'الغرفة الساكنة',
    'room_invite-friend-room': 'الغرفة المشتركة',
    'room_halloween-room': 'غرفة الحجاب',
    'room_winter-solstice-room': 'أطول ليلة',
    'room_new-year-room': 'رسالة للذات المستقبلية',
    
    // Additional Room Subtitles (Arabic)
    'sub_default-choice': 'الأمان أم المخاطرة. كل مرة.',
    'sub_thought-loop': 'شاهد عقلك يكرر نفسه',
    'sub_expect-vs-happens': 'الفجوة بين الأمل والواقع',
    'sub_question-never-answer': 'معظم المستخدمين يغادرون',
    'sub_not-special': 'لا راحة. فقط الحقيقة.',
    'sub_shortcut-always-take': 'الأنماط تكشف نفسها',
    'sub_blurred-self': 'الذاكرة تعمل هكذا',
    'sub_image-zooming': 'البحث عن إجابات في لا شيء',
    'sub_comfort-color': 'كم ستبقى؟',
    'sub_line-you-crossed': 'نرى أين ترددت',
    'sub_read-aloud': 'كلمات يجب أن تنطقها',
    'sub_voice-without-meaning': 'صوت بدون كلمات',
    'sub_sentence-you-avoid': 'لا يمكنك المغادرة حتى تكتبها',
    'sub_if-nothing-changes': 'إسقاط لسنواتك الخمس القادمة',
    'sub_real-priorities': 'بناءً على الوقت، ليس الكلمات',
    'sub_last-conversation': 'اكتب ما لن ترسله أبداً',
    'sub_who-without-job': 'أعد كتابة نفسك',
    'sub_comfort-zone-scanner': 'نرى ترددك',
    'sub_say-it-once': 'لا إعادة. لا إعادة تنفيذ.',
    'sub_listen-to-yourself': 'صوتك، بطيء',
    'sub_life-as-shapes': 'تجريدي. بدون تسميات. ملكك.',
    'sub_face-without-features': 'أزل حتى لا يبقى شيء',
    'sub_what-keeps-awake': 'متاح بعد 10 مساءً',
    'sub_you-at-317am': 'محدد بالوقت. شخصي.',
    'sub_thought-that-returns': 'تعود كل زيارة',
    'sub_room-you-visit-most': 'أين تذهب أكثر؟',
    'sub_room-you-avoid': 'ماذا تتجنب؟',
    'sub_left-too-early': 'كان هناك المزيد',
    'sub_secret-room': 'لقد وجدتها',
    'sub_idle-room': 'وجدت من خلال الصبر',
    'sub_glitch-room': 'شيء ما خطأ',
    'sub_void-room': 'جرح في الكود',
    'sub_pattern-room': 'ما يراه الموقع فيك',
    'sub_collective-room': 'الجميع هنا. لا أحد يبقى.',
    'sub_theyre-watching': 'أنت لست وحدك',
    'sub_the-last-visitor': 'شخص كان هنا قبلك',
    'sub_dont-look-away': 'يعرف متى تغادر',
    'sub_the-whisper': 'استمع جيداً',
    'sub_you-forgot-something': 'ما هو؟',
    'sub_the-mirror-lies': 'اكسرها لترى الحقيقة',
    'sub_something-follows': 'استمر بالركض',
    'sub_the-countdown': 'شيء يحدث عند الصفر',
    'sub_eyes-in-the-dark': 'يتكاثرون',
    'sub_the-voice': 'شخص محبوس هنا',
    'sub_it-knows-your-name': 'لم يكن عليك المجيء',
    'sub_the-door': 'شخص يطرق',
    'sub_boss-room-free': 'الغرفة الحرة النهائية',
    'sub_boss-room-horror': 'واجه الظلام النهائي',
    'sub_boss-room-premium': 'الغرفة العميقة النهائية',
    'sub_halloween-room': '31 أكتوبر - الحجاب رقيق',
    'sub_winter-solstice-room': '21 ديسمبر - أطلق واحتفظ',
    'sub_new-year-room': '31 ديسمبر - اكتب لمن ستصبح',
    'sub_weekend-room': 'الراحة وجهة',
    'sub_full-moon-room': 'يفتح فقط في ذروة القمر',
    'sub_317am-room': 'محدد بالوقت. شخصي.',
    'sub_time-capsule': 'اكتب لذاتك المستقبلية',
    'sub_the-revisit': 'انظر كيف تغيرت إجاباتك',
    'sub_contradiction-room': 'حقائقك تختلف',
    'sub_export-journey': 'حمّل مسارك',
    'sub_anonymous-echoes': 'شظايا من غرباء',
    'sub_the-waiting-room': 'افتح بالحضور',
    'sub_invite-friend-room': 'بعض الرحلات يجب أن تُشهد',
    'sub_daily-quote': 'حكمة تتغير يومياً',
    'sub_daily-joke': 'فكاهة سوداء، تتحدث يومياً',
    'sub_mouse-tracker': 'نرى كيف تتحرك',
    'sub_scroll-confession': 'ما مدى سرعة تمريرك؟',
    'sub_waiting-game': 'هل تستطيع ألا تفعل شيئاً؟',
    'sub_day-in-pixels': 'الوقت يصبح شكلاً',
    'sub_memory-maze': 'النمط يتغير',
    'sub_decision-shadow': 'اختياراتك تكشف الأنماط',
    'sub_anonymous-mirrors': 'أفكار من غرباء',
    'sub_regret-machine': 'استكشف ما كان يمكن أن يكون',
    'sub_memory-reconstruction': 'أعد بناء ذاكرة تتلاشى',
    'sub_whisper-wall': 'حقائق مجهولة تطفو في الظلام',
    'sub_collective-memory': 'شظايا مشتركة مما نتذكر',
    'sub_shared-moments': 'كيف حالك، بصدق؟',
    'sub_eclipse-shadows': 'تحدث في الظلام التام',
    'sub_dawn-awakening': 'استقبل أول ضوء',
    'sub_the-archive': 'تاريخ رحلتك الكامل',
    'sub_mirror-of-all-selves': 'الذكاء الاصطناعي يكشف صورتك السلوكية',
    'sub_the-ending': 'وداع استحققته',
    
    // Interaction hints (Arabic)
    'hint_click_enter': 'انقر للدخول',
    'hint_take_time': 'خذ وقتك',
    'hint_each_room': 'كل غرفة تحمل سؤالاً',
    'hint_no_rush': 'لا استعجال هنا',
    
    // Behavior reflections (Arabic)
    'behavior_stillness': 'توقفت.',
    'behavior_patience': 'تنتظر أطول من معظمهم.',
    'behavior_silence': 'الصمت يتحدث.',
    
    // Achievements (Arabic)
    'achievements': 'الإنجازات',
    'achievements_subtitle': 'معالم في رحلتك',
    'points': 'نقاط',
    'unlocked': 'مفتوح',
    'exploration': 'الاستكشاف',
    'consistency': 'الاستمرارية',
    'discovery': 'الاكتشاف',
    'special': 'خاص',
    'all': 'الكل',
    
    // Room Content - Common (Arabic)
    'how_old_are_you': 'كم عمرك؟',
    'based_on_life_expectancy': 'بناءً على متوسط العمر المتوقع، لديك تقريباً:',
    'summers': 'صيف',
    'meals': 'وجبات',
    'birthdays': 'أعياد ميلاد',
    'full_moons': 'بدر',
    'sunrises': 'شروق شمس',
    'books_might_finish': 'كتب قد تنهيها',
    'quote_not_warning': 'هذا ليس تحذيراً. مجرد عد.',
    'each_square_is_week': 'كل مربع هو أسبوع واحد. هناك حوالي 4000.',
    'your_age': 'عمرك',
    'see_your_life': 'انظر حياتك',
    'weeks_lived': '{count} أسبوع عاشها',
    'weeks_remaining': '{count} متبقية (إذا كنت متوسطاً)',
    'quote_week_never_back': 'كل مربع ممتلئ هو أسبوع لن يعود.',
    'write_one_true_thing': 'اكتب شيئاً حقيقياً عن نفسك.',
    'cannot_edit_delete': 'لا يمكنك تعديله. لا يمكنك حذفه.',
    'i_placeholder': 'أنا...',
    'submit_forever': 'أرسل للأبد',
    'previous_truths': 'حقائقك السابقة',
    'write_another': 'اكتب أخرى',
    'quote_cannot_unsaid': 'هذا لا يمكن أن يُقال مرة أخرى.',
    'content_nothing_waiting': 'كنت هنا لمدة {seconds} ثانية.',
    'content_nothing_still': 'لا شيء لا يزال يحدث.',
    'content_nothing_stayed': 'ومع ذلك، بقيت.',
    'content_thought_visit': 'زيارة #{count}',
    'content_thought_always': 'هذه الفكرة ستكون هنا كلما عدت.',
    'content_thought_never_leave': 'بعض الأفكار لا تغادر أبداً. فقط تنتظر.',
    'click_it_harder': 'انقر عليه. سيصبح أصعب.',
    'click': 'انقر',
    'times_clicked': 'مرات النقر',
    'quote_things_chase': 'الأشياء التي نطاردها تصغر كلما طاردناها.',
    'scenario_stranger': 'غريب يطلب المساعدة',
    'scenario_help': 'ساعده',
    'scenario_keep_walking': 'واصل المشي',
    'scenario_free_hour': 'لديك ساعة فراغ',
    'scenario_start_new': 'ابدأ شيئاً جديداً',
    'scenario_scroll': 'تصفح هاتفك',
    'scenario_disagrees': 'شخص يختلف معك',
    'scenario_engage': 'شارك',
    'scenario_avoid': 'تجنب الصراع',
    'scenario_risky': 'فرصة تبدو محفوفة بالمخاطر',
    'scenario_take_it': 'خذها',
    'scenario_wait_safer': 'انتظر واحدة أكثر أماناً',
    'scenario_speak_up': 'يمكنك التحدث الآن',
    'scenario_say_it': 'قلها',
    'scenario_stay_quiet': 'ابق صامتاً',
    'choose_safety': 'من الوقت، تختار الأمان',
    'quote_become_choose': 'نصبح ما نختاره مراراً.',
    
    // AI Room Content (Arabic)
    'imagine_result_alt': 'صورتك الذاتية',
    'imagine_empty_error': 'اكتب شيئاً عن نفسك أولاً',
    'imagine_short_error': 'أخبرنا المزيد عن نفسك',
    'rate_limit_error': 'طلبات كثيرة جداً. انتظر لحظة.',
    'credits_error': 'الخدمة غير متاحة مؤقتاً',
    'generation_error': 'لم نتمكن من إنشاء الصورة. حاول مجدداً.',
    'dream_result_alt': 'تصور حلمك',
    'future_result_alt': 'ذاتك المستقبلية',
    
    // Audio descriptions (Arabic)
    'audio_enabled_desc': 'ستُشغل الأصوات المحيطية في الغرف',
    'audio_disabled_desc': 'ستكون الغرف صامتة',
    'data_exported_desc': 'تم حفظ رحلتك',
    'data_cleared_desc': 'تم إعادة تعيين رحلتك',
    
    // New Phase 1-3 Rooms (Arabic)
    'room_letters-never-sent': 'رسائل لم تُرسل',
    'sub_letters-never-sent': 'اكتب لشخص لن يقرأها أبداً',
    'room_breath-sync': 'تناغم النَفَس',
    'sub_breath-sync': 'تنفس مع الغرفة',
    'room_entropy-garden': 'حديقة الفوضى',
    'sub_entropy-garden': 'النمو والتلاشي متشابكان',
    'room_memory-palace': 'قصر الذاكرة',
    'sub_memory-palace': 'ابنِ ملاذك الذهني',
    'room_morning-ritual': 'طقس الصباح',
    'sub_morning-ritual': 'ابدأ يومك بنية',
    'room_evening-ritual': 'طقس المساء',
    'sub_evening-ritual': 'أغلق اليوم بتأمل',
    'room_weekly-ritual': 'الطقس الأسبوعي',
    'sub_weekly-ritual': 'أيام الأحد للنظر للوراء',
    
    // Letters Never Sent Room (Arabic)
    'letters_title': 'رسائل لم تُرسل',
    'letters_subtitle': 'اكتب لشخص لن يقرأها أبداً. سيرد الذكاء الاصطناعي كما قد يفعلون.',
    'letters_select_recipient': 'لمن تكتب؟',
    'letters_past_self': 'ذاتي السابقة',
    'letters_future_self': 'ذاتي المستقبلية',
    'letters_someone_lost': 'شخص فقدته',
    'letters_someone_wronged': 'شخص أخطأت بحقه',
    'letters_placeholder': 'عزيزي...',
    'letters_send': 'أرسل إلى الفراغ',
    'letters_sending': 'جاري الإرسال...',
    'letters_response': 'يظهر رد...',
    'letters_write_another': 'اكتب رسالة أخرى',
    'letters_quote': 'بعض الكلمات مقدر لها أن تُكتب لكن لا تُرسل.',
    
    // Breath Sync Room (Arabic)
    'breath_title': 'تناغم النَفَس',
    'breath_subtitle': 'طابق تنفسك مع إيقاع الغرفة.',
    'breath_inhale': 'شهيق',
    'breath_hold': 'احبس',
    'breath_exhale': 'زفير',
    'breath_cycles': 'دورات مكتملة',
    'breath_quote': 'النَفَس هو الجسر بين الجسد والعقل.',
    
    // Entropy Garden Room (Arabic)
    'entropy_title': 'حديقة الفوضى',
    'entropy_subtitle': 'نباتاتك تنمو مع كل زيارة، لكنها تذبل عندما تبتعد.',
    'entropy_plant': 'ازرع بذرة',
    'entropy_water': 'اسقِ',
    'entropy_days_alive': 'أيام حية',
    'entropy_health': 'الصحة',
    'entropy_empty': 'حديقتك فارغة. ازرع شيئاً.',
    'entropy_quote': 'كل شيء ينمو. كل شيء يذبل. هذا هو الطريق.',
    
    // Memory Palace Room (Arabic)
    'memory_palace_title': 'قصر الذاكرة',
    'memory_palace_subtitle': 'ابنِ ملاذاً ذهنياً. ضع أشياء في غرف تصنعها.',
    'memory_palace_add_room': 'أضف غرفة',
    'memory_palace_add_object': 'أضف شيء',
    'memory_palace_room_name': 'اسم الغرفة...',
    'memory_palace_object_name': 'اسم الشيء...',
    'memory_palace_empty': 'قصرك فارغ. أنشئ غرفتك الأولى.',
    'memory_palace_quote': 'نبني عقولنا غرفة تلو الأخرى.',
    
    // Ritual Rooms (Arabic)
    'ritual_locked': 'هذا الطقس غير متاح بعد',
    'morning_ritual_title': 'طقس الصباح',
    'morning_ritual_intro': 'لحظة لتحديد نوايا لليوم القادم.',
    'morning_ritual_available': 'متاح من 6 ص - 9 ص',
    'evening_ritual_title': 'طقس المساء',
    'evening_ritual_intro': 'لحظة للتأمل في اليوم الذي مضى.',
    'evening_ritual_available': 'متاح من 8 م - 11 م',
    'weekly_ritual_title': 'الطقس الأسبوعي',
    'weekly_ritual_intro': 'لحظة للتأمل في الأسبوع الماضي وتحديد نوايا للقادم.',
    'weekly_locked': 'الطقس الأسبوعي ينتظرك',
    'weekly_time': 'عد يوم الأحد',
    'today_is': 'اليوم هو',
    'your_week': 'أسبوعك',
    'days_active': 'أيام نشطة',
    'weekly_summary_note': 'الآن، دعنا نتأمل بعمق أكبر.',
    'weekly_highlight': 'ما كان أبرز حدث في أسبوعك؟',
    'weekly_challenge': 'ما الذي تحداك أكثر هذا الأسبوع؟',
    'weekly_growth': 'كيف نموت هذا الأسبوع؟',
    'weekly_intention': 'ما النية التي تحددها للأسبوع القادم؟',
    'weekly_placeholder': 'خذ وقتك...',
    'reflect': 'تأمل',
    'reflecting': 'جاري التأمل...',
    'complete': 'اكتمل',
    'weekly_quote': 'ينتهي أسبوع. يبدأ آخر. تحمل ما يهم.',
    
    // Personal Timeline (Arabic)
    'personal_timeline': 'الجدول الزمني الشخصي',
    'timeline_subtitle': 'رحلتك عبر الغرف',
    'timeline_empty': 'جدولك الزمني فارغ. ابدأ الاستكشاف.',
    'timeline_today': 'اليوم',
    'timeline_yesterday': 'أمس',
    'timeline_this_week': 'هذا الأسبوع',
    'timeline_earlier': 'سابقاً',
    
    // Weather System (Arabic)
    'weather_clear': 'صافٍ',
    'weather_rain': 'مطر',
    'weather_fog': 'ضباب',
    'weather_snow': 'ثلج',
    
    // Notifications (Arabic)
    'notifications': 'الإشعارات',
    'notifications_desc': 'احصل على إشعارات للمحتوى المقفل بالوقت',
    'notify_time_locked': 'الغرف المقفلة بالوقت',
    'notify_full_moon': 'ليالي البدر',
    'notify_daily_reset': 'إعادة تعيين التحديات اليومية',
    'notify_time_capsule': 'فتح كبسولات الزمن',
    'notifications_enabled': 'تم تفعيل الإشعارات',
    'notifications_disabled': 'تم إيقاف الإشعارات',
    
    // Community Submissions (Arabic)
    'community_submissions': 'مساهمات المجتمع',
    'community_subtitle': 'قدم أفكار غرفك',
    'community_room_title': 'عنوان الغرفة',
    'community_room_description': 'وصف الغرفة',
    'community_room_category': 'الفئة',
    'community_submit': 'قدم الفكرة',
    'community_submitted': 'شكراً لمساهمتك!',
    'community_your_submissions': 'مساهماتك',
    'community_empty': 'لا توجد مساهمات بعد. كن الأول!',
    
    // Proactive Guide (Arabic)
    'guide_hesitation': 'تأخذ وقتك؟ لا بأس. هل تريد اقتراحاً؟',
    'guide_long_absence': 'أهلاً بعودتك. مضى وقت طويل.',
    'guide_milestone': 'لقد قطعت شوطاً طويلاً. هل تريد التأمل في رحلتك؟',
    'guide_dismiss': 'تجاهل',
    'guide_accept': 'أرني',
    
    // Voice Mode (Arabic)
    'voice_mode': 'وضع الصوت',
    'voice_mode_desc': 'دع المرشد يتحدث إليك',
    'voice_mode_enabled': 'تم تفعيل وضع الصوت',
    'voice_mode_disabled': 'تم إيقاف وضع الصوت',
    
    // Offline Support (Arabic)
    'offline_available': 'متاح بدون اتصال',
    'offline_cached': 'هذه الغرفة محفوظة للعرض بدون اتصال',
    
    // Journey Export (Arabic)
    'journey_export': 'تصدير الرحلة',
    'journey_import': 'استيراد الرحلة',
    'journey_export_json': 'تصدير كـ JSON',
    'journey_import_json': 'استيراد من JSON',
    'journey_exported': 'تم تصدير الرحلة بنجاح',
    'journey_imported': 'تم استيراد الرحلة بنجاح',
    
    // Phase 4 - Seasonal Rooms (Arabic)
    'room_spring-equinox-room': 'الاعتدال الربيعي',
    'sub_spring-equinox-room': 'أطلق وتجدد',
    'room_summer-solstice-room': 'الانقلاب الصيفي',
    'sub_summer-solstice-room': 'أطول يوم',
    'room_autumn-equinox-room': 'الاعتدال الخريفي',
    'sub_autumn-equinox-room': 'احصد وتقبل',
    'room_valentine-room': 'غرفة حب الذات',
    'sub_valentine-room': 'الحب الذي تستحقه',
    
    // Spring Equinox Room (Arabic)
    'spring_date': '20 مارس',
    'spring_title': 'الاعتدال الربيعي',
    'spring_message_1': 'الليل والنهار متساويان.',
    'spring_message_2': 'لحظة توازن مثالية.',
    'spring_message_3': 'ماذا ستطلق لتفسح المجال للنمو؟',
    'spring_letting_go': 'أنا أتخلى عن:',
    'spring_letting_go_placeholder': 'ما لم يعد يخدمك...',
    'spring_planting': 'أنا أزرع:',
    'spring_planting_placeholder': 'ما تريد أن ينمو...',
    'spring_bloom': 'دعها تزهر',
    'spring_released': 'تم إطلاقه:',
    'spring_planted': 'تم زرعه:',
    'spring_closing': 'من هذه اللحظة، تبدأ حياة جديدة.',
    'spring_quote': 'الطبيعة لا تتعجل، ومع ذلك كل شيء يتحقق.',
    
    // Summer Solstice Room (Arabic)
    'summer_date': '21 يونيو',
    'summer_title': 'أطول يوم',
    'summer_message_1': 'هذا أطول يوم في السنة.',
    'summer_message_2': 'الضوء يصل إلى ذروته.',
    'summer_message_3': 'ماذا ستفعل بمزيد من الوقت؟',
    'summer_message_4': 'صف يومك الأكثر امتلاءً وحيوية.',
    'summer_placeholder': 'في يومي الأكثر امتلاءً، سأ...',
    'summer_submit': 'احتضن الضوء',
    'summer_closing': 'الشمس سمعتك. الآن تبدأ بالغروب.',
    'summer_quote': 'في ذروة الضوء، نكتشف ما نريد حقاً إضاءته.',
    
    // Autumn Equinox Room (Arabic)
    'autumn_date': '22 سبتمبر',
    'autumn_title': 'الاعتدال الخريفي',
    'autumn_message_1': 'يعود التوازن.',
    'autumn_message_2': 'الحصاد جاهز.',
    'autumn_message_3': 'ماذا زرعت؟ ما الذي يمكنك تقبله؟',
    'autumn_harvesting': 'أنا أحصد:',
    'autumn_harvesting_placeholder': 'ما زرعته هذا العام...',
    'autumn_accepting': 'أنا أتقبل:',
    'autumn_accepting_placeholder': 'ما لا يمكنك تغييره...',
    'autumn_release': 'دع الأوراق تسقط',
    'autumn_harvested': 'تم حصاده:',
    'autumn_accepted': 'تم قبوله:',
    'autumn_closing': 'كل شيء يسقط في وقته.',
    'autumn_quote': 'الخريف يرينا كم هو جميل أن نترك الأشياء تذهب.',
    
    // Valentine's Day Room (Arabic)
    'valentine_date': '14 فبراير',
    'valentine_title': 'الحب الذي تستحقه',
    'valentine_message_1': 'اليوم ليس عن الآخرين.',
    'valentine_message_2': 'إنه عن الشخص الذي يكون معك دائماً.',
    'valentine_message_3': 'الشخص الذي يسمع كل فكرة.',
    'valentine_message_4': 'اكتب لنفسك رسالة حب.',
    'valentine_prompt': 'رسالة لنفسك:',
    'valentine_placeholder': 'عزيزتي أنا، أريدك أن تعرفي...',
    'valentine_submit': 'أرسل بحب',
    'valentine_closing': 'أنت تستحق الحب الذي تمنحه للآخرين.',
    'valentine_quote': 'أنت نفسك تستحق حبك وعاطفتك.',
    
    // Update changelog
    'update_seasonal_rooms': 'أربع غرف موسمية جديدة: الاعتدال الربيعي، الانقلاب الصيفي، الاعتدال الخريفي، وعيد الحب',
    'update_fragment_trading': 'نظام تداول الشظايا - ضحِّ بالشظايا لفتح الغرف المقفلة زمنياً أو معاينة غرف الرعب أو تلقي رسائل الذكاء الاصطناعي',
    'update_new_themes': 'سمتان جديدتان: الكسوف (ظلام تام) والفجر (تدرجات دافئة، 5-7 صباحاً فقط)',
     'update_regret_machine': 'آلة الندم - سرديات جدول زمني بديل مدعومة بالذكاء الاصطناعي',
     'update_memory_reconstruction': 'إعادة بناء الذاكرة - الذكاء الاصطناعي يملأ التفاصيل الحسية للذكريات الباهتة',
     
     // Phase 7 - Regret Machine Room
     'regret_machine_title': 'آلة الندم',
     'regret_machine_subtitle': 'صِف قراراً تتمنى لو اتخذته بشكل مختلف. ستريك الآلة ما كان يمكن أن يحدث.',
     'regret_placeholder': 'أنا نادم على...\n\nصِف خياراً اتخذته وتتمنى لو كان مختلفاً. ماذا كنت ستفعل بدلاً من ذلك؟',
     'regret_hint': 'هذا ليس عن اللوم. إنه عن الفهم.',
     'regret_explore': 'استكشف الجدول الزمني',
     'regret_generating': 'إعادة بناء واقع بديل...',
     'regret_alternate_timeline': 'الجدول الزمني البديل',
     'regret_explore_another': 'استكشف آخر',
     'regret_error_short': 'يرجى وصف ندمك بمزيد من التفصيل',
     'room_regret-machine': 'آلة الندم',
     
     // Phase 7 - Memory Reconstruction Room
     'memory_reconstruction_title': 'إعادة بناء الذاكرة',
     'memory_reconstruction_subtitle': 'صِف ذكرى باهتة. دعنا نساعدك على الشعور بالعودة إليها.',
     'memory_placeholder': 'أتذكر...\n\nصِف ذكرى بعيدة أو مجزأة. ما الشظايا المتبقية؟ ما المشاعر الباقية؟',
     'memory_timeframe_placeholder': 'متى كان هذا؟ (مثل: "الطفولة"، "قبل عشر سنوات")',
     'memory_hint': 'الذاكرة ليست تسجيلاً. إنها إعادة بناء.',
     'memory_reconstruct': 'أعِد بناء الذاكرة',
     'memory_reconstructing': 'جمع الشظايا معاً...',
     'memory_reconstructed': 'الذاكرة المُعاد بناؤها',
     'memory_explore_another': 'استكشف ذكرى أخرى',
     'memory_error_short': 'يرجى وصف ذاكرتك بمزيد من التفصيل',
     'room_memory-reconstruction': 'إعادة بناء الذاكرة',
    'update_eclipse_room': 'ظلال الكسوف - تحدث في الظلام التام، اكشف في ومضات الضوء',
    'update_dawn_room': 'يقظة الفجر - استقبل أول ضوء بتمارين التنفس والنوايا',
    
    // New themes
    'eclipse_theme_required': 'تتطلب هذه الغرفة سمة الكسوف. الظلام التام ينتظر.',
    'dawn_theme_required': 'تتطلب هذه الغرفة سمة الفجر خلال الساعات الأولى.',
    'dawn_time_hint': 'متاح من 5 إلى 7 صباحاً فقط',
    'change_theme': 'غيّر السمة في الإعدادات',
    
    // Eclipse Shadows Room
    'eclipse_shadows_title': 'ظلال الكسوف',
    'eclipse_shadows_intro': 'في الظلام التام، تبدو الأسرار أكثر أماناً. أجب بصدق—فقط ومضات قصيرة ستُضيء كلماتك.',
    'enter_darkness': 'ادخل الظلام',
    'shadow_question_1': 'ما الذي تخفيه عن نفسك؟',
    'shadow_question_2': 'ما الحقيقة التي ستقولها لو لم يسمعك أحد؟',
    'shadow_question_3': 'ما الذي يطاردك في الثالثة صباحاً؟',
    'shadow_question_4': 'ماذا ستفعل لو لم يكن الخوف موجوداً؟',
    'shadow_question_5': 'ما الذي لست مستعداً لمواجهته؟',
    'speak_into_dark': 'تحدث في الظلام...',
    'release': 'أطلق',
    'eclipse_reveal_prompt': 'انقر على كل ظل لكشف ما اعترفت به.',
    'hidden_in_shadow': '▓▓▓▓▓▓▓▓▓▓▓▓',
    'eclipse_reveal_hint': 'بعض الحقائق أوضح في الظلام.',
    'eclipse_shadows_quote': 'في الظلام التام، نرى أنفسنا أخيراً.',
    
    // Dawn Awakening Room
    'dawn_awakening_title': 'يقظة الفجر',
    'dawn_awakening_intro': 'أول ضوء يحمل الإمكانية. قبل أن يستيقظ العالم، حدد نواياك.',
    'greet_the_light': 'استقبل الضوء',
    'breathe_in_light': 'تنفس الضوء',
    'hold_warmth': 'احتفظ بالدفء',
    'release_night': 'أطلق الليل',
    'dawn_prompt_1': 'ما الذي تريد أن تحمله معك اليوم؟',
    'dawn_prompt_2': 'ما الذي ستتخلى عنه؟',
    'dawn_prompt_3': 'ما هو فعل اللطف الصغير الذي ستقدمه لنفسك؟',
    'set_your_intention': 'حدد نيتك...',
    'plant_seed': 'ازرع هذه البذرة',
    'dawn_awakening_quote': 'كل فجر دعوة للبدء من جديد.',
    
    // Fragment Trading
    'fragment_trading': 'تداول الشظايا',
    'sacrifice_to_unlock': 'ضحِّ بالشظايا لكشف الأسرار',
    'your_fragments': 'شظاياك',
    'fragments_any': 'أي شظايا',
    'fragments_free': 'شظايا الوضوح',
    'fragments_premium': 'شظايا العمق',
    'fragments_horror': 'شظايا الظل',
    'trade_successful': 'تمت المبادلة بنجاح!',
    'trade_failed': 'فشلت المبادلة',
    'not_enough_fragments': 'شظايا غير كافية',
    'continue_trading': 'متابعة التداول',
    'trading_fragments': 'جارٍ تداول الشظايا...',
    'fragment_trade_warning': 'لا يمكن استعادة الشظايا المضحى بها',
    'unlock-time-locked': 'فتح غرفة مقفلة زمنياً',
    'unlock-time-locked_desc': 'ادخل غرفة مقفلة زمنياً دون انتظار',
    'preview-horror': 'معاينة غرفة رعب',
    'preview-horror_desc': 'ألقِ نظرة على غرفة رعب قبل الدخول',
    'ai-reflection-trade': 'تأمل الذكاء الاصطناعي',
    'ai-reflection-trade_desc': 'احصل على رسالة شخصية من الذكاء الاصطناعي',
    'unlock-premium-preview': 'تجربة غرفة مميزة',
    'unlock-premium-preview_desc': 'ادخل غرفة مميزة لزيارة واحدة',
    'fragment_trading_room': 'مذبح الشظايا',
    'fragment_trading_intro': 'هنا، يمكنك التضحية بشظاياك المجمعة لكشف الأسرار المخفية في هذه الغرف.',
    'your_collection': 'مجموعتك',
    'fragment_clarity': 'الوضوح',
    'fragment_depth': 'العمق',
    'fragment_shadow': 'الظل',
    'total_fragments': 'المجموع',
    'open_altar': 'اقترب من المذبح',
    'recent_trades': 'القرابين الأخيرة',
    'fragment_trade_ending': 'ما أُعطي لا يمكن استرداده. لكن ما اكتُسب... يبقى.',
    'room_fragment-trade': 'مذبح الشظايا',
    'sub_fragment-trade': 'ضحِّ لكشف الأسرار',
    // Phase 8
    'update_anniversary_system': 'نظام الذكرى السنوية والمعالم - تتبع رحلتك عبر الزمن',
    'update_archive_room': 'الأرشيف - تاريخ الرحلة الكامل يُفتح عند 25 غرفة',
    'update_mirror_room': 'مرآة كل الذوات - صورة سلوكية بالذكاء الاصطناعي عند 50 غرفة',
    'update_ending_room': 'النهاية - وداع شخصي عند 75 غرفة',
    'room_the-archive': 'الأرشيف',
    'room_mirror-of-all-selves': 'مرآة كل الذوات',
    'room_the-ending': 'النهاية',
    // Phase 9
    'update_fragment_rarities': 'ندرة الشظايا - شظايا قابلة للتحصيل عادية ونادرة وأسطورية',
    'update_fragment_gallery': 'معرض الشظايا - عرض مرئي لمجموعتك',
    'update_seasonal_fragments': 'شظايا موسمية وسرية - مقتنيات محدودة الوقت',
    'fragment_collection': 'المجموعة',
    'fragment_collection_title': 'مجموعة الشظايا',
    'fragment_collection_subtitle': 'كل غرفة تترك شيئاً خلفها. اجمعها جميعاً.',
    'collection_milestones': 'المعالم',
    'collection_secrets': 'الأسرار',
    'collection_footer': 'بعض الشظايا تظهر فقط في ظروف معينة.',
    'rarity_common': 'عادي',
    'rarity_rare': 'نادر',
    'rarity_legendary': 'أسطوري',
    // Phase 10
    'accessibility': 'إمكانية الوصول',
    'accessibility_desc': 'تخصيص القراءة وتفضيلات الحركة',
    'font_family': 'عائلة الخط',
    'font_default': 'أحادي المسافة',
    'font_dyslexia': 'صديق لعسر القراءة',
    'font_sans': 'بدون حواف',
    'font_changed': 'تم تحديث الخط',
    'line_height': 'ارتفاع السطر',
    'line_compact': 'مضغوط',
    'line_normal': 'عادي',
    'line_relaxed': 'مريح',
    'line_spacious': 'واسع',
    'line_height_changed': 'تم تحديث ارتفاع السطر',
    'reduced_motion_full_desc': 'تعطيل جميع الرسوم المتحركة والتأثيرات',
    'larger_cursors': 'أهداف أكبر',
    'larger_cursors_desc': 'زيادة حجم أهداف النقر',
    'update_accessibility_suite': 'مجموعة إمكانية الوصول - خطوط عسر القراءة والتحكم في ارتفاع السطر',
    'update_dyslexia_font': 'خيار خط صديق لعسر القراءة في الإعدادات',
    'update_line_height': 'ارتفاع سطر قابل للتعديل لتحسين القراءة',
    'update_enhanced_reduced_motion': 'حركة مخفضة محسنة - تعطيل كامل لجميع التأثيرات',
    // Phase 11
    'update_shared_moments': 'لحظات مشتركة - تسجيلات عاطفية مجهولة مع الآخرين',
    'update_whisper_wall': 'جدار الهمس - اترك حقائق مجهولة في الظلام',
    'update_collective_memory': 'الذاكرة الجماعية - شظايا مشتركة مما نتذكره',
    'whisper_prompt': 'اترك همسة على الجدار. مجهولة. دائمة.',
    'whisper_placeholder': 'ما الذي تحمله بصمت؟',
    'whisper_send': 'اهمس',
    'whisper_added': 'همستك انضمت إلى الجدار.',
    'whisper_another': 'اهمس مرة أخرى',
    'shared_moments_title': 'الآن، في هذه اللحظة...',
    'shared_moments_subtitle': 'كيف حالك، بصدق؟',
    'feeling_grateful': 'ممتن',
    'feeling_lost': 'ضائع',
    'feeling_hopeful': 'متفائل',
    'feeling_heavy': 'ثقيل',
    'feeling_peaceful': 'مسالم',
    'feeling_restless': 'قلق',
    'room_whisper-wall': 'جدار الهمس',
    'room_collective-memory': 'الذاكرة الجماعية',
    'room_shared-moments': 'لحظات مشتركة',

    // v3.21.0 - New Rooms
    'room_mask-you-wear': 'القناع الذي ترتديه',
    'sub_mask-you-wear': 'أي وجه تظهره؟',
    'room_recurring-dream': 'حلمك المتكرر',
    'sub_recurring-dream': 'الحلم الذي يعود دائماً',
    'room_promise-you-broke': 'الوعد الذي كسرته',
    'sub_promise-you-broke': 'صدق حول الوعود المكسورة',
    'room_words-you-swallowed': 'كلمات ابتلعتها',
    'sub_words-you-swallowed': 'أشياء لم تقلها أبداً',
    'room_age-you-feel': 'العمر الذي تشعر به',
    'sub_age-you-feel': 'كم عمرك حقاً؟',
    'room_biggest-lie': 'أكبر كذبة لديك',
    'sub_biggest-lie': 'الكذبة التي تقولها أكثر',
    'room_person-you-pretend-to-be': 'الشخص الذي تتظاهر أنك هو',
    'sub_person-you-pretend-to-be': 'أداؤك',
    'room_person-you-pretend': 'الشخص الذي تتظاهر أنك هو',
    'sub_person-you-pretend': 'أداؤك',
    'room_what-you-collect': 'ماذا تجمع',
    'sub_what-you-collect': 'الأشياء تكشف الهوية',
    'room_apology-you-owe': 'الاعتذار الذي تدين به',
    'sub_apology-you-owe': 'اكتب ما كان يجب أن تقوله',
    'room_unfinished-story': 'قصتك غير المكتملة',
    'sub_unfinished-story': 'الشيء الذي لم تكمله',
    'room_version-others-see': 'النسخة التي يراها الآخرون',
    'sub_version-others-see': 'كيف يراك الآخرون',
    'room_secret-talent': 'موهبتك السرية',
    'sub_secret-talent': 'ما لا يعرفه أحد عنك',
    'room_fear-you-feed': 'الخوف الذي تغذيه',
    'sub_fear-you-feed': 'أي خوف يتحكم بك',
    'room_guilty-pleasure': 'متعتك المذنبة',
    'sub_guilty-pleasure': 'ما تخفي استمتاعك به',
    'room_rule-you-break': 'القاعدة التي تكسرها دائماً',
    'sub_rule-you-break': 'نمط تمردك',
    'room_childhood-promise': 'وعد طفولتك',
    'sub_childhood-promise': 'ما وعدت به نفسك',
    'room_compliment-you-reject': 'المجاملة التي ترفضها',
    'sub_compliment-you-reject': 'لماذا لا تقبل المديح',
    'room_autopilot-mode': 'وضع الطيار الآلي',
    'sub_autopilot-mode': 'ما تفعله بدون تفكير',
    'room_boundary-you-need': 'الحد الذي تحتاجه',
    'sub_boundary-you-need': 'ما يجب أن تحميه',
    'room_inner-critic': 'ناقدك الداخلي',
    'sub_inner-critic': 'الصوت الذي يحكم',
    'room_last-sunday': 'الأحد الماضي',
    'sub_last-sunday': 'ماذا فعلت فعلاً؟',
    'room_wasted-hours': 'ساعاتك الضائعة',
    'sub_wasted-hours': 'أين يختفي الوقت',
    'room_year-that-changed': 'السنة التي غيرتك',
    'sub_year-that-changed': 'نقطة التحول',
    'room_tomorrows-regret': 'ندم الغد',
    'sub_tomorrows-regret': 'ماذا ستتمنى لو فعلت؟',
    'room_morning-autopilot': 'طيارك الآلي الصباحي',
    'sub_morning-autopilot': 'طقوس الساعة الأولى',
    'room_season-you-dread': 'الموسم الذي تخشاه',
    'sub_season-you-dread': 'أي فصل أثقل؟',
    'room_ten-years-ago': 'قبل عشر سنوات',
    'sub_ten-years-ago': 'من كنت حينها؟',
    'room_last-normal-day': 'آخر يوم عادي',
    'sub_last-normal-day': 'قبل أن يتغير كل شيء',
    'room_clock-you-ignore': 'الساعة التي تتجاهلها',
    'sub_clock-you-ignore': 'وقت تتظاهر أنه لا يمر',
    'room_your-deadline': 'موعدك النهائي',
    'sub_your-deadline': 'ماذا تؤجل؟',
    'room_the-pause': 'التوقف',
    'sub_the-pause': 'تنفس للحظة',
    'room_static-room': 'ثابت',
    'sub_static-room': 'ضوضاء بيضاء وسكون',
    'room_gap-between-thoughts': 'الفجوة بين الأفكار',
    'sub_gap-between-thoughts': 'اعثر على المساحة',
    'room_sit-with-discomfort': 'اجلس مع الانزعاج',
    'sub_sit-with-discomfort': 'لا تصلح شيئاً',
    'room_boring-room': 'الغرفة المملة',
    'sub_boring-room': 'لا شيء مثير على الإطلاق',
    'room_unplug-room': 'افصل',
    'sub_unplug-room': 'تخيل بلا أجهزة',
    'room_unplug': 'افصل',
    'sub_unplug': 'تخيل بلا أجهزة',
    'room_last-page': 'الصفحة الأخيرة',
    'sub_last-page': 'القصة تنتهي هنا',
    'room_scrolling-pattern': 'نمط التمرير',
    'sub_scrolling-pattern': 'كيف تستهلك المحتوى',
    'room_tab-you-keep-open': 'التبويب المفتوح دائماً',
    'sub_tab-you-keep-open': 'ما يبقى في متصفحك',
    'room_digital-shadow': 'ظلك الرقمي',
    'sub_digital-shadow': 'بصمتك الرقمية',
    'room_notification-you-ignore': 'الإشعار الذي تتجاهله',
    'sub_notification-you-ignore': 'ما ترفضه',
    'room_typing-speed': 'سرعة كتابتك',
    'sub_typing-speed': 'بأي سرعة تعبر؟',
    'room_link-you-almost-clicked': 'الرابط الذي كدت تضغطه',
    'sub_link-you-almost-clicked': 'أنماط التردد',
    'room_bookmark-graveyard': 'مقبرة المفضلات',
    'sub_bookmark-graveyard': 'حُفظت ولم تُعد زيارتها',
    'room_search-you-deleted': 'البحث الذي حذفته',
    'sub_search-you-deleted': 'ما بحثت عنه ومحوته',
    'room_gravity-room': 'غرفة الجاذبية',
    'sub_gravity-room': 'كل شيء يسقط ببطء',
    'room_ink-blot': 'بقعة حبر',
    'sub_ink-blot': 'ماذا ترى؟',
    'room_hallway-of-mirrors': 'ممر المرايا',
    'sub_hallway-of-mirrors': 'انعكاسات لا نهائية',
    'room_dissolving-text': 'نص يذوب',
    'sub_dissolving-text': 'الكلمات تذوب أثناء القراءة',
    'room_the-pendulum': 'البندول',
    'sub_the-pendulum': 'حركة مُنوِّمة',
    'room_pixel-rain': 'مطر البكسل',
    'sub_pixel-rain': 'هطول رقمي',
    'room_the-threshold': 'العتبة',
    'sub_the-threshold': 'بين هنا وهناك',
    'most_loved_rooms': 'الغرف الأكثر حباً',
    'rate_room': 'قيّم هذه الغرفة',
    'update_52_new_rooms': '52 غرفة جديدة عبر فئات الهوية والوقت والتأمل والميتا والتجريبية',
    'update_room_rating': 'نظام تقييم الغرف - إعجاب/عدم إعجاب مع قسم الغرف الأكثر حباً',
    'update_bug_fix_reflections': 'إصلاح عرض وتخزين التأملات المجهولة',
    'update_100_rooms': 'أكثر من 100 غرفة متاحة الآن في الممر',
    // v3.22.0
    'room_the-friend-you-lost': 'الصديق الذي فقدته',
    'sub_the-friend-you-lost': 'اكتب لشخص ابتعدت عنه.',
    'room_conversation-you-replay': 'المحادثة التي تعيدها',
    'sub_conversation-you-replay': 'أعد بناءها من الذاكرة.',
    'room_the-thank-you': 'الشكر',
    'sub_the-thank-you': 'امتنان لم تعبّر عنه أبداً.',
    'room_people-you-forgot': 'أشخاص نسيتهم',
    'sub_people-you-forgot': 'حاول تذكر أسماء منسية.',
    'room_the-advice-you-give': 'النصيحة التي تقدمها',
    'sub_the-advice-you-give': 'نصيحة تعطيها لكن لا تتبعها.',
    'room_what-others-dont-know': 'ما لا يعرفه الآخرون',
    'sub_what-others-dont-know': 'طبقاتك المخفية.',
    'room_the-lie-you-believe': 'الكذبة التي تصدقها',
    'sub_the-lie-you-believe': 'حقيقة مريحة غير صحيحة.',
    'room_your-shadow-self': 'ظلك الداخلي',
    'sub_your-shadow-self': 'الجزء الذي تقمعه.',
    'room_the-story-you-tell': 'القصة التي ترويها',
    'sub_the-story-you-tell': 'كيف تروي حياتك.',
    'room_your-safe-place': 'مكانك الآمن',
    'sub_your-safe-place': 'صف المكان الذي تشعر فيه بالأمان.',
    'room_the-risk-you-didnt-take': 'المخاطرة التي لم تأخذها',
    'sub_the-risk-you-didnt-take': 'طرق لم تسلكها.',
    'room_your-last-excuse': 'عذرك الأخير',
    'sub_your-last-excuse': 'العذر الذي تستخدمه أكثر.',
    'room_the-habit-you-hide': 'العادة التي تخفيها',
    'sub_the-habit-you-hide': 'روتين سري.',
    'room_what-you-postpone': 'ما تؤجله',
    'sub_what-you-postpone': 'الشيء الذي دائماً "غداً".',
    'room_the-door-you-closed': 'الباب الذي أغلقته',
    'sub_the-door-you-closed': 'فرص رفضتها.',
    'journal_title': 'يوميات التأمل',
    'journal_subtitle': 'أفكارك عبر الغرف',
    'journal_empty': 'لا تأملات بعد. زر الغرف واكتب أفكارك.',
    'journal': 'اليوميات',
    'room_chains': 'سلاسل الغرف',
    'chain_identity_journey': 'رحلة الهوية',
    'chain_time_reflection': 'تأمل الزمن',
    'chain_truth_path': 'مسار الحقيقة',
    'recently_visited': 'زيارات أخيرة',
    'update_reflection_journal': 'يوميات التأمل - احفظ أفكارك عبر الغرف',
    'update_room_chains': 'سلاسل الغرف - أكمل الغرف بالتسلسل لأجزاء إضافية',
    'update_15_new_rooms': '15 غرفة تفاعلية جديدة عبر 3 فئات',
    'update_recently_visited': 'قسم الزيارات الأخيرة للعودة السريعة',
    'ai_companion': 'الرفيق',
    'companion_create': 'أنشئ رفيقك',
    'companion_setup_desc': 'انعكاس يسير بجانبك.',
    'companion_name': 'سمّ رفيقك',
    'companion_name_placeholder': 'مثلاً: صدى، مرآة، ظل...',
    'companion_lens': 'عدسة الشخصية',
    'companion_mood': 'مزاج التواصل',
    'companion_fears': 'ما الذي تخاف منه؟ (اختياري)',
    'companion_struggles': 'ما الذي تعاني منه؟ (اختياري)',
    'create_companion': 'إنشاء',
    'companion_input': 'قل شيئاً...',
    'companion_greeting': 'رفيقك هنا. قل شيئاً.',
    'companion_starter_1': 'ما الذي يشغل بالي اليوم؟',
    'companion_starter_2': 'أشعر بأنني عالق.',
    'companion_starter_3': 'أخبرني بشيء أتجنبه.',
    'fears_placeholder': 'مثلاً: الفشل، الرفض، النسيان...',
    'struggles_placeholder': 'مثلاً: الإفراط في التفكير، التسويف، الوحدة...',
    'back_step': 'رجوع',
    'lens_reflective': 'تأملي',
    'lens_reflective_desc': 'أسئلة عميقة، التعرف على الأنماط',
    'lens_supportive': 'داعم',
    'lens_supportive_desc': 'دافئ، مشجع، مُصدّق',
    'lens_challenging': 'تحدّي',
    'lens_challenging_desc': 'مباشر، مثير للتفكير',
    'lens_curious': 'فضولي',
    'lens_curious_desc': 'أسئلة غير متوقعة، دهشة',
    'lens_shadow': 'ظل',
    'lens_shadow_desc': 'الجانب الخفي، انزعاج صادق',
    'companion_mood_calm': 'هادئ',
    'companion_mood_calm_desc': 'متزن وسلمي',
    'companion_mood_intense': 'حاد',
    'companion_mood_intense_desc': 'عاجل وعميق',
    'companion_mood_playful': 'مرح',
    'companion_mood_playful_desc': 'ذكي وخفيف',
    'companion_mood_melancholic': 'حزين',
    'companion_mood_melancholic_desc': 'حزن جميل',
    'update_ai_companion': 'رفيق ذكي - ذكاء اصطناعي يتذكر رحلتك',
  },

  es: {
    // Index
    'title': 'LAS HABITACIONES HUMANAS',
    'tagline': 'Una colección de habitaciones que reflejan quién eres cuando nadie te mira.',
    'enter': 'Entrar',
    'footer': 'No es terapia. No es social. No es productivo.',
    
    // Hallway
    'back': '← Volver',
    'hallway': 'El Pasillo',
    'challenges': 'Desafíos',
    'random': 'Aleatorio',
    'open_rooms': 'Habitaciones Abiertas',
    'dark_rooms': 'Las Habitaciones Oscuras',
    'enter_risk': 'entra bajo tu propio riesgo',
    'premium_rooms': 'Habitaciones Premium',
    'unlock_to_access': 'Desbloquear para acceder',
    'daily_rooms': 'Habitaciones Diarias',
    'refreshes_daily': 'se actualiza cada día',
    'time_locked': 'Habitaciones Bloqueadas por Tiempo',
    'seasonal_rooms': 'Habitaciones Estacionales',
    'available_now': 'Disponible ahora',
    'fragments': 'Fragmentos',
    'boss_unlocked': 'Sala del Jefe Desbloqueada',
    'collect_unlock': 'Colecta para desbloquear la Sala del Jefe',
    'dark_fragments': 'Fragmentos Oscuros',
    'dark_boss_unlocked': 'Jefe Oscuro Desbloqueado',
    'hidden_discovered': 'Habitación Oculta Descubierta',
    'stillness_revealed': 'Tu quietud reveló algo...',
    'constellation_map': 'Mapa de Constelaciones',
    'your_stats': 'Tus Estadísticas',
    'completed': 'Completado',
    'game_rooms': 'Salas de Juego',
    'experimental_rooms': 'Salas Experimentales',
    'deep_rooms': 'Salas Profundas',
    'meta_rooms': 'Salas Meta',
    'search_rooms': 'Buscar salas...',
    'no_results': 'No hay salas que coincidan',
    'clear_filters': 'Limpiar filtros',
    'explore': 'Explorar',
    'all_categories': 'Todas',
    
    // About
    'what_is_this': '¿Qué es este lugar?',
    'not_productivity': 'Las Habitaciones Humanas no es una herramienta de productividad.',
    'not_social': 'No es una red social.',
    'not_therapy': 'No es terapia.',
    'pause_experiences': 'Es una colección de pequeñas experiencias diseñadas para hacerte pausar.',
    'each_room': 'Cada habitación toma de 30 segundos a 2 minutos.',
    'make_think': 'Algunas habitaciones te harán pensar.',
    'make_uncomfortable': 'Algunas te harán sentir incómodo.',
    'make_calm': 'Algunas te calmarán.',
    'make_aware': 'Todas te harán consciente.',
    'no_notifications': 'No hay notificaciones aquí.',
    'no_points': 'No hay puntos que coleccionar.',
    'no_validation': 'Sin validación social.',
    'just_truth': 'Solo tú, una habitación, y la verdad.',
    'enter_rooms': 'Entrar a las habitaciones',
    
    // Common
    'loading': 'Cargando...',
    'error': 'Error',
    'continue': 'Continuar',
    'submit': 'Enviar',
    'cancel': 'Cancelar',
    'close': 'Cerrar',
    'next': 'Siguiente',
    'previous': 'Anterior',
    'start': 'Iniciar',
    'stop': 'Detener',
    'yes': 'Sí',
    'no': 'No',
    'leave_room': '← Salir de la habitación',
    'return': '← Volver',
    'return_hallway': 'Volver al Pasillo',
    'escape': '← Escapar',
    'run': '← Huir',
    'leave': '← Salir',
    'begin': 'Comenzar',
    'watch': 'Mira...',
    'correct': 'Correcto',
    'round': 'Ronda',
    'enter_again': 'Entrar de nuevo',
    'steps': 'Pasos',
    'depth': 'Profundidad',
    
    // Keyboard Shortcuts
    'keyboard_shortcuts': 'Atajos de Teclado',
    'shortcut_hallway': 'Ir al Pasillo',
    'shortcut_random': 'Habitación Aleatoria',
    'shortcut_back': 'Volver',
    'shortcut_bookmark': 'Alternar Marcador',
    'shortcut_reading': 'Modo Lectura',
    'shortcut_search': 'Enfocar Búsqueda',
    'shortcut_stats': 'Ver Estadísticas',
    'shortcut_achievements': 'Ver Logros',
    'shortcut_cat_nav': 'Navegación',
    'shortcut_cat_rooms': 'Habitaciones',
    'shortcut_cat_access': 'Accesibilidad',
    'press_to_close': 'Presiona',
    'to_close': 'para cerrar',
    'or': 'o',
    
    // Bookmarks
    'add_bookmark': 'Añadir Marcador',
    'remove_bookmark': 'Quitar Marcador',
    'bookmarks': 'Marcadores',
    'bookmarked_rooms': 'Marcados',
    'no_bookmarks': 'Sin marcadores aún',
    
    // Reading Mode
    'reading_mode': 'Modo Lectura',
    'reading_mode_on': 'Modo lectura activado',
    'reading_mode_off': 'Modo lectura desactivado',
    
    // Updates Page
    'updates': 'Actualizaciones',
    'updates_title': 'Novedades',
    'updates_subtitle': 'Cambios y mejoras recientes',
    'updates_footer': 'Construyendo habitaciones, una actualización a la vez.',
    'new': 'NUEVO',
    'subscribe_rss': 'Suscribirse vía RSS',
    'latest': 'ÚLTIMO',
    'whats_new_intro': 'Últimas actualizaciones:',
    'view_all_updates': 'Ver todas las actualizaciones',
    'update_updates_page': 'Página de actualizaciones añadida',
    'update_full_i18n': 'Traducción completa a 6 idiomas',
    'update_hallway_animations': 'Animaciones suaves al cambiar categorías',
    'update_daily_challenges': 'Mejora en los desafíos diarios',
    'update_game_rooms': '15 nuevas salas de juego',
    'update_experimental_rooms': '14 salas experimentales',
    'update_random_room': 'Botón de habitación aleatoria',
    'update_stats_route': 'Ruta de estadísticas corregida',
    'update_streak_system': 'Sistema de rachas diarias',
    'update_weekly_summary': 'Resúmenes de progreso semanal',
    'update_rtl_support': 'Soporte de idiomas RTL',
    'update_dark_rooms': 'Nueva categoría de habitaciones oscuras',
    'update_boss_rooms': 'Salas de jefe con fragmentos',
    'update_audio_system': 'Audio ambiental en habitaciones',
    'update_constellation_map': 'Mapa de constelaciones del viaje',
    'update_fragment_system': 'Sistema de fragmentos de habitaciones',
    'update_mobile_layout': 'Mejoras en diseño móvil',
    'update_multi_language': 'Soporte multiidioma (6 idiomas)',
    'update_premium_system': 'Sistema de habitaciones premium',
    'update_room_endings': 'Citas finales únicas',
    'update_ui_redesign': 'Rediseño completo de la interfaz',
    'update_theme_effects': 'Efectos visuales por tema (brasas, estrellas, glitches)',
    'update_void_theme': 'Nuevo tema Vacío - modo oscuridad absoluta',
    'update_achievements': 'Sistema de logros con 19 hitos',
    'update_ai_reflection': 'Sala de reflexión con IA',
    'update_settings_redesign': 'Configuraciones rediseñadas con 4 temas',
    'update_theme_exclusive_rooms': 'Salas exclusivas por tema',
    'update_discovery_localization': 'Mensajes de descubrimiento traducidos',
    'update_site_mood_system': 'Sistema de estado de ánimo del sitio',
    'update_voice_imperfection': 'Salas con voz imperfecta',
    'update_mobile_optimization': 'Optimización móvil con objetivos de 48px',
    'update_interaction_clues': 'Pistas sutiles de interacción',
    'update_theme_exclusive_hallway': 'Salas exclusivas visibles en el pasillo',
    'update_advanced_easter_eggs': 'Easter eggs avanzados: secuencia de esquinas, morse, visitante nocturno',
    'update_hidden_discoveries': 'Revelaciones de texto oculto',
    'update_dual_mode_system': 'Sistema dual Clásico y Alternativo',
    'update_alternate_mode': 'Modo alternativo - una realidad paralela experimental',
    'update_hidden_discovery': 'Disparadores de descubrimiento ocultos: triple toque, patrones de scroll',
    'update_mode_effects': 'Efectos visuales específicos por modo, glitches y audio ambiental',
    'update_translation_navigator': 'Navegador de Traducción - panel de cobertura visual',
    'update_full_localization': 'Cobertura de traducción casi completa en 6 idiomas',
    'update_meta_room_translations': 'Contenido de salas meta traducido (La Negativa, Tú Paralelo, Tu Sombra)',
    'update_animated_timeline': 'Línea de tiempo animada con transiciones al desplazar',
    'update_toast_notifications': 'Notificación emergente para nuevas actualizaciones',
    'update_rss_feed': 'Feed RSS para suscripciones de cambios',
    'update_bookmarked_filter': 'Pestaña de Habitaciones Marcadas en el Pasillo',
    'update_keyboard_shortcuts': 'Atajos de teclado (presiona ? para ver)',
    'update_reading_mode': 'Modo lectura para mejor tipografía y enfoque',
    'update_accessibility': 'Indicadores de enfoque y navegación con teclado mejorados',
    'update_ambient_crossfade': 'Transiciones suaves entre pistas de audio ambiental',
    'update_settings_audio': 'Controles de audio centralizados en Configuración',
    'update_arabic_complete': 'Cobertura completa de traducción árabe',
    'update_duplicate_icon_fix': 'Iconos de audio duplicados corregidos en el encabezado',

    // Game UI
    'game_watching': 'observando...',
    'game_repeat': 'repetir',
    'game_echo_fades': 'el eco se desvanece...',
    'game_escaped': 'Escapaste.',
    'game_found_exit': 'Encontraste la salida.',
    'game_looping': 'Estás en un bucle.',
    'game_deeper': 'Más profundo...',
    'game_doors_shift': 'Las puertas cambian.',
    'maze_left': 'izquierda',
    'maze_right': 'derecha',
    'maze_forward': 'adelante',
    'maze_back': 'atrás',
    'maze_past': 'pasado',
    'maze_future': 'futuro',
    'maze_here': 'aquí',
    'maze_there': 'allí',
    'maze_truth': 'verdad',
    'maze_lies': 'mentiras',
    'maze_light': 'luz',
    'maze_dark': 'oscuridad',
    'maze_memory': 'memoria',
    'maze_dream': 'sueño',
    'maze_silence': 'silencio',
    'maze_noise': 'ruido',
    'random_room': 'Habitación Aleatoria',
    'ai_reflection': 'Reflexión IA',
    'support': 'Soporte',
    'choose_direction': 'Elige una dirección. Algunas puertas responden.',
    'rooms_explored': 'habitaciones exploradas',
    'your_journey': 'Tu Viaje',
    'complete_rooms_fragments': 'Completa habitaciones para recolectar fragmentos.',
    'no_rooms_completed': 'Ninguna habitación completada aún.',
    
    // Stats
    'total_time': 'Tiempo Total',
    'rooms_visited': 'Habitaciones Visitadas',
    'total_visits': 'Total de Visitas',
    'most_visited': 'Más Visitada',
    'longest_session': 'Sesión Más Larga',
    'fragments_collected': 'Fragmentos Recolectados',
    'completed_rooms': 'Habitaciones Completadas',
    'privacy_note': 'Todos los datos se almacenan localmente en tu dispositivo',
    
    // Daily Challenges
    'daily_challenges': 'Desafíos Diarios',
    'challenges_completed': 'completado',
    'challenges_total': 'total',
    'resets_midnight': 'Se reinicia a medianoche',
    
    // Weekly Summary
    'weekly_summary': 'Resumen Semanal',
    'week': 'Semana',
    'this_week': 'Esta Semana',
    'rooms': 'Habitaciones',
    'days': 'Días',
    'vs_last_week': 'vs semana pasada',
    'refresh_insight': 'Actualizar perspectiva',
    
    // Game Room Titles
    'room_memory-puzzle': 'Rompecabezas de Memoria',
    'room_shadow-chase': 'Persecución de Sombras',
    'room_timing-room': 'Sala de Tiempo',
    'room_pattern-whisper': 'Susurro de Patrones',
    'room_silence-puzzle': 'Rompecabezas del Silencio',
    'room_decay-game': 'Juego de Decadencia',
    'room_observation-test': 'Prueba de Observación',
    'room_balance-room': 'Sala del Equilibrio',
    'room_word-drift': 'Deriva de Palabras',
    'room_color-echo': 'Eco de Color',
    'room_mirror-tap': 'Toque del Espejo',
    'room_maze-of-doors': 'Laberinto de Puertas',
    'room_pulse-match': 'Coincidencia de Pulso',
    'room_light-path': 'Camino de Luz',
    'room_sound-match': 'Coincidencia de Sonido',
    
    // Game Room Subtitles
    'sub_memory-puzzle': 'Combina patrones que cambian sutilmente',
    'sub_shadow-chase': 'Atrapa lo que se mueve impredeciblemente',
    'sub_timing-room': 'Haz clic exactamente en el momento',
    'sub_pattern-whisper': 'Repite la secuencia que se desvanece',
    'sub_silence-puzzle': 'Resuelve sin instrucciones',
    'sub_decay-game': 'Toca para preservar lo que se desvanece',
    'sub_observation-test': 'Encuentra diferencias sutiles',
    'sub_balance-room': 'Mantén los objetos flotantes alineados',
    'sub_word-drift': 'Atrapa las letras antes de que caigan',
    'sub_color-echo': 'Repite la secuencia de tonos sutiles',
    'sub_mirror-tap': 'Cada toque resuena en otro lugar',
    'sub_maze-of-doors': 'Las puertas se reorganizan después de cada elección',
    'sub_pulse-match': 'Muévete al ritmo de la sala',
    'sub_light-path': 'Guía la luz a través de la oscuridad cambiante',
    'sub_sound-match': 'Toca formas para coincidir tonos suaves',
    
    // Experimental Room Titles
    'room_mirror-room': 'Sala del Espejo',
    'room_echo-room': 'Sala del Eco',
    'room_pulse-room': 'Sala del Pulso',
    'room_vanishing-room': 'Sala que Desvanece',
    'room_letter-room': 'Sala de Cartas',
    'room_empty-room': 'Sala Vacía',
    'room_reverse-room': 'Sala Inversa',
    'room_whisper-room': 'Sala del Susurro',
    'room_shadow-room': 'Sala de Sombras',
    'room_labyrinth-room': 'Sala del Laberinto',
    'room_color-room': 'Sala de Colores',
    'room_surrender-room': 'Sala de Rendición',
    'room_noise-room': 'Sala del Ruido',
    'room_observer-room': 'Sala del Observador',
    
    // Experimental Room Subtitles
    'sub_mirror-room': 'Reflejos ligeramente distorsionados',
    'sub_echo-room': 'Los clics desencadenan ecos retardados',
    'sub_pulse-room': 'El ambiente respira',
    'sub_vanishing-room': 'Los elementos se desvanecen al interactuar',
    'sub_letter-room': 'Las palabras se revuelven como memoria fallida',
    'sub_empty-room': 'Vacío hasta que encuentres secretos',
    'sub_reverse-room': 'Las acciones se comportan al revés',
    'sub_whisper-room': 'Verdades apenas audibles',
    'sub_shadow-room': 'Tu compañero oscuro',
    'sub_labyrinth-room': 'Encuentra tu camino',
    'sub_color-room': 'Meditación cromática',
    'sub_surrender-room': 'Suelta el control',
    'sub_noise-room': 'Pensamientos estáticos',
    'sub_observer-room': 'Siendo observado',
    
    // Daily Joke Room
    'joke_number': 'Chiste #{number}',
    'joke_today': 'El Chiste de Hoy',
    'joke_reveal': 'Revelar el remate',
    'joke_come_back': 'Vuelve mañana para un nuevo chiste.',
    'joke_quote': 'La risa es la distancia más corta entre dos personas.',
    
    // Settings Page
    'settings': 'Configuración',
    'settings_subtitle': 'Personaliza tu viaje',
    'language': 'Idioma',
    'ambient_audio': 'Audio Ambiental',
    'ambient_audio_desc': 'Sonidos de fondo para las salas',
    'reduced_motion': 'Movimiento Reducido',
    'reduced_motion_desc': 'Minimizar animaciones',
    'data_management': 'Gestión de Datos',
    'export_journey': 'Exportar Datos del Viaje',
    'clear_all_data': 'Borrar Todos los Datos',
    'audio_enabled': 'Audio Activado',
    'audio_disabled': 'Audio Desactivado',
    'data_exported': 'Datos Exportados',
    'clear_data_confirm': '¿Estás seguro? Esto borrará todo tu progreso.',
    'data_cleared': 'Datos Borrados',
    // Theme settings
    'theme': 'Tema',
    'theme_dark': 'Oscuro',
    'theme_dark_desc': 'Oscuridad pura',
    'theme_bone': 'Hueso',
    'theme_bone_desc': 'Palidez mortal',
    'theme_ash': 'Ceniza',
    'theme_ash_desc': 'Restos del fuego',
    'theme_fog': 'Niebla',
    'theme_fog_desc': 'Incertidumbre etérea',
    'theme_twilight': 'Crepúsculo',
    'theme_twilight_desc': 'Entre día y noche',
    'theme_eclipse': 'Eclipse',
    'theme_eclipse_desc': 'Luz devorada por la oscuridad',
    'theme_dawn': 'Amanecer',
    'theme_dawn_desc': 'Primera luz del despertar',
    'theme_changed': 'Tema actualizado',
    'font_size': 'Tamaño de Fuente',
    'font_small': 'Pequeño',
    'font_medium': 'Mediano',
    'font_large': 'Grande',
    'font_size_changed': 'Tamaño de fuente actualizado',
    'high_contrast': 'Alto Contraste',
    'high_contrast_desc': 'Aumentar contraste de texto y bordes',
    'high_contrast_enabled': 'Alto contraste activado',
    'high_contrast_disabled': 'Alto contraste desactivado',
    'theme_void': 'Vacío',
    'theme_void_desc': 'Solo quedan sombras',
    
    // Ambient Music Control
    'ambient_music': 'Música Ambiental',
    'ambient_music_desc': 'Controla los paisajes sonoros de fondo',
    'volume': 'Volumen',
    'ambient_track': 'Pista Ambiental',
    'playing': 'Reproduciendo',
    'play': 'Reproducir',
    'ambient_default': 'Calma',
    'ambient_night': 'Noche',
    'ambient_scary': 'Espeluznante',
    'ambient_truth': 'Verdad',
    'ambient_identity': 'Reflejo',
    'ambient_mortality': 'Tiempo',
    'ambient_nothing': 'Vacío',
    'ambient_meta': 'Digital',
    'ambient_daily': 'Cálido',
    'ambient_seasonal': 'Mágico',
    'ambient_premium': 'Profundo',
    
    // Easter Egg Discoveries
    'discovery_label': 'Descubrimiento',
    'discovery_konami': 'Conoces los viejos caminos.',
    'discovery_secret_sequence': 'Deletreaste la verdad.',
    'discovery_long_press': 'La paciencia revela secretos.',
    'discovery_triple_click': 'La persistencia da frutos.',
    'discovery_silence': 'La quietud habla.',
    
    // Theme-Exclusive Rooms
    'ash_exclusive': 'Exclusivo del tema Ceniza',
    'twilight_exclusive': 'Exclusivo del tema Crepúsculo',
    'void_exclusive': 'Exclusivo del tema Vacío',
    'theme_required': 'Requiere tema {theme}',
    'ash_embers_title': 'De las Cenizas',
    'ash_embers_line1': 'Todo arde eventualmente.',
    'ash_embers_line2': 'Lo que importa es lo que surge después.',
    'ash_embers_line3': 'Toca la pantalla. Crea brasas.',
    'ash_embers_quote': 'De las cenizas, recordamos lo que una vez ardió.',
    'room_ash-embers': 'Cenizas y Brasas',
    'sub_ash-embers': 'Solo visible en tema Ceniza',
    'twilight_stars_title': 'Entre Día y Noche',
    'twilight_stars_line1': 'Las estrellas recuerdan a todos los que miraron hacia arriba.',
    'twilight_stars_line2': 'Conéctalas. Haz tu propia constelación.',
    'twilight_stars_quote': 'En el crepúsculo, vemos lo que la luz del día oculta.',
    'room_twilight-stars': 'Estrellas del Crepúsculo',
    'sub_twilight-stars': 'Solo visible en tema Crepúsculo',
    'void_abyss_title': 'El Abismo',
    'void_abyss_line1': 'Mira al vacío. El vacío te devuelve la mirada.',
    'void_word_1': 'nada',
    'void_word_2': 'vacío',
    'void_word_3': 'silencio',
    'void_word_4': 'ido',
    'void_word_5': 'solo',
    'void_word_6': 'fin',
    'void_revealed': 'revelado',
    'void_abyss_quote': 'En la oscuridad absoluta, finalmente nos vemos.',
    'room_void-abyss': 'El Abismo',
    'sub_void-abyss': 'Solo visible en tema Vacío',
    'shared_room': 'Sala Compartida',
    'imagine_room': 'Imaginar',
    'imagine_title': 'Imagínate',
    'imagine_subtitle': 'Describe cómo te ves. Deja que la sala te muestre lo que emerge de tus palabras.',
    'imagine_placeholder': 'Me veo como alguien que...\n\nDescribe tu esencia, tu naturaleza, cómo te sientes por dentro.',
    'imagine_hint': 'Sé honesto. La imagen refleja tus palabras.',
    'imagine_generate': 'Ver Yo',
    'imagine_generating': 'Creando...',
    'imagine_try_again': 'Describir de Nuevo',
    'imagine_quote': 'No nos vemos claramente. Vemos lo que creemos.',
    'room_imagine-yourself': 'Imagínate',
    'sub_imagine-yourself': 'Descríbete. Ve lo que emerge.',
    
    // Theme Exclusive Section
    'theme_exclusive_rooms': 'Salas Exclusivas por Tema',
    'theme_exclusive_hint': 'Cambia el tema en configuración para desbloquear estas salas.',
    'requires_theme': 'Requiere',
    'unlocked': 'Desbloqueado',
    'switch_to': 'Cambiar a',
    
    // Advanced Easter Eggs
    'corner_sequence': 'Trazaste los límites.',
    'morse_sos': 'S.O.S. recibido.',
    'reverse_scroll': 'Volviendo... pero más rápido.',
    'midnight_visitor': 'La hora de las brujas te da la bienvenida.',
    'shake': 'Sacudiste algo.',
    
    // Experience Mode
    'classic_mode': 'Clásico',
    'alternate_mode': 'Alternativo',
    'switch_to_alternate': 'Entrar a realidad alternativa',
    'switch_to_classic': 'Volver al clásico',
    'enter_parallel_reality': 'Entrar a una realidad paralela',
    'return_to_familiar': 'Volver a lo familiar',
    'something_shifted': 'Algo cambió',
    'reality_feels_different': 'La realidad se siente diferente ahora.',
    'alternate_tagline': 'Las mismas habitaciones. Verdades diferentes.',
    'alternate_enter': 'Descender',
    'alternate_footer': 'Las cosas no son lo que parecen.',
    'alternate_rooms': '░▓ Re4l1dad P4ralela ▓░',
    'alternate_rooms_hint': '[ estas hab1taciones solo ex1sten en el paralelo ]',
    'experience_mode': 'Modo de Experiencia',
    'classic_mode_desc': 'La realidad familiar',
    'alternate_mode_desc': 'Una dimensión paralela',
    
    // Alternate Mode Exclusive Rooms (Spanish)
    'room_the-other-side': 'El Otro Lado',
    'room_the-unraveling': 'El Deshilado',
    'room_the-echo': 'El Eco',
    'sub_the-other-side': 'muévete lentamente en la oscuridad',
    'sub_the-unraveling': 'la realidad se deshace con cada palabra',
    'sub_the-echo': 'lo que dices nunca se va',
    
    // Meta Rooms (Spanish)
    'room_the-refusal': 'El Rechazo',
    'sub_the-refusal': 'A veces el sitio te rechaza',
    'room_parallel-you': 'Tú Paralelo',
    'sub_parallel-you': 'Otra versión de ti',
    'room_your-shadow': 'Tu Sombra',
    'sub_your-shadow': 'Lo que te sigue',
    
    // AI Rooms (Spanish)
    'room_ai-reflection-room': 'Sala de Reflexión IA',
    'room_dream-visualization': 'Visualización de Sueños',
    'room_future-self': 'Tu Yo Futuro',
    'sub_ai-reflection-room': 'Deja que la IA reflexione contigo',
    'sub_dream-visualization': 'Describe tu sueño. Míralo tomar forma.',
    'sub_future-self': 'Un mensaje del futuro',
    
    // Meta Room Content - The Refusal (Spanish)
    'refusal_line1': 'Esta sala te rechaza.',
    'refusal_line2': 'No todo se abre cuando tú quieres.',
    'refusal_line3': 'A veces la respuesta es simplemente: no.',
    'refusal_line4': 'Prueba el botón. No ayudará.',
    'refusal_button': 'Entrar a la Sala',
    
    // Meta Room Content - Parallel You (Spanish)
    'parallel_choice_1': 'Elegiste la velocidad.',
    'parallel_alternate_1': 'Otro tú eligió la paciencia.',
    'parallel_reflection_1': 'Todavía están esperando.',
    'parallel_choice_2': 'Te fuiste temprano.',
    'parallel_alternate_2': 'Otro tú se quedó hasta el final.',
    'parallel_reflection_2': 'Vieron lo que te perdiste.',
    'parallel_choice_3': 'Evitaste la pregunta difícil.',
    'parallel_alternate_3': 'Otro tú la respondió.',
    'parallel_reflection_3': 'Cargan esa verdad ahora.',
    'parallel_choice_4': 'Pasaste corriendo.',
    'parallel_alternate_4': 'Otro tú saboreó cada momento.',
    'parallel_reflection_4': 'Recuerdan más.',
    'parallel_choice_5': 'Seguiste desplazándote.',
    'parallel_alternate_5': 'Otro tú se detuvo.',
    'parallel_reflection_5': 'Encontraron algo.',
    'parallel_choice_6': 'Elegiste la comodidad.',
    'parallel_alternate_6': 'Otro tú eligió la incomodidad.',
    'parallel_reflection_6': 'Crecieron con ello.',
    'parallel_quote': 'Cada momento se divide en caminos infinitos. Solo caminas uno.',
    
    // Meta Room Content - Your Shadow (Spanish)
    'shadow_generated': 'Generado desde tu comportamiento',
    'shadow_intro': 'Tu sombra...',
    'shadow_follows': 'Esta sombra te sigue por cada sala.',
    'shadow_quote': 'No somos lo que pensamos. Somos lo que hacemos.',
    'shadow_trait_completion': 'busca completar',
    'shadow_trait_surface': 'prefiere la superficie',
    'shadow_trait_lingers': 'permanece en espacios',
    'shadow_trait_moves_quickly': 'se mueve rápido',
    'shadow_trait_avoids_depth': 'evita la profundidad',
    'shadow_trait_stillness': 'abraza la quietud',
    'shadow_trait_chases': 'persigue algo sin nombre',
    'shadow_trait_darkness': 'atraído por la oscuridad',
    'shadow_trait_emptiness': 'cómodo con el vacío',
    'shadow_trait_undefined': 'indefinido',
    'shadow_trait_forming': 'aún formándose',
    
    // Translation Navigator (Spanish)
    'translation_navigator': 'Navegador de Traducciones',
    'translation_coverage': 'Cobertura de traducciones por idioma',
    'keys': 'claves',
    'room_translations': 'Traducciones de Salas',
    'show_all': 'Mostrar Todo',
    'show_missing': 'Mostrar Faltantes',
    'all_translated': '¡Todas las salas están traducidas!',
    'missing_translations': 'traducciones faltantes',
    
    // Additional Room Titles (Spanish)
    'room_how-many-times-left': 'Cuántas Veces Quedan',
    'room_life-in-weeks': 'Tu Vida en Semanas',
    'room_delay-button': 'El Botón de Retraso',
    'room_one-honest-sentence': 'Una Frase Honesta',
    'room_this-moment': 'Este Momento No Se Repetirá',
    'room_reality-receipt': 'Recibo de Realidad',
    'room_nothing-happens': 'Aquí No Pasa Nada',
    'room_default-choice': 'Tu Elección por Defecto',
    'room_thought-loop': 'El Bucle de Pensamientos',
    'room_expect-vs-happens': 'Lo que Esperas vs Lo que Pasa',
    'room_question-never-answer': 'La Pregunta que Nunca Respondes',
    'room_not-special': 'No Eres Especial (Y Está Bien)',
    'room_shortcut-always-take': 'El Atajo que Siempre Tomas',
    'room_blurred-self': 'Yo Borroso',
    'room_image-zooming': 'La Imagen que Amplías',
    'room_comfort-color': 'El Color de Confort',
    'room_line-you-crossed': 'La Línea que Cruzaste',
    'room_read-aloud': 'Lee Esto en Voz Alta',
    'room_voice-without-meaning': 'Tu Voz Sin Significado',
    'room_sentence-you-avoid': 'La Frase que Evitas',
    'room_if-nothing-changes': 'Si Nada Cambia',
    'room_real-priorities': 'Tus Prioridades Reales',
    'room_last-conversation': 'La Última Conversación',
    'room_who-without-job': '¿Quién Eres Sin Tu Trabajo?',
    'room_comfort-zone-scanner': 'Escáner de Zona de Confort',
    'room_say-it-once': 'Dilo Una Vez',
    'room_listen-to-yourself': 'Escúchate',
    'room_life-as-shapes': 'Tu Vida como Formas',
    'room_face-without-features': 'Rostro Sin Rasgos',
    'room_what-keeps-awake': 'Lo que Te Mantiene Despierto',
    'room_you-at-317am': 'Tú a las 3:17 AM',
    'room_thought-that-returns': 'El Pensamiento que Regresa',
    'room_room-you-visit-most': 'La Sala que Más Visitas',
    'room_room-you-avoid': 'La Sala que Evitas',
    'room_left-too-early': 'Te Fuiste Muy Pronto',
    'room_daily-quote': 'Cita del Día',
    'room_daily-joke': 'Chiste del Día',
    'room_theyre-watching': 'Te Están Observando',
    'room_the-last-visitor': 'El Último Visitante',
    'room_dont-look-away': 'No Mires a Otro Lado',
    'room_the-whisper': 'El Susurro',
    'room_you-forgot-something': 'Olvidaste Algo',
    'room_the-mirror-lies': 'El Espejo Miente',
    'room_something-follows': 'Algo Te Sigue',
    'room_the-countdown': 'La Cuenta Regresiva',
    'room_eyes-in-the-dark': 'Ojos en la Oscuridad',
    'room_the-voice': 'La Voz',
    'room_it-knows-your-name': 'Sabe Tu Nombre',
    'room_the-door': 'La Puerta',
    'room_boss-room-free': 'La Suma de Tus Verdades',
    'room_boss-room-horror': 'El Corazón del Miedo',
    'room_boss-room-premium': 'La Verdad Más Profunda',
    'room_mouse-tracker': 'El Rastreador de Ratón',
    'room_scroll-confession': 'Confesión de Desplazamiento',
    'room_waiting-game': 'El Juego de Espera',
    'room_day-in-pixels': 'Tu Día en Píxeles',
    'room_memory-maze': 'Laberinto de Memoria',
    'room_decision-shadow': 'Sombra de Decisión',
    'room_anonymous-mirrors': 'Espejos Anónimos',
    'room_collective-room': 'La Sala Colectiva',
    'room_secret-room': 'La Sala Secreta',
    'room_glitch-room': 'Sala Inestable',
    'room_full-moon-room': 'Sala de Luna Llena',
    'room_weekend-room': 'Sala de Fin de Semana',
    'room_317am-room': 'Sala de las 3:17 AM',
    'room_idle-room': 'La Sala Quieta',
    'room_invite-friend-room': 'La Sala Compartida',
    'room_halloween-room': 'La Sala del Velo',
    'room_winter-solstice-room': 'La Noche Más Larga',
    'room_new-year-room': 'Carta a Tu Yo Futuro',
    'room_void-room': 'El Vacío',
    'room_pattern-room': 'Tus Patrones',
    'room_time-capsule': 'Cápsula del Tiempo',
    'room_the-revisit': 'La Revisita',
    'room_contradiction-room': 'La Sala de Contradicción',
    'room_export-journey': 'Exportar Tu Viaje',
    'room_anonymous-echoes': 'Ecos Anónimos',
    'room_the-waiting-room': 'La Sala de Espera',
    'room_eclipse-shadows': 'Sombras del Eclipse',
    'room_dawn-awakening': 'Despertar del Amanecer',
    
    // Additional Room Subtitles (Spanish)
    'sub_how-many-times-left': 'Un conteo de lo que queda',
    'sub_life-in-weeks': '4.000 cuadrados. Eso es todo.',
    'sub_delay-button': 'Se hace más difícil cada vez',
    'sub_one-honest-sentence': 'Sin editar. Sin borrar.',
    'sub_this-moment': 'Estás aquí. Una vez.',
    'sub_reality-receipt': 'Un resumen de tus días',
    'sub_nothing-happens': 'Exactamente lo que dice',
    'sub_eclipse-shadows': 'Habla en la oscuridad total',
    'sub_dawn-awakening': 'Saluda a la primera luz',
    'sub_default-choice': 'Seguridad o riesgo. Siempre.',
    'sub_thought-loop': 'Mira cómo tu mente se repite',
    'sub_expect-vs-happens': 'La brecha entre esperanza y realidad',
    'sub_question-never-answer': 'La mayoría se va',
    'sub_not-special': 'Sin consuelo. Solo verdad.',
    'sub_shortcut-always-take': 'Los patrones se revelan',
    'sub_blurred-self': 'La memoria funciona así',
    'sub_image-zooming': 'Buscando respuestas en nada',
    'sub_comfort-color': '¿Cuánto tiempo te quedarás?',
    'sub_line-you-crossed': 'Vemos dónde dudaste',
    'sub_read-aloud': 'Palabras que debes pronunciar',
    'sub_voice-without-meaning': 'Sonido sin palabras',
    'sub_sentence-you-avoid': 'No puedes irte hasta escribirla',
    'sub_if-nothing-changes': 'Una proyección de tus próximos cinco años',
    'sub_real-priorities': 'Basado en tiempo, no en palabras',
    'sub_last-conversation': 'Escribe lo que nunca enviarás',
    'sub_who-without-job': 'Reescríbete',
    'sub_comfort-zone-scanner': 'Vemos tu vacilación',
    'sub_say-it-once': 'Sin repetición. Sin rehacer.',
    'sub_listen-to-yourself': 'Tu voz, ralentizada',
    'sub_life-as-shapes': 'Abstracto. Sin etiquetas. Tuyo.',
    'sub_face-without-features': 'Quita hasta que no quede nada',
    'sub_what-keeps-awake': 'Disponible después de las 10 PM',
    'sub_you-at-317am': 'Específico del tiempo. Personal.',
    'sub_thought-that-returns': 'Regresa cada visita',
    'sub_room-you-visit-most': 'Tu patrón revelado',
    'sub_room-you-avoid': 'Lo que evitas dice algo',
    'sub_left-too-early': 'Se activa cuando sales rápido',
    'sub_daily-quote': 'Sabiduría que cambia diariamente',
    'sub_daily-joke': 'Humor negro, actualizado diariamente',
    'sub_theyre-watching': 'No estás solo',
    'sub_the-last-visitor': 'Alguien estuvo aquí antes que tú',
    'sub_dont-look-away': 'Sabe cuándo te vas',
    'sub_the-whisper': 'Escucha con atención',
    'sub_you-forgot-something': '¿Qué era?',
    'sub_the-mirror-lies': 'Rompe para ver la verdad',
    'sub_something-follows': 'Sigue corriendo',
    'sub_the-countdown': 'Algo pasa en cero',
    'sub_eyes-in-the-dark': 'Se multiplican',
    'sub_the-voice': 'Alguien está atrapado aquí',
    'sub_it-knows-your-name': 'No deberías haber venido',
    'sub_the-door': 'Algo está tocando',
    'sub_boss-room-free': 'La sala libre final',
    'sub_boss-room-horror': 'Enfrenta la oscuridad final',
    'sub_boss-room-premium': 'La sala profunda final',
    'sub_mouse-tracker': 'Vemos cómo te mueves',
    'sub_scroll-confession': '¿Qué tan rápido te apresuras?',
    'sub_waiting-game': '¿Puedes no hacer nada?',
    'sub_day-in-pixels': 'El tiempo se vuelve forma',
    'sub_memory-maze': 'El patrón cambia',
    'sub_decision-shadow': 'Tus elecciones revelan patrones',
    'sub_anonymous-mirrors': 'Pensamientos de desconocidos',
    'sub_collective-room': 'Todos están aquí. Nadie se queda.',
    'sub_secret-room': 'Lo encontraste',
    'sub_glitch-room': 'Algo está mal',
    'sub_full-moon-room': 'Solo abre durante el pico lunar',
    'sub_weekend-room': 'El descanso es un destino',
    'sub_317am-room': 'Específico del tiempo. Personal.',
    'sub_idle-room': 'Encontrado a través de la paciencia',
    'sub_invite-friend-room': 'Algunos viajes deben ser presenciados',
    'sub_halloween-room': '31 de octubre - El velo es delgado',
    'sub_winter-solstice-room': '21 de diciembre - Libera y guarda',
    'sub_new-year-room': '31 de diciembre - Escribe a quien serás',
    'sub_void-room': 'Una herida en el código',
    'sub_pattern-room': 'Lo que el sitio ve en ti',
    'sub_time-capsule': 'Escribe a tu yo futuro',
    'sub_the-revisit': 'Mira cómo cambiaron tus respuestas',
    'sub_contradiction-room': 'Tus verdades no concuerdan',
    'sub_export-journey': 'Descarga tu camino',
    'sub_anonymous-echoes': 'Fragmentos de desconocidos',
    'sub_the-waiting-room': 'Desbloquea con presencia',
    
    // New Phase 1-3 Rooms (Spanish)
    'room_letters-never-sent': 'Cartas Nunca Enviadas',
    'sub_letters-never-sent': 'Escribe a alguien que nunca lo leerá',
    'room_breath-sync': 'Sincronía de Respiración',
    'sub_breath-sync': 'Respira con la sala',
    'room_entropy-garden': 'Jardín de Entropía',
    'sub_entropy-garden': 'Crecimiento y decadencia entrelazados',
    'room_memory-palace': 'Palacio de la Memoria',
    'sub_memory-palace': 'Construye tu santuario mental',
    'room_morning-ritual': 'Ritual Matutino',
    'sub_morning-ritual': 'Comienza tu día con intención',
    'room_evening-ritual': 'Ritual Nocturno',
    'sub_evening-ritual': 'Cierra el día con reflexión',
    'room_weekly-ritual': 'Ritual Semanal',
    'sub_weekly-ritual': 'Los domingos son para mirar atrás',
    
    // Letters Never Sent Room (Spanish)
    'letters_title': 'Cartas Nunca Enviadas',
    'letters_subtitle': 'Escribe a alguien que nunca lo leerá. La IA responderá como ellos podrían.',
    'letters_select_recipient': '¿A quién escribes?',
    'letters_past_self': 'Mi Yo Pasado',
    'letters_future_self': 'Mi Yo Futuro',
    'letters_someone_lost': 'Alguien que Perdí',
    'letters_someone_wronged': 'Alguien a quien Lastimé',
    'letters_placeholder': 'Querido/a...',
    'letters_send': 'Enviar al Vacío',
    'letters_sending': 'Enviando...',
    'letters_response': 'Una respuesta emerge...',
    'letters_write_another': 'Escribir Otra Carta',
    'letters_quote': 'Algunas palabras están destinadas a escribirse pero nunca enviarse.',
    
    // Breath Sync Room (Spanish)
    'breath_title': 'Sincronía de Respiración',
    'breath_subtitle': 'Sincroniza tu respiración con el ritmo de la sala.',
    'breath_inhale': 'Inhala',
    'breath_hold': 'Mantén',
    'breath_exhale': 'Exhala',
    'breath_cycles': 'Ciclos completados',
    'breath_quote': 'La respiración es el puente entre cuerpo y mente.',
    
    // Entropy Garden Room (Spanish)
    'entropy_title': 'Jardín de Entropía',
    'entropy_subtitle': 'Tus plantas crecen con cada visita, pero decaen cuando te alejas.',
    'entropy_plant': 'Plantar una Semilla',
    'entropy_water': 'Regar',
    'entropy_days_alive': 'días vivo',
    'entropy_health': 'Salud',
    'entropy_empty': 'Tu jardín está vacío. Planta algo.',
    'entropy_quote': 'Todo crece. Todo se marchita. Ese es el camino.',
    
    // Memory Palace Room (Spanish)
    'memory_palace_title': 'Palacio de la Memoria',
    'memory_palace_subtitle': 'Construye un santuario mental. Coloca objetos en salas que creas.',
    'memory_palace_add_room': 'Añadir Sala',
    'memory_palace_add_object': 'Añadir Objeto',
    'memory_palace_room_name': 'Nombre de la sala...',
    'memory_palace_object_name': 'Nombre del objeto...',
    'memory_palace_empty': 'Tu palacio está vacío. Crea tu primera sala.',
    'memory_palace_quote': 'Construimos nuestras mentes una sala a la vez.',
    
    // Ritual Rooms (Spanish)
    'ritual_locked': 'Este ritual aún no está disponible',
    'morning_ritual_title': 'Ritual Matutino',
    'morning_ritual_intro': 'Un momento para establecer intenciones para el día.',
    'morning_ritual_available': 'Disponible 6 AM - 9 AM',
    'evening_ritual_title': 'Ritual Nocturno',
    'evening_ritual_intro': 'Un momento para reflexionar sobre el día.',
    'evening_ritual_available': 'Disponible 8 PM - 11 PM',
    'weekly_ritual_title': 'Ritual Semanal',
    'weekly_ritual_intro': 'Un momento para reflexionar sobre la semana que fue, y establecer intenciones para la próxima.',
    'weekly_locked': 'El Ritual Semanal te espera',
    'weekly_time': 'Regresa el domingo',
    'today_is': 'Hoy es',
    'your_week': 'Tu Semana',
    'days_active': 'Días Activos',
    'weekly_summary_note': 'Ahora, reflexionemos más profundamente.',
    'weekly_highlight': '¿Cuál fue el momento destacado de tu semana?',
    'weekly_challenge': '¿Qué te desafió más esta semana?',
    'weekly_growth': '¿Cómo creciste esta semana?',
    'weekly_intention': '¿Qué intención estableces para la próxima semana?',
    'weekly_placeholder': 'Tómate tu tiempo...',
    'reflect': 'Reflexionar',
    'reflecting': 'Reflexionando...',
    'complete': 'Completar',
    'weekly_quote': 'Una semana termina. Otra comienza. Llevas lo que importa.',
    
    // Personal Timeline (Spanish)
    'personal_timeline': 'Línea de Tiempo Personal',
    'timeline_subtitle': 'Tu viaje a través de las salas',
    'timeline_empty': 'Tu línea de tiempo está vacía. Comienza a explorar.',
    'timeline_today': 'Hoy',
    'timeline_yesterday': 'Ayer',
    'timeline_this_week': 'Esta Semana',
    'timeline_earlier': 'Antes',
    
    // Weather System (Spanish)
    'weather_clear': 'Despejado',
    'weather_rain': 'Lluvia',
    'weather_fog': 'Niebla',
    'weather_snow': 'Nieve',
    
    // Notifications (Spanish)
    'notifications': 'Notificaciones',
    'notifications_desc': 'Recibe notificaciones sobre contenido bloqueado por tiempo',
    'notify_time_locked': 'Salas bloqueadas por tiempo',
    'notify_full_moon': 'Noches de luna llena',
    'notify_daily_reset': 'Reinicio de desafíos diarios',
    'notify_time_capsule': 'Desbloqueo de cápsulas de tiempo',
    'notifications_enabled': 'Notificaciones habilitadas',
    'notifications_disabled': 'Notificaciones deshabilitadas',
    
    // Community Submissions (Spanish)
    'community_submissions': 'Contribuciones de la Comunidad',
    'community_subtitle': 'Envía tus ideas de salas',
    'community_room_title': 'Título de la Sala',
    'community_room_description': 'Descripción de la Sala',
    'community_room_category': 'Categoría',
    'community_submit': 'Enviar Idea',
    'community_submitted': '¡Gracias por tu contribución!',
    'community_your_submissions': 'Tus Contribuciones',
    'community_empty': 'Aún no hay contribuciones. ¡Sé el primero!',
    
    // Proactive Guide (Spanish)
    'guide_hesitation': '¿Tomándote tu tiempo? Está bien. ¿Te gustaría una sugerencia?',
    'guide_long_absence': 'Bienvenido de vuelta. Ha pasado un tiempo.',
    'guide_milestone': 'Has llegado lejos. ¿Te gustaría reflexionar sobre tu viaje?',
    'guide_dismiss': 'Descartar',
    'guide_accept': 'Muéstrame',
    
    // Voice Mode (Spanish)
    'voice_mode': 'Modo de Voz',
    'voice_mode_desc': 'Deja que el guía te hable',
    'voice_mode_enabled': 'Modo de voz habilitado',
    'voice_mode_disabled': 'Modo de voz deshabilitado',
    
    // Offline Support (Spanish)
    'offline_available': 'Disponible sin conexión',
    'offline_cached': 'Esta sala está almacenada para ver sin conexión',
    
    // Journey Export (Spanish)
    'journey_export': 'Exportar Viaje',
    'journey_import': 'Importar Viaje',
    'journey_export_json': 'Exportar como JSON',
    'journey_import_json': 'Importar desde JSON',
    'journey_exported': 'Viaje exportado con éxito',
    'journey_imported': 'Viaje importado con éxito',
    
    // Phase 4 - Seasonal Rooms (Spanish)
    'room_spring-equinox-room': 'Equinoccio de Primavera',
    'sub_spring-equinox-room': 'Libera y renueva',
    'room_summer-solstice-room': 'Solsticio de Verano',
    'sub_summer-solstice-room': 'El día más largo',
    'room_autumn-equinox-room': 'Equinoccio de Otoño',
    'sub_autumn-equinox-room': 'Cosecha y acepta',
    'room_valentine-room': 'Sala de Amor Propio',
    'sub_valentine-room': 'El amor que mereces',
    
    // Spring Equinox Room (Spanish)
    'spring_date': '20 DE MARZO',
    'spring_title': 'Equinoccio de Primavera',
    'spring_message_1': 'El día y la noche son iguales.',
    'spring_message_2': 'Un momento de equilibrio perfecto.',
    'spring_message_3': '¿Qué liberarás para dar espacio al crecimiento?',
    'spring_letting_go': 'Estoy soltando:',
    'spring_letting_go_placeholder': 'Lo que ya no te sirve...',
    'spring_planting': 'Estoy plantando:',
    'spring_planting_placeholder': 'Lo que deseas que crezca...',
    'spring_bloom': 'Déjalo florecer',
    'spring_released': 'Liberado:',
    'spring_planted': 'Plantado:',
    'spring_closing': 'Desde este momento, comienza una nueva vida.',
    'spring_quote': 'La naturaleza no se apresura, pero todo se cumple.',
    
    // Summer Solstice Room (Spanish)
    'summer_date': '21 DE JUNIO',
    'summer_title': 'El Día Más Largo',
    'summer_message_1': 'Este es el día más largo del año.',
    'summer_message_2': 'La luz alcanza su punto máximo.',
    'summer_message_3': '¿Qué harías con más tiempo?',
    'summer_message_4': 'Describe tu día más pleno y vibrante.',
    'summer_placeholder': 'En mi día más pleno, yo...',
    'summer_submit': 'Abraza la luz',
    'summer_closing': 'El sol te ha escuchado. Ahora comienza su descenso.',
    'summer_quote': 'En el pico de la luz, descubrimos lo que realmente queremos iluminar.',
    
    // Autumn Equinox Room (Spanish)
    'autumn_date': '22 DE SEPTIEMBRE',
    'autumn_title': 'Equinoccio de Otoño',
    'autumn_message_1': 'El equilibrio regresa.',
    'autumn_message_2': 'La cosecha está lista.',
    'autumn_message_3': '¿Qué has cultivado? ¿Qué puedes aceptar?',
    'autumn_harvesting': 'Estoy cosechando:',
    'autumn_harvesting_placeholder': 'Lo que has cultivado este año...',
    'autumn_accepting': 'Estoy aceptando:',
    'autumn_accepting_placeholder': 'Lo que no puedes cambiar...',
    'autumn_release': 'Deja caer las hojas',
    'autumn_harvested': 'Cosechado:',
    'autumn_accepted': 'Aceptado:',
    'autumn_closing': 'Todo cae en su momento.',
    'autumn_quote': 'El otoño nos muestra lo hermoso que es dejar ir las cosas.',
    
    // Valentine's Day Room (Spanish)
    'valentine_date': '14 DE FEBRERO',
    'valentine_title': 'El Amor Que Mereces',
    'valentine_message_1': 'Hoy no se trata de otros.',
    'valentine_message_2': 'Se trata de quien siempre está contigo.',
    'valentine_message_3': 'Quien escucha cada pensamiento.',
    'valentine_message_4': 'Escríbete una carta de amor.',
    'valentine_prompt': 'Una carta para ti:',
    'valentine_placeholder': 'Querido/a yo, quiero que sepas...',
    'valentine_submit': 'Enviar con amor',
    'valentine_closing': 'Mereces el amor que das a otros.',
    'valentine_quote': 'Tú mismo mereces tu amor y afecto.',
    
    // Update changelog
    'update_seasonal_rooms': 'Cuatro nuevas salas estacionales: Equinoccio de Primavera, Solsticio de Verano, Equinoccio de Otoño y San Valentín',
    'update_fragment_trading': 'Sistema de intercambio de fragmentos - sacrifica fragmentos para desbloquear salas con tiempo, previsualizar salas de terror o recibir mensajes de IA',
    'update_new_themes': 'Dos nuevos temas: Eclipse (oscuridad total) y Amanecer (gradientes cálidos, solo 5-7 AM)',
     'update_regret_machine': 'La Máquina del Arrepentimiento - narrativas de líneas temporales alternativas impulsadas por IA',
     'update_memory_reconstruction': 'Reconstrucción de Memoria - la IA completa los detalles sensoriales de recuerdos que se desvanecen',
     
     // Phase 7 - Regret Machine Room
     'regret_machine_title': 'La Máquina del Arrepentimiento',
     'regret_machine_subtitle': 'Describe una decisión que desearías haber tomado de manera diferente. La máquina te mostrará lo que podría haber sido.',
     'regret_placeholder': 'Me arrepiento de...\n\nDescribe una elección que hiciste y que desearías que hubiera sido diferente. ¿Qué habrías hecho en su lugar?',
     'regret_hint': 'Esto no es sobre culpa. Es sobre comprensión.',
     'regret_explore': 'Explorar la Línea Temporal',
     'regret_generating': 'Reconstruyendo una realidad alternativa...',
     'regret_alternate_timeline': 'La Línea Temporal Alternativa',
     'regret_explore_another': 'Explorar Otra',
     'regret_error_short': 'Por favor describe tu arrepentimiento con más detalle',
     'room_regret-machine': 'La Máquina del Arrepentimiento',
     
     // Phase 7 - Memory Reconstruction Room
     'memory_reconstruction_title': 'Reconstrucción de Memoria',
     'memory_reconstruction_subtitle': 'Describe un recuerdo que se desvanece. Déjanos ayudarte a sentir el camino de regreso.',
     'memory_placeholder': 'Recuerdo...\n\nDescribe un recuerdo distante o fragmentado. ¿Qué fragmentos quedan? ¿Qué sentimientos persisten?',
     'memory_timeframe_placeholder': '¿Cuándo fue esto? (ej: "infancia", "hace diez años")',
     'memory_hint': 'La memoria no es una grabación. Es una reconstrucción.',
     'memory_reconstruct': 'Reconstruir Memoria',
     'memory_reconstructing': 'Uniendo los fragmentos...',
     'memory_reconstructed': 'El Recuerdo Reconstruido',
     'memory_explore_another': 'Explorar Otro Recuerdo',
     'memory_error_short': 'Por favor describe tu recuerdo con más detalle',
     'room_memory-reconstruction': 'Reconstrucción de Memoria',
    'update_eclipse_room': 'Sombras del Eclipse - habla en la oscuridad total, revela en destellos de luz',
    'update_dawn_room': 'Despertar del Amanecer - saluda la primera luz con ejercicios de respiración e intenciones',
    
    // New themes
    'eclipse_theme_required': 'Esta sala requiere el tema Eclipse. La oscuridad total te espera.',
    'dawn_theme_required': 'Esta sala requiere el tema Amanecer durante las primeras horas.',
    'dawn_time_hint': 'Disponible solo de 5 a 7 AM',
    'change_theme': 'Cambiar tema en Configuración',
    
    // Eclipse Shadows Room
    'eclipse_shadows_title': 'Sombras del Eclipse',
    'eclipse_shadows_intro': 'En la oscuridad completa, los secretos se sienten más seguros. Responde honestamente—solo breves destellos iluminarán tus palabras.',
    'enter_darkness': 'Entrar en la oscuridad',
    'shadow_question_1': '¿Qué escondes de ti mismo?',
    'shadow_question_2': '¿Qué verdad dirías si nadie pudiera escuchar?',
    'shadow_question_3': '¿Qué te persigue a las 3 AM?',
    'shadow_question_4': '¿Qué harías si el miedo no existiera?',
    'shadow_question_5': '¿Qué no estás listo para enfrentar?',
    'speak_into_dark': 'Habla en la oscuridad...',
    'release': 'Liberar',
    'eclipse_reveal_prompt': 'Haz clic en cada sombra para revelar lo que confesaste.',
    'hidden_in_shadow': '▓▓▓▓▓▓▓▓▓▓▓▓',
    'eclipse_reveal_hint': 'Algunas verdades son más claras en la oscuridad.',
    'eclipse_shadows_quote': 'En la oscuridad total, finalmente nos vemos a nosotros mismos.',
    
    // Dawn Awakening Room
    'dawn_awakening_title': 'Despertar del Amanecer',
    'dawn_awakening_intro': 'La primera luz trae posibilidad. Antes de que el mundo despierte, establece tus intenciones.',
    'greet_the_light': 'Saluda la luz',
    'breathe_in_light': 'Respira la luz',
    'hold_warmth': 'Mantén el calor',
    'release_night': 'Libera la noche',
    'dawn_prompt_1': '¿Qué quieres llevar contigo hoy?',
    'dawn_prompt_2': '¿Qué dejarás ir?',
    'dawn_prompt_3': '¿Qué pequeño acto de amabilidad te ofrecerás?',
    'set_your_intention': 'Establece tu intención...',
    'plant_seed': 'Planta esta semilla',
    'dawn_awakening_quote': 'Cada amanecer es una invitación para comenzar de nuevo.',
    
    // Fragment Trading
    'fragment_trading': 'Intercambio de Fragmentos',
    'sacrifice_to_unlock': 'Sacrifica fragmentos para desbloquear secretos',
    'your_fragments': 'Tus Fragmentos',
    'fragments_any': 'cualquier fragmento',
    'fragments_free': 'fragmentos de claridad',
    'fragments_premium': 'fragmentos de profundidad',
    'fragments_horror': 'fragmentos de sombra',
    'trade_successful': '¡Intercambio Exitoso!',
    'trade_failed': 'Intercambio Fallido',
    'not_enough_fragments': 'Fragmentos insuficientes',
    'continue_trading': 'Continuar Intercambiando',
    'trading_fragments': 'Intercambiando fragmentos...',
    'fragment_trade_warning': 'Los fragmentos sacrificados no se pueden recuperar',
    'unlock-time-locked': 'Desbloquear Sala con Tiempo',
    'unlock-time-locked_desc': 'Accede a una sala con tiempo sin esperar',
    'preview-horror': 'Previsualización de Terror',
    'preview-horror_desc': 'Observa una sala de terror antes de entrar',
    'ai-reflection-trade': 'Reflexión IA',
    'ai-reflection-trade_desc': 'Recibe un mensaje personalizado de la IA',
    'unlock-premium-preview': 'Prueba de Sala Premium',
    'unlock-premium-preview_desc': 'Accede a una sala premium por una visita',
    'fragment_trading_room': 'El Altar de Fragmentos',
    'fragment_trading_intro': 'Aquí puedes sacrificar tus fragmentos recolectados para desbloquear secretos ocultos en estas salas.',
    'your_collection': 'Tu Colección',
    'fragment_clarity': 'Claridad',
    'fragment_depth': 'Profundidad',
    'fragment_shadow': 'Sombra',
    'total_fragments': 'Total',
    'open_altar': 'Acercarse al Altar',
    'recent_trades': 'Ofrendas Recientes',
    'fragment_trade_ending': 'Lo que se dio no puede recuperarse. Pero lo que se ganó... eso permanece.',
    'room_fragment-trade': 'El Altar de Fragmentos',
    'sub_fragment-trade': 'Sacrifica para desbloquear secretos',
    'update_anniversary_system': 'Sistema de Aniversario y Hitos - sigue tu viaje a lo largo del tiempo',
    'update_archive_room': 'El Archivo - historial completo desbloqueado a las 25 salas',
    'update_mirror_room': 'Espejo de Todos los Yoes - retrato conductual IA a las 50 salas',
    'update_ending_room': 'El Final - una despedida personalizada a las 75 salas',
    'room_the-archive': 'El Archivo',
    'room_mirror-of-all-selves': 'Espejo de Todos los Yoes',
    'room_the-ending': 'El Final',
    // Phase 9
    'update_fragment_rarities': 'Rarezas de Fragmentos - fragmentos coleccionables Comunes, Raros y Legendarios',
    'update_fragment_gallery': 'Galería de Fragmentos - vitrina visual para tu colección',
    'update_seasonal_fragments': 'Fragmentos estacionales y secretos - coleccionables por tiempo limitado',
    'fragment_collection': 'Colección',
    'fragment_collection_title': 'Colección de Fragmentos',
    'fragment_collection_subtitle': 'Cada sala deja algo atrás. Colecciónalos todos.',
    'collection_milestones': 'Hitos',
    'collection_secrets': 'Secretos',
    'collection_footer': 'Algunos fragmentos solo aparecen bajo ciertas condiciones.',
    'rarity_common': 'Común',
    'rarity_rare': 'Raro',
    'rarity_legendary': 'Legendario',
    // Phase 10-11
    'accessibility': 'Accesibilidad',
    'accessibility_desc': 'Personalizar legibilidad y preferencias de movimiento',
    'font_family': 'Familia de Fuente',
    'font_default': 'Monoespaciada',
    'font_dyslexia': 'Amigable con Dislexia',
    'font_sans': 'Sans Serif',
    'font_changed': 'Fuente actualizada',
    'line_height': 'Altura de Línea',
    'line_compact': 'Compacto',
    'line_normal': 'Normal',
    'line_relaxed': 'Relajado',
    'line_spacious': 'Espacioso',
    'line_height_changed': 'Altura de línea actualizada',
    'reduced_motion_full_desc': 'Deshabilitar todas las animaciones y efectos de partículas',
    'larger_cursors': 'Objetivos Más Grandes',
    'larger_cursors_desc': 'Aumentar el tamaño de los objetivos de clic',
    'update_accessibility_suite': 'Suite de Accesibilidad - fuentes para dislexia, control de altura de línea',
    'update_dyslexia_font': 'Opción de fuente amigable con dislexia en ajustes',
    'update_line_height': 'Altura de línea ajustable para mejor legibilidad',
    'update_enhanced_reduced_motion': 'Movimiento reducido mejorado - desactiva completamente todos los efectos',
    'update_shared_moments': 'Momentos Compartidos - registros emocionales anónimos',
    'update_whisper_wall': 'El Muro de Susurros - deja verdades anónimas en la oscuridad',
    'update_collective_memory': 'Memoria Colectiva - fragmentos compartidos de lo que recordamos',
    'whisper_prompt': 'Deja un susurro en el muro. Anónimo. Permanente.',
    'whisper_placeholder': '¿Qué llevas en silencio?',
    'whisper_send': 'Susurrar',
    'whisper_added': 'Tu susurro se une al muro.',
    'whisper_another': 'Susurrar de nuevo',
    'shared_moments_title': 'Ahora mismo, en este momento...',
    'shared_moments_subtitle': '¿Cómo estás, honestamente?',
    'feeling_grateful': 'Agradecido',
    'feeling_lost': 'Perdido',
    'feeling_hopeful': 'Esperanzado',
    'feeling_heavy': 'Pesado',
    'feeling_peaceful': 'En paz',
    'feeling_restless': 'Inquieto',
    'room_whisper-wall': 'El Muro de Susurros',
    'room_collective-memory': 'Memoria Colectiva',
    'room_shared-moments': 'Momentos Compartidos',
    'sub_regret-machine': 'Explora lo que podría haber sido',
    'sub_memory-reconstruction': 'Reconstruye un recuerdo que se desvanece',
    'sub_whisper-wall': 'Verdades anónimas flotan en la oscuridad',
    'sub_collective-memory': 'Fragmentos compartidos de lo que recordamos',
    'sub_shared-moments': '¿Cómo estás, honestamente?',
    'sub_the-archive': 'Tu historial completo de viaje',
    'sub_mirror-of-all-selves': 'La IA revela tu retrato conductual',
    'sub_the-ending': 'Una despedida que te ganaste',

    // v3.21.0 - New Rooms
    'room_mask-you-wear': 'La Máscara que Usas',
    'sub_mask-you-wear': '¿Qué cara muestras?',
    'room_recurring-dream': 'Tu Sueño Recurrente',
    'sub_recurring-dream': 'El sueño que sigue volviendo',
    'room_promise-you-broke': 'La Promesa que Rompiste',
    'sub_promise-you-broke': 'Honestidad sobre promesas rotas',
    'room_words-you-swallowed': 'Palabras que Tragaste',
    'sub_words-you-swallowed': 'Cosas que nunca dijiste',
    'room_age-you-feel': 'La Edad que Sientes',
    'sub_age-you-feel': '¿Cuántos años tienes realmente?',
    'room_biggest-lie': 'Tu Mayor Mentira',
    'sub_biggest-lie': 'La mentira que más cuentas',
    'room_person-you-pretend-to-be': 'La Persona que Finges Ser',
    'sub_person-you-pretend-to-be': 'Tu actuación',
    'room_person-you-pretend': 'La Persona que Finges Ser',
    'sub_person-you-pretend': 'Tu actuación',
    'room_what-you-collect': 'Lo que Coleccionas',
    'sub_what-you-collect': 'Los objetos revelan identidad',
    'room_apology-you-owe': 'La Disculpa que Debes',
    'sub_apology-you-owe': 'Escribe lo que debiste decir',
    'room_unfinished-story': 'Tu Historia Inconclusa',
    'sub_unfinished-story': 'Lo que nunca completaste',
    'room_version-others-see': 'La Versión que Otros Ven',
    'sub_version-others-see': 'Cómo te perciben los demás',
    'room_secret-talent': 'Tu Talento Secreto',
    'sub_secret-talent': 'Lo que nadie sabe que puedes hacer',
    'room_fear-you-feed': 'El Miedo que Alimentas',
    'sub_fear-you-feed': '¿Qué miedo te controla?',
    'room_guilty-pleasure': 'Tu Placer Culpable',
    'sub_guilty-pleasure': 'Lo que escondes disfrutar',
    'room_rule-you-break': 'La Regla que Siempre Rompes',
    'sub_rule-you-break': 'Tu patrón de rebelión',
    'room_childhood-promise': 'Tu Promesa de Infancia',
    'sub_childhood-promise': 'Lo que te prometiste',
    'room_compliment-you-reject': 'El Cumplido que Rechazas',
    'sub_compliment-you-reject': 'Por qué no aceptas elogios',
    'room_autopilot-mode': 'Tu Modo Automático',
    'sub_autopilot-mode': 'Lo que haces sin pensar',
    'room_boundary-you-need': 'El Límite que Necesitas',
    'sub_boundary-you-need': 'Lo que debes proteger',
    'room_inner-critic': 'Tu Crítico Interior',
    'sub_inner-critic': 'La voz que juzga',
    'room_last-sunday': 'El Domingo Pasado',
    'sub_last-sunday': '¿Qué hiciste realmente?',
    'room_wasted-hours': 'Tus Horas Perdidas',
    'sub_wasted-hours': 'Donde desaparece el tiempo',
    'room_year-that-changed': 'El Año que te Cambió',
    'sub_year-that-changed': 'El punto de inflexión',
    'room_tomorrows-regret': 'El Arrepentimiento de Mañana',
    'sub_tomorrows-regret': '¿Qué desearías haber hecho?',
    'room_morning-autopilot': 'Tu Piloto Automático Matutino',
    'sub_morning-autopilot': 'El ritual de la primera hora',
    'room_season-you-dread': 'La Estación que Temes',
    'sub_season-you-dread': '¿Qué estación pesa más?',
    'room_ten-years-ago': 'Hace Diez Años',
    'sub_ten-years-ago': '¿Quién eras entonces?',
    'room_last-normal-day': 'Tu Último Día Normal',
    'sub_last-normal-day': 'Antes de que todo cambiara',
    'room_clock-you-ignore': 'El Reloj que Ignoras',
    'sub_clock-you-ignore': 'Tiempo que finges que no pasa',
    'room_your-deadline': 'Tu Fecha Límite',
    'sub_your-deadline': '¿Qué estás posponiendo?',
    'room_the-pause': 'La Pausa',
    'sub_the-pause': 'Solo respira un momento',
    'room_static-room': 'Estático',
    'sub_static-room': 'Ruido blanco y quietud',
    'room_gap-between-thoughts': 'El Espacio Entre Pensamientos',
    'sub_gap-between-thoughts': 'Encuentra el espacio',
    'room_sit-with-discomfort': 'Siéntate con la Incomodidad',
    'sub_sit-with-discomfort': 'No arregles nada',
    'room_boring-room': 'La Habitación Aburrida',
    'sub_boring-room': 'Nada interesante en absoluto',
    'room_unplug-room': 'Desconectar',
    'sub_unplug-room': 'Imagina sin dispositivos',
    'room_unplug': 'Desconectar',
    'sub_unplug': 'Imagina sin dispositivos',
    'room_last-page': 'La Última Página',
    'sub_last-page': 'La historia termina aquí',
    'room_scrolling-pattern': 'Tu Patrón de Scroll',
    'sub_scrolling-pattern': 'Cómo consumes contenido',
    'room_tab-you-keep-open': 'La Pestaña que Mantienes Abierta',
    'sub_tab-you-keep-open': 'Lo que permanece en tu navegador',
    'room_digital-shadow': 'Tu Sombra Digital',
    'sub_digital-shadow': 'Tu huella digital',
    'room_notification-you-ignore': 'La Notificación que Ignoras',
    'sub_notification-you-ignore': 'Lo que descartas',
    'room_typing-speed': 'Tu Velocidad al Escribir',
    'sub_typing-speed': '¿Qué tan rápido te expresas?',
    'room_link-you-almost-clicked': 'El Enlace que Casi Clicaste',
    'sub_link-you-almost-clicked': 'Patrones de duda',
    'room_bookmark-graveyard': 'Tu Cementerio de Marcadores',
    'sub_bookmark-graveyard': 'Guardados pero nunca revisitados',
    'room_search-you-deleted': 'La Búsqueda que Borraste',
    'sub_search-you-deleted': 'Lo que buscaste y borraste',
    'room_gravity-room': 'Sala de Gravedad',
    'sub_gravity-room': 'Todo cae lentamente',
    'room_ink-blot': 'Mancha de Tinta',
    'sub_ink-blot': '¿Qué ves?',
    'room_hallway-of-mirrors': 'El Pasillo de Espejos',
    'sub_hallway-of-mirrors': 'Reflejos infinitos',
    'room_dissolving-text': 'Texto Disolviéndose',
    'sub_dissolving-text': 'Las palabras se derriten al leer',
    'room_the-pendulum': 'El Péndulo',
    'sub_the-pendulum': 'Movimiento hipnótico',
    'room_pixel-rain': 'Lluvia de Píxeles',
    'sub_pixel-rain': 'Precipitación digital',
    'room_the-threshold': 'El Umbral',
    'sub_the-threshold': 'Entre aquí y allá',
    'most_loved_rooms': 'Habitaciones Más Queridas',
    'rate_room': 'Califica esta habitación',
    'update_52_new_rooms': '52 nuevas habitaciones en categorías de Identidad, Tiempo, Contemplación, Meta y Experimental',
    'update_room_rating': 'Sistema de calificación - pulgar arriba/abajo con sección de Habitaciones Más Queridas',
    'update_bug_fix_reflections': 'Corrección en la visualización y almacenamiento de reflexiones anónimas',
    'update_100_rooms': 'Más de 100 habitaciones disponibles en el Pasillo',
    // v3.22.0
    'room_the-friend-you-lost': 'El Amigo Que Perdiste',
    'sub_the-friend-you-lost': 'Escribe a alguien de quien te alejaste.',
    'room_conversation-you-replay': 'La Conversación Que Repites',
    'sub_conversation-you-replay': 'Recréala de memoria.',
    'room_the-thank-you': 'El Agradecimiento',
    'sub_the-thank-you': 'Gratitud que nunca expresaste.',
    'room_people-you-forgot': 'Personas Que Olvidaste',
    'sub_people-you-forgot': 'Intenta recordar nombres olvidados.',
    'room_the-advice-you-give': 'El Consejo Que Das',
    'sub_the-advice-you-give': 'Consejo que das pero nunca sigues.',
    'room_what-others-dont-know': 'Lo Que Otros No Saben',
    'sub_what-others-dont-know': 'Tus capas ocultas.',
    'room_the-lie-you-believe': 'La Mentira Que Crees',
    'sub_the-lie-you-believe': 'Una verdad cómoda que no es real.',
    'room_your-shadow-self': 'Tu Yo Sombra',
    'sub_your-shadow-self': 'La parte que reprimes.',
    'room_the-story-you-tell': 'La Historia Que Cuentas',
    'sub_the-story-you-tell': 'Cómo narras tu vida.',
    'room_your-safe-place': 'Tu Lugar Seguro',
    'sub_your-safe-place': 'Describe dónde te sientes seguro.',
    'room_the-risk-you-didnt-take': 'El Riesgo Que No Tomaste',
    'sub_the-risk-you-didnt-take': 'Caminos no recorridos.',
    'room_your-last-excuse': 'Tu Última Excusa',
    'sub_your-last-excuse': 'La excusa que más usas.',
    'room_the-habit-you-hide': 'El Hábito Que Ocultas',
    'sub_the-habit-you-hide': 'Rutinas secretas.',
    'room_what-you-postpone': 'Lo Que Pospones',
    'sub_what-you-postpone': 'Lo que siempre es "mañana".',
    'room_the-door-you-closed': 'La Puerta Que Cerraste',
    'sub_the-door-you-closed': 'Oportunidades que rechazaste.',
    'journal_title': 'Diario de Reflexiones',
    'journal_subtitle': 'Tus pensamientos a través de las habitaciones',
    'journal_empty': 'Sin reflexiones aún. Visita habitaciones y escribe tus pensamientos.',
    'journal': 'Diario',
    'room_chains': 'Cadenas de Habitaciones',
    'chain_identity_journey': 'Viaje de Identidad',
    'chain_time_reflection': 'Reflexión del Tiempo',
    'chain_truth_path': 'Camino de la Verdad',
    'recently_visited': 'Visitados Recientemente',
    'update_reflection_journal': 'Diario de Reflexiones - guarda tus pensamientos entre habitaciones',
    'update_room_chains': 'Cadenas de Habitaciones - completa habitaciones en secuencia',
    'update_15_new_rooms': '15 nuevas habitaciones interactivas en 3 categorías',
    'update_recently_visited': 'Sección de visitados recientemente para volver rápido',
    'ai_companion': 'Compañero',
    'companion_create': 'Crea Tu Compañero',
    'companion_setup_desc': 'Un reflejo que camina a tu lado.',
    'companion_name': 'Nombra a tu compañero',
    'companion_name_placeholder': 'ej., Eco, Espejo, Sombra...',
    'companion_lens': 'Lente de personalidad',
    'companion_mood': 'Tono de comunicación',
    'companion_fears': '¿A qué le temes? (opcional)',
    'companion_struggles': '¿Con qué luchas? (opcional)',
    'create_companion': 'Crear',
    'companion_input': 'Di algo...',
    'companion_greeting': 'Tu compañero está aquí. Di algo.',
    'companion_starter_1': '¿Qué tengo en mente hoy?',
    'companion_starter_2': 'Me siento atascado.',
    'companion_starter_3': 'Dime algo que evito.',
    'fears_placeholder': 'ej., fracaso, rechazo, ser olvidado...',
    'struggles_placeholder': 'ej., pensar demasiado, procrastinar, soledad...',
    'back_step': 'Atrás',
    'lens_reflective': 'Reflexivo',
    'lens_reflective_desc': 'Preguntas profundas, reconocimiento de patrones',
    'lens_supportive': 'Solidario',
    'lens_supportive_desc': 'Cálido, alentador, validador',
    'lens_challenging': 'Desafiante',
    'lens_challenging_desc': 'Directo, provocador',
    'lens_curious': 'Curioso',
    'lens_curious_desc': 'Preguntas inesperadas, asombro',
    'lens_shadow': 'Sombra',
    'lens_shadow_desc': 'El lado oculto, incomodidad honesta',
    'companion_mood_calm': 'Calma',
    'companion_mood_calm_desc': 'Mesurado y pacífico',
    'companion_mood_intense': 'Intenso',
    'companion_mood_intense_desc': 'Urgente y profundo',
    'companion_mood_playful': 'Juguetón',
    'companion_mood_playful_desc': 'Ingenioso y ligero',
    'companion_mood_melancholic': 'Melancólico',
    'companion_mood_melancholic_desc': 'Tristeza hermosa',
    'update_ai_companion': 'Compañero IA - una IA personal que recuerda tu viaje',
  },

  pt: {
    // Index
    'title': 'OS QUARTOS HUMANOS',
    'tagline': 'Uma coleção de quartos que refletem quem você é quando ninguém está olhando.',
    'enter': 'Entrar',
    'footer': 'Não é terapia. Não é social. Não é produtivo.',
    
    // Hallway
    'back': '← Voltar',
    'hallway': 'O Corredor',
    'challenges': 'Desafios',
    'random': 'Aleatório',
    'open_rooms': 'Quartos Abertos',
    'dark_rooms': 'Os Quartos Escuros',
    'enter_risk': 'entre por sua conta e risco',
    'premium_rooms': 'Quartos Premium',
    'unlock_to_access': 'Desbloqueie para acessar',
    'daily_rooms': 'Quartos Diários',
    'refreshes_daily': 'atualiza todos os dias',
    'time_locked': 'Quartos Bloqueados por Tempo',
    'seasonal_rooms': 'Quartos Sazonais',
    'available_now': 'Disponível agora',
    'fragments': 'Fragmentos',
    'boss_unlocked': 'Sala do Chefe Desbloqueada',
    'collect_unlock': 'Colete para desbloquear a Sala do Chefe',
    'game_rooms': 'Salas de Jogos',
    'experimental_rooms': 'Salas Experimentais',
    'deep_rooms': 'Salas Profundas',
    'meta_rooms': 'Salas Meta',
    'search_rooms': 'Buscar salas...',
    'no_results': 'Nenhuma sala corresponde à sua busca',
    'clear_filters': 'Limpar filtros',
    'explore': 'Explorar',
    'all_categories': 'Todas',
    
    // About
    'what_is_this': 'O que é este lugar?',
    'not_productivity': 'Os Quartos Humanos não é uma ferramenta de produtividade.',
    'not_social': 'Não é uma rede social.',
    'not_therapy': 'Não é terapia.',
    'pause_experiences': 'É uma coleção de pequenas experiências projetadas para fazer você pausar.',
    'each_room': 'Cada quarto leva de 30 segundos a 2 minutos.',
    'make_think': 'Alguns quartos vão fazer você pensar.',
    'make_uncomfortable': 'Alguns vão fazer você se sentir desconfortável.',
    'make_calm': 'Alguns vão te acalmar.',
    'make_aware': 'Todos vão te tornar consciente.',
    'no_notifications': 'Não há notificações aqui.',
    'no_points': 'Não há pontos para coletar.',
    'no_validation': 'Sem validação social.',
    'just_truth': 'Apenas você, um quarto, e a verdade.',
    'enter_rooms': 'Entrar nos quartos',
    
    // Common
    'loading': 'Carregando...',
    'error': 'Erro',
    'continue': 'Continuar',
    'submit': 'Enviar',
    'cancel': 'Cancelar',
    'close': 'Fechar',
    'next': 'Próximo',
    'previous': 'Anterior',
    'start': 'Iniciar',
    'stop': 'Parar',
    'yes': 'Sim',
    'no': 'Não',
    'leave_room': '← Sair do quarto',
    'return': '← Voltar',
    'return_hallway': 'Voltar ao Corredor',
    'begin': 'Começar',
    'watch': 'Observe...',
    'correct': 'Correto',
    'round': 'Rodada',
    'enter_again': 'Entrar novamente',
    'steps': 'Passos',
    'depth': 'Profundidade',
    
    // Keyboard Shortcuts
    'keyboard_shortcuts': 'Atalhos de Teclado',
    'shortcut_hallway': 'Ir ao Corredor',
    'shortcut_random': 'Quarto Aleatório',
    'shortcut_back': 'Voltar',
    'shortcut_bookmark': 'Alternar Favorito',
    'shortcut_reading': 'Modo Leitura',
    'shortcut_search': 'Focar Busca',
    'shortcut_stats': 'Ver Estatísticas',
    'shortcut_achievements': 'Ver Conquistas',
    'shortcut_cat_nav': 'Navegação',
    'shortcut_cat_rooms': 'Quartos',
    'shortcut_cat_access': 'Acessibilidade',
    'press_to_close': 'Pressione',
    'to_close': 'para fechar',
    'or': 'ou',
    
    // Bookmarks
    'add_bookmark': 'Adicionar Favorito',
    'remove_bookmark': 'Remover Favorito',
    'bookmarks': 'Favoritos',
    'bookmarked_rooms': 'Salvos',
    'no_bookmarks': 'Sem favoritos ainda',
    
    // Reading Mode
    'reading_mode': 'Modo Leitura',
    'reading_mode_on': 'Modo leitura ativado',
    'reading_mode_off': 'Modo leitura desativado',
    
    // Updates Page
    'updates': 'Atualizações',
    'updates_title': 'Novidades',
    'updates_subtitle': 'Mudanças e melhorias recentes',
    'updates_footer': 'Construindo quartos, uma atualização por vez.',
    'new': 'NOVO',
    'subscribe_rss': 'Inscreva-se via RSS',
    'latest': 'MAIS RECENTE',
    'whats_new_intro': 'Últimas atualizações:',
    'view_all_updates': 'Ver todas as atualizações',
    'update_updates_page': 'Página de atualizações adicionada',
    'update_full_i18n': 'Tradução completa para 6 idiomas',
    'update_hallway_animations': 'Animações suaves ao trocar categorias',
    'update_daily_challenges': 'Melhorias nos desafios diários',
    'update_game_rooms': '15 novos quartos de jogo',
    'update_experimental_rooms': '14 quartos experimentais',
    'update_random_room': 'Botão de quarto aleatório',
    'update_stats_route': 'Rota de estatísticas corrigida',
    'update_streak_system': 'Sistema de sequências diárias',
    'update_weekly_summary': 'Resumos de progresso semanal',
    'update_rtl_support': 'Suporte a idiomas RTL',
    'update_dark_rooms': 'Nova categoria de quartos escuros',
    'update_boss_rooms': 'Salas de chefe com fragmentos',
    'update_audio_system': 'Áudio ambiente nos quartos',
    'update_constellation_map': 'Mapa de constelações da jornada',
    'update_fragment_system': 'Sistema de fragmentos de quartos',
    'update_mobile_layout': 'Melhorias no layout mobile',
    'update_multi_language': 'Suporte multi-idioma (6 idiomas)',
    'update_premium_system': 'Sistema de quartos premium',
    'update_room_endings': 'Citações finais únicas',
    'update_ui_redesign': 'Redesenho completo da interface',
    'update_theme_effects': 'Efeitos visuais específicos por tema',
    'update_void_theme': 'Novo tema Vazio',
    'update_achievements': 'Sistema de conquistas',
    'update_ai_reflection': 'Sala de reflexão com IA',
    'update_settings_redesign': 'Configurações redesenhadas',
    'update_theme_exclusive_rooms': 'Salas exclusivas por tema',
    'update_discovery_localization': 'Mensagens de descoberta traduzidas',
    'update_site_mood_system': 'Sistema de humor do site',
    'update_voice_imperfection': 'Salas com voz imperfeita',
    'update_mobile_optimization': 'Otimização mobile',
    'update_interaction_clues': 'Dicas sutis de interação',
    'update_theme_exclusive_hallway': 'Salas exclusivas visíveis no corredor',
    'update_advanced_easter_eggs': 'Easter eggs avançados',
    'update_hidden_discoveries': 'Revelações de texto oculto',
    'update_dual_mode_system': 'Sistema de modo duplo Clássico/Alternativo',
    'update_alternate_mode': 'Modo alternativo - uma realidade paralela',
    'update_hidden_discovery': 'Gatilhos de descoberta ocultos',
    'update_mode_effects': 'Efeitos visuais específicos por modo',
    'update_translation_navigator': 'Navegador de Tradução - painel visual',
    'update_full_localization': 'Cobertura quase total em 6 idiomas',
    'update_meta_room_translations': 'Conteúdo das meta salas traduzido',
    'update_animated_timeline': 'Timeline animada com transições ao rolar',
    'update_toast_notifications': 'Notificação toast para novas atualizações',
    'update_rss_feed': 'Feed RSS para assinatura de mudanças',
    'update_bookmarked_filter': 'Aba de Quartos Favoritos no Corredor',
    'update_keyboard_shortcuts': 'Atalhos de teclado (pressione ? para ver)',
    'update_reading_mode': 'Modo leitura para tipografia e foco aprimorados',
    'update_accessibility': 'Indicadores de foco e navegação por teclado melhorados',
    'update_ambient_crossfade': 'Transições suaves entre faixas de áudio ambiente',
    'update_settings_audio': 'Controles de áudio centralizados na página de Configurações',
    'update_arabic_complete': 'Cobertura completa da tradução árabe',
    'update_duplicate_icon_fix': 'Ícones de áudio duplicados corrigidos no cabeçalho',

    // Game UI
    'game_watching': 'observando...',
    'game_repeat': 'repetir',
    'game_echo_fades': 'o eco desvanece...',
    'game_escaped': 'Você escapou.',
    'game_found_exit': 'Você encontrou a saída.',
    'game_looping': 'Você está em loop.',
    'game_deeper': 'Indo mais fundo...',
    'game_doors_shift': 'As portas mudam.',
    'maze_left': 'esquerda',
    'maze_right': 'direita',
    'maze_forward': 'frente',
    'maze_back': 'trás',
    'maze_past': 'passado',
    'maze_future': 'futuro',
    'maze_here': 'aqui',
    'maze_there': 'lá',
    'maze_truth': 'verdade',
    'maze_lies': 'mentiras',
    'maze_light': 'luz',
    'maze_dark': 'escuridão',
    'maze_memory': 'memória',
    'maze_dream': 'sonho',
    'maze_silence': 'silêncio',
    'maze_noise': 'ruído',
    'random_room': 'Quarto Aleatório',
    'ai_reflection': 'Reflexão IA',
    'support': 'Suporte',
    'choose_direction': 'Escolha uma direção. Algumas portas respondem.',
    'rooms_explored': 'quartos explorados',
    'your_journey': 'Sua Jornada',
    'complete_rooms_fragments': 'Complete quartos para coletar fragmentos.',
    'no_rooms_completed': 'Nenhum quarto completado ainda.',
    'constellation_map': 'Mapa das Constelações',
    'your_stats': 'Suas Estatísticas',
    
    // Stats
    'total_time': 'Tempo Total',
    'rooms_visited': 'Quartos Visitados',
    'total_visits': 'Total de Visitas',
    'most_visited': 'Mais Visitado',
    'longest_session': 'Sessão Mais Longa',
    'fragments_collected': 'Fragmentos Coletados',
    'completed_rooms': 'Quartos Completados',
    'privacy_note': 'Todos os dados são armazenados localmente no seu dispositivo',
    
    // Daily Challenges
    'daily_challenges': 'Desafios Diários',
    'challenges_completed': 'completado',
    'challenges_total': 'total',
    'resets_midnight': 'Reinicia à meia-noite',
    
    // Weekly Summary
    'weekly_summary': 'Resumo Semanal',
    'week': 'Semana',
    'this_week': 'Esta Semana',
    'rooms': 'Quartos',
    'days': 'Dias',
    'vs_last_week': 'vs semana passada',
    'refresh_insight': 'Atualizar insight',
    
    // Game Room Titles
    'room_memory-puzzle': 'Quebra-Cabeça de Memória',
    'room_shadow-chase': 'Perseguição de Sombras',
    'room_timing-room': 'Sala de Tempo',
    'room_pattern-whisper': 'Sussurro de Padrões',
    'room_silence-puzzle': 'Quebra-Cabeça do Silêncio',
    'room_decay-game': 'Jogo da Decadência',
    'room_observation-test': 'Teste de Observação',
    'room_balance-room': 'Sala do Equilíbrio',
    'room_word-drift': 'Deriva de Palavras',
    'room_color-echo': 'Eco de Cores',
    'room_mirror-tap': 'Toque do Espelho',
    'room_maze-of-doors': 'Labirinto de Portas',
    'room_pulse-match': 'Combinação de Pulso',
    'room_light-path': 'Caminho de Luz',
    'room_sound-match': 'Combinação de Som',
    
    // Game Room Subtitles
    'sub_memory-puzzle': 'Combine padrões que mudam sutilmente',
    'sub_shadow-chase': 'Pegue o que se move imprevisivelmente',
    'sub_timing-room': 'Clique exatamente no momento certo',
    'sub_pattern-whisper': 'Repita a sequência que desaparece',
    'sub_silence-puzzle': 'Resolva sem instruções',
    'sub_decay-game': 'Toque para preservar o que desvanece',
    'sub_observation-test': 'Encontre diferenças sutis',
    'sub_balance-room': 'Mantenha objetos flutuantes alinhados',
    'sub_word-drift': 'Pegue as letras antes de caírem',
    'sub_color-echo': 'Repita a sequência de tons sutis',
    'sub_mirror-tap': 'Cada toque ecoa em outro lugar',
    'sub_maze-of-doors': 'As portas se reorganizam após cada escolha',
    'sub_pulse-match': 'Mova-se no ritmo da sala',
    'sub_light-path': 'Guie a luz através da escuridão mutável',
    'sub_sound-match': 'Toque formas para combinar tons suaves',
    
    // Experimental Room Titles
    'room_mirror-room': 'Sala do Espelho',
    'room_echo-room': 'Sala do Eco',
    'room_pulse-room': 'Sala do Pulso',
    'room_vanishing-room': 'Sala que Desaparece',
    'room_letter-room': 'Sala das Cartas',
    'room_empty-room': 'Sala Vazia',
    'room_reverse-room': 'Sala Inversa',
    'room_whisper-room': 'Sala do Sussurro',
    'room_shadow-room': 'Sala das Sombras',
    'room_labyrinth-room': 'Sala do Labirinto',
    'room_color-room': 'Sala das Cores',
    'room_surrender-room': 'Sala da Rendição',
    'room_noise-room': 'Sala do Ruído',
    'room_observer-room': 'Sala do Observador',
    
    // Experimental Room Subtitles
    'sub_mirror-room': 'Reflexos ligeiramente distorcidos',
    'sub_echo-room': 'Cliques desencadeiam ecos atrasados',
    'sub_pulse-room': 'O ambiente respira',
    'sub_vanishing-room': 'Elementos desaparecem ao interagir',
    'sub_letter-room': 'Palavras se embaralham como memória falha',
    'sub_empty-room': 'Vazio até você encontrar segredos',
    'sub_reverse-room': 'Ações se comportam ao contrário',
    'sub_whisper-room': 'Verdades mal audíveis',
    'sub_shadow-room': 'Seu companheiro sombrio',
    'sub_labyrinth-room': 'Encontre seu caminho',
    'sub_color-room': 'Meditação cromática',
    'sub_surrender-room': 'Libere o controle',
    'sub_noise-room': 'Pensamentos estáticos',
    'sub_observer-room': 'Sendo observado',
    
    // Daily Joke Room
    'joke_number': 'Piada #{number}',
    'joke_today': 'Piada do Dia',
    'joke_reveal': 'Revelar a graça',
    'joke_come_back': 'Volte amanhã para uma nova piada.',
    'joke_quote': 'O riso é a menor distância entre duas pessoas.',
    
    // Settings Page
    'settings': 'Configurações',
    'settings_subtitle': 'Personalize sua jornada',
    'language': 'Idioma',
    'ambient_audio': 'Áudio Ambiente',
    'ambient_audio_desc': 'Sons de fundo para os quartos',
    'reduced_motion': 'Movimento Reduzido',
    'reduced_motion_desc': 'Minimizar animações',
    'data_management': 'Gerenciamento de Dados',
    'export_journey': 'Exportar Dados da Jornada',
    'clear_all_data': 'Limpar Todos os Dados',
    'audio_enabled': 'Áudio Ativado',
    'audio_disabled': 'Áudio Desativado',
    'data_exported': 'Dados Exportados',
    'clear_data_confirm': 'Tem certeza? Isso apagará todo o seu progresso.',
    'data_cleared': 'Dados Limpos',
    // Theme settings
    'theme': 'Tema',
    'theme_dark': 'Escuro',
    'theme_dark_desc': 'Escuridão pura',
    'theme_bone': 'Osso',
    'theme_bone_desc': 'Palidez mortal',
    'theme_ash': 'Cinza',
    'theme_ash_desc': 'Após o fogo',
    'theme_fog': 'Névoa',
    'theme_fog_desc': 'Incerteza etérea',
    'theme_twilight': 'Crepúsculo',
    'theme_twilight_desc': 'Entre dia e noite',
    'theme_eclipse': 'Eclipse',
    'theme_eclipse_desc': 'Luz devorada pela escuridão',
    'theme_dawn': 'Aurora',
    'theme_dawn_desc': 'Primeira luz do despertar',
    'theme_changed': 'Tema atualizado',
    'font_size': 'Tamanho da Fonte',
    'font_small': 'Pequeno',
    'font_medium': 'Médio',
    'font_large': 'Grande',
    'font_size_changed': 'Tamanho da fonte atualizado',
    'high_contrast': 'Alto Contraste',
    'high_contrast_desc': 'Aumentar contraste de texto e bordas',
    'high_contrast_enabled': 'Alto contraste ativado',
    'high_contrast_disabled': 'Alto contraste desativado',
    'theme_void': 'Vazio',
    'theme_void_desc': 'Só restam sombras',
    
    // Ambient Music Control
    'ambient_music': 'Música Ambiente',
    'ambient_music_desc': 'Controle as paisagens sonoras de fundo',
    'volume': 'Volume',
    'ambient_track': 'Faixa Ambiente',
    'playing': 'Tocando',
    'play': 'Tocar',
    'ambient_default': 'Calmo',
    'ambient_night': 'Noite',
    'ambient_scary': 'Assustador',
    'ambient_truth': 'Verdade',
    'ambient_identity': 'Reflexão',
    'ambient_mortality': 'Tempo',
    'ambient_nothing': 'Vazio',
    'ambient_meta': 'Digital',
    'ambient_daily': 'Quente',
    'ambient_seasonal': 'Mágico',
    'ambient_premium': 'Profundo',
    
    // Easter Egg Discoveries
    'discovery_label': 'Descoberta',
    'discovery_konami': 'Você conhece os velhos caminhos.',
    'discovery_secret_sequence': 'Você soletrou a verdade.',
    'discovery_long_press': 'A paciência revela segredos.',
    'discovery_triple_click': 'A persistência compensa.',
    'discovery_silence': 'A quietude fala.',
    
    // Theme-Exclusive Rooms
    'ash_exclusive': 'Exclusivo do tema Cinza',
    'twilight_exclusive': 'Exclusivo do tema Crepúsculo',
    'void_exclusive': 'Exclusivo do tema Vazio',
    'theme_required': 'Requer tema {theme}',
    'ash_embers_title': 'Das Cinzas',
    'ash_embers_line1': 'Tudo queima eventualmente.',
    'ash_embers_line2': 'O que importa é o que surge depois.',
    'ash_embers_line3': 'Toque na tela. Crie brasas.',
    'ash_embers_quote': 'Das cinzas, lembramos o que uma vez queimou.',
    'room_ash-embers': 'Cinzas e Brasas',
    'sub_ash-embers': 'Só visível no tema Cinza',
    'twilight_stars_title': 'Entre Dia e Noite',
    'twilight_stars_line1': 'As estrelas lembram de todos que olharam para cima.',
    'twilight_stars_line2': 'Conecte-as. Faça sua própria constelação.',
    'twilight_stars_quote': 'No crepúsculo, vemos o que a luz do dia esconde.',
    'room_twilight-stars': 'Estrelas do Crepúsculo',
    'sub_twilight-stars': 'Só visível no tema Crepúsculo',
    'void_abyss_title': 'O Abismo',
    'void_abyss_line1': 'Olhe para o vazio. O vazio olha de volta.',
    'void_word_1': 'nada',
    'void_word_2': 'vazio',
    'void_word_3': 'silêncio',
    'void_word_4': 'ido',
    'void_word_5': 'sozinho',
    'void_word_6': 'fim',
    'void_revealed': 'revelado',
    'void_abyss_quote': 'Na escuridão absoluta, finalmente nos vemos.',
    'room_void-abyss': 'O Abismo',
    'sub_void-abyss': 'Só visível no tema Vazio',
    'shared_room': 'Quarto Compartilhado',
    'imagine_room': 'Imaginar',
    'imagine_title': 'Imagine Você',
    'imagine_subtitle': 'Descreva como você se vê. Deixe o quarto mostrar o que emerge das suas palavras.',
    'imagine_placeholder': 'Me vejo como alguém que...\n\nDescreva sua essência, sua natureza, como você se sente por dentro.',
    'imagine_hint': 'Seja honesto. A imagem reflete suas palavras.',
    'imagine_generate': 'Ver a Mim',
    'imagine_generating': 'Criando...',
    'imagine_try_again': 'Descrever Novamente',
    'imagine_quote': 'Não nos vemos claramente. Vemos o que acreditamos.',
    'room_imagine-yourself': 'Imagine Você',
    'sub_imagine-yourself': 'Descreva-se. Veja o que emerge.',
    
    // Theme Exclusive Section
    'theme_exclusive_rooms': 'Quartos Exclusivos por Tema',
    'theme_exclusive_hint': 'Mude o tema nas configurações para desbloquear estes quartos.',
    'requires_theme': 'Requer',
    'unlocked': 'Desbloqueado',
    'switch_to': 'Mudar para',
    
    // Advanced Easter Eggs
    'corner_sequence': 'Você traçou os limites.',
    'morse_sos': 'S.O.S. recebido.',
    'reverse_scroll': 'Voltando... mas mais rápido.',
    'midnight_visitor': 'A hora das bruxas te dá boas-vindas.',
    'shake': 'Você sacudiu algo.',
    
    // Experience Mode
    'experience_mode': 'Modo de Experiência',
    'classic_mode': 'Clássico',
    'classic_mode_desc': 'A realidade familiar',
    'alternate_mode': '▒ Alternativo ▒',
    'alternate_mode_desc': '[ Você não deveria estar aqui ]',
    'switch_to_alternate': '░▓█ descer █▓░',
    'switch_to_classic': '← Voltar à superfície',
    'enter_parallel_reality': '[ A porta está aberta ]',
    'return_to_familiar': 'Voltar ao familiar',
    'something_shifted': '[ Algo mudou ]',
    'reality_feels_different': '[ A realidade parece diferente agora ]',
    'alternate_tagline': 'Os mesmos quartos. Verdades diferentes.',
    'alternate_enter': 'Descer',
    'alternate_footer': '[ As coisas não são o que parecem ]',
    'alternate_rooms': '░▓ Re4l1dade P4ralela ▓░',
    'alternate_rooms_hint': '[ estes qu4rtos só ex1stem no paralelo ]',
    
    // Alternate Mode Exclusive Rooms (Portuguese)
    'room_the-other-side': 'O Outro Lado',
    'room_the-unraveling': 'O Desfiamento',
    'room_the-echo': 'O Eco',
    'sub_the-other-side': 'mova-se lentamente no escuro',
    'sub_the-unraveling': 'a realidade se desfaz a cada palavra',
    'sub_the-echo': 'o que você diz nunca vai embora',
    
    // Meta Rooms (Portuguese)
    'room_the-refusal': 'A Recusa',
    'sub_the-refusal': 'Às vezes o site te recusa',
    'room_parallel-you': 'Você Paralelo',
    'sub_parallel-you': 'Outra versão de você',
    'room_your-shadow': 'Sua Sombra',
    'sub_your-shadow': 'O que te segue',
    
    // AI Rooms (Portuguese)
    'room_ai-reflection-room': 'Sala de Reflexão IA',
    'sub_ai-reflection-room': 'Deixe a IA refletir com você',
    'sub_dream-visualization': 'Descreva seu sonho. Veja-o tomar forma.',
    'sub_future-self': 'Uma mensagem do futuro',
    
    // Meta Room Content - The Refusal (Portuguese)
    'refusal_line1': 'Esta sala te recusa.',
    'refusal_line2': 'Nem tudo se abre quando você quer.',
    'refusal_line3': 'Às vezes a resposta é simplesmente: não.',
    'refusal_line4': 'Tente o botão. Não vai ajudar.',
    'refusal_button': 'Entrar na Sala',
    
    // Meta Room Content - Parallel You (Portuguese)
    'parallel_choice_1': 'Você escolheu velocidade.',
    'parallel_alternate_1': 'Outro você escolheu paciência.',
    'parallel_reflection_1': 'Eles ainda estão esperando.',
    'parallel_choice_2': 'Você saiu cedo.',
    'parallel_alternate_2': 'Outro você ficou até o fim.',
    'parallel_reflection_2': 'Eles viram o que você perdeu.',
    'parallel_choice_3': 'Você evitou a pergunta difícil.',
    'parallel_alternate_3': 'Outro você respondeu.',
    'parallel_reflection_3': 'Eles carregam essa verdade agora.',
    'parallel_choice_4': 'Você passou correndo.',
    'parallel_alternate_4': 'Outro você saboreou cada momento.',
    'parallel_reflection_4': 'Eles lembram mais.',
    'parallel_choice_5': 'Você continuou rolando.',
    'parallel_alternate_5': 'Outro você parou.',
    'parallel_reflection_5': 'Eles encontraram algo.',
    'parallel_choice_6': 'Você escolheu conforto.',
    'parallel_alternate_6': 'Outro você escolheu desconforto.',
    'parallel_reflection_6': 'Eles cresceram com isso.',
    'parallel_quote': 'Cada momento se divide em caminhos infinitos. Você só caminha um.',
    
    // Meta Room Content - Your Shadow (Portuguese)
    'shadow_generated': 'Gerado do seu comportamento',
    'shadow_intro': 'Sua sombra...',
    'shadow_follows': 'Esta sombra te segue por cada sala.',
    'shadow_quote': 'Não somos o que pensamos. Somos o que fazemos.',
    'shadow_trait_completion': 'busca completar',
    'shadow_trait_surface': 'prefere a superfície',
    'shadow_trait_lingers': 'permanece em espaços',
    'shadow_trait_moves_quickly': 'move-se rápido',
    'shadow_trait_avoids_depth': 'evita profundidade',
    'shadow_trait_stillness': 'abraça a quietude',
    'shadow_trait_chases': 'persegue algo sem nome',
    'shadow_trait_darkness': 'atraído pela escuridão',
    'shadow_trait_emptiness': 'confortável com o vazio',
    'shadow_trait_undefined': 'indefinido',
    'shadow_trait_forming': 'ainda se formando',
    
    // Translation Navigator (Portuguese)
    'translation_navigator': 'Navegador de Traduções',
    'translation_coverage': 'Cobertura de tradução por idioma',
    'keys': 'chaves',
    'room_translations': 'Traduções de Salas',
    'show_all': 'Mostrar Tudo',
    'show_missing': 'Mostrar Faltantes',
    'all_translated': 'Todas as salas estão traduzidas!',
    'missing_translations': 'traduções faltantes',
    
    // Additional Room Titles (Portuguese)
    'room_how-many-times-left': 'Quantas Vezes Restam',
    'room_life-in-weeks': 'Sua Vida em Semanas',
    'room_delay-button': 'O Botão de Atraso',
    'room_one-honest-sentence': 'Uma Frase Honesta',
    'room_this-moment': 'Este Momento Não Se Repetirá',
    'room_reality-receipt': 'Recibo da Realidade',
    'room_nothing-happens': 'Nada Acontece Aqui',
    'room_default-choice': 'Sua Escolha Padrão',
    'room_thought-loop': 'O Loop de Pensamentos',
    'room_expect-vs-happens': 'O que Você Espera vs O que Acontece',
    'room_question-never-answer': 'A Pergunta que Você Nunca Responde',
    'room_not-special': 'Você Não É Especial (E Tudo Bem)',
    'room_shortcut-always-take': 'O Atalho que Você Sempre Pega',
    'room_blurred-self': 'Eu Borrado',
    'room_image-zooming': 'A Imagem que Você Amplia',
    'room_comfort-color': 'A Cor do Conforto',
    'room_line-you-crossed': 'A Linha que Você Cruzou',
    'room_read-aloud': 'Leia Isso em Voz Alta',
    'room_voice-without-meaning': 'Sua Voz Sem Significado',
    'room_sentence-you-avoid': 'A Frase que Você Evita',
    'room_if-nothing-changes': 'Se Nada Mudar',
    'room_real-priorities': 'Suas Prioridades Reais',
    'room_last-conversation': 'A Última Conversa',
    'room_who-without-job': 'Quem É Você Sem Seu Trabalho?',
    'room_comfort-zone-scanner': 'Scanner de Zona de Conforto',
    'room_say-it-once': 'Diga Uma Vez',
    'room_listen-to-yourself': 'Ouça a Si Mesmo',
    'room_life-as-shapes': 'Sua Vida como Formas',
    'room_face-without-features': 'Rosto Sem Traços',
    'room_what-keeps-awake': 'O que Te Mantém Acordado',
    'room_you-at-317am': 'Você às 3:17 AM',
    'room_thought-that-returns': 'O Pensamento que Retorna',
    'room_room-you-visit-most': 'A Sala que Você Mais Visita',
    'room_room-you-avoid': 'A Sala que Você Evita',
    'room_left-too-early': 'Você Saiu Muito Cedo',
    'room_daily-quote': 'Citação do Dia',
    'room_daily-joke': 'Piada do Dia',
    'room_theyre-watching': 'Estão Te Observando',
    'room_the-last-visitor': 'O Último Visitante',
    'room_dont-look-away': 'Não Desvie o Olhar',
    'room_the-whisper': 'O Sussurro',
    'room_you-forgot-something': 'Você Esqueceu Algo',
    'room_the-mirror-lies': 'O Espelho Mente',
    'room_something-follows': 'Algo Te Segue',
    'room_the-countdown': 'A Contagem Regressiva',
    'room_eyes-in-the-dark': 'Olhos no Escuro',
    'room_the-voice': 'A Voz',
    'room_it-knows-your-name': 'Sabe Seu Nome',
    'room_the-door': 'A Porta',
    'room_boss-room-free': 'A Soma das Suas Verdades',
    'room_boss-room-horror': 'O Coração do Medo',
    'room_boss-room-premium': 'A Verdade Mais Profunda',
    'room_mouse-tracker': 'O Rastreador de Mouse',
    'room_scroll-confession': 'Confissão de Rolagem',
    'room_waiting-game': 'O Jogo da Espera',
    'room_day-in-pixels': 'Seu Dia em Pixels',
    'room_memory-maze': 'Labirinto de Memória',
    'room_decision-shadow': 'Sombra de Decisão',
    'room_anonymous-mirrors': 'Espelhos Anônimos',
    'room_collective-room': 'A Sala Coletiva',
    'room_secret-room': 'A Sala Secreta',
    'room_glitch-room': 'Sala Instável',
    'room_full-moon-room': 'Sala da Lua Cheia',
    'room_weekend-room': 'Sala de Fim de Semana',
    'room_317am-room': 'Sala das 3:17 AM',
    'room_idle-room': 'A Sala Parada',
    'room_invite-friend-room': 'A Sala Compartilhada',
    'room_halloween-room': 'A Sala do Véu',
    'room_winter-solstice-room': 'A Noite Mais Longa',
    'room_new-year-room': 'Carta ao Seu Eu Futuro',
    'room_void-room': 'O Vazio',
    'room_pattern-room': 'Seus Padrões',
    'room_time-capsule': 'Cápsula do Tempo',
    'room_the-revisit': 'A Revisita',
    'room_contradiction-room': 'A Sala da Contradição',
    'room_export-journey': 'Exportar Sua Jornada',
    'room_anonymous-echoes': 'Ecos Anônimos',
    'room_the-waiting-room': 'A Sala de Espera',
    'room_dream-visualization': 'Visualização de Sonhos',
    'room_future-self': 'Seu Eu Futuro',
    'room_eclipse-shadows': 'Sombras do Eclipse',
    'room_dawn-awakening': 'Despertar da Aurora',
    
    // Additional Room Subtitles (Portuguese)
    'sub_how-many-times-left': 'Uma contagem do que resta',
    'sub_life-in-weeks': '4.000 quadrados. É tudo.',
    'sub_delay-button': 'Fica mais difícil a cada vez',
    'sub_one-honest-sentence': 'Sem editar. Sem apagar.',
    'sub_this-moment': 'Você está aqui. Uma vez.',
    'sub_reality-receipt': 'Um resumo dos seus dias',
    'sub_nothing-happens': 'Exatamente o que diz',
    'sub_eclipse-shadows': 'Fale na escuridão total',
    'sub_dawn-awakening': 'Saudações à primeira luz',
    'sub_default-choice': 'Segurança ou risco. Sempre.',
    'sub_thought-loop': 'Veja sua mente se repetir',
    'sub_expect-vs-happens': 'A lacuna entre esperança e realidade',
    'sub_question-never-answer': 'A maioria sai',
    'sub_not-special': 'Sem conforto. Só verdade.',
    'sub_shortcut-always-take': 'Os padrões se revelam',
    'sub_blurred-self': 'A memória funciona assim',
    'sub_image-zooming': 'Buscando respostas no nada',
    'sub_comfort-color': 'Quanto tempo você vai ficar?',
    'sub_line-you-crossed': 'Vemos onde você hesitou',
    'sub_read-aloud': 'Palavras que você deve falar',
    'sub_voice-without-meaning': 'Som sem palavras',
    'sub_sentence-you-avoid': 'Você não pode sair até escrever',
    'sub_if-nothing-changes': 'Uma projeção dos seus próximos cinco anos',
    'sub_real-priorities': 'Baseado em tempo, não em palavras',
    'sub_last-conversation': 'Escreva o que nunca enviará',
    'sub_who-without-job': 'Reescreva-se',
    'sub_comfort-zone-scanner': 'Vemos sua hesitação',
    'sub_say-it-once': 'Sem repetição. Sem refazer.',
    'sub_listen-to-yourself': 'Sua voz, desacelerada',
    'sub_life-as-shapes': 'Abstrato. Sem rótulos. Seu.',
    'sub_face-without-features': 'Remova até não restar nada',
    'sub_what-keeps-awake': 'Disponível após 22h',
    'sub_you-at-317am': 'Específico do tempo. Pessoal.',
    'sub_thought-that-returns': 'Retorna a cada visita',
    'sub_room-you-visit-most': 'Seu padrão revelado',
    'sub_room-you-avoid': 'O que você evita diz algo',
    'sub_left-too-early': 'Acionado quando você sai rápido',
    'sub_daily-quote': 'Sabedoria que muda diariamente',
    'sub_daily-joke': 'Humor negro, atualizado diariamente',
    'sub_theyre-watching': 'Você não está sozinho',
    'sub_the-last-visitor': 'Alguém esteve aqui antes de você',
    'sub_dont-look-away': 'Sabe quando você sai',
    'sub_the-whisper': 'Ouça com atenção',
    'sub_you-forgot-something': 'O que era?',
    'sub_the-mirror-lies': 'Quebre para ver a verdade',
    'sub_something-follows': 'Continue correndo',
    'sub_the-countdown': 'Algo acontece no zero',
    'sub_eyes-in-the-dark': 'Eles se multiplicam',
    'sub_the-voice': 'Alguém está preso aqui',
    'sub_it-knows-your-name': 'Você não deveria ter vindo',
    'sub_the-door': 'Algo está batendo',
    'sub_boss-room-free': 'A sala livre final',
    'sub_boss-room-horror': 'Enfrente a escuridão final',
    'sub_boss-room-premium': 'A sala profunda final',
    'sub_mouse-tracker': 'Vemos como você se move',
    'sub_scroll-confession': 'Quão rápido você se apressa?',
    'sub_waiting-game': 'Você consegue não fazer nada?',
    'sub_day-in-pixels': 'O tempo vira forma',
    'sub_memory-maze': 'O padrão muda',
    'sub_decision-shadow': 'Suas escolhas revelam padrões',
    'sub_anonymous-mirrors': 'Pensamentos de estranhos',
    'sub_collective-room': 'Todos estão aqui. Ninguém fica.',
    'sub_secret-room': 'Você encontrou',
    'sub_glitch-room': 'Algo está errado',
    'sub_full-moon-room': 'Só abre durante o pico lunar',
    'sub_weekend-room': 'O descanso é um destino',
    'sub_317am-room': 'Específico do tempo. Pessoal.',
    'sub_idle-room': 'Encontrado através da paciência',
    'sub_invite-friend-room': 'Algumas jornadas devem ser testemunhadas',
    'sub_halloween-room': '31 de outubro - O véu está fino',
    'sub_winter-solstice-room': '21 de dezembro - Libere e guarde',
    'sub_new-year-room': '31 de dezembro - Escreva para quem você será',
    'sub_void-room': 'Uma ferida no código',
    'sub_pattern-room': 'O que o site vê em você',
    'sub_time-capsule': 'Escreva ao seu eu futuro',
    'sub_the-revisit': 'Veja como suas respostas mudaram',
    'sub_contradiction-room': 'Suas verdades não concordam',
    'sub_export-journey': 'Baixe seu caminho',
    'sub_anonymous-echoes': 'Fragmentos de estranhos',
    'sub_the-waiting-room': 'Desbloqueie com presença',
    
    // New Phase 1-3 Rooms (Portuguese)
    'room_letters-never-sent': 'Cartas Nunca Enviadas',
    'sub_letters-never-sent': 'Escreva para alguém que nunca lerá',
    'room_breath-sync': 'Sincronização de Respiração',
    'sub_breath-sync': 'Respire com a sala',
    'room_entropy-garden': 'Jardim de Entropia',
    'sub_entropy-garden': 'Crescimento e decadência entrelaçados',
    'room_memory-palace': 'Palácio da Memória',
    'sub_memory-palace': 'Construa seu santuário mental',
    'room_morning-ritual': 'Ritual Matinal',
    'sub_morning-ritual': 'Comece seu dia com intenção',
    'room_evening-ritual': 'Ritual Noturno',
    'sub_evening-ritual': 'Feche o dia com reflexão',
    'room_weekly-ritual': 'Ritual Semanal',
    'sub_weekly-ritual': 'Domingos são para olhar para trás',
    
    // Letters Never Sent Room (Portuguese)
    'letters_title': 'Cartas Nunca Enviadas',
    'letters_subtitle': 'Escreva para alguém que nunca lerá. A IA responderá como eles poderiam.',
    'letters_select_recipient': 'Para quem você está escrevendo?',
    'letters_past_self': 'Meu Eu Passado',
    'letters_future_self': 'Meu Eu Futuro',
    'letters_someone_lost': 'Alguém que Perdi',
    'letters_someone_wronged': 'Alguém que Magoei',
    'letters_placeholder': 'Querido/a...',
    'letters_send': 'Enviar ao Vazio',
    'letters_sending': 'Enviando...',
    'letters_response': 'Uma resposta emerge...',
    'letters_write_another': 'Escrever Outra Carta',
    'letters_quote': 'Algumas palavras são feitas para serem escritas mas nunca enviadas.',
    
    // Breath Sync Room (Portuguese)
    'breath_title': 'Sincronização de Respiração',
    'breath_subtitle': 'Sincronize sua respiração com o ritmo da sala.',
    'breath_inhale': 'Inspire',
    'breath_hold': 'Segure',
    'breath_exhale': 'Expire',
    'breath_cycles': 'Ciclos completados',
    'breath_quote': 'A respiração é a ponte entre corpo e mente.',
    
    // Entropy Garden Room (Portuguese)
    'entropy_title': 'Jardim de Entropia',
    'entropy_subtitle': 'Suas plantas crescem a cada visita, mas decaem quando você se afasta.',
    'entropy_plant': 'Plantar uma Semente',
    'entropy_water': 'Regar',
    'entropy_days_alive': 'dias viva',
    'entropy_health': 'Saúde',
    'entropy_empty': 'Seu jardim está vazio. Plante algo.',
    'entropy_quote': 'Tudo cresce. Tudo murcha. Esse é o caminho.',
    
    // Memory Palace Room (Portuguese)
    'memory_palace_title': 'Palácio da Memória',
    'memory_palace_subtitle': 'Construa um santuário mental. Coloque objetos em salas que você cria.',
    'memory_palace_add_room': 'Adicionar Sala',
    'memory_palace_add_object': 'Adicionar Objeto',
    'memory_palace_room_name': 'Nome da sala...',
    'memory_palace_object_name': 'Nome do objeto...',
    'memory_palace_empty': 'Seu palácio está vazio. Crie sua primeira sala.',
    'memory_palace_quote': 'Construímos nossas mentes uma sala de cada vez.',
    
    // Ritual Rooms (Portuguese)
    'ritual_locked': 'Este ritual ainda não está disponível',
    'morning_ritual_title': 'Ritual Matinal',
    'morning_ritual_intro': 'Um momento para definir intenções para o dia.',
    'morning_ritual_available': 'Disponível 6h - 9h',
    'evening_ritual_title': 'Ritual Noturno',
    'evening_ritual_intro': 'Um momento para refletir sobre o dia.',
    'evening_ritual_available': 'Disponível 20h - 23h',
    'weekly_ritual_title': 'Ritual Semanal',
    'weekly_ritual_intro': 'Um momento para refletir sobre a semana que passou, e definir intenções para a próxima.',
    'weekly_locked': 'O Ritual Semanal te espera',
    'weekly_time': 'Volte no domingo',
    'today_is': 'Hoje é',
    'your_week': 'Sua Semana',
    'days_active': 'Dias Ativos',
    'weekly_summary_note': 'Agora, vamos refletir mais profundamente.',
    'weekly_highlight': 'Qual foi o destaque da sua semana?',
    'weekly_challenge': 'O que mais te desafiou esta semana?',
    'weekly_growth': 'Como você cresceu esta semana?',
    'weekly_intention': 'Que intenção você define para a próxima semana?',
    'weekly_placeholder': 'Leve seu tempo...',
    'reflect': 'Refletir',
    'reflecting': 'Refletindo...',
    'complete': 'Completar',
    'weekly_quote': 'Uma semana termina. Outra começa. Você carrega o que importa.',
    
    // Personal Timeline (Portuguese)
    'personal_timeline': 'Linha do Tempo Pessoal',
    'timeline_subtitle': 'Sua jornada através das salas',
    'timeline_empty': 'Sua linha do tempo está vazia. Comece a explorar.',
    'timeline_today': 'Hoje',
    'timeline_yesterday': 'Ontem',
    'timeline_this_week': 'Esta Semana',
    'timeline_earlier': 'Antes',
    
    // Weather System (Portuguese)
    'weather_clear': 'Limpo',
    'weather_rain': 'Chuva',
    'weather_fog': 'Névoa',
    'weather_snow': 'Neve',
    
    // Notifications (Portuguese)
    'notifications': 'Notificações',
    'notifications_desc': 'Receba notificações sobre conteúdo bloqueado por tempo',
    'notify_time_locked': 'Salas bloqueadas por tempo',
    'notify_full_moon': 'Noites de lua cheia',
    'notify_daily_reset': 'Reinício de desafios diários',
    'notify_time_capsule': 'Desbloqueio de cápsulas do tempo',
    'notifications_enabled': 'Notificações habilitadas',
    'notifications_disabled': 'Notificações desabilitadas',
    
    // Community Submissions (Portuguese)
    'community_submissions': 'Contribuições da Comunidade',
    'community_subtitle': 'Envie suas ideias de salas',
    'community_room_title': 'Título da Sala',
    'community_room_description': 'Descrição da Sala',
    'community_room_category': 'Categoria',
    'community_submit': 'Enviar Ideia',
    'community_submitted': 'Obrigado pela sua contribuição!',
    'community_your_submissions': 'Suas Contribuições',
    'community_empty': 'Ainda não há contribuições. Seja o primeiro!',
    
    // Proactive Guide (Portuguese)
    'guide_hesitation': 'Tomando seu tempo? Tudo bem. Gostaria de uma sugestão?',
    'guide_long_absence': 'Bem-vindo de volta. Faz um tempo.',
    'guide_milestone': 'Você veio longe. Gostaria de refletir sobre sua jornada?',
    'guide_dismiss': 'Dispensar',
    'guide_accept': 'Mostre-me',
    
    // Voice Mode (Portuguese)
    'voice_mode': 'Modo de Voz',
    'voice_mode_desc': 'Deixe o guia falar com você',
    'voice_mode_enabled': 'Modo de voz habilitado',
    'voice_mode_disabled': 'Modo de voz desabilitado',
    
    // Offline Support (Portuguese)
    'offline_available': 'Disponível offline',
    'offline_cached': 'Esta sala está armazenada para visualização offline',
    
    // Journey Export (Portuguese)
    'journey_export': 'Exportar Jornada',
    'journey_import': 'Importar Jornada',
    'journey_export_json': 'Exportar como JSON',
    'journey_import_json': 'Importar de JSON',
    'journey_exported': 'Jornada exportada com sucesso',
    'journey_imported': 'Jornada importada com sucesso',
    
    // Phase 4 - Seasonal Rooms (Portuguese)
    'room_spring-equinox-room': 'Equinócio de Primavera',
    'sub_spring-equinox-room': 'Liberte e renove',
    'room_summer-solstice-room': 'Solstício de Verão',
    'sub_summer-solstice-room': 'O dia mais longo',
    'room_autumn-equinox-room': 'Equinócio de Outono',
    'sub_autumn-equinox-room': 'Colha e aceite',
    'room_valentine-room': 'Sala de Amor Próprio',
    'sub_valentine-room': 'O amor que você merece',
    
    // Spring Equinox Room (Portuguese)
    'spring_date': '20 DE MARÇO',
    'spring_title': 'Equinócio de Primavera',
    'spring_message_1': 'Dia e noite são iguais.',
    'spring_message_2': 'Um momento de equilíbrio perfeito.',
    'spring_message_3': 'O que você vai liberar para dar espaço ao crescimento?',
    'spring_letting_go': 'Estou deixando ir:',
    'spring_letting_go_placeholder': 'O que não te serve mais...',
    'spring_planting': 'Estou plantando:',
    'spring_planting_placeholder': 'O que você quer que cresça...',
    'spring_bloom': 'Deixe florescer',
    'spring_released': 'Liberado:',
    'spring_planted': 'Plantado:',
    'spring_closing': 'A partir deste momento, uma nova vida começa.',
    'spring_quote': 'A natureza não tem pressa, mas tudo se realiza.',
    
    // Summer Solstice Room (Portuguese)
    'summer_date': '21 DE JUNHO',
    'summer_title': 'O Dia Mais Longo',
    'summer_message_1': 'Este é o dia mais longo do ano.',
    'summer_message_2': 'A luz atinge seu pico.',
    'summer_message_3': 'O que você faria com mais tempo?',
    'summer_message_4': 'Descreva seu dia mais pleno e vibrante.',
    'summer_placeholder': 'No meu dia mais pleno, eu...',
    'summer_submit': 'Abrace a luz',
    'summer_closing': 'O sol te ouviu. Agora começa sua descida.',
    'summer_quote': 'No auge da luz, descobrimos o que realmente queremos iluminar.',
    
    // Autumn Equinox Room (Portuguese)
    'autumn_date': '22 DE SETEMBRO',
    'autumn_title': 'Equinócio de Outono',
    'autumn_message_1': 'O equilíbrio retorna.',
    'autumn_message_2': 'A colheita está pronta.',
    'autumn_message_3': 'O que você cultivou? O que pode aceitar?',
    'autumn_harvesting': 'Estou colhendo:',
    'autumn_harvesting_placeholder': 'O que você cultivou este ano...',
    'autumn_accepting': 'Estou aceitando:',
    'autumn_accepting_placeholder': 'O que você não pode mudar...',
    'autumn_release': 'Deixe as folhas caírem',
    'autumn_harvested': 'Colhido:',
    'autumn_accepted': 'Aceito:',
    'autumn_closing': 'Tudo cai no seu tempo.',
    'autumn_quote': 'O outono nos mostra como é bonito deixar as coisas irem.',
    
    // Valentine's Day Room (Portuguese)
    'valentine_date': '14 DE FEVEREIRO',
    'valentine_title': 'O Amor Que Você Merece',
    'valentine_message_1': 'Hoje não é sobre os outros.',
    'valentine_message_2': 'É sobre quem está sempre com você.',
    'valentine_message_3': 'Quem ouve cada pensamento.',
    'valentine_message_4': 'Escreva uma carta de amor para si mesmo.',
    'valentine_prompt': 'Uma carta para você:',
    'valentine_placeholder': 'Querido eu, quero que você saiba...',
    'valentine_submit': 'Enviar com amor',
    'valentine_closing': 'Você merece o amor que dá aos outros.',
    'valentine_quote': 'Você mesmo merece seu amor e carinho.',
    
    // Update changelog
    'update_seasonal_rooms': 'Quatro novas salas sazonais: Equinócio de Primavera, Solstício de Verão, Equinócio de Outono e Dia dos Namorados',
    'update_fragment_trading': 'Sistema de troca de fragmentos - sacrifique fragmentos para desbloquear salas com tempo, pré-visualizar salas de terror ou receber mensagens de IA',
    'update_new_themes': 'Dois novos temas: Eclipse (escuridão total) e Aurora (gradientes quentes, apenas 5-7 AM)',
     'update_regret_machine': 'A Máquina do Arrependimento - narrativas de linhas do tempo alternativas movidas por IA',
     'update_memory_reconstruction': 'Reconstrução de Memória - a IA preenche detalhes sensoriais de memórias que desvanecem',
     
     // Phase 7 - Regret Machine Room
     'regret_machine_title': 'A Máquina do Arrependimento',
     'regret_machine_subtitle': 'Descreva uma decisão que gostaria de ter tomado diferente. A máquina mostrará o que poderia ter sido.',
     'regret_placeholder': 'Eu me arrependo de...\n\nDescreva uma escolha que você fez e que gostaria que tivesse sido diferente. O que você teria feito em vez disso?',
     'regret_hint': 'Isso não é sobre culpa. É sobre compreensão.',
     'regret_explore': 'Explorar a Linha do Tempo',
     'regret_generating': 'Reconstruindo uma realidade alternativa...',
     'regret_alternate_timeline': 'A Linha do Tempo Alternativa',
     'regret_explore_another': 'Explorar Outra',
     'regret_error_short': 'Por favor descreva seu arrependimento com mais detalhes',
     'room_regret-machine': 'A Máquina do Arrependimento',
     
     // Phase 7 - Memory Reconstruction Room
     'memory_reconstruction_title': 'Reconstrução de Memória',
     'memory_reconstruction_subtitle': 'Descreva uma memória que desvanece. Deixe-nos ajudá-lo a sentir o caminho de volta.',
     'memory_placeholder': 'Eu lembro...\n\nDescreva uma memória distante ou fragmentada. Que fragmentos permanecem? Que sentimentos persistem?',
     'memory_timeframe_placeholder': 'Quando foi isso? (ex: "infância", "dez anos atrás")',
     'memory_hint': 'Memória não é uma gravação. É uma reconstrução.',
     'memory_reconstruct': 'Reconstruir Memória',
     'memory_reconstructing': 'Juntando os fragmentos...',
     'memory_reconstructed': 'A Memória Reconstruída',
     'memory_explore_another': 'Explorar Outra Memória',
     'memory_error_short': 'Por favor descreva sua memória com mais detalhes',
     'room_memory-reconstruction': 'Reconstrução de Memória',
    'update_eclipse_room': 'Sombras do Eclipse - fale na escuridão total, revele em flashes de luz',
    'update_dawn_room': 'Despertar da Aurora - saúde a primeira luz com exercícios de respiração e intenções',
    
    // New themes
    'eclipse_theme_required': 'Esta sala requer o tema Eclipse. A escuridão total aguarda.',
    'dawn_theme_required': 'Esta sala requer o tema Aurora durante as primeiras horas.',
    'dawn_time_hint': 'Disponível apenas das 5 às 7 AM',
    'change_theme': 'Alterar tema nas Configurações',
    
    // Eclipse Shadows Room
    'eclipse_shadows_title': 'Sombras do Eclipse',
    'eclipse_shadows_intro': 'Na escuridão completa, os segredos parecem mais seguros. Responda honestamente—apenas breves flashes iluminarão suas palavras.',
    'enter_darkness': 'Entrar na escuridão',
    'shadow_question_1': 'O que você esconde de si mesmo?',
    'shadow_question_2': 'Que verdade você diria se ninguém pudesse ouvir?',
    'shadow_question_3': 'O que te assombra às 3 AM?',
    'shadow_question_4': 'O que você faria se o medo não existisse?',
    'shadow_question_5': 'O que você não está pronto para enfrentar?',
    'speak_into_dark': 'Fale na escuridão...',
    'release': 'Liberar',
    'eclipse_reveal_prompt': 'Clique em cada sombra para revelar o que você confessou.',
    'hidden_in_shadow': '▓▓▓▓▓▓▓▓▓▓▓▓',
    'eclipse_reveal_hint': 'Algumas verdades são mais claras no escuro.',
    'eclipse_shadows_quote': 'Na escuridão total, finalmente nos vemos.',
    
    // Dawn Awakening Room
    'dawn_awakening_title': 'Despertar da Aurora',
    'dawn_awakening_intro': 'A primeira luz traz possibilidade. Antes do mundo acordar, defina suas intenções.',
    'greet_the_light': 'Saúde a luz',
    'breathe_in_light': 'Respire a luz',
    'hold_warmth': 'Segure o calor',
    'release_night': 'Libere a noite',
    'dawn_prompt_1': 'O que você quer levar consigo hoje?',
    'dawn_prompt_2': 'O que você vai deixar ir?',
    'dawn_prompt_3': 'Que pequeno ato de gentileza você vai oferecer a si mesmo?',
    'set_your_intention': 'Defina sua intenção...',
    'plant_seed': 'Plante esta semente',
    'dawn_awakening_quote': 'Cada aurora é um convite para recomeçar.',
    
    // Fragment Trading
    'fragment_trading': 'Troca de Fragmentos',
    'sacrifice_to_unlock': 'Sacrifique fragmentos para desbloquear segredos',
    'your_fragments': 'Seus Fragmentos',
    'fragments_any': 'quaisquer fragmentos',
    'fragments_free': 'fragmentos de clareza',
    'fragments_premium': 'fragmentos de profundidade',
    'fragments_horror': 'fragmentos de sombra',
    'trade_successful': 'Troca Bem-sucedida!',
    'trade_failed': 'Troca Falhou',
    'not_enough_fragments': 'Fragmentos insuficientes',
    'continue_trading': 'Continuar Trocando',
    'trading_fragments': 'Trocando fragmentos...',
    'fragment_trade_warning': 'Fragmentos sacrificados não podem ser recuperados',
    'unlock-time-locked': 'Desbloquear Sala com Tempo',
    'unlock-time-locked_desc': 'Acesse uma sala com tempo sem esperar',
    'preview-horror': 'Pré-visualização de Terror',
    'preview-horror_desc': 'Espie uma sala de terror antes de entrar',
    'ai-reflection-trade': 'Reflexão IA',
    'ai-reflection-trade_desc': 'Receba uma mensagem personalizada da IA',
    'unlock-premium-preview': 'Teste de Sala Premium',
    'unlock-premium-preview_desc': 'Acesse uma sala premium por uma visita',
    'fragment_trading_room': 'O Altar de Fragmentos',
    'fragment_trading_intro': 'Aqui, você pode sacrificar seus fragmentos coletados para desbloquear segredos escondidos nestas salas.',
    'your_collection': 'Sua Coleção',
    'fragment_clarity': 'Clareza',
    'fragment_depth': 'Profundidade',
    'fragment_shadow': 'Sombra',
    'total_fragments': 'Total',
    'open_altar': 'Aproximar-se do Altar',
    'recent_trades': 'Oferendas Recentes',
    'fragment_trade_ending': 'O que foi dado não pode ser recuperado. Mas o que foi ganho... isso permanece.',
    'room_fragment-trade': 'O Altar de Fragmentos',
    'sub_fragment-trade': 'Sacrifique para desbloquear segredos',
    'update_anniversary_system': 'Sistema de Aniversário e Marcos - acompanhe sua jornada ao longo do tempo',
    'update_archive_room': 'O Arquivo - histórico completo desbloqueado em 25 salas',
    'update_mirror_room': 'Espelho de Todos os Eus - retrato comportamental IA em 50 salas',
    'update_ending_room': 'O Final - uma despedida personalizada em 75 salas',
    'room_the-archive': 'O Arquivo',
    'room_mirror-of-all-selves': 'Espelho de Todos os Eus',
    'room_the-ending': 'O Final',
    // Phase 9
    'update_fragment_rarities': 'Raridades de Fragmentos - fragmentos colecionáveis Comuns, Raros e Lendários',
    'update_fragment_gallery': 'Galeria de Fragmentos - vitrine visual para sua coleção',
    'update_seasonal_fragments': 'Fragmentos sazonais e secretos - colecionáveis por tempo limitado',
    'fragment_collection': 'Coleção',
    'fragment_collection_title': 'Coleção de Fragmentos',
    'fragment_collection_subtitle': 'Cada sala deixa algo para trás. Colete todos.',
    'collection_milestones': 'Marcos',
    'collection_secrets': 'Segredos',
    'collection_footer': 'Alguns fragmentos só aparecem em certas condições.',
    'rarity_common': 'Comum',
    'rarity_rare': 'Raro',
    'rarity_legendary': 'Lendário',
    // Phase 10-11
    'accessibility': 'Acessibilidade',
    'accessibility_desc': 'Personalizar legibilidade e preferências de movimento',
    'font_family': 'Família da Fonte',
    'font_default': 'Monoespaçada',
    'font_dyslexia': 'Amigável para Dislexia',
    'font_sans': 'Sans Serif',
    'font_changed': 'Fonte atualizada',
    'line_height': 'Altura da Linha',
    'line_compact': 'Compacto',
    'line_normal': 'Normal',
    'line_relaxed': 'Relaxado',
    'line_spacious': 'Espaçoso',
    'line_height_changed': 'Altura da linha atualizada',
    'reduced_motion_full_desc': 'Desabilitar todas as animações e efeitos de partículas',
    'larger_cursors': 'Alvos Maiores',
    'larger_cursors_desc': 'Aumentar o tamanho dos alvos de clique',
    'update_accessibility_suite': 'Suite de Acessibilidade - fontes para dislexia, controle de altura de linha',
    'update_dyslexia_font': 'Opção de fonte amigável para dislexia nas configurações',
    'update_line_height': 'Altura de linha ajustável para melhor legibilidade',
    'update_enhanced_reduced_motion': 'Movimento reduzido aprimorado - desativa completamente todos os efeitos',
    'update_shared_moments': 'Momentos Compartilhados - check-ins emocionais anônimos',
    'update_whisper_wall': 'O Muro dos Sussurros - deixe verdades anônimas no escuro',
    'update_collective_memory': 'Memória Coletiva - fragmentos compartilhados do que lembramos',
    'whisper_prompt': 'Deixe um sussurro no muro. Anônimo. Permanente.',
    'whisper_placeholder': 'O que você carrega em silêncio?',
    'whisper_send': 'Sussurrar',
    'whisper_added': 'Seu sussurro se junta ao muro.',
    'whisper_another': 'Sussurrar novamente',
    'shared_moments_title': 'Agora mesmo, neste momento...',
    'shared_moments_subtitle': 'Como você está, honestamente?',
    'feeling_grateful': 'Grato',
    'feeling_lost': 'Perdido',
    'feeling_hopeful': 'Esperançoso',
    'feeling_heavy': 'Pesado',
    'feeling_peaceful': 'Em paz',
    'feeling_restless': 'Inquieto',
    'room_whisper-wall': 'O Muro dos Sussurros',
    'room_collective-memory': 'Memória Coletiva',
    'room_shared-moments': 'Momentos Compartilhados',
    'sub_regret-machine': 'Explore o que poderia ter sido',
    'sub_memory-reconstruction': 'Reconstrua uma memória que se desvanece',
    'sub_whisper-wall': 'Verdades anônimas flutuam no escuro',
    'sub_collective-memory': 'Fragmentos compartilhados do que lembramos',
    'sub_shared-moments': 'Como você está, honestamente?',
    'sub_the-archive': 'Seu histórico completo de jornada',
    'sub_mirror-of-all-selves': 'A IA revela seu retrato comportamental',
    'sub_the-ending': 'Uma despedida que você mereceu',

    // v3.21.0 - New Rooms
    'room_mask-you-wear': 'A Máscara que Você Usa',
    'sub_mask-you-wear': 'Que rosto você mostra?',
    'room_recurring-dream': 'Seu Sonho Recorrente',
    'sub_recurring-dream': 'O sonho que continua voltando',
    'room_promise-you-broke': 'A Promessa que Você Quebrou',
    'sub_promise-you-broke': 'Honestidade sobre promessas quebradas',
    'room_words-you-swallowed': 'Palavras que Você Engoliu',
    'sub_words-you-swallowed': 'Coisas que você nunca disse',
    'room_age-you-feel': 'A Idade que Você Sente',
    'sub_age-you-feel': 'Quantos anos você realmente tem?',
    'room_biggest-lie': 'Sua Maior Mentira',
    'sub_biggest-lie': 'A mentira que você mais conta',
    'room_person-you-pretend-to-be': 'A Pessoa que Você Finge Ser',
    'sub_person-you-pretend-to-be': 'Sua atuação',
    'room_person-you-pretend': 'A Pessoa que Você Finge Ser',
    'sub_person-you-pretend': 'Sua atuação',
    'room_what-you-collect': 'O que Você Coleciona',
    'sub_what-you-collect': 'Objetos revelam identidade',
    'room_apology-you-owe': 'O Pedido de Desculpas que Você Deve',
    'sub_apology-you-owe': 'Escreva o que deveria ter dito',
    'room_unfinished-story': 'Sua História Inacabada',
    'sub_unfinished-story': 'O que você nunca completou',
    'room_version-others-see': 'A Versão que os Outros Veem',
    'sub_version-others-see': 'Como os outros te percebem',
    'room_secret-talent': 'Seu Talento Secreto',
    'sub_secret-talent': 'O que ninguém sabe que você faz',
    'room_fear-you-feed': 'O Medo que Você Alimenta',
    'sub_fear-you-feed': 'Qual medo te controla?',
    'room_guilty-pleasure': 'Seu Prazer Culpado',
    'sub_guilty-pleasure': 'O que você esconde gostar',
    'room_rule-you-break': 'A Regra que Você Sempre Quebra',
    'sub_rule-you-break': 'Seu padrão de rebelião',
    'room_childhood-promise': 'Sua Promessa de Infância',
    'sub_childhood-promise': 'O que você prometeu a si mesmo',
    'room_compliment-you-reject': 'O Elogio que Você Rejeita',
    'sub_compliment-you-reject': 'Por que você não aceita elogios',
    'room_autopilot-mode': 'Seu Modo Automático',
    'sub_autopilot-mode': 'O que você faz sem pensar',
    'room_boundary-you-need': 'O Limite que Você Precisa',
    'sub_boundary-you-need': 'O que você deve proteger',
    'room_inner-critic': 'Seu Crítico Interior',
    'sub_inner-critic': 'A voz que julga',
    'room_last-sunday': 'Último Domingo',
    'sub_last-sunday': 'O que você realmente fez?',
    'room_wasted-hours': 'Suas Horas Perdidas',
    'sub_wasted-hours': 'Onde o tempo desaparece',
    'room_year-that-changed': 'O Ano que te Mudou',
    'sub_year-that-changed': 'O ponto de virada',
    'room_tomorrows-regret': 'O Arrependimento de Amanhã',
    'sub_tomorrows-regret': 'O que você desejará ter feito?',
    'room_morning-autopilot': 'Seu Piloto Automático Matinal',
    'sub_morning-autopilot': 'O ritual da primeira hora',
    'room_season-you-dread': 'A Estação que Você Teme',
    'sub_season-you-dread': 'Qual estação pesa mais?',
    'room_ten-years-ago': 'Dez Anos Atrás',
    'sub_ten-years-ago': 'Quem era você então?',
    'room_last-normal-day': 'Seu Último Dia Normal',
    'sub_last-normal-day': 'Antes de tudo mudar',
    'room_clock-you-ignore': 'O Relógio que Você Ignora',
    'sub_clock-you-ignore': 'Tempo que finge não passar',
    'room_your-deadline': 'Seu Prazo',
    'sub_your-deadline': 'O que está adiando?',
    'room_the-pause': 'A Pausa',
    'sub_the-pause': 'Apenas respire por um momento',
    'room_static-room': 'Estático',
    'sub_static-room': 'Ruído branco e quietude',
    'room_gap-between-thoughts': 'O Espaço Entre Pensamentos',
    'sub_gap-between-thoughts': 'Encontre o espaço',
    'room_sit-with-discomfort': 'Sente com o Desconforto',
    'sub_sit-with-discomfort': 'Não conserte nada',
    'room_boring-room': 'O Quarto Chato',
    'sub_boring-room': 'Nada interessante',
    'room_unplug-room': 'Desconectar',
    'sub_unplug-room': 'Imagine sem dispositivos',
    'room_unplug': 'Desconectar',
    'sub_unplug': 'Imagine sem dispositivos',
    'room_last-page': 'A Última Página',
    'sub_last-page': 'A história termina aqui',
    'room_scrolling-pattern': 'Seu Padrão de Rolagem',
    'sub_scrolling-pattern': 'Como você consome conteúdo',
    'room_tab-you-keep-open': 'A Aba que Mantém Aberta',
    'sub_tab-you-keep-open': 'O que fica no navegador',
    'room_digital-shadow': 'Sua Sombra Digital',
    'sub_digital-shadow': 'Sua pegada digital',
    'room_notification-you-ignore': 'A Notificação que Ignora',
    'sub_notification-you-ignore': 'O que você descarta',
    'room_typing-speed': 'Sua Velocidade de Digitação',
    'sub_typing-speed': 'Quão rápido você se expressa?',
    'room_link-you-almost-clicked': 'O Link que Quase Clicou',
    'sub_link-you-almost-clicked': 'Padrões de hesitação',
    'room_bookmark-graveyard': 'Seu Cemitério de Favoritos',
    'sub_bookmark-graveyard': 'Salvos mas nunca revisitados',
    'room_search-you-deleted': 'A Busca que Deletou',
    'sub_search-you-deleted': 'O que procurou e apagou',
    'room_gravity-room': 'Sala da Gravidade',
    'sub_gravity-room': 'Tudo cai devagar',
    'room_ink-blot': 'Mancha de Tinta',
    'sub_ink-blot': 'O que você vê?',
    'room_hallway-of-mirrors': 'O Corredor de Espelhos',
    'sub_hallway-of-mirrors': 'Reflexos infinitos',
    'room_dissolving-text': 'Texto Dissolvendo',
    'sub_dissolving-text': 'Palavras derretem ao ler',
    'room_the-pendulum': 'O Pêndulo',
    'sub_the-pendulum': 'Movimento hipnótico',
    'room_pixel-rain': 'Chuva de Pixels',
    'sub_pixel-rain': 'Precipitação digital',
    'room_the-threshold': 'O Limiar',
    'sub_the-threshold': 'Entre aqui e lá',
    'most_loved_rooms': 'Quartos Mais Amados',
    'rate_room': 'Avalie este quarto',
    'update_52_new_rooms': '52 novos quartos nas categorias Identidade, Tempo, Contemplação, Meta e Experimental',
    'update_room_rating': 'Sistema de avaliação - polegar para cima/baixo com seção Quartos Mais Amados',
    'update_bug_fix_reflections': 'Correção na exibição e armazenamento de reflexões anônimas',
    'update_100_rooms': 'Mais de 100 quartos disponíveis no Corredor',
    // v3.22.0
    'room_the-friend-you-lost': 'O Amigo Que Você Perdeu',
    'sub_the-friend-you-lost': 'Escreva para alguém de quem se afastou.',
    'room_conversation-you-replay': 'A Conversa Que Você Repete',
    'sub_conversation-you-replay': 'Recrie-a de memória.',
    'room_the-thank-you': 'O Agradecimento',
    'sub_the-thank-you': 'Gratidão que nunca expressou.',
    'room_people-you-forgot': 'Pessoas Que Você Esqueceu',
    'sub_people-you-forgot': 'Tente lembrar nomes esquecidos.',
    'room_the-advice-you-give': 'O Conselho Que Você Dá',
    'sub_the-advice-you-give': 'Conselho que dá mas nunca segue.',
    'room_what-others-dont-know': 'O Que Os Outros Não Sabem',
    'sub_what-others-dont-know': 'Suas camadas escondidas.',
    'room_the-lie-you-believe': 'A Mentira Que Você Acredita',
    'sub_the-lie-you-believe': 'Uma inverdade confortável.',
    'room_your-shadow-self': 'Seu Eu Sombra',
    'sub_your-shadow-self': 'A parte que você suprime.',
    'room_the-story-you-tell': 'A História Que Você Conta',
    'sub_the-story-you-tell': 'Como você narra sua vida.',
    'room_your-safe-place': 'Seu Lugar Seguro',
    'sub_your-safe-place': 'Descreva onde se sente seguro.',
    'room_the-risk-you-didnt-take': 'O Risco Que Você Não Correu',
    'sub_the-risk-you-didnt-take': 'Caminhos não percorridos.',
    'room_your-last-excuse': 'Sua Última Desculpa',
    'sub_your-last-excuse': 'A desculpa que mais usa.',
    'room_the-habit-you-hide': 'O Hábito Que Você Esconde',
    'sub_the-habit-you-hide': 'Rotinas secretas.',
    'room_what-you-postpone': 'O Que Você Adia',
    'sub_what-you-postpone': 'A coisa sempre "amanhã".',
    'room_the-door-you-closed': 'A Porta Que Você Fechou',
    'sub_the-door-you-closed': 'Oportunidades que rejeitou.',
    'journal_title': 'Diário de Reflexões',
    'journal_subtitle': 'Seus pensamentos entre quartos',
    'journal_empty': 'Sem reflexões ainda. Visite quartos e escreva seus pensamentos.',
    'journal': 'Diário',
    'room_chains': 'Cadeias de Quartos',
    'chain_identity_journey': 'Jornada de Identidade',
    'chain_time_reflection': 'Reflexão do Tempo',
    'chain_truth_path': 'Caminho da Verdade',
    'recently_visited': 'Visitados Recentemente',
    'update_reflection_journal': 'Diário de Reflexões - salve seus pensamentos entre quartos',
    'update_room_chains': 'Cadeias de Quartos - complete quartos em sequência',
    'update_15_new_rooms': '15 novos quartos interativos em 3 categorias',
    'update_recently_visited': 'Seção de visitados recentemente para retorno rápido',
    'ai_companion': 'Companheiro',
    'companion_create': 'Crie Seu Companheiro',
    'companion_setup_desc': 'Um reflexo que caminha ao seu lado.',
    'companion_name': 'Nomeie seu companheiro',
    'companion_name_placeholder': 'ex., Eco, Espelho, Sombra...',
    'companion_lens': 'Lente de personalidade',
    'companion_mood': 'Tom de comunicação',
    'companion_fears': 'Do que você tem medo? (opcional)',
    'companion_struggles': 'Com o que você luta? (opcional)',
    'create_companion': 'Criar',
    'companion_input': 'Diga algo...',
    'companion_greeting': 'Seu companheiro está aqui. Diga algo.',
    'companion_starter_1': 'O que está na minha mente hoje?',
    'companion_starter_2': 'Me sinto travado.',
    'companion_starter_3': 'Me diga algo que eu evito.',
    'fears_placeholder': 'ex., fracasso, rejeição, ser esquecido...',
    'struggles_placeholder': 'ex., pensar demais, procrastinar, solidão...',
    'back_step': 'Voltar',
    'lens_reflective': 'Reflexivo',
    'lens_reflective_desc': 'Perguntas profundas, reconhecimento de padrões',
    'lens_supportive': 'Apoiador',
    'lens_supportive_desc': 'Caloroso, encorajador, validador',
    'lens_challenging': 'Desafiador',
    'lens_challenging_desc': 'Direto, provocador',
    'lens_curious': 'Curioso',
    'lens_curious_desc': 'Perguntas inesperadas, admiração',
    'lens_shadow': 'Sombra',
    'lens_shadow_desc': 'O lado oculto, desconforto honesto',
    'companion_mood_calm': 'Calmo',
    'companion_mood_calm_desc': 'Comedido e pacífico',
    'companion_mood_intense': 'Intenso',
    'companion_mood_intense_desc': 'Urgente e profundo',
    'companion_mood_playful': 'Brincalhão',
    'companion_mood_playful_desc': 'Espirituoso e leve',
    'companion_mood_melancholic': 'Melancólico',
    'companion_mood_melancholic_desc': 'Tristeza bonita',
    'update_ai_companion': 'Companheiro IA - uma IA pessoal que lembra sua jornada',
  },

  zh: {
    // Index
    'title': '人类房间',
    'tagline': '一系列反映你无人关注时真实自我的房间。',
    'enter': '进入',
    'footer': '不是治疗。不是社交。不是效率工具。',
    
    // Hallway
    'back': '← 返回',
    'hallway': '走廊',
    'challenges': '挑战',
    'random': '随机',
    'open_rooms': '开放房间',
    'dark_rooms': '黑暗房间',
    'enter_risk': '后果自负',
    'premium_rooms': '高级房间',
    'unlock_to_access': '解锁访问',
    'daily_rooms': '每日房间',
    'refreshes_daily': '每天刷新',
    'time_locked': '时间锁定房间',
    'seasonal_rooms': '季节房间',
    'available_now': '现在可用',
    'fragments': '碎片',
    'boss_unlocked': 'Boss房间已解锁',
    'collect_unlock': '收集以解锁Boss房间',
    'game_rooms': '游戏房间',
    'experimental_rooms': '实验房间',
    'deep_rooms': '深层房间',
    'meta_rooms': '元房间',
    'search_rooms': '搜索房间...',
    'no_results': '没有匹配的房间',
    'clear_filters': '清除筛选',
    'explore': '探索',
    'all_categories': '全部',
    
    // About
    'what_is_this': '这是什么地方？',
    'not_productivity': '人类房间不是效率工具。',
    'not_social': '不是社交网络。',
    'not_therapy': '不是治疗。',
    'pause_experiences': '这是一系列旨在让你暂停的小体验。',
    'each_room': '每个房间需要30秒到2分钟。',
    'make_think': '有些房间会让你思考。',
    'make_uncomfortable': '有些会让你不舒服。',
    'make_calm': '有些会让你平静。',
    'make_aware': '所有房间都会让你觉醒。',
    'no_notifications': '这里没有通知。',
    'no_points': '没有积分可收集。',
    'no_validation': '没有社交验证。',
    'just_truth': '只有你、房间和真相。',
    'enter_rooms': '进入房间',
    
    // Common
    'loading': '加载中...',
    'error': '错误',
    'continue': '继续',
    'submit': '提交',
    'cancel': '取消',
    'close': '关闭',
    'next': '下一个',
    'previous': '上一个',
    'start': '开始',
    'stop': '停止',
    'yes': '是',
    'no': '否',
    'leave_room': '← 离开房间',
    'return': '← 返回',
    'return_hallway': '返回走廊',
    'begin': '开始',
    'watch': '观看...',
    'correct': '正确',
    'round': '轮',
    'enter_again': '再次进入',
    'steps': '步数',
    'depth': '深度',
    
    // Keyboard Shortcuts
    'keyboard_shortcuts': '键盘快捷键',
    'shortcut_hallway': '前往走廊',
    'shortcut_random': '随机房间',
    'shortcut_back': '返回',
    'shortcut_bookmark': '切换书签',
    'shortcut_reading': '阅读模式',
    'shortcut_search': '聚焦搜索',
    'shortcut_stats': '查看统计',
    'shortcut_achievements': '查看成就',
    'shortcut_cat_nav': '导航',
    'shortcut_cat_rooms': '房间',
    'shortcut_cat_access': '辅助功能',
    'press_to_close': '按',
    'to_close': '关闭',
    'or': '或',
    
    // Bookmarks
    'add_bookmark': '添加书签',
    'remove_bookmark': '移除书签',
    'bookmarks': '书签',
    'bookmarked_rooms': '收藏',
    'no_bookmarks': '暂无书签',
    
    // Reading Mode
    'reading_mode': '阅读模式',
    'reading_mode_on': '阅读模式已启用',
    'reading_mode_off': '阅读模式已禁用',
    
    // Updates Page
    'updates': '更新',
    'updates_title': '新功能',
    'updates_subtitle': '最近的更改和改进',
    'updates_footer': '一次更新，构建一个房间。',
    'new': '新',
    'subscribe_rss': '通过RSS订阅',
    'latest': '最新',
    'whats_new_intro': '最新更新:',
    'view_all_updates': '查看所有更新',
    'update_updates_page': '添加更新页面',
    'update_full_i18n': '完整的6语言翻译',
    'update_hallway_animations': '平滑的分类切换动画',
    'update_daily_challenges': '改进每日挑战显示',
    'update_game_rooms': '添加15个新游戏房间',
    'update_experimental_rooms': '添加14个实验房间',
    'update_random_room': '随机房间按钮',
    'update_stats_route': '修复统计页面路由',
    'update_streak_system': '每日连续系统',
    'update_weekly_summary': '每周进度总结',
    'update_rtl_support': '从右到左语言支持',
    'update_dark_rooms': '新的黑暗房间类别',
    'update_boss_rooms': '带碎片要求的Boss房间',
    'update_audio_system': '房间环境音效',
    'update_constellation_map': '视觉旅程星座图',
    'update_fragment_system': '房间完成碎片收集',
    'update_mobile_layout': '移动响应式改进',
    'update_multi_language': '多语言支持（6种语言）',
    'update_premium_system': '高级房间和解锁系统',
    'update_room_endings': '独特的房间结尾引语',
    'update_ui_redesign': '完整UI设计刷新',
    'update_theme_effects': '主题特定视觉效果（余烬、星星、故障）',
    'update_void_theme': '新的虚空主题 - 绝对黑暗模式',
    'update_achievements': '成就系统（19个里程碑）',
    'update_ai_reflection': 'AI反思房间',
    'update_settings_redesign': '重新设计的设置（4个主题）',
    'update_theme_exclusive_rooms': '主题专属房间',
    'update_discovery_localization': '发现消息本地化',
    'update_site_mood_system': '网站情绪系统',
    'update_voice_imperfection': '不完美语音房间',
    'update_mobile_optimization': '移动端优化（48px触摸目标）',
    'update_interaction_clues': '微妙的交互提示',
    'update_theme_exclusive_hallway': '走廊中可见主题专属房间',
    'update_advanced_easter_eggs': '高级彩蛋：角落序列、摩尔斯电码、午夜访客',
    'update_hidden_discoveries': '隐藏文本揭示',
    'update_dual_mode_system': '经典和交替双模式体验系统',
    'update_alternate_mode': '交替模式 - 平行实验现实',
    'update_hidden_discovery': '隐藏发现触发器：三次点击、滚动模式、空闲唤醒',
    'update_mode_effects': '模式特定视觉效果、故障和环境音频',
    'update_translation_navigator': '翻译导航器 - 可视覆盖面板',
    'update_full_localization': '6种语言近100%翻译覆盖',
    'update_meta_room_translations': '元房间内容本地化（拒绝、平行你、你的影子）',
    'update_animated_timeline': '动画更新时间线，滚动触发动画',
    'update_toast_notifications': '新版本首次访问时的更新提示',
    'update_rss_feed': 'RSS订阅更新日志',
    'update_bookmarked_filter': '走廊中的收藏房间标签页',
    'update_keyboard_shortcuts': '键盘快捷键（按 ? 查看）',
    'update_reading_mode': '阅读模式，增强排版和专注',
    'update_accessibility': '改进的焦点指示器和键盘导航',
    'update_ambient_crossfade': '环境音轨之间平滑淡入淡出过渡',
    'update_settings_audio': '设置页面中的集中音频控制',
    'update_arabic_complete': '完整的阿拉伯语翻译覆盖',
    'update_duplicate_icon_fix': '修复标题中重复的音频图标',

    // Game UI
    'game_watching': '观看中...',
    'game_repeat': '重复',
    'game_echo_fades': '回声消逝...',
    'game_escaped': '你逃脱了。',
    'game_found_exit': '你找到了出口。',
    'game_looping': '你在循环。',
    'game_deeper': '更深...',
    'game_doors_shift': '门在移动。',
    'maze_left': '左',
    'maze_right': '右',
    'maze_forward': '前',
    'maze_back': '后',
    'maze_past': '过去',
    'maze_future': '未来',
    'maze_here': '这里',
    'maze_there': '那里',
    'maze_truth': '真相',
    'maze_lies': '谎言',
    'maze_light': '光明',
    'maze_dark': '黑暗',
    'maze_memory': '记忆',
    'maze_dream': '梦',
    'maze_silence': '沉默',
    'maze_noise': '噪音',
    'random_room': '随机房间',
    'ai_reflection': 'AI反思',
    'support': '支持',
    'choose_direction': '选择一个方向。有些门会回应。',
    'rooms_explored': '已探索房间',
    'your_journey': '你的旅程',
    'complete_rooms_fragments': '完成房间以收集碎片。',
    'no_rooms_completed': '尚未完成任何房间。',
    'constellation_map': '星座图',
    'your_stats': '你的统计',
    
    // Stats
    'total_time': '总时间',
    'rooms_visited': '访问的房间',
    'total_visits': '总访问次数',
    'most_visited': '最常访问',
    'longest_session': '最长会话',
    'fragments_collected': '收集的碎片',
    'completed_rooms': '完成的房间',
    'privacy_note': '所有数据都存储在您的设备本地',
    
    // Daily Challenges
    'daily_challenges': '每日挑战',
    'challenges_completed': '已完成',
    'challenges_total': '总计',
    'resets_midnight': '午夜重置',
    
    // Weekly Summary
    'weekly_summary': '周总结',
    'week': '周',
    'this_week': '本周',
    'rooms': '房间',
    'days': '天',
    'vs_last_week': '对比上周',
    'refresh_insight': '刷新见解',
    
    // Game Room Titles
    'room_memory-puzzle': '记忆拼图',
    'room_shadow-chase': '追影',
    'room_timing-room': '时机房间',
    'room_pattern-whisper': '图案低语',
    'room_silence-puzzle': '沉默拼图',
    'room_decay-game': '衰变游戏',
    'room_observation-test': '观察测试',
    'room_balance-room': '平衡房间',
    'room_word-drift': '漂流的文字',
    'room_color-echo': '色彩回声',
    'room_mirror-tap': '镜子敲击',
    'room_maze-of-doors': '门的迷宫',
    'room_pulse-match': '脉搏匹配',
    'room_light-path': '光之路径',
    'room_sound-match': '声音匹配',
    
    // Game Room Subtitles
    'sub_memory-puzzle': '匹配微妙变化的图案',
    'sub_shadow-chase': '捕捉不可预测的移动',
    'sub_timing-room': '在精确的时刻点击',
    'sub_pattern-whisper': '重复渐逝的序列',
    'sub_silence-puzzle': '无指示解谜',
    'sub_decay-game': '触摸保存褪去的事物',
    'sub_observation-test': '发现细微差异',
    'sub_balance-room': '保持浮动物体对齐',
    'sub_word-drift': '在字母落下前抓住它们',
    'sub_color-echo': '重复微妙的色调序列',
    'sub_mirror-tap': '每次敲击在别处回响',
    'sub_maze-of-doors': '每次选择后门会重新排列',
    'sub_pulse-match': '随房间的节奏移动',
    'sub_light-path': '引导光穿过变化的黑暗',
    'sub_sound-match': '敲击形状匹配柔和音调',
    
    // Experimental Room Titles
    'room_mirror-room': '镜子房间',
    'room_echo-room': '回声房间',
    'room_pulse-room': '脉搏房间',
    'room_vanishing-room': '消失的房间',
    'room_letter-room': '信件房间',
    'room_empty-room': '空房间',
    'room_reverse-room': '反转房间',
    'room_whisper-room': '低语房间',
    'room_shadow-room': '影子房间',
    'room_labyrinth-room': '迷宫房间',
    'room_color-room': '色彩房间',
    'room_surrender-room': '投降房间',
    'room_noise-room': '噪音房间',
    'room_observer-room': '观察者房间',
    
    // Experimental Room Subtitles
    'sub_mirror-room': '略微扭曲的倒影',
    'sub_echo-room': '点击触发延迟回声',
    'sub_pulse-room': '环境在呼吸',
    'sub_vanishing-room': '互动时元素渐隐',
    'sub_letter-room': '文字像失败的记忆一样混乱',
    'sub_empty-room': '空白直到你发现秘密',
    'sub_reverse-room': '动作表现相反',
    'sub_whisper-room': '几乎听不到的真相',
    'sub_shadow-room': '你的黑暗伙伴',
    'sub_labyrinth-room': '找到你的路',
    'sub_color-room': '色彩冥想',
    'sub_surrender-room': '放下控制',
    'sub_noise-room': '静态思想',
    'sub_observer-room': '被注视着',
    
    // Daily Joke Room
    'joke_number': '笑话 #{number}',
    'joke_today': '今日笑话',
    'joke_reveal': '揭晓笑点',
    'joke_come_back': '明天再来看新笑话。',
    'joke_quote': '笑声是两个人之间最短的距离。',
    
    // Settings Page
    'settings': '设置',
    'settings_subtitle': '自定义你的旅程',
    'language': '语言',
    'ambient_audio': '环境音效',
    'ambient_audio_desc': '房间背景音',
    'reduced_motion': '减少动效',
    'reduced_motion_desc': '减少动画和过渡',
    'data_management': '数据管理',
    'export_journey': '导出旅程数据',
    'clear_all_data': '清除所有数据',
    'audio_enabled': '音效已启用',
    'audio_disabled': '音效已禁用',
    'data_exported': '数据已导出',
    'clear_data_confirm': '确定吗？这将清除你的所有进度。',
    'data_cleared': '数据已清除',
    // Theme settings
    'theme': '主题',
    'theme_dark': '黑暗',
    'theme_dark_desc': '纯粹的黑暗',
    'theme_bone': '骨白',
    'theme_bone_desc': '苍白的死亡',
    'theme_ash': '灰烬',
    'theme_ash_desc': '火后余烬',
    'theme_fog': '迷雾',
    'theme_fog_desc': '虚幻的不确定',
    'theme_twilight': '暮光',
    'theme_twilight_desc': '昼夜之间',
    'theme_eclipse': '日蚀',
    'theme_eclipse_desc': '光被黑暗吞噬',
    'theme_dawn': '黎明',
    'theme_dawn_desc': '觉醒的第一道光',
    'theme_changed': '主题已更新',
    'font_size': '字体大小',
    'font_small': '小',
    'font_medium': '中',
    'font_large': '大',
    'font_size_changed': '字体大小已更新',
    'high_contrast': '高对比度',
    'high_contrast_desc': '增加文字和边框对比度',
    'high_contrast_enabled': '高对比度已启用',
    'high_contrast_disabled': '高对比度已禁用',
    
    // Ambient Music Control
    'ambient_music': '环境音乐',
    'ambient_music_desc': '控制背景音景',
    'volume': '音量',
    'ambient_track': '环境音轨',
    'playing': '播放中',
    'play': '播放',
    'ambient_default': '平静',
    'ambient_night': '夜晚',
    'ambient_scary': '诡异',
    'ambient_truth': '真相',
    'ambient_identity': '反思',
    'ambient_mortality': '时间',
    'ambient_nothing': '虚空',
    'ambient_meta': '数字',
    'ambient_daily': '温暖',
    'ambient_seasonal': '神奇',
    'ambient_premium': '深沉',
    
    'shared_room': '共享房间',
    'imagine_room': '想象',
    'imagine_title': '想象你自己',
    'imagine_subtitle': '描述你如何看待自己。让房间展示从你的话语中浮现的形象。',
    'imagine_placeholder': '我把自己看作一个...\n\n描述你的本质，你的性格，你内心的感受。',
    'imagine_hint': '诚实一点。图像反映你的话语。',
    'imagine_generate': '看见自己',
    'imagine_generating': '创建中...',
    'imagine_try_again': '重新描述',
    'imagine_quote': '我们无法清晰地看到自己。我们只看到我们相信的。',
    'room_imagine-yourself': '想象你自己',
    'sub_imagine-yourself': '描述你自己。看看会浮现什么。',
    'theme_void': '虚空',
    'theme_void_desc': '只剩下阴影',
    
    // Easter Egg Discoveries
    'discovery_label': '发现',
    'discovery_konami': '你知道古老的方法。',
    'discovery_secret_sequence': '你拼写出了真相。',
    'discovery_long_press': '耐心揭示秘密。',
    'discovery_triple_click': '坚持得到回报。',
    'discovery_silence': '寂静在说话。',
    
    // Theme-Exclusive Rooms
    'ash_exclusive': '灰烬主题专属',
    'twilight_exclusive': '暮光主题专属',
    'void_exclusive': '虚空主题专属',
    'theme_required': '需要{theme}主题',
    'ash_embers_title': '从灰烬中',
    'ash_embers_line1': '一切终将燃烧。',
    'ash_embers_line2': '重要的是之后升起的东西。',
    'ash_embers_line3': '触摸屏幕。创造余烬。',
    'ash_embers_quote': '从灰烬中，我们记得曾经燃烧的东西。',
    'room_ash-embers': '灰烬与余烬',
    'sub_ash-embers': '仅在灰烬主题中可见',
    'twilight_stars_title': '在昼夜之间',
    'twilight_stars_line1': '星星记得每一个仰望过的人。',
    'twilight_stars_line2': '连接它们。创造你自己的星座。',
    'twilight_stars_quote': '在暮光中，我们看到白昼隐藏的东西。',
    'room_twilight-stars': '暮光之星',
    'sub_twilight-stars': '仅在暮光主题中可见',
    'void_abyss_title': '深渊',
    'void_abyss_line1': '凝视虚空。虚空也在凝视你。',
    'void_word_1': '虚无',
    'void_word_2': '空虚',
    'void_word_3': '寂静',
    'void_word_4': '消逝',
    'void_word_5': '孤独',
    'void_word_6': '终结',
    'void_revealed': '已揭示',
    'void_abyss_quote': '在绝对的黑暗中，我们终于看见自己。',
    'room_void-abyss': '深渊',
    'sub_void-abyss': '仅在虚空主题中可见',
    
    // Theme Exclusive Section
    'theme_exclusive_rooms': '主题专属房间',
    'theme_exclusive_hint': '在设置中更改主题以解锁这些房间。',
    'requires_theme': '需要',
    'unlocked': '已解锁',
    'switch_to': '切换到',
    
    // Advanced Easter Eggs
    'corner_sequence': '你描绘了边界。',
    'morse_sos': '收到S.O.S.。',
    'reverse_scroll': '返回... 但更快。',
    'midnight_visitor': '午夜时分欢迎你。',
    'shake': '你摇动了什么。',
    
    // Experience Mode
    'experience_mode': '体验模式',
    'classic_mode': '经典',
    'classic_mode_desc': '熟悉的现实',
    'alternate_mode': '▒ 异界 ▒',
    'alternate_mode_desc': '[ 你不该在这里 ]',
    'switch_to_alternate': '░▓█ 下沉 █▓░',
    'switch_to_classic': '← 返回表面',
    'enter_parallel_reality': '[ 门已打开 ]',
    'return_to_familiar': '返回熟悉',
    'something_shifted': '[ 有什么改变了 ]',
    'reality_feels_different': '[ 现实感觉不同了 ]',
    'alternate_tagline': '同样的房间。不同的真相。',
    'alternate_enter': '下沉',
    'alternate_footer': '[ 事物并非表象 ]',
    'alternate_rooms': '░▓ 平行现0实 ▓░',
    'alternate_rooms_hint': '[ 这些房间只存在于平0行 ]',
    
    // Alternate Mode Exclusive Rooms (Chinese)
    'room_the-other-side': '另一边',
    'room_the-unraveling': '瓦解',
    'room_the-echo': '回声',
    'sub_the-other-side': '在黑暗中缓慢移动',
    'sub_the-unraveling': '每一个字都让现实崩塌',
    'sub_the-echo': '你说的话永远不会离开',
    
    // Meta Rooms (Chinese)
    'room_the-refusal': '拒绝',
    'sub_the-refusal': '有时网站会拒绝你',
    'room_parallel-you': '平行的你',
    'sub_parallel-you': '你的另一个版本',
    'room_your-shadow': '你的影子',
    'sub_your-shadow': '跟随你的东西',
    
    // AI Rooms (Chinese)
    'room_ai-reflection-room': 'AI反思室',
    'sub_ai-reflection-room': '让AI与你一起反思',
    'sub_dream-visualization': '描述你的梦。看它成形。',
    'sub_future-self': '来自未来的信息',
    
    // Meta Room Content - The Refusal (Chinese)
    'refusal_line1': '这个房间拒绝你。',
    'refusal_line2': '不是所有东西都在你想要时打开。',
    'refusal_line3': '有时答案就是：不。',
    'refusal_line4': '试试按钮。没有用的。',
    'refusal_button': '进入房间',
    
    // Meta Room Content - Parallel You (Chinese)
    'parallel_choice_1': '你选择了速度。',
    'parallel_alternate_1': '另一个你选择了耐心。',
    'parallel_reflection_1': '他们还在等待。',
    'parallel_choice_2': '你提前离开了。',
    'parallel_alternate_2': '另一个你留到了最后。',
    'parallel_reflection_2': '他们看到了你错过的。',
    'parallel_choice_3': '你避开了难题。',
    'parallel_alternate_3': '另一个你回答了。',
    'parallel_reflection_3': '他们现在承载着那个真相。',
    'parallel_choice_4': '你匆匆而过。',
    'parallel_alternate_4': '另一个你品味每一刻。',
    'parallel_reflection_4': '他们记得更多。',
    'parallel_choice_5': '你继续滑动。',
    'parallel_alternate_5': '另一个你停了下来。',
    'parallel_reflection_5': '他们发现了什么。',
    'parallel_choice_6': '你选择了舒适。',
    'parallel_alternate_6': '另一个你选择了不适。',
    'parallel_reflection_6': '他们从中成长。',
    'parallel_quote': '每一刻都分裂成无限条路。你只走一条。',
    
    // Meta Room Content - Your Shadow (Chinese)
    'shadow_generated': '根据你的行为生成',
    'shadow_intro': '你的影子...',
    'shadow_follows': '这个影子跟随你穿过每个房间。',
    'shadow_quote': '我们不是我们所想的。我们是我们所做的。',
    'shadow_trait_completion': '寻求完成',
    'shadow_trait_surface': '偏好表面',
    'shadow_trait_lingers': '在空间中停留',
    'shadow_trait_moves_quickly': '行动迅速',
    'shadow_trait_avoids_depth': '回避深度',
    'shadow_trait_stillness': '拥抱静止',
    'shadow_trait_chases': '追逐无名之物',
    'shadow_trait_darkness': '被黑暗吸引',
    'shadow_trait_emptiness': '对空虚感到舒适',
    'shadow_trait_undefined': '未定义',
    'shadow_trait_forming': '仍在形成',
    
    // Translation Navigator (Chinese)
    'translation_navigator': '翻译导航',
    'translation_coverage': '各语言房间翻译覆盖率',
    'keys': '键',
    'room_translations': '房间翻译',
    'show_all': '显示全部',
    'show_missing': '显示缺失',
    'all_translated': '所有房间已翻译！',
    'missing_translations': '缺失翻译',
    
    // Additional Room Titles (Chinese)
    'room_how-many-times-left': '还剩多少次',
    'room_life-in-weeks': '你的人生周历',
    'room_delay-button': '延迟按钮',
    'room_one-honest-sentence': '一句真话',
    'room_this-moment': '此刻不会再来',
    'room_reality-receipt': '现实收据',
    'room_nothing-happens': '这里什么都不会发生',
    'room_default-choice': '你的默认选择',
    'room_thought-loop': '思维循环',
    'room_expect-vs-happens': '期望与现实',
    'room_question-never-answer': '你从不回答的问题',
    'room_not-special': '你并不特别（没关系）',
    'room_shortcut-always-take': '你总是走的捷径',
    'room_blurred-self': '模糊的自我',
    'room_image-zooming': '你一直放大的图片',
    'room_comfort-color': '舒适的颜色',
    'room_line-you-crossed': '你跨过的那条线',
    'room_read-aloud': '大声读出来',
    'room_voice-without-meaning': '没有意义的声音',
    'room_sentence-you-avoid': '你回避的句子',
    'room_if-nothing-changes': '如果什么都不改变',
    'room_real-priorities': '你真正的优先级',
    'room_last-conversation': '最后的对话',
    'room_who-without-job': '没有工作的你是谁？',
    'room_comfort-zone-scanner': '舒适区扫描仪',
    'room_say-it-once': '只说一次',
    'room_listen-to-yourself': '倾听自己',
    'room_life-as-shapes': '形状中的人生',
    'room_face-without-features': '没有特征的脸',
    'room_what-keeps-awake': '让你失眠的事',
    'room_you-at-317am': '凌晨3:17的你',
    'room_thought-that-returns': '不断回来的想法',
    'room_room-you-visit-most': '你最常访问的房间',
    'room_room-you-avoid': '你回避的房间',
    'room_left-too-early': '你离开得太早',
    'room_daily-quote': '每日名言',
    'room_daily-joke': '每日笑话',
    'room_theyre-watching': '他们在看着你',
    'room_the-last-visitor': '最后的访客',
    'room_dont-look-away': '不要移开目光',
    'room_the-whisper': '低语',
    'room_you-forgot-something': '你忘了什么',
    'room_the-mirror-lies': '镜子在说谎',
    'room_something-follows': '有东西跟着你',
    'room_the-countdown': '倒计时',
    'room_eyes-in-the-dark': '黑暗中的眼睛',
    'room_the-voice': '那个声音',
    'room_it-knows-your-name': '它知道你的名字',
    'room_the-door': '那扇门',
    'room_boss-room-free': '你真理的总和',
    'room_boss-room-horror': '恐惧之心',
    'room_boss-room-premium': '最深的真相',
    'room_mouse-tracker': '鼠标追踪器',
    'room_scroll-confession': '滚动的忏悔',
    'room_waiting-game': '等待游戏',
    'room_day-in-pixels': '像素化的一天',
    'room_memory-maze': '记忆迷宫',
    'room_decision-shadow': '决定的影子',
    'room_anonymous-mirrors': '匿名镜子',
    'room_collective-room': '集体房间',
    'room_secret-room': '秘密房间',
    'room_glitch-room': '不稳定的房间',
    'room_full-moon-room': '满月房间',
    'room_weekend-room': '周末房间',
    'room_317am-room': '凌晨3:17房间',
    'room_idle-room': '静止房间',
    'room_invite-friend-room': '共享房间',
    'room_halloween-room': '面纱房间',
    'room_winter-solstice-room': '最长的夜',
    'room_new-year-room': '给未来自己的信',
    'room_void-room': '虚空',
    'room_pattern-room': '你的模式',
    'room_time-capsule': '时间胶囊',
    'room_the-revisit': '重访',
    'room_contradiction-room': '矛盾房间',
    'room_export-journey': '导出你的旅程',
    'room_anonymous-echoes': '匿名回声',
    'room_the-waiting-room': '等候室',
    'room_dream-visualization': '梦境可视化',
    'room_future-self': '未来的自己',
    'room_eclipse-shadows': '日食之影',
    'room_dawn-awakening': '黎明觉醒',
    
    // Additional Room Subtitles (Chinese)
    'sub_how-many-times-left': '剩余的次数',
    'sub_life-in-weeks': '4000个方格。仅此而已。',
    'sub_delay-button': '每次都更难',
    'sub_one-honest-sentence': '不能编辑。不能删除。',
    'sub_this-moment': '你在这里。仅此一次。',
    'sub_reality-receipt': '你日子的总结',
    'sub_nothing-happens': '正如所说',
    'sub_eclipse-shadows': '在完全黑暗中说话',
    'sub_dawn-awakening': '迎接第一缕光',
    'sub_default-choice': '安全还是冒险。每次都是。',
    'sub_thought-loop': '看着你的思维重复',
    'sub_expect-vs-happens': '希望与现实的差距',
    'sub_question-never-answer': '大多数人都离开了',
    'sub_not-special': '没有安慰。只有真相。',
    'sub_shortcut-always-take': '模式会显现',
    'sub_blurred-self': '记忆就是这样运作的',
    'sub_image-zooming': '在虚无中寻找答案',
    'sub_comfort-color': '你会待多久？',
    'sub_line-you-crossed': '我们看到你犹豫的地方',
    'sub_read-aloud': '你必须说出的话',
    'sub_voice-without-meaning': '没有文字的声音',
    'sub_sentence-you-avoid': '写完才能离开',
    'sub_if-nothing-changes': '对你未来五年的预测',
    'sub_real-priorities': '基于时间，而非言语',
    'sub_last-conversation': '写下你永远不会发送的话',
    'sub_who-without-job': '重新书写自己',
    'sub_comfort-zone-scanner': '我们看到你的犹豫',
    'sub_say-it-once': '不能重来。不能重做。',
    'sub_listen-to-yourself': '你的声音，慢放',
    'sub_life-as-shapes': '抽象。无标签。你的。',
    'sub_face-without-features': '移除直到什么都不剩',
    'sub_what-keeps-awake': '晚上10点后可用',
    'sub_you-at-317am': '时间特定。个人。',
    'sub_thought-that-returns': '每次访问都会回来',
    'sub_room-you-visit-most': '你的模式揭示',
    'sub_room-you-avoid': '你跳过的说明了什么',
    'sub_left-too-early': '当你快速离开时触发',
    'sub_daily-quote': '每天更新的智慧',
    'sub_daily-joke': '每天更新的黑色幽默',
    'sub_theyre-watching': '你并不孤单',
    'sub_the-last-visitor': '有人在你之前来过',
    'sub_dont-look-away': '它知道你什么时候离开',
    'sub_the-whisper': '仔细听',
    'sub_you-forgot-something': '是什么？',
    'sub_the-mirror-lies': '打碎它看真相',
    'sub_something-follows': '继续跑',
    'sub_the-countdown': '零时有事发生',
    'sub_eyes-in-the-dark': '它们在增加',
    'sub_the-voice': '有人被困在这里',
    'sub_it-knows-your-name': '你不该来的',
    'sub_the-door': '有东西在敲门',
    'sub_boss-room-free': '最终免费房间',
    'sub_boss-room-horror': '面对最终的黑暗',
    'sub_boss-room-premium': '最终深层房间',
    'sub_mouse-tracker': '我们看到你如何移动',
    'sub_scroll-confession': '你滚动得有多快？',
    'sub_waiting-game': '你能什么都不做吗？',
    'sub_day-in-pixels': '时间变成形状',
    'sub_memory-maze': '图案在变化',
    'sub_decision-shadow': '你的选择揭示模式',
    'sub_anonymous-mirrors': '来自陌生人的想法',
    'sub_collective-room': '每个人都在这里。没人留下。',
    'sub_secret-room': '你找到了',
    'sub_glitch-room': '有什么不对劲',
    'sub_full-moon-room': '只在月亮高峰时开放',
    'sub_weekend-room': '休息是一个目的地',
    'sub_317am-room': '时间特定。个人。',
    'sub_idle-room': '通过耐心发现',
    'sub_invite-friend-room': '有些旅程需要见证',
    'sub_halloween-room': '10月31日 - 面纱很薄',
    'sub_winter-solstice-room': '12月21日 - 释放与保留',
    'sub_new-year-room': '12月31日 - 写给未来的你',
    'sub_void-room': '代码中的伤口',
    'sub_pattern-room': '网站在你身上看到的',
    'sub_time-capsule': '写给未来的自己',
    'sub_the-revisit': '看看你的答案如何改变',
    'sub_contradiction-room': '你的真理不一致',
    'sub_export-journey': '下载你的路径',
    'sub_anonymous-echoes': '来自陌生人的碎片',
    'sub_the-waiting-room': '用存在解锁',
    
    // New Phase 1-3 Rooms (Chinese)
    'room_letters-never-sent': '从未寄出的信',
    'sub_letters-never-sent': '写给永远不会读到的人',
    'room_breath-sync': '呼吸同步',
    'sub_breath-sync': '与房间一起呼吸',
    'room_entropy-garden': '熵变花园',
    'sub_entropy-garden': '生长与衰败交织',
    'room_memory-palace': '记忆宫殿',
    'sub_memory-palace': '建造你的心灵圣所',
    'room_morning-ritual': '晨间仪式',
    'sub_morning-ritual': '带着意图开始新的一天',
    'room_evening-ritual': '夜间仪式',
    'sub_evening-ritual': '以反思结束一天',
    'room_weekly-ritual': '每周仪式',
    'sub_weekly-ritual': '周日是回顾的日子',
    
    // Letters Never Sent Room (Chinese)
    'letters_title': '从未寄出的信',
    'letters_subtitle': '写给永远不会读到的人。AI会像他们可能的那样回复。',
    'letters_select_recipient': '你在写给谁？',
    'letters_past_self': '过去的自己',
    'letters_future_self': '未来的自己',
    'letters_someone_lost': '失去的人',
    'letters_someone_wronged': '我伤害过的人',
    'letters_placeholder': '亲爱的...',
    'letters_send': '发送到虚空',
    'letters_sending': '发送中...',
    'letters_response': '一个回复浮现...',
    'letters_write_another': '再写一封信',
    'letters_quote': '有些话注定要写下但永不寄出。',
    
    // Breath Sync Room (Chinese)
    'breath_title': '呼吸同步',
    'breath_subtitle': '让你的呼吸与房间的节奏同步。',
    'breath_inhale': '吸气',
    'breath_hold': '屏息',
    'breath_exhale': '呼气',
    'breath_cycles': '完成的循环',
    'breath_quote': '呼吸是身体与心灵之间的桥梁。',
    
    // Entropy Garden Room (Chinese)
    'entropy_title': '熵变花园',
    'entropy_subtitle': '你的植物随每次访问生长，但当你离开时会衰败。',
    'entropy_plant': '种下种子',
    'entropy_water': '浇水',
    'entropy_days_alive': '存活天数',
    'entropy_health': '健康',
    'entropy_empty': '你的花园是空的。种点什么吧。',
    'entropy_quote': '万物生长。万物凋零。这就是道。',
    
    // Memory Palace Room (Chinese)
    'memory_palace_title': '记忆宫殿',
    'memory_palace_subtitle': '建造一个心灵圣所。在你创建的房间里放置物品。',
    'memory_palace_add_room': '添加房间',
    'memory_palace_add_object': '添加物品',
    'memory_palace_room_name': '房间名称...',
    'memory_palace_object_name': '物品名称...',
    'memory_palace_empty': '你的宫殿是空的。创建你的第一个房间。',
    'memory_palace_quote': '我们一间房间一间房间地构建我们的心智。',
    
    // Ritual Rooms (Chinese)
    'ritual_locked': '这个仪式尚不可用',
    'morning_ritual_title': '晨间仪式',
    'morning_ritual_intro': '为新的一天设定意图的时刻。',
    'morning_ritual_available': '可用时间：早6点-9点',
    'evening_ritual_title': '夜间仪式',
    'evening_ritual_intro': '反思一天的时刻。',
    'evening_ritual_available': '可用时间：晚8点-11点',
    'weekly_ritual_title': '每周仪式',
    'weekly_ritual_intro': '反思过去一周，为下周设定意图的时刻。',
    'weekly_locked': '每周仪式等待着你',
    'weekly_time': '周日再来',
    'today_is': '今天是',
    'your_week': '你的一周',
    'days_active': '活跃天数',
    'weekly_summary_note': '现在，让我们更深入地反思。',
    'weekly_highlight': '你这周的亮点是什么？',
    'weekly_challenge': '这周什么最挑战你？',
    'weekly_growth': '这周你如何成长了？',
    'weekly_intention': '你为下周设定什么意图？',
    'weekly_placeholder': '慢慢来...',
    'reflect': '反思',
    'reflecting': '反思中...',
    'complete': '完成',
    'weekly_quote': '一周结束。另一周开始。你带走重要的东西。',
    
    // Personal Timeline (Chinese)
    'personal_timeline': '个人时间线',
    'timeline_subtitle': '你穿越房间的旅程',
    'timeline_empty': '你的时间线是空的。开始探索吧。',
    'timeline_today': '今天',
    'timeline_yesterday': '昨天',
    'timeline_this_week': '本周',
    'timeline_earlier': '更早',
    
    // Weather System (Chinese)
    'weather_clear': '晴朗',
    'weather_rain': '雨',
    'weather_fog': '雾',
    'weather_snow': '雪',
    
    // Notifications (Chinese)
    'notifications': '通知',
    'notifications_desc': '获取时间锁定内容的通知',
    'notify_time_locked': '时间锁定房间',
    'notify_full_moon': '满月之夜',
    'notify_daily_reset': '每日挑战重置',
    'notify_time_capsule': '时间胶囊解锁',
    'notifications_enabled': '通知已启用',
    'notifications_disabled': '通知已禁用',
    
    // Community Submissions (Chinese)
    'community_submissions': '社区投稿',
    'community_subtitle': '提交你的房间创意',
    'community_room_title': '房间标题',
    'community_room_description': '房间描述',
    'community_room_category': '类别',
    'community_submit': '提交创意',
    'community_submitted': '感谢你的投稿！',
    'community_your_submissions': '你的投稿',
    'community_empty': '还没有投稿。成为第一个！',
    
    // Proactive Guide (Chinese)
    'guide_hesitation': '在思考？没关系。想要一个建议吗？',
    'guide_long_absence': '欢迎回来。已经有一段时间了。',
    'guide_milestone': '你已经走了很远。想要反思你的旅程吗？',
    'guide_dismiss': '忽略',
    'guide_accept': '给我看看',
    
    // Voice Mode (Chinese)
    'voice_mode': '语音模式',
    'voice_mode_desc': '让向导对你说话',
    'voice_mode_enabled': '语音模式已启用',
    'voice_mode_disabled': '语音模式已禁用',
    
    // Offline Support (Chinese)
    'offline_available': '离线可用',
    'offline_cached': '此房间已缓存供离线查看',
    
    // Journey Export (Chinese)
    'journey_export': '导出旅程',
    'journey_import': '导入旅程',
    'journey_export_json': '导出为JSON',
    'journey_import_json': '从JSON导入',
    'journey_exported': '旅程导出成功',
    'journey_imported': '旅程导入成功',
    
    // Phase 4 - Seasonal Rooms (Chinese)
    'room_spring-equinox-room': '春分',
    'sub_spring-equinox-room': '释放与更新',
    'room_summer-solstice-room': '夏至',
    'sub_summer-solstice-room': '最长的一天',
    'room_autumn-equinox-room': '秋分',
    'sub_autumn-equinox-room': '收获与接受',
    'room_valentine-room': '自爱房间',
    'sub_valentine-room': '你值得拥有的爱',
    
    // Spring Equinox Room (Chinese)
    'spring_date': '3月20日',
    'spring_title': '春分',
    'spring_message_1': '昼夜等长。',
    'spring_message_2': '完美平衡的时刻。',
    'spring_message_3': '你会释放什么来为成长腾出空间？',
    'spring_letting_go': '我要放下：',
    'spring_letting_go_placeholder': '那些不再服务于你的...',
    'spring_planting': '我要播种：',
    'spring_planting_placeholder': '你希望成长的...',
    'spring_bloom': '让它绽放',
    'spring_released': '已释放：',
    'spring_planted': '已播种：',
    'spring_closing': '从这一刻起，新生命开始了。',
    'spring_quote': '大自然从不匆忙，但一切都会完成。',
    
    // Summer Solstice Room (Chinese)
    'summer_date': '6月21日',
    'summer_title': '最长的一天',
    'summer_message_1': '这是一年中最长的一天。',
    'summer_message_2': '光芒达到巅峰。',
    'summer_message_3': '如果有更多时间，你会做什么？',
    'summer_message_4': '描述你最充实、最有活力的一天。',
    'summer_placeholder': '在我最充实的一天，我会...',
    'summer_submit': '拥抱光明',
    'summer_closing': '太阳听到了你。现在它开始下沉。',
    'summer_quote': '在光芒的巅峰，我们发现我们真正想要照亮的是什么。',
    
    // Autumn Equinox Room (Chinese)
    'autumn_date': '9月22日',
    'autumn_title': '秋分',
    'autumn_message_1': '平衡回归。',
    'autumn_message_2': '收获已经准备好。',
    'autumn_message_3': '你培育了什么？你能接受什么？',
    'autumn_harvesting': '我在收获：',
    'autumn_harvesting_placeholder': '今年你培育的...',
    'autumn_accepting': '我在接受：',
    'autumn_accepting_placeholder': '你无法改变的...',
    'autumn_release': '让树叶落下',
    'autumn_harvested': '已收获：',
    'autumn_accepted': '已接受：',
    'autumn_closing': '万物在其时刻落下。',
    'autumn_quote': '秋天向我们展示了放手是多么美丽。',
    
    // Valentine's Day Room (Chinese)
    'valentine_date': '2月14日',
    'valentine_title': '你值得拥有的爱',
    'valentine_message_1': '今天不是关于别人。',
    'valentine_message_2': '而是关于那个一直陪伴你的人。',
    'valentine_message_3': '那个听到每一个想法的人。',
    'valentine_message_4': '给自己写一封情书。',
    'valentine_prompt': '给自己的一封信：',
    'valentine_placeholder': '亲爱的我，我想让你知道...',
    'valentine_submit': '带着爱发送',
    'valentine_closing': '你值得拥有你给予他人的爱。',
    'valentine_quote': '你自己值得你的爱和关怀。',
    
    // Update changelog
    'update_seasonal_rooms': '四个新的季节房间：春分、夏至、秋分和情人节',
    'update_fragment_trading': '碎片交易系统 - 牺牲碎片解锁时间锁定房间、预览恐怖房间或接收AI消息',
    'update_new_themes': '两个新主题：日蚀（纯黑暗）和黎明（暖色渐变，仅限早上5-7点）',
     'update_regret_machine': '遗憾机器 - AI驱动的替代时间线叙事',
     'update_memory_reconstruction': '记忆重建 - AI填充褪色记忆的感官细节',
     
     // Phase 7 - Regret Machine Room
     'regret_machine_title': '遗憾机器',
     'regret_machine_subtitle': '描述一个你希望做出不同选择的决定。机器会向你展示可能发生的事情。',
     'regret_placeholder': '我后悔...\n\n描述一个你做出的选择，你希望它有所不同。你会怎么做？',
     'regret_hint': '这不是关于责备。这是关于理解。',
     'regret_explore': '探索时间线',
     'regret_generating': '重建替代现实...',
     'regret_alternate_timeline': '替代时间线',
     'regret_explore_another': '探索另一个',
     'regret_error_short': '请更详细地描述你的遗憾',
     'room_regret-machine': '遗憾机器',
     
     // Phase 7 - Memory Reconstruction Room
     'memory_reconstruction_title': '记忆重建',
     'memory_reconstruction_subtitle': '描述一段褪色的记忆。让我们帮助你找回那种感觉。',
     'memory_placeholder': '我记得...\n\n描述一段遥远或支离破碎的记忆。还剩下什么碎片？什么感觉还在？',
     'memory_timeframe_placeholder': '这是什么时候？（例如："童年"、"十年前"）',
     'memory_hint': '记忆不是录像。它是一种重建。',
     'memory_reconstruct': '重建记忆',
     'memory_reconstructing': '拼凑碎片...',
     'memory_reconstructed': '重建的记忆',
     'memory_explore_another': '探索另一段记忆',
     'memory_error_short': '请更详细地描述你的记忆',
     'room_memory-reconstruction': '记忆重建',
    'update_eclipse_room': '日蚀之影 - 在完全黑暗中说话，在闪光中揭示',
    'update_dawn_room': '黎明觉醒 - 用呼吸练习和意念迎接第一道光',
    
    // New themes
    'eclipse_theme_required': '此房间需要日蚀主题。完全的黑暗在等待。',
    'dawn_theme_required': '此房间需要在清晨时段使用黎明主题。',
    'dawn_time_hint': '仅限早上5-7点',
    'change_theme': '在设置中更改主题',
    
    // Eclipse Shadows Room
    'eclipse_shadows_title': '日蚀之影',
    'eclipse_shadows_intro': '在完全的黑暗中，秘密感觉更安全。诚实回答——只有短暂的闪光会照亮你的话语。',
    'enter_darkness': '进入黑暗',
    'shadow_question_1': '你对自己隐藏了什么？',
    'shadow_question_2': '如果没人能听到，你会说什么真话？',
    'shadow_question_3': '凌晨3点什么困扰着你？',
    'shadow_question_4': '如果恐惧不存在，你会做什么？',
    'shadow_question_5': '你还没准备好面对什么？',
    'speak_into_dark': '在黑暗中说话...',
    'release': '释放',
    'eclipse_reveal_prompt': '点击每个阴影揭示你的坦白。',
    'hidden_in_shadow': '▓▓▓▓▓▓▓▓▓▓▓▓',
    'eclipse_reveal_hint': '有些真相在黑暗中更清晰。',
    'eclipse_shadows_quote': '在完全的黑暗中，我们终于看见了自己。',
    
    // Dawn Awakening Room
    'dawn_awakening_title': '黎明觉醒',
    'dawn_awakening_intro': '第一道光带来可能。在世界醒来之前，设定你的意念。',
    'greet_the_light': '迎接光芒',
    'breathe_in_light': '吸入光芒',
    'hold_warmth': '保持温暖',
    'release_night': '释放夜晚',
    'dawn_prompt_1': '今天你想带着什么？',
    'dawn_prompt_2': '你会放下什么？',
    'dawn_prompt_3': '你会给自己什么小小的善意？',
    'set_your_intention': '设定你的意念...',
    'plant_seed': '种下这颗种子',
    'dawn_awakening_quote': '每一个黎明都是重新开始的邀请。',
    
    // Fragment Trading
    'fragment_trading': '碎片交易',
    'sacrifice_to_unlock': '牺牲碎片解锁秘密',
    'your_fragments': '你的碎片',
    'fragments_any': '任意碎片',
    'fragments_free': '清晰碎片',
    'fragments_premium': '深度碎片',
    'fragments_horror': '阴影碎片',
    'trade_successful': '交易成功！',
    'trade_failed': '交易失败',
    'not_enough_fragments': '碎片不足',
    'continue_trading': '继续交易',
    'trading_fragments': '正在交易碎片...',
    'fragment_trade_warning': '牺牲的碎片无法恢复',
    'unlock-time-locked': '解锁时间锁定房间',
    'unlock-time-locked_desc': '无需等待即可进入时间锁定房间',
    'preview-horror': '恐怖房间预览',
    'preview-horror_desc': '进入前先窥探恐怖房间',
    'ai-reflection-trade': 'AI反思',
    'ai-reflection-trade_desc': '接收来自AI的个性化消息',
    'unlock-premium-preview': '高级房间试用',
    'unlock-premium-preview_desc': '访问高级房间一次',
    'fragment_trading_room': '碎片祭坛',
    'fragment_trading_intro': '在这里，你可以牺牲收集的碎片来解锁这些房间中隐藏的秘密。',
    'your_collection': '你的收藏',
    'fragment_clarity': '清晰',
    'fragment_depth': '深度',
    'fragment_shadow': '阴影',
    'total_fragments': '总计',
    'open_altar': '接近祭坛',
    'recent_trades': '最近的供奉',
    'fragment_trade_ending': '给予的无法收回。但获得的...会留下。',
    'room_fragment-trade': '碎片祭坛',
    'sub_fragment-trade': '牺牲以解锁秘密',
    'update_anniversary_system': '周年纪念与里程碑系统 - 追踪你的旅程',
    'update_archive_room': '档案馆 - 25个房间解锁完整旅程历史',
    'update_mirror_room': '万我之镜 - 50个房间解锁AI行为画像',
    'update_ending_room': '结局 - 75个房间解锁个性化告别',
    'room_the-archive': '档案馆',
    'room_mirror-of-all-selves': '万我之镜',
    'room_the-ending': '结局',
    // Phase 9
    'update_fragment_rarities': '碎片稀有度 - 普通、稀有和传奇可收集碎片',
    'update_fragment_gallery': '碎片画廊 - 你的收藏展示柜',
    'update_seasonal_fragments': '季节和秘密碎片 - 限时收藏品',
    'fragment_collection': '收藏',
    'fragment_collection_title': '碎片收藏',
    'fragment_collection_subtitle': '每个房间都会留下什么。收集它们全部。',
    'collection_milestones': '里程碑',
    'collection_secrets': '秘密',
    'collection_footer': '某些碎片只在特定条件下出现。',
    'rarity_common': '普通',
    'rarity_rare': '稀有',
    'rarity_legendary': '传奇',
    // Phase 10-11
    'accessibility': '无障碍',
    'accessibility_desc': '自定义可读性和动效偏好',
    'font_family': '字体系列',
    'font_default': '等宽字体',
    'font_dyslexia': '阅读障碍友好',
    'font_sans': '无衬线',
    'font_changed': '字体已更新',
    'line_height': '行高',
    'line_compact': '紧凑',
    'line_normal': '正常',
    'line_relaxed': '宽松',
    'line_spacious': '宽敞',
    'line_height_changed': '行高已更新',
    'reduced_motion_full_desc': '禁用所有动画和粒子效果',
    'larger_cursors': '更大目标',
    'larger_cursors_desc': '增加点击目标大小',
    'update_accessibility_suite': '无障碍套件 - 阅读障碍字体、行高控制',
    'update_dyslexia_font': '设置中的阅读障碍友好字体选项',
    'update_line_height': '可调节行高以提高可读性',
    'update_enhanced_reduced_motion': '增强减少动效 - 完全禁用所有效果',
    'update_shared_moments': '共享时刻 - 与他人的匿名情感签到',
    'update_whisper_wall': '低语墙 - 在黑暗中留下匿名真相',
    'update_collective_memory': '集体记忆 - 我们记忆的共享碎片',
    'whisper_prompt': '在墙上留下一句低语。匿名。永久。',
    'whisper_placeholder': '你默默承受着什么？',
    'whisper_send': '低语',
    'whisper_added': '你的低语加入了墙壁。',
    'whisper_another': '再次低语',
    'shared_moments_title': '此刻，在这一刻...',
    'shared_moments_subtitle': '你真的好吗？',
    'feeling_grateful': '感恩',
    'feeling_lost': '迷失',
    'feeling_hopeful': '充满希望',
    'feeling_heavy': '沉重',
    'feeling_peaceful': '平静',
    'feeling_restless': '不安',
    'room_whisper-wall': '低语墙',
    'room_collective-memory': '集体记忆',
    'room_shared-moments': '共享时刻',
    'sub_regret-machine': '探索可能发生的事',
    'sub_memory-reconstruction': '重建一段褪色的记忆',
    'sub_whisper-wall': '匿名真相在黑暗中漂浮',
    'sub_collective-memory': '我们记忆的共享碎片',
    'sub_shared-moments': '你好吗，说实话？',
    'sub_the-archive': '你完整的旅程历史',
    'sub_mirror-of-all-selves': 'AI揭示你的行为画像',
    'sub_the-ending': '你赢得的告别',

    // v3.21.0 - New Rooms
    'room_mask-you-wear': '你戴的面具',
    'sub_mask-you-wear': '你展示的是哪张脸？',
    'room_recurring-dream': '你的重复之梦',
    'sub_recurring-dream': '那个不断回来的梦',
    'room_promise-you-broke': '你违背的承诺',
    'sub_promise-you-broke': '关于破碎承诺的诚实',
    'room_words-you-swallowed': '你咽下的话',
    'sub_words-you-swallowed': '你从未说出的话',
    'room_age-you-feel': '你感觉的年龄',
    'sub_age-you-feel': '你真正多大了？',
    'room_biggest-lie': '你最大的谎言',
    'sub_biggest-lie': '你说得最多的谎言',
    'room_person-you-pretend-to-be': '你假装的人',
    'sub_person-you-pretend-to-be': '你的表演',
    'room_person-you-pretend': '你假装的人',
    'sub_person-you-pretend': '你的表演',
    'room_what-you-collect': '你收集什么',
    'sub_what-you-collect': '物品揭示身份',
    'room_apology-you-owe': '你欠的道歉',
    'sub_apology-you-owe': '写下你该说的话',
    'room_unfinished-story': '你未完成的故事',
    'sub_unfinished-story': '你从未完成的事',
    'room_version-others-see': '别人看到的你',
    'sub_version-others-see': '别人如何看待你',
    'room_secret-talent': '你的秘密才能',
    'sub_secret-talent': '没人知道你能做什么',
    'room_fear-you-feed': '你喂养的恐惧',
    'sub_fear-you-feed': '哪个恐惧控制着你',
    'room_guilty-pleasure': '你的愧疚快乐',
    'sub_guilty-pleasure': '你隐藏享受的东西',
    'room_rule-you-break': '你总是打破的规则',
    'sub_rule-you-break': '你的叛逆模式',
    'room_childhood-promise': '你童年的承诺',
    'sub_childhood-promise': '你向自己承诺的',
    'room_compliment-you-reject': '你拒绝的赞美',
    'sub_compliment-you-reject': '为什么你不能接受赞美',
    'room_autopilot-mode': '你的自动驾驶模式',
    'sub_autopilot-mode': '你不假思索做的事',
    'room_boundary-you-need': '你需要的界限',
    'sub_boundary-you-need': '你应该保护什么',
    'room_inner-critic': '你内心的批评者',
    'sub_inner-critic': '评判的声音',
    'room_last-sunday': '上个星期天',
    'sub_last-sunday': '你实际做了什么？',
    'room_wasted-hours': '你浪费的时间',
    'sub_wasted-hours': '时间消失在哪里',
    'room_year-that-changed': '改变你的那一年',
    'sub_year-that-changed': '转折点',
    'room_tomorrows-regret': '明天的遗憾',
    'sub_tomorrows-regret': '你会希望做了什么？',
    'room_morning-autopilot': '你的早晨自动驾驶',
    'sub_morning-autopilot': '第一小时的仪式',
    'room_season-you-dread': '你恐惧的季节',
    'sub_season-you-dread': '哪个季节最沉重？',
    'room_ten-years-ago': '十年前的今天',
    'sub_ten-years-ago': '那时你是谁？',
    'room_last-normal-day': '你最后的正常日子',
    'sub_last-normal-day': '在一切改变之前',
    'room_clock-you-ignore': '你忽略的时钟',
    'sub_clock-you-ignore': '你假装不在流逝的时间',
    'room_your-deadline': '你的截止日期',
    'sub_your-deadline': '你在推迟什么？',
    'room_the-pause': '暂停',
    'sub_the-pause': '呼吸一下',
    'room_static-room': '静态',
    'sub_static-room': '白噪音和寂静',
    'room_gap-between-thoughts': '思想之间的间隙',
    'sub_gap-between-thoughts': '找到那个空间',
    'room_sit-with-discomfort': '与不适同坐',
    'sub_sit-with-discomfort': '不要修复任何事',
    'room_boring-room': '无聊的房间',
    'sub_boring-room': '完全没有趣味',
    'room_unplug-room': '断开连接',
    'sub_unplug-room': '想象没有设备',
    'room_unplug': '断开连接',
    'sub_unplug': '想象没有设备',
    'room_last-page': '最后一页',
    'sub_last-page': '故事在这里结束',
    'room_scrolling-pattern': '你的滚动模式',
    'sub_scrolling-pattern': '你如何消费内容',
    'room_tab-you-keep-open': '你一直打开的标签页',
    'sub_tab-you-keep-open': '什么留在你的浏览器中',
    'room_digital-shadow': '你的数字影子',
    'sub_digital-shadow': '你的网络足迹',
    'room_notification-you-ignore': '你忽略的通知',
    'sub_notification-you-ignore': '你无视的东西',
    'room_typing-speed': '你的打字速度',
    'sub_typing-speed': '你表达得有多快？',
    'room_link-you-almost-clicked': '你差点点击的链接',
    'sub_link-you-almost-clicked': '犹豫模式',
    'room_bookmark-graveyard': '你的书签墓地',
    'sub_bookmark-graveyard': '保存了却从未回访',
    'room_search-you-deleted': '你删除的搜索',
    'sub_search-you-deleted': '你搜索并删除的东西',
    'room_gravity-room': '重力房间',
    'sub_gravity-room': '一切缓慢下落',
    'room_ink-blot': '墨迹',
    'sub_ink-blot': '你看到了什么？',
    'room_hallway-of-mirrors': '镜子走廊',
    'sub_hallway-of-mirrors': '无尽的倒影',
    'room_dissolving-text': '溶解的文字',
    'sub_dissolving-text': '文字在阅读时融化',
    'room_the-pendulum': '钟摆',
    'sub_the-pendulum': '催眠般的运动',
    'room_pixel-rain': '像素雨',
    'sub_pixel-rain': '数字降水',
    'room_the-threshold': '门槛',
    'sub_the-threshold': '在这里和那里之间',
    'most_loved_rooms': '最受喜爱的房间',
    'rate_room': '评价这个房间',
    'update_52_new_rooms': '52个新房间，涵盖身份、时间、沉思、元和实验类别',
    'update_room_rating': '房间评分系统 - 点赞/点踩反馈及最受喜爱房间板块',
    'update_bug_fix_reflections': '修复匿名反思的显示和存储问题',
    'update_100_rooms': '走廊中已有超过100个房间',
    // v3.22.0
    'room_the-friend-you-lost': '失去的朋友',
    'sub_the-friend-you-lost': '写给一个渐行渐远的人。',
    'room_conversation-you-replay': '反复回放的对话',
    'sub_conversation-you-replay': '从记忆中重现它。',
    'room_the-thank-you': '感谢',
    'sub_the-thank-you': '从未表达过的感激。',
    'room_people-you-forgot': '你忘记的人',
    'sub_people-you-forgot': '试着回忆被遗忘的名字。',
    'room_the-advice-you-give': '你给的建议',
    'sub_the-advice-you-give': '你给别人但自己不遵循的建议。',
    'room_what-others-dont-know': '别人不知道的',
    'sub_what-others-dont-know': '你隐藏的层面。',
    'room_the-lie-you-believe': '你相信的谎言',
    'sub_the-lie-you-believe': '一个令人安慰的不真实。',
    'room_your-shadow-self': '你的影子自我',
    'sub_your-shadow-self': '你压抑的部分。',
    'room_the-story-you-tell': '你讲述的故事',
    'sub_the-story-you-tell': '你如何叙述自己的人生。',
    'room_your-safe-place': '你的安全之地',
    'sub_your-safe-place': '描述你感到安全的地方。',
    'room_the-risk-you-didnt-take': '你没有冒的险',
    'sub_the-risk-you-didnt-take': '未曾走过的路。',
    'room_your-last-excuse': '你的最后借口',
    'sub_your-last-excuse': '你最常用的借口。',
    'room_the-habit-you-hide': '你隐藏的习惯',
    'sub_the-habit-you-hide': '秘密的日常。',
    'room_what-you-postpone': '你推迟的事',
    'sub_what-you-postpone': '总是"明天"的事情。',
    'room_the-door-you-closed': '你关上的门',
    'sub_the-door-you-closed': '你拒绝的机会。',
    'journal_title': '反思日记',
    'journal_subtitle': '你在各房间的想法',
    'journal_empty': '还没有反思。访问房间并写下你的想法。',
    'journal': '日记',
    'room_chains': '房间链',
    'chain_identity_journey': '身份之旅',
    'chain_time_reflection': '时间反思',
    'chain_truth_path': '真相之路',
    'recently_visited': '最近访问',
    'update_reflection_journal': '反思日记 - 在各房间保存你的想法',
    'update_room_chains': '房间链 - 按顺序完成房间获得额外碎片',
    'update_15_new_rooms': '3个新类别中的15个新互动房间',
    'update_recently_visited': '最近访问区域便于快速返回',
    'ai_companion': '伙伴',
    'companion_create': '创建你的伙伴',
    'companion_setup_desc': '一个与你同行的倒影。',
    'companion_name': '给伙伴起个名字',
    'companion_name_placeholder': '例如：回声、镜像、影子...',
    'companion_lens': '性格镜头',
    'companion_mood': '沟通氛围',
    'companion_fears': '你害怕什么？（可选）',
    'companion_struggles': '你在挣扎什么？（可选）',
    'create_companion': '创建',
    'companion_input': '说些什么...',
    'companion_greeting': '你的伙伴在这里。说些什么。',
    'companion_starter_1': '今天我在想什么？',
    'companion_starter_2': '我感到困住了。',
    'companion_starter_3': '告诉我一些我在逃避的事。',
    'fears_placeholder': '例如：失败、被拒绝、被遗忘...',
    'struggles_placeholder': '例如：过度思考、拖延、孤独...',
    'back_step': '返回',
    'lens_reflective': '反思型',
    'lens_reflective_desc': '深层问题，模式识别',
    'lens_supportive': '支持型',
    'lens_supportive_desc': '温暖、鼓励、认可',
    'lens_challenging': '挑战型',
    'lens_challenging_desc': '直接、发人深省',
    'lens_curious': '好奇型',
    'lens_curious_desc': '意想不到的问题，惊奇',
    'lens_shadow': '阴影型',
    'lens_shadow_desc': '隐藏的一面，诚实的不适',
    'companion_mood_calm': '平静',
    'companion_mood_calm_desc': '沉稳而平和',
    'companion_mood_intense': '激烈',
    'companion_mood_intense_desc': '紧迫而深刻',
    'companion_mood_playful': '俏皮',
    'companion_mood_playful_desc': '机智而轻快',
    'companion_mood_melancholic': '忧郁',
    'companion_mood_melancholic_desc': '美丽的悲伤',
    'update_ai_companion': 'AI伙伴 - 记住你旅程的个人AI',
  },

  fr: {
    // Index
    'title': 'LES CHAMBRES HUMAINES',
    'tagline': 'Une collection de chambres qui reflètent qui vous êtes quand personne ne regarde.',
    'enter': 'Entrer',
    'footer': 'Pas de thérapie. Pas de social. Pas de productivité.',
    
    // Hallway
    'back': '← Retour',
    'hallway': 'Le Couloir',
    'challenges': 'Défis',
    'random': 'Aléatoire',
    'open_rooms': 'Chambres Ouvertes',
    'dark_rooms': 'Les Chambres Sombres',
    'enter_risk': 'entrez à vos risques',
    'premium_rooms': 'Chambres Premium',
    'unlock_to_access': 'Débloquer pour accéder',
    'daily_rooms': 'Chambres Quotidiennes',
    'refreshes_daily': 'se rafraîchit chaque jour',
    'time_locked': 'Chambres Verrouillées par le Temps',
    'seasonal_rooms': 'Chambres Saisonnières',
    'available_now': 'Disponible maintenant',
    'fragments': 'Fragments',
    'boss_unlocked': 'Salle du Boss Débloquée',
    'collect_unlock': 'Collectez pour débloquer la Salle du Boss',
    'game_rooms': 'Salles de Jeux',
    'experimental_rooms': 'Salles Expérimentales',
    'deep_rooms': 'Salles Profondes',
    'meta_rooms': 'Salles Méta',
    'search_rooms': 'Rechercher des salles...',
    'no_results': 'Aucune salle ne correspond',
    'clear_filters': 'Effacer les filtres',
    'explore': 'Explorer',
    'all_categories': 'Toutes',
    
    // About
    'what_is_this': "Qu'est-ce que cet endroit?",
    'not_productivity': "Les Chambres Humaines n'est pas un outil de productivité.",
    'not_social': "Ce n'est pas un réseau social.",
    'not_therapy': "Ce n'est pas une thérapie.",
    'pause_experiences': "C'est une collection de petites expériences conçues pour vous faire marquer une pause.",
    'each_room': 'Chaque chambre prend de 30 secondes à 2 minutes.',
    'make_think': 'Certaines chambres vous feront réfléchir.',
    'make_uncomfortable': 'Certaines vous mettront mal à l\'aise.',
    'make_calm': 'Certaines vous calmeront.',
    'make_aware': 'Toutes vous rendront conscient.',
    'no_notifications': "Il n'y a pas de notifications ici.",
    'no_points': 'Pas de points à collecter.',
    'no_validation': 'Pas de validation sociale.',
    'just_truth': 'Juste vous, une chambre, et la vérité.',
    'enter_rooms': 'Entrer dans les chambres',
    
    // Common
    'loading': 'Chargement...',
    'error': 'Erreur',
    'continue': 'Continuer',
    'submit': 'Soumettre',
    'cancel': 'Annuler',
    'close': 'Fermer',
    'next': 'Suivant',
    'previous': 'Précédent',
    'start': 'Commencer',
    'stop': 'Arrêter',
    'yes': 'Oui',
    'no': 'Non',
    'leave_room': '← Quitter la chambre',
    'return': '← Retour',
    'return_hallway': 'Retourner au Couloir',
    'begin': 'Commencer',
    'watch': 'Regardez...',
    'correct': 'Correct',
    'round': 'Manche',
    'enter_again': 'Entrer à nouveau',
    'steps': 'Pas',
    'depth': 'Profondeur',
    
    // Keyboard Shortcuts
    'keyboard_shortcuts': 'Raccourcis Clavier',
    'shortcut_hallway': 'Aller au Couloir',
    'shortcut_random': 'Chambre Aléatoire',
    'shortcut_back': 'Retour',
    'shortcut_bookmark': 'Basculer Favori',
    'shortcut_reading': 'Mode Lecture',
    'shortcut_search': 'Focaliser Recherche',
    'shortcut_stats': 'Voir Statistiques',
    'shortcut_achievements': 'Voir Réalisations',
    'shortcut_cat_nav': 'Navigation',
    'shortcut_cat_rooms': 'Chambres',
    'shortcut_cat_access': 'Accessibilité',
    'press_to_close': 'Appuyez sur',
    'to_close': 'pour fermer',
    'or': 'ou',
    
    // Bookmarks
    'add_bookmark': 'Ajouter Favori',
    'remove_bookmark': 'Retirer Favori',
    'bookmarks': 'Favoris',
    'bookmarked_rooms': 'Favoris',
    'no_bookmarks': 'Pas encore de favoris',
    
    // Reading Mode
    'reading_mode': 'Mode Lecture',
    'reading_mode_on': 'Mode lecture activé',
    'reading_mode_off': 'Mode lecture désactivé',
    
    // Updates Page
    'updates': 'Mises à jour',
    'updates_title': 'Nouveautés',
    'updates_subtitle': 'Changements et améliorations récents',
    'updates_footer': 'Construction de chambres, une mise à jour à la fois.',
    'new': 'NOUVEAU',
    'subscribe_rss': "S'abonner via RSS",
    'latest': 'DERNIER',
    'whats_new_intro': 'Dernières mises à jour:',
    'view_all_updates': 'Voir toutes les mises à jour',
    'update_updates_page': 'Page de mises à jour ajoutée',
    'update_full_i18n': 'Traduction complète en 6 langues',
    'update_hallway_animations': 'Animations fluides de changement de catégorie',
    'update_daily_challenges': 'Amélioration des défis quotidiens',
    'update_game_rooms': '15 nouvelles salles de jeu',
    'update_experimental_rooms': '14 salles expérimentales',
    'update_random_room': 'Bouton de chambre aléatoire',
    'update_stats_route': 'Route des statistiques corrigée',
    'update_streak_system': 'Système de séries quotidiennes',
    'update_weekly_summary': 'Résumés de progression hebdomadaire',
    'update_rtl_support': 'Support des langues RTL',
    'update_dark_rooms': 'Nouvelle catégorie de chambres sombres',
    'update_boss_rooms': 'Salles de boss avec fragments',
    'update_audio_system': 'Audio ambiant dans les chambres',
    'update_constellation_map': 'Carte constellation du voyage',
    'update_fragment_system': 'Système de fragments de chambres',
    'update_mobile_layout': 'Améliorations du design mobile',
    'update_multi_language': 'Support multilingue (6 langues)',
    'update_premium_system': 'Système de chambres premium',
    'update_room_endings': 'Citations de fin uniques',
    'update_ui_redesign': 'Refonte complète de l\'interface',
    'update_theme_effects': 'Effets visuels par thème (braises, étoiles, glitches)',
    'update_void_theme': 'Nouveau thème Vide - mode obscurité absolue',
    'update_achievements': 'Système de réalisations avec 19 jalons',
    'update_ai_reflection': 'Chambre de réflexion IA',
    'update_settings_redesign': 'Paramètres redessinés avec 4 thèmes',
    'update_theme_exclusive_rooms': 'Chambres exclusives par thème',
    'update_discovery_localization': 'Messages de découverte traduits',
    'update_site_mood_system': 'Système d\'humeur du site',
    'update_voice_imperfection': 'Chambres à voix imparfaite',
    'update_mobile_optimization': 'Optimisation mobile avec cibles de 48px',
    'update_interaction_clues': 'Indices d\'interaction subtils',
    'update_theme_exclusive_hallway': 'Chambres exclusives visibles dans le couloir',
    'update_advanced_easter_eggs': 'Easter eggs avancés : séquence de coins, morse, visiteur de minuit',
    'update_hidden_discoveries': 'Révélations de texte caché',
    'update_dual_mode_system': 'Système double mode Classique et Alternatif',
    'update_alternate_mode': 'Mode alternatif - une réalité parallèle expérimentale',
    'update_hidden_discovery': 'Déclencheurs de découverte cachés : triple tap, motifs de défilement',
    'update_mode_effects': 'Effets visuels spécifiques au mode, glitches et audio ambiant',
    'update_translation_navigator': 'Navigateur de Traduction - tableau de couverture visuel',
    'update_full_localization': 'Couverture de traduction presque complète en 6 langues',
    'update_meta_room_translations': 'Contenu des méta-chambres traduit (Le Refus, Toi Parallèle, Ton Ombre)',
    'update_animated_timeline': 'Timeline animée avec transitions au défilement',
    'update_toast_notifications': 'Notification toast pour les nouvelles mises à jour',
    'update_rss_feed': 'Flux RSS pour les abonnements aux changements',
    'update_bookmarked_filter': 'Onglet Chambres Favoris dans le Couloir',
    'update_keyboard_shortcuts': 'Raccourcis clavier (appuyez sur ? pour afficher)',
    'update_reading_mode': 'Mode lecture pour typographie et concentration améliorées',
    'update_accessibility': 'Indicateurs de focus et navigation clavier améliorés',
    'update_ambient_crossfade': 'Transitions fluides entre les pistes audio ambiantes',
    'update_settings_audio': 'Contrôles audio centralisés dans la page Paramètres',
    'update_arabic_complete': 'Couverture complète de la traduction arabe',
    'update_duplicate_icon_fix': 'Icônes audio en double corrigées dans l\'en-tête',

    // Game UI
    'game_watching': 'regardant...',
    'game_repeat': 'répéter',
    'game_echo_fades': 'l\'écho s\'estompe...',
    'game_escaped': 'Vous vous êtes échappé.',
    'game_found_exit': 'Vous avez trouvé la sortie.',
    'game_looping': 'Vous tournez en boucle.',
    'game_deeper': 'Plus profond...',
    'game_doors_shift': 'Les portes changent.',
    'maze_left': 'gauche',
    'maze_right': 'droite',
    'maze_forward': 'avant',
    'maze_back': 'arrière',
    'maze_past': 'passé',
    'maze_future': 'futur',
    'maze_here': 'ici',
    'maze_there': 'là-bas',
    'maze_truth': 'vérité',
    'maze_lies': 'mensonges',
    'maze_light': 'lumière',
    'maze_dark': 'obscurité',
    'maze_memory': 'mémoire',
    'maze_dream': 'rêve',
    'maze_silence': 'silence',
    'maze_noise': 'bruit',
    'random_room': 'Chambre Aléatoire',
    'ai_reflection': 'Réflexion IA',
    'support': 'Soutien',
    'choose_direction': 'Choisissez une direction. Certaines portes répondent.',
    'rooms_explored': 'chambres explorées',
    'your_journey': 'Votre Voyage',
    'complete_rooms_fragments': 'Complétez des chambres pour collecter des fragments.',
    'no_rooms_completed': 'Aucune chambre terminée pour le moment.',
    'constellation_map': 'Carte des Constellations',
    'your_stats': 'Vos Statistiques',
    
    // Stats
    'total_time': 'Temps Total',
    'rooms_visited': 'Chambres Visitées',
    'total_visits': 'Total des Visites',
    'most_visited': 'Plus Visitée',
    'longest_session': 'Session la Plus Longue',
    'fragments_collected': 'Fragments Collectés',
    'completed_rooms': 'Chambres Terminées',
    'privacy_note': 'Toutes les données sont stockées localement sur votre appareil',
    
    // Daily Challenges
    'daily_challenges': 'Défis Quotidiens',
    'challenges_completed': 'terminé',
    'challenges_total': 'total',
    'resets_midnight': 'Réinitialise à minuit',
    
    // Weekly Summary
    'weekly_summary': 'Résumé Hebdomadaire',
    'week': 'Semaine',
    'this_week': 'Cette Semaine',
    'rooms': 'Chambres',
    'days': 'Jours',
    'vs_last_week': 'vs semaine dernière',
    'refresh_insight': 'Rafraîchir la perspective',
    
    // Game Room Titles
    'room_memory-puzzle': 'Puzzle de Mémoire',
    'room_shadow-chase': 'Chasse aux Ombres',
    'room_timing-room': 'Salle du Timing',
    'room_pattern-whisper': 'Murmure de Motifs',
    'room_silence-puzzle': 'Puzzle du Silence',
    'room_decay-game': 'Jeu de Décadence',
    'room_observation-test': "Test d'Observation",
    'room_balance-room': "Salle de l'Équilibre",
    'room_word-drift': 'Dérive des Mots',
    'room_color-echo': 'Écho de Couleur',
    'room_mirror-tap': 'Tape du Miroir',
    'room_maze-of-doors': 'Labyrinthe de Portes',
    'room_pulse-match': 'Correspondance du Pouls',
    'room_light-path': 'Chemin de Lumière',
    'room_sound-match': 'Correspondance de Son',
    
    // Game Room Subtitles
    'sub_memory-puzzle': 'Associez des motifs qui changent subtilement',
    'sub_shadow-chase': 'Attrapez ce qui bouge de manière imprévisible',
    'sub_timing-room': 'Cliquez exactement au bon moment',
    'sub_pattern-whisper': 'Répétez la séquence qui s\'estompe',
    'sub_silence-puzzle': 'Résolvez sans instructions',
    'sub_decay-game': 'Touchez pour préserver ce qui disparaît',
    'sub_observation-test': 'Trouvez les différences subtiles',
    'sub_balance-room': 'Gardez les objets flottants alignés',
    'sub_word-drift': 'Attrapez les lettres avant qu\'elles ne tombent',
    'sub_color-echo': 'Répétez la séquence de tons subtils',
    'sub_mirror-tap': 'Chaque tape résonne ailleurs',
    'sub_maze-of-doors': 'Les portes se réorganisent après chaque choix',
    'sub_pulse-match': 'Bougez au rythme de la salle',
    'sub_light-path': "Guidez la lumière à travers l'obscurité changeante",
    'sub_sound-match': 'Tapez des formes pour correspondre aux tons doux',
    
    // Experimental Room Titles
    'room_mirror-room': 'Salle du Miroir',
    'room_echo-room': "Salle de l'Écho",
    'room_pulse-room': 'Salle du Pouls',
    'room_vanishing-room': 'Salle qui Disparaît',
    'room_letter-room': 'Salle des Lettres',
    'room_empty-room': 'Salle Vide',
    'room_reverse-room': 'Salle Inversée',
    'room_whisper-room': 'Salle du Murmure',
    'room_shadow-room': 'Salle des Ombres',
    'room_labyrinth-room': 'Salle du Labyrinthe',
    'room_color-room': 'Salle des Couleurs',
    'room_surrender-room': 'Salle de la Reddition',
    'room_noise-room': 'Salle du Bruit',
    'room_observer-room': "Salle de l'Observateur",
    
    // Experimental Room Subtitles
    'sub_mirror-room': 'Reflets légèrement déformés',
    'sub_echo-room': 'Les clics déclenchent des échos retardés',
    'sub_pulse-room': "L'environnement respire",
    'sub_vanishing-room': 'Les éléments disparaissent en interagissant',
    'sub_letter-room': 'Les mots se brouillent comme une mémoire défaillante',
    'sub_empty-room': 'Vide jusqu\'à ce que vous trouviez des secrets',
    'sub_reverse-room': 'Les actions se comportent à l\'opposé',
    'sub_whisper-room': 'Vérités à peine audibles',
    'sub_shadow-room': 'Votre compagnon sombre',
    'sub_labyrinth-room': 'Trouvez votre chemin',
    'sub_color-room': 'Méditation chromatique',
    'sub_surrender-room': 'Lâchez le contrôle',
    'sub_noise-room': 'Pensées statiques',
    'sub_observer-room': 'Être observé',
    
    // Daily Joke Room
    'joke_number': 'Blague #{number}',
    'joke_today': 'La Blague du Jour',
    'joke_reveal': 'Révéler la chute',
    'joke_come_back': 'Revenez demain pour une nouvelle blague.',
    'joke_quote': 'Le rire est la plus courte distance entre deux personnes.',
    
    // Settings Page
    'settings': 'Paramètres',
    'settings_subtitle': 'Personnalisez votre voyage',
    'language': 'Langue',
    'ambient_audio': 'Audio Ambiant',
    'ambient_audio_desc': 'Sons de fond pour les chambres',
    'reduced_motion': 'Mouvement Réduit',
    'reduced_motion_desc': 'Minimiser les animations',
    'data_management': 'Gestion des Données',
    'export_journey': 'Exporter les Données du Voyage',
    'clear_all_data': 'Effacer Toutes les Données',
    'audio_enabled': 'Audio Activé',
    'audio_disabled': 'Audio Désactivé',
    'data_exported': 'Données Exportées',
    'clear_data_confirm': 'Êtes-vous sûr ? Cela effacera toute votre progression.',
    'data_cleared': 'Données Effacées',
    // Theme settings
    'theme': 'Thème',
    'theme_dark': 'Sombre',
    'theme_dark_desc': 'Obscurité pure',
    'theme_bone': 'Os',
    'theme_bone_desc': 'Pâleur mortelle',
    'theme_ash': 'Cendre',
    'theme_ash_desc': 'Après le feu',
    'theme_fog': 'Brouillard',
    'theme_fog_desc': 'Incertitude éthérée',
    'theme_twilight': 'Crépuscule',
    'theme_twilight_desc': 'Entre jour et nuit',
    'theme_eclipse': 'Éclipse',
    'theme_eclipse_desc': 'Lumière dévorée par l\'obscurité',
    'theme_dawn': 'Aube',
    'theme_dawn_desc': 'Première lumière de l\'éveil',
    'theme_changed': 'Thème mis à jour',
    'font_size': 'Taille de Police',
    'font_small': 'Petite',
    'font_medium': 'Moyenne',
    'font_large': 'Grande',
    'font_size_changed': 'Taille de police mise à jour',
    'high_contrast': 'Contraste Élevé',
    'high_contrast_desc': 'Augmenter le contraste du texte et des bordures',
    'high_contrast_enabled': 'Contraste élevé activé',
    'high_contrast_disabled': 'Contraste élevé désactivé',
    
    // Ambient Music Control
    'ambient_music': 'Musique Ambiante',
    'ambient_music_desc': 'Contrôlez les paysages sonores de fond',
    'volume': 'Volume',
    'ambient_track': 'Piste Ambiante',
    'playing': 'Lecture',
    'play': 'Jouer',
    'ambient_default': 'Calme',
    'ambient_night': 'Nuit',
    'ambient_scary': 'Inquiétant',
    'ambient_truth': 'Vérité',
    'ambient_identity': 'Réflexion',
    'ambient_mortality': 'Temps',
    'ambient_nothing': 'Vide',
    'ambient_meta': 'Numérique',
    'ambient_daily': 'Chaleureux',
    'ambient_seasonal': 'Magique',
    'ambient_premium': 'Profond',
    
    'shared_room': 'Chambre Partagée',
    'imagine_room': 'Imaginer',
    'imagine_title': 'Imaginez-Vous',
    'imagine_subtitle': 'Décrivez comment vous vous voyez. Laissez la chambre vous montrer ce qui émerge de vos mots.',
    'imagine_placeholder': 'Je me vois comme quelqu\'un qui...\n\nDécrivez votre essence, votre nature, comment vous vous sentez à l\'intérieur.',
    'imagine_hint': 'Soyez honnête. L\'image reflète vos mots.',
    'imagine_generate': 'Voir Moi-Même',
    'imagine_generating': 'Création...',
    'imagine_try_again': 'Décrire à Nouveau',
    'imagine_quote': 'Nous ne nous voyons pas clairement. Nous voyons ce que nous croyons.',
    'room_imagine-yourself': 'Imaginez-Vous',
    'sub_imagine-yourself': 'Décrivez-vous. Voyez ce qui émerge.',
    'theme_void': 'Vide',
    'theme_void_desc': 'Il ne reste que des ombres',
    
    // Easter Egg Discoveries
    'discovery_label': 'Découverte',
    'discovery_konami': 'Vous connaissez les anciennes voies.',
    'discovery_secret_sequence': 'Vous avez épelé la vérité.',
    'discovery_long_press': 'La patience révèle des secrets.',
    'discovery_triple_click': 'La persévérance paie.',
    'discovery_silence': 'Le silence parle.',
    
    // Theme-Exclusive Rooms
    'ash_exclusive': 'Exclusif au thème Cendre',
    'twilight_exclusive': 'Exclusif au thème Crépuscule',
    'void_exclusive': 'Exclusif au thème Vide',
    'theme_required': 'Nécessite le thème {theme}',
    'ash_embers_title': 'Des Cendres',
    'ash_embers_line1': 'Tout brûle éventuellement.',
    'ash_embers_line2': 'Ce qui compte, c\'est ce qui s\'élève après.',
    'ash_embers_line3': 'Touchez l\'écran. Créez des braises.',
    'ash_embers_quote': 'Des cendres, nous nous souvenons de ce qui a brûlé.',
    'room_ash-embers': 'Cendres et Braises',
    'sub_ash-embers': 'Visible uniquement dans le thème Cendre',
    'twilight_stars_title': 'Entre Jour et Nuit',
    'twilight_stars_line1': 'Les étoiles se souviennent de tous ceux qui ont levé les yeux.',
    'twilight_stars_line2': 'Connectez-les. Créez votre propre constellation.',
    'twilight_stars_quote': 'Au crépuscule, nous voyons ce que le jour cache.',
    'room_twilight-stars': 'Étoiles du Crépuscule',
    'sub_twilight-stars': 'Visible uniquement dans le thème Crépuscule',
    'void_abyss_title': 'L\'Abîme',
    'void_abyss_line1': 'Regardez dans le vide. Le vide vous regarde.',
    'void_word_1': 'rien',
    'void_word_2': 'vide',
    'void_word_3': 'silence',
    'void_word_4': 'parti',
    'void_word_5': 'seul',
    'void_word_6': 'fin',
    'void_revealed': 'révélé',
    'void_abyss_quote': 'Dans l\'obscurité absolue, nous nous voyons enfin.',
    'room_void-abyss': 'L\'Abîme',
    'sub_void-abyss': 'Visible uniquement dans le thème Vide',
    
    // Theme Exclusive Section
    'theme_exclusive_rooms': 'Chambres Exclusives par Thème',
    'theme_exclusive_hint': 'Changez de thème dans les paramètres pour déverrouiller ces chambres.',
    'requires_theme': 'Nécessite',
    'unlocked': 'Déverrouillé',
    'switch_to': 'Passer à',
    
    // Advanced Easter Eggs
    'corner_sequence': 'Vous avez tracé les limites.',
    'morse_sos': 'S.O.S. reçu.',
    'reverse_scroll': 'Retour en arrière... plus vite.',
    'midnight_visitor': 'L\'heure des sorcières vous accueille.',
    'shake': 'Vous avez secoué quelque chose.',
    
    // Experience Mode
    'experience_mode': "Mode d'Expérience",
    'classic_mode': 'Classique',
    'classic_mode_desc': 'La réalité familière',
    'alternate_mode': 'Alt̶erné',
    'alternate_mode_desc': 'Vous ne devriez pas être ici',
    'switch_to_alternate': '░ descendre ░',
    'switch_to_classic': '← Revenir à la surface',
    'enter_parallel_reality': 'La porte est ouverte',
    'return_to_familiar': 'Revenir au familier',
    'something_shifted': 'Quelque chose a changé',
    'reality_feels_different': 'La réalité semble différente maintenant.',
    'alternate_tagline': 'Les mêmes chambres. Des vérités différentes.',
    'alternate_enter': 'Descendre',
    'alternate_footer': 'Les choses ne sont pas ce quelles semblent.',
    'alternate_rooms': '░▓ Réal1té P4rallèle ▓░',
    'alternate_rooms_hint': '[ ces chambres n\'ex1stent que dans le parallèle ]',
    
    // Alternate Mode Exclusive Rooms (French)
    'room_the-other-side': 'L\'Autre Côté',
    'room_the-unraveling': 'L\'Effilochage',
    'room_the-echo': 'L\'Écho',
    'sub_the-other-side': 'avancez lentement dans l\'obscurité',
    'sub_the-unraveling': 'la réalité s\'effiloche à chaque mot',
    'sub_the-echo': 'ce que vous dites ne part jamais',
    
    // Meta Rooms (French)
    'room_the-refusal': 'Le Refus',
    'sub_the-refusal': 'Parfois le site vous refuse',
    'room_parallel-you': 'Vous Parallèle',
    'sub_parallel-you': 'Une autre version de vous',
    'room_your-shadow': 'Votre Ombre',
    'sub_your-shadow': 'Ce qui vous suit',
    
    // AI Rooms (French)
    'room_ai-reflection-room': 'Salle de Réflexion IA',
    'sub_ai-reflection-room': 'Laissez l\'IA réfléchir avec vous',
    'sub_dream-visualization': 'Décrivez votre rêve. Voyez-le prendre forme.',
    'sub_future-self': 'Un message du futur',
    
    // Meta Room Content - The Refusal (French)
    'refusal_line1': 'Cette chambre vous refuse.',
    'refusal_line2': 'Tout ne s\'ouvre pas quand vous le voulez.',
    'refusal_line3': 'Parfois la réponse est simplement : non.',
    'refusal_line4': 'Essayez le bouton. Ça n\'aidera pas.',
    'refusal_button': 'Entrer dans la Chambre',
    
    // Meta Room Content - Parallel You (French)
    'parallel_choice_1': 'Vous avez choisi la vitesse.',
    'parallel_alternate_1': 'Un autre vous a choisi la patience.',
    'parallel_reflection_1': 'Ils attendent encore.',
    'parallel_choice_2': 'Vous êtes parti tôt.',
    'parallel_alternate_2': 'Un autre vous est resté jusqu\'à la fin.',
    'parallel_reflection_2': 'Ils ont vu ce que vous avez manqué.',
    'parallel_choice_3': 'Vous avez évité la question difficile.',
    'parallel_alternate_3': 'Un autre vous y a répondu.',
    'parallel_reflection_3': 'Ils portent cette vérité maintenant.',
    'parallel_choice_4': 'Vous êtes passé en courant.',
    'parallel_alternate_4': 'Un autre vous a savouré chaque moment.',
    'parallel_reflection_4': 'Ils se souviennent de plus.',
    'parallel_choice_5': 'Vous avez continué à défiler.',
    'parallel_alternate_5': 'Un autre vous s\'est arrêté.',
    'parallel_reflection_5': 'Ils ont trouvé quelque chose.',
    'parallel_choice_6': 'Vous avez choisi le confort.',
    'parallel_alternate_6': 'Un autre vous a choisi l\'inconfort.',
    'parallel_reflection_6': 'Ils ont grandi avec.',
    'parallel_quote': 'Chaque moment se divise en chemins infinis. Vous n\'en marchez qu\'un.',
    
    // Meta Room Content - Your Shadow (French)
    'shadow_generated': 'Généré depuis votre comportement',
    'shadow_intro': 'Votre ombre...',
    'shadow_follows': 'Cette ombre vous suit dans chaque chambre.',
    'shadow_quote': 'Nous ne sommes pas ce que nous pensons. Nous sommes ce que nous faisons.',
    'shadow_trait_completion': 'cherche à compléter',
    'shadow_trait_surface': 'préfère la surface',
    'shadow_trait_lingers': 'reste dans les espaces',
    'shadow_trait_moves_quickly': 'se déplace vite',
    'shadow_trait_avoids_depth': 'évite la profondeur',
    'shadow_trait_stillness': 'embrasse l\'immobilité',
    'shadow_trait_chases': 'poursuit quelque chose sans nom',
    'shadow_trait_darkness': 'attiré par l\'obscurité',
    'shadow_trait_emptiness': 'à l\'aise avec le vide',
    'shadow_trait_undefined': 'non défini',
    'shadow_trait_forming': 'encore en formation',
    
    // Translation Navigator (French)
    'translation_navigator': 'Navigateur de Traductions',
    'translation_coverage': 'Couverture de traduction par langue',
    'keys': 'clés',
    'room_translations': 'Traductions de Chambres',
    'show_all': 'Afficher Tout',
    'show_missing': 'Afficher Manquantes',
    'all_translated': 'Toutes les chambres sont traduites !',
    'missing_translations': 'traductions manquantes',
    
    // Additional Room Titles (French)
    'room_how-many-times-left': 'Combien de Fois Restantes',
    'room_life-in-weeks': 'Votre Vie en Semaines',
    'room_delay-button': 'Le Bouton de Délai',
    'room_one-honest-sentence': 'Une Phrase Honnête',
    'room_this-moment': 'Ce Moment Ne Se Répétera Jamais',
    'room_reality-receipt': 'Reçu de Réalité',
    'room_nothing-happens': 'Rien Ne Se Passe Ici',
    'room_default-choice': 'Votre Choix par Défaut',
    'room_thought-loop': 'La Boucle de Pensées',
    'room_expect-vs-happens': 'Ce que Vous Attendez vs Ce qui Arrive',
    'room_question-never-answer': 'La Question à Laquelle Vous Ne Répondez Jamais',
    'room_not-special': 'Vous N\'êtes Pas Spécial (Et C\'est Bien)',
    'room_shortcut-always-take': 'Le Raccourci que Vous Prenez Toujours',
    'room_blurred-self': 'Moi Flou',
    'room_image-zooming': 'L\'Image que Vous Zoomez',
    'room_comfort-color': 'La Couleur de Confort',
    'room_line-you-crossed': 'La Ligne que Vous Avez Franchie',
    'room_read-aloud': 'Lisez Ceci à Voix Haute',
    'room_voice-without-meaning': 'Votre Voix Sans Signification',
    'room_sentence-you-avoid': 'La Phrase que Vous Évitez',
    'room_if-nothing-changes': 'Si Rien Ne Change',
    'room_real-priorities': 'Vos Vraies Priorités',
    'room_last-conversation': 'La Dernière Conversation',
    'room_who-without-job': 'Qui Êtes-Vous Sans Votre Travail?',
    'room_comfort-zone-scanner': 'Scanner de Zone de Confort',
    'room_say-it-once': 'Dites-le Une Fois',
    'room_listen-to-yourself': 'Écoutez-Vous',
    'room_life-as-shapes': 'Votre Vie en Formes',
    'room_face-without-features': 'Visage Sans Traits',
    'room_what-keeps-awake': 'Ce qui Vous Garde Éveillé',
    'room_you-at-317am': 'Vous à 3h17',
    'room_thought-that-returns': 'La Pensée qui Revient',
    'room_room-you-visit-most': 'La Chambre que Vous Visitez le Plus',
    'room_room-you-avoid': 'La Chambre que Vous Évitez',
    'room_left-too-early': 'Vous Êtes Parti Trop Tôt',
    'room_daily-quote': 'Citation du Jour',
    'room_daily-joke': 'Blague du Jour',
    'room_theyre-watching': 'Ils Vous Observent',
    'room_the-last-visitor': 'Le Dernier Visiteur',
    'room_dont-look-away': 'Ne Détournez Pas le Regard',
    'room_the-whisper': 'Le Murmure',
    'room_you-forgot-something': 'Vous Avez Oublié Quelque Chose',
    'room_the-mirror-lies': 'Le Miroir Ment',
    'room_something-follows': 'Quelque Chose Vous Suit',
    'room_the-countdown': 'Le Compte à Rebours',
    'room_eyes-in-the-dark': 'Des Yeux dans le Noir',
    'room_the-voice': 'La Voix',
    'room_it-knows-your-name': 'Il Connaît Votre Nom',
    'room_the-door': 'La Porte',
    'room_boss-room-free': 'La Somme de Vos Vérités',
    'room_boss-room-horror': 'Le Cœur de la Peur',
    'room_boss-room-premium': 'La Vérité la Plus Profonde',
    'room_mouse-tracker': 'Le Traqueur de Souris',
    'room_scroll-confession': 'Confession de Défilement',
    'room_waiting-game': 'Le Jeu de l\'Attente',
    'room_day-in-pixels': 'Votre Jour en Pixels',
    'room_memory-maze': 'Labyrinthe de Mémoire',
    'room_decision-shadow': 'Ombre de Décision',
    'room_anonymous-mirrors': 'Miroirs Anonymes',
    'room_collective-room': 'La Chambre Collective',
    'room_secret-room': 'La Chambre Secrète',
    'room_glitch-room': 'Chambre Instable',
    'room_full-moon-room': 'Chambre de Pleine Lune',
    'room_weekend-room': 'Chambre du Week-end',
    'room_317am-room': 'Chambre de 3h17',
    'room_idle-room': 'La Chambre Immobile',
    'room_invite-friend-room': 'La Chambre Partagée',
    'room_halloween-room': 'La Chambre du Voile',
    'room_winter-solstice-room': 'La Nuit la Plus Longue',
    'room_new-year-room': 'Lettre à Votre Futur Vous',
    'room_void-room': 'Le Vide',
    'room_pattern-room': 'Vos Motifs',
    'room_time-capsule': 'Capsule Temporelle',
    'room_the-revisit': 'La Revisite',
    'room_contradiction-room': 'La Chambre de Contradiction',
    'room_export-journey': 'Exporter Votre Voyage',
    'room_anonymous-echoes': 'Échos Anonymes',
    'room_the-waiting-room': 'La Salle d\'Attente',
    'room_dream-visualization': 'Visualisation de Rêve',
    'room_future-self': 'Votre Moi Futur',
    'room_eclipse-shadows': 'Ombres de l\'Éclipse',
    'room_dawn-awakening': 'Éveil de l\'Aube',
    
    // Additional Room Subtitles (French)
    'sub_how-many-times-left': 'Un décompte de ce qui reste',
    'sub_life-in-weeks': '4 000 carrés. C\'est tout.',
    'sub_delay-button': 'Ça devient plus dur à chaque fois',
    'sub_one-honest-sentence': 'Pas de modification. Pas de suppression.',
    'sub_this-moment': 'Vous êtes ici. Une fois.',
    'sub_reality-receipt': 'Un résumé de vos jours',
    'sub_nothing-happens': 'Exactement ce que ça dit',
    'sub_eclipse-shadows': 'Parlez dans l\'obscurité totale',
    'sub_dawn-awakening': 'Saluez la première lumière',
    'sub_default-choice': 'Sécurité ou risque. À chaque fois.',
    'sub_thought-loop': 'Regardez votre esprit se répéter',
    'sub_expect-vs-happens': 'L\'écart entre espoir et réalité',
    'sub_question-never-answer': 'La plupart partent',
    'sub_not-special': 'Pas de réconfort. Juste la vérité.',
    'sub_shortcut-always-take': 'Les motifs se révèlent',
    'sub_blurred-self': 'La mémoire fonctionne ainsi',
    'sub_image-zooming': 'Chercher des réponses dans le néant',
    'sub_comfort-color': 'Combien de temps resterez-vous?',
    'sub_line-you-crossed': 'Nous voyons où vous avez hésité',
    'sub_read-aloud': 'Des mots que vous devez prononcer',
    'sub_voice-without-meaning': 'Du son sans paroles',
    'sub_sentence-you-avoid': 'Vous ne pouvez pas partir sans l\'écrire',
    'sub_if-nothing-changes': 'Une projection de vos cinq prochaines années',
    'sub_real-priorities': 'Basé sur le temps, pas les mots',
    'sub_last-conversation': 'Écrivez ce que vous n\'enverrez jamais',
    'sub_who-without-job': 'Réécrivez-vous',
    'sub_comfort-zone-scanner': 'Nous voyons votre hésitation',
    'sub_say-it-once': 'Pas de reprise. Pas de refaire.',
    'sub_listen-to-yourself': 'Votre voix, ralentie',
    'sub_life-as-shapes': 'Abstrait. Sans étiquettes. Le vôtre.',
    'sub_face-without-features': 'Enlevez jusqu\'à ce qu\'il ne reste rien',
    'sub_what-keeps-awake': 'Disponible après 22h',
    'sub_you-at-317am': 'Spécifique au temps. Personnel.',
    'sub_thought-that-returns': 'Revient à chaque visite',
    'sub_room-you-visit-most': 'Votre motif révélé',
    'sub_room-you-avoid': 'Ce que vous évitez dit quelque chose',
    'sub_left-too-early': 'Déclenché quand vous partez vite',
    'sub_daily-quote': 'Sagesse qui change chaque jour',
    'sub_daily-joke': 'Humour noir, mis à jour quotidiennement',
    'sub_theyre-watching': 'Vous n\'êtes pas seul',
    'sub_the-last-visitor': 'Quelqu\'un était là avant vous',
    'sub_dont-look-away': 'Il sait quand vous partez',
    'sub_the-whisper': 'Écoutez attentivement',
    'sub_you-forgot-something': 'Qu\'était-ce?',
    'sub_the-mirror-lies': 'Brisez-le pour voir la vérité',
    'sub_something-follows': 'Continuez à courir',
    'sub_the-countdown': 'Quelque chose arrive à zéro',
    'sub_eyes-in-the-dark': 'Ils se multiplient',
    'sub_the-voice': 'Quelqu\'un est piégé ici',
    'sub_it-knows-your-name': 'Vous n\'auriez pas dû venir',
    'sub_the-door': 'Quelque chose frappe',
    'sub_boss-room-free': 'La chambre libre finale',
    'sub_boss-room-horror': 'Affrontez l\'obscurité finale',
    'sub_boss-room-premium': 'La chambre profonde finale',
    'sub_mouse-tracker': 'Nous voyons comment vous bougez',
    'sub_scroll-confession': 'À quelle vitesse vous précipitez-vous?',
    'sub_waiting-game': 'Pouvez-vous ne rien faire?',
    'sub_day-in-pixels': 'Le temps devient forme',
    'sub_memory-maze': 'Le motif change',
    'sub_decision-shadow': 'Vos choix révèlent des motifs',
    'sub_anonymous-mirrors': 'Pensées d\'inconnus',
    'sub_collective-room': 'Tout le monde est là. Personne ne reste.',
    'sub_secret-room': 'Vous l\'avez trouvée',
    'sub_glitch-room': 'Quelque chose ne va pas',
    'sub_full-moon-room': 'S\'ouvre uniquement pendant le pic lunaire',
    'sub_weekend-room': 'Le repos est une destination',
    'sub_317am-room': 'Spécifique au temps. Personnel.',
    'sub_idle-room': 'Trouvé par la patience',
    'sub_invite-friend-room': 'Certains voyages doivent être témoignés',
    'sub_halloween-room': '31 octobre - Le voile est mince',
    'sub_winter-solstice-room': '21 décembre - Libérer et garder',
    'sub_new-year-room': '31 décembre - Écrivez à qui vous serez',
    'sub_void-room': 'Une blessure dans le code',
    'sub_pattern-room': 'Ce que le site voit en vous',
    'sub_time-capsule': 'Écrivez à votre futur vous',
    'sub_the-revisit': 'Voyez comment vos réponses ont changé',
    'sub_contradiction-room': 'Vos vérités ne concordent pas',
    'sub_export-journey': 'Téléchargez votre chemin',
    'sub_anonymous-echoes': 'Fragments d\'inconnus',
    'sub_the-waiting-room': 'Débloquez avec présence',
    
    // New Phase 1-3 Rooms (French)
    'room_letters-never-sent': 'Lettres Jamais Envoyées',
    'sub_letters-never-sent': 'Écrivez à quelqu\'un qui ne lira jamais',
    'room_breath-sync': 'Synchronisation Respiratoire',
    'sub_breath-sync': 'Respirez avec la salle',
    'room_entropy-garden': 'Jardin d\'Entropie',
    'sub_entropy-garden': 'Croissance et décadence entrelacées',
    'room_memory-palace': 'Palais de la Mémoire',
    'sub_memory-palace': 'Construisez votre sanctuaire mental',
    'room_morning-ritual': 'Rituel du Matin',
    'sub_morning-ritual': 'Commencez votre journée avec intention',
    'room_evening-ritual': 'Rituel du Soir',
    'sub_evening-ritual': 'Terminez la journée avec réflexion',
    'room_weekly-ritual': 'Rituel Hebdomadaire',
    'sub_weekly-ritual': 'Les dimanches sont pour regarder en arrière',
    
    // Letters Never Sent Room (French)
    'letters_title': 'Lettres Jamais Envoyées',
    'letters_subtitle': 'Écrivez à quelqu\'un qui ne lira jamais. L\'IA répondra comme ils pourraient.',
    'letters_select_recipient': 'À qui écrivez-vous?',
    'letters_past_self': 'Mon Moi Passé',
    'letters_future_self': 'Mon Moi Futur',
    'letters_someone_lost': 'Quelqu\'un que J\'ai Perdu',
    'letters_someone_wronged': 'Quelqu\'un que J\'ai Blessé',
    'letters_placeholder': 'Cher/Chère...',
    'letters_send': 'Envoyer dans le Vide',
    'letters_sending': 'Envoi...',
    'letters_response': 'Une réponse émerge...',
    'letters_write_another': 'Écrire Une Autre Lettre',
    'letters_quote': 'Certains mots sont destinés à être écrits mais jamais envoyés.',
    
    // Breath Sync Room (French)
    'breath_title': 'Synchronisation Respiratoire',
    'breath_subtitle': 'Synchronisez votre respiration avec le rythme de la salle.',
    'breath_inhale': 'Inspirez',
    'breath_hold': 'Retenez',
    'breath_exhale': 'Expirez',
    'breath_cycles': 'Cycles complétés',
    'breath_quote': 'La respiration est le pont entre le corps et l\'esprit.',
    
    // Entropy Garden Room (French)
    'entropy_title': 'Jardin d\'Entropie',
    'entropy_subtitle': 'Vos plantes poussent à chaque visite, mais dépérissent quand vous vous éloignez.',
    'entropy_plant': 'Planter une Graine',
    'entropy_water': 'Arroser',
    'entropy_days_alive': 'jours en vie',
    'entropy_health': 'Santé',
    'entropy_empty': 'Votre jardin est vide. Plantez quelque chose.',
    'entropy_quote': 'Tout pousse. Tout se fane. C\'est le chemin.',
    
    // Memory Palace Room (French)
    'memory_palace_title': 'Palais de la Mémoire',
    'memory_palace_subtitle': 'Construisez un sanctuaire mental. Placez des objets dans des salles que vous créez.',
    'memory_palace_add_room': 'Ajouter une Salle',
    'memory_palace_add_object': 'Ajouter un Objet',
    'memory_palace_room_name': 'Nom de la salle...',
    'memory_palace_object_name': 'Nom de l\'objet...',
    'memory_palace_empty': 'Votre palais est vide. Créez votre première salle.',
    'memory_palace_quote': 'Nous construisons nos esprits une salle à la fois.',
    
    // Ritual Rooms (French)
    'ritual_locked': 'Ce rituel n\'est pas encore disponible',
    'morning_ritual_title': 'Rituel du Matin',
    'morning_ritual_intro': 'Un moment pour définir des intentions pour la journée.',
    'morning_ritual_available': 'Disponible 6h - 9h',
    'evening_ritual_title': 'Rituel du Soir',
    'evening_ritual_intro': 'Un moment pour réfléchir sur la journée.',
    'evening_ritual_available': 'Disponible 20h - 23h',
    'weekly_ritual_title': 'Rituel Hebdomadaire',
    'weekly_ritual_intro': 'Un moment pour réfléchir sur la semaine passée, et définir des intentions pour la prochaine.',
    'weekly_locked': 'Le Rituel Hebdomadaire vous attend',
    'weekly_time': 'Revenez dimanche',
    'today_is': 'Aujourd\'hui c\'est',
    'your_week': 'Votre Semaine',
    'days_active': 'Jours Actifs',
    'weekly_summary_note': 'Maintenant, réfléchissons plus profondément.',
    'weekly_highlight': 'Quel a été le moment fort de votre semaine?',
    'weekly_challenge': 'Qu\'est-ce qui vous a le plus défié cette semaine?',
    'weekly_growth': 'Comment avez-vous grandi cette semaine?',
    'weekly_intention': 'Quelle intention définissez-vous pour la semaine prochaine?',
    'weekly_placeholder': 'Prenez votre temps...',
    'reflect': 'Réfléchir',
    'reflecting': 'Réflexion...',
    'complete': 'Terminer',
    'weekly_quote': 'Une semaine se termine. Une autre commence. Vous emportez ce qui compte.',
    
    // Personal Timeline (French)
    'personal_timeline': 'Chronologie Personnelle',
    'timeline_subtitle': 'Votre voyage à travers les chambres',
    'timeline_empty': 'Votre chronologie est vide. Commencez à explorer.',
    'timeline_today': 'Aujourd\'hui',
    'timeline_yesterday': 'Hier',
    'timeline_this_week': 'Cette Semaine',
    'timeline_earlier': 'Plus tôt',
    
    // Weather System (French)
    'weather_clear': 'Clair',
    'weather_rain': 'Pluie',
    'weather_fog': 'Brouillard',
    'weather_snow': 'Neige',
    
    // Notifications (French)
    'notifications': 'Notifications',
    'notifications_desc': 'Recevez des notifications pour le contenu verrouillé par le temps',
    'notify_time_locked': 'Chambres verrouillées par le temps',
    'notify_full_moon': 'Nuits de pleine lune',
    'notify_daily_reset': 'Réinitialisation des défis quotidiens',
    'notify_time_capsule': 'Déverrouillage des capsules temporelles',
    'notifications_enabled': 'Notifications activées',
    'notifications_disabled': 'Notifications désactivées',
    
    // Community Submissions (French)
    'community_submissions': 'Contributions de la Communauté',
    'community_subtitle': 'Soumettez vos idées de chambres',
    'community_room_title': 'Titre de la Chambre',
    'community_room_description': 'Description de la Chambre',
    'community_room_category': 'Catégorie',
    'community_submit': 'Soumettre l\'Idée',
    'community_submitted': 'Merci pour votre contribution!',
    'community_your_submissions': 'Vos Contributions',
    'community_empty': 'Pas encore de contributions. Soyez le premier!',
    
    // Proactive Guide (French)
    'guide_hesitation': 'Vous prenez votre temps? C\'est bien. Voulez-vous une suggestion?',
    'guide_long_absence': 'Bon retour. Ça fait un moment.',
    'guide_milestone': 'Vous avez fait du chemin. Voulez-vous réfléchir à votre voyage?',
    'guide_dismiss': 'Ignorer',
    'guide_accept': 'Montrez-moi',
    
    // Voice Mode (French)
    'voice_mode': 'Mode Vocal',
    'voice_mode_desc': 'Laissez le guide vous parler',
    'voice_mode_enabled': 'Mode vocal activé',
    'voice_mode_disabled': 'Mode vocal désactivé',
    
    // Offline Support (French)
    'offline_available': 'Disponible hors ligne',
    'offline_cached': 'Cette chambre est mise en cache pour la visualisation hors ligne',
    
    // Journey Export (French)
    'journey_export': 'Exporter le Voyage',
    'journey_import': 'Importer le Voyage',
    'journey_export_json': 'Exporter en JSON',
    'journey_import_json': 'Importer depuis JSON',
    'journey_exported': 'Voyage exporté avec succès',
    'journey_imported': 'Voyage importé avec succès',
    
    // Phase 4 - Seasonal Rooms (French)
    'room_spring-equinox-room': 'Équinoxe de Printemps',
    'sub_spring-equinox-room': 'Libérez et renouvelez',
    'room_summer-solstice-room': 'Solstice d\'Été',
    'sub_summer-solstice-room': 'Le jour le plus long',
    'room_autumn-equinox-room': 'Équinoxe d\'Automne',
    'sub_autumn-equinox-room': 'Récoltez et acceptez',
    'room_valentine-room': 'Chambre d\'Amour de Soi',
    'sub_valentine-room': 'L\'amour que vous méritez',
    
    // Spring Equinox Room (French)
    'spring_date': '20 MARS',
    'spring_title': 'Équinoxe de Printemps',
    'spring_message_1': 'Le jour et la nuit sont égaux.',
    'spring_message_2': 'Un moment d\'équilibre parfait.',
    'spring_message_3': 'Que libérerez-vous pour faire place à la croissance?',
    'spring_letting_go': 'Je laisse partir:',
    'spring_letting_go_placeholder': 'Ce qui ne vous sert plus...',
    'spring_planting': 'Je plante:',
    'spring_planting_placeholder': 'Ce que vous souhaitez voir grandir...',
    'spring_bloom': 'Laissez fleurir',
    'spring_released': 'Libéré:',
    'spring_planted': 'Planté:',
    'spring_closing': 'À partir de ce moment, une nouvelle vie commence.',
    'spring_quote': 'La nature ne se presse pas, mais tout s\'accomplit.',
    
    // Summer Solstice Room (French)
    'summer_date': '21 JUIN',
    'summer_title': 'Le Jour le Plus Long',
    'summer_message_1': 'C\'est le jour le plus long de l\'année.',
    'summer_message_2': 'La lumière atteint son apogée.',
    'summer_message_3': 'Que feriez-vous avec plus de temps?',
    'summer_message_4': 'Décrivez votre journée la plus pleine et vibrante.',
    'summer_placeholder': 'Dans ma journée la plus pleine, je...',
    'summer_submit': 'Embrasser la lumière',
    'summer_closing': 'Le soleil vous a entendu. Maintenant il commence sa descente.',
    'summer_quote': 'Au sommet de la lumière, nous découvrons ce que nous voulons vraiment illuminer.',
    
    // Autumn Equinox Room (French)
    'autumn_date': '22 SEPTEMBRE',
    'autumn_title': 'Équinoxe d\'Automne',
    'autumn_message_1': 'L\'équilibre revient.',
    'autumn_message_2': 'La récolte est prête.',
    'autumn_message_3': 'Qu\'avez-vous cultivé? Que pouvez-vous accepter?',
    'autumn_harvesting': 'Je récolte:',
    'autumn_harvesting_placeholder': 'Ce que vous avez cultivé cette année...',
    'autumn_accepting': 'J\'accepte:',
    'autumn_accepting_placeholder': 'Ce que vous ne pouvez pas changer...',
    'autumn_release': 'Laissez tomber les feuilles',
    'autumn_harvested': 'Récolté:',
    'autumn_accepted': 'Accepté:',
    'autumn_closing': 'Tout tombe en son temps.',
    'autumn_quote': 'L\'automne nous montre combien il est beau de laisser partir les choses.',
    
    // Valentine's Day Room (French)
    'valentine_date': '14 FÉVRIER',
    'valentine_title': 'L\'Amour Que Vous Méritez',
    'valentine_message_1': 'Aujourd\'hui n\'est pas question des autres.',
    'valentine_message_2': 'C\'est question de celui qui est toujours avec vous.',
    'valentine_message_3': 'Celui qui entend chaque pensée.',
    'valentine_message_4': 'Écrivez-vous une lettre d\'amour.',
    'valentine_prompt': 'Une lettre pour vous:',
    'valentine_placeholder': 'Cher moi, je veux que tu saches...',
    'valentine_submit': 'Envoyer avec amour',
    'valentine_closing': 'Vous méritez l\'amour que vous donnez aux autres.',
    'valentine_quote': 'Vous-même méritez votre amour et votre affection.',
    
    // Update changelog
    'update_seasonal_rooms': 'Quatre nouvelles chambres saisonnières: Équinoxe de Printemps, Solstice d\'Été, Équinoxe d\'Automne et Saint-Valentin',
    'update_fragment_trading': 'Système d\'échange de fragments - sacrifiez des fragments pour débloquer des salles à temps, prévisualiser des salles d\'horreur ou recevoir des messages IA',
    'update_new_themes': 'Deux nouveaux thèmes: Éclipse (obscurité totale) et Aube (dégradés chauds, seulement 5-7h)',
     'update_regret_machine': 'La Machine à Regrets - récits de chronologies alternatives alimentés par l\'IA',
     'update_memory_reconstruction': 'Reconstruction de Mémoire - l\'IA remplit les détails sensoriels des souvenirs qui s\'estompent',
     
     // Phase 7 - Regret Machine Room
     'regret_machine_title': 'La Machine à Regrets',
     'regret_machine_subtitle': 'Décrivez une décision que vous auriez aimé prendre différemment. La machine vous montrera ce qui aurait pu être.',
     'regret_placeholder': 'Je regrette...\n\nDécrivez un choix que vous avez fait et que vous auriez voulu différent. Qu\'auriez-vous fait à la place?',
     'regret_hint': 'Ce n\'est pas une question de blâme. C\'est une question de compréhension.',
     'regret_explore': 'Explorer la Chronologie',
     'regret_generating': 'Reconstruction d\'une réalité alternative...',
     'regret_alternate_timeline': 'La Chronologie Alternative',
     'regret_explore_another': 'Explorer Une Autre',
     'regret_error_short': 'Veuillez décrire votre regret plus en détail',
     'room_regret-machine': 'La Machine à Regrets',
     
     // Phase 7 - Memory Reconstruction Room
     'memory_reconstruction_title': 'Reconstruction de Mémoire',
     'memory_reconstruction_subtitle': 'Décrivez un souvenir qui s\'estompe. Laissez-nous vous aider à retrouver le chemin.',
     'memory_placeholder': 'Je me souviens...\n\nDécrivez un souvenir lointain ou fragmenté. Quels fragments restent? Quels sentiments persistent?',
     'memory_timeframe_placeholder': 'Quand était-ce? (ex: "enfance", "il y a dix ans")',
     'memory_hint': 'La mémoire n\'est pas un enregistrement. C\'est une reconstruction.',
     'memory_reconstruct': 'Reconstruire le Souvenir',
     'memory_reconstructing': 'Rassembler les fragments...',
     'memory_reconstructed': 'Le Souvenir Reconstruit',
     'memory_explore_another': 'Explorer Un Autre Souvenir',
     'memory_error_short': 'Veuillez décrire votre souvenir plus en détail',
     'room_memory-reconstruction': 'Reconstruction de Mémoire',
    'update_eclipse_room': 'Ombres de l\'Éclipse - parlez dans l\'obscurité totale, révélez dans les éclairs de lumière',
    'update_dawn_room': 'Éveil de l\'Aube - saluez la première lumière avec des exercices de respiration et des intentions',
    
    // New themes
    'eclipse_theme_required': 'Cette salle nécessite le thème Éclipse. L\'obscurité totale vous attend.',
    'dawn_theme_required': 'Cette salle nécessite le thème Aube pendant les premières heures.',
    'dawn_time_hint': 'Disponible uniquement de 5h à 7h',
    'change_theme': 'Changer de thème dans les Paramètres',
    
    // Eclipse Shadows Room
    'eclipse_shadows_title': 'Ombres de l\'Éclipse',
    'eclipse_shadows_intro': 'Dans l\'obscurité complète, les secrets semblent plus sûrs. Répondez honnêtement—seuls de brefs éclairs illumineront vos mots.',
    'enter_darkness': 'Entrer dans l\'obscurité',
    'shadow_question_1': 'Que cachez-vous de vous-même?',
    'shadow_question_2': 'Quelle vérité diriez-vous si personne ne pouvait entendre?',
    'shadow_question_3': 'Qu\'est-ce qui vous hante à 3h du matin?',
    'shadow_question_4': 'Que feriez-vous si la peur n\'existait pas?',
    'shadow_question_5': 'Qu\'est-ce que vous n\'êtes pas prêt à affronter?',
    'speak_into_dark': 'Parlez dans l\'obscurité...',
    'release': 'Libérer',
    'eclipse_reveal_prompt': 'Cliquez sur chaque ombre pour révéler ce que vous avez confessé.',
    'hidden_in_shadow': '▓▓▓▓▓▓▓▓▓▓▓▓',
    'eclipse_reveal_hint': 'Certaines vérités sont plus claires dans l\'obscurité.',
    'eclipse_shadows_quote': 'Dans l\'obscurité totale, nous nous voyons enfin.',
    
    // Dawn Awakening Room
    'dawn_awakening_title': 'Éveil de l\'Aube',
    'dawn_awakening_intro': 'La première lumière apporte la possibilité. Avant que le monde ne s\'éveille, définissez vos intentions.',
    'greet_the_light': 'Saluez la lumière',
    'breathe_in_light': 'Respirez la lumière',
    'hold_warmth': 'Gardez la chaleur',
    'release_night': 'Libérez la nuit',
    'dawn_prompt_1': 'Que voulez-vous emporter avec vous aujourd\'hui?',
    'dawn_prompt_2': 'Qu\'allez-vous laisser aller?',
    'dawn_prompt_3': 'Quel petit acte de gentillesse vous offrirez-vous?',
    'set_your_intention': 'Définissez votre intention...',
    'plant_seed': 'Plantez cette graine',
    'dawn_awakening_quote': 'Chaque aube est une invitation à recommencer.',
    
    // Fragment Trading
    'fragment_trading': 'Échange de Fragments',
    'sacrifice_to_unlock': 'Sacrifiez des fragments pour débloquer des secrets',
    'your_fragments': 'Vos Fragments',
    'fragments_any': 'n\'importe quels fragments',
    'fragments_free': 'fragments de clarté',
    'fragments_premium': 'fragments de profondeur',
    'fragments_horror': 'fragments d\'ombre',
    'trade_successful': 'Échange Réussi !',
    'trade_failed': 'Échange Échoué',
    'not_enough_fragments': 'Fragments insuffisants',
    'continue_trading': 'Continuer à Échanger',
    'trading_fragments': 'Échange de fragments...',
    'fragment_trade_warning': 'Les fragments sacrifiés ne peuvent pas être récupérés',
    'unlock-time-locked': 'Débloquer Salle Temporelle',
    'unlock-time-locked_desc': 'Accédez à une salle temporelle sans attendre',
    'preview-horror': 'Aperçu Salle d\'Horreur',
    'preview-horror_desc': 'Jetez un œil à une salle d\'horreur avant d\'entrer',
    'ai-reflection-trade': 'Réflexion IA',
    'ai-reflection-trade_desc': 'Recevez un message personnalisé de l\'IA',
    'unlock-premium-preview': 'Essai Salle Premium',
    'unlock-premium-preview_desc': 'Accédez à une salle premium pour une visite',
    'fragment_trading_room': 'L\'Autel des Fragments',
    'fragment_trading_intro': 'Ici, vous pouvez sacrifier vos fragments collectés pour débloquer des secrets cachés dans ces salles.',
    'your_collection': 'Votre Collection',
    'fragment_clarity': 'Clarté',
    'fragment_depth': 'Profondeur',
    'fragment_shadow': 'Ombre',
    'total_fragments': 'Total',
    'open_altar': 'Approcher l\'Autel',
    'recent_trades': 'Offrandes Récentes',
    'fragment_trade_ending': 'Ce qui a été donné ne peut être récupéré. Mais ce qui a été gagné... cela reste.',
    'room_fragment-trade': 'L\'Autel des Fragments',
    'sub_fragment-trade': 'Sacrifiez pour débloquer des secrets',
    'update_anniversary_system': 'Système d\'Anniversaire et Jalons - suivez votre parcours dans le temps',
    'update_archive_room': 'Les Archives - historique complet débloqué à 25 salles',
    'update_mirror_room': 'Miroir de Tous les Soi - portrait comportemental IA à 50 salles',
    'update_ending_room': 'La Fin - un adieu personnalisé à 75 salles',
    'room_the-archive': 'Les Archives',
    'room_mirror-of-all-selves': 'Miroir de Tous les Soi',
    'room_the-ending': 'La Fin',
    // Phase 9
    'update_fragment_rarities': 'Raretés de Fragments - fragments collectibles Communs, Rares et Légendaires',
    'update_fragment_gallery': 'Galerie de Fragments - vitrine visuelle pour votre collection',
    'update_seasonal_fragments': 'Fragments saisonniers et secrets - collectibles à durée limitée',
    'fragment_collection': 'Collection',
    'fragment_collection_title': 'Collection de Fragments',
    'fragment_collection_subtitle': 'Chaque salle laisse quelque chose derrière. Collectionnez-les tous.',
    'collection_milestones': 'Jalons',
    'collection_secrets': 'Secrets',
    'collection_footer': 'Certains fragments n\'apparaissent que dans certaines conditions.',
    'rarity_common': 'Commun',
    'rarity_rare': 'Rare',
    'rarity_legendary': 'Légendaire',
    // Phase 10-11
    'accessibility': 'Accessibilité',
    'accessibility_desc': 'Personnaliser la lisibilité et les préférences de mouvement',
    'font_family': 'Famille de Police',
    'font_default': 'Monospace',
    'font_dyslexia': 'Adaptée à la Dyslexie',
    'font_sans': 'Sans Empattement',
    'font_changed': 'Police mise à jour',
    'line_height': 'Hauteur de Ligne',
    'line_compact': 'Compact',
    'line_normal': 'Normal',
    'line_relaxed': 'Détendu',
    'line_spacious': 'Spacieux',
    'line_height_changed': 'Hauteur de ligne mise à jour',
    'reduced_motion_full_desc': 'Désactiver toutes les animations et effets de particules',
    'larger_cursors': 'Cibles Plus Grandes',
    'larger_cursors_desc': 'Augmenter la taille des cibles de clic',
    'update_accessibility_suite': 'Suite d\'Accessibilité - polices pour dyslexie, contrôle de hauteur de ligne',
    'update_dyslexia_font': 'Option de police adaptée à la dyslexie dans les paramètres',
    'update_line_height': 'Hauteur de ligne ajustable pour une meilleure lisibilité',
    'update_enhanced_reduced_motion': 'Mouvement réduit amélioré - désactive complètement tous les effets',
    'update_shared_moments': 'Moments Partagés - bilans émotionnels anonymes',
    'update_whisper_wall': 'Le Mur des Murmures - laissez des vérités anonymes dans l\'obscurité',
    'update_collective_memory': 'Mémoire Collective - fragments partagés de ce dont nous nous souvenons',
    'whisper_prompt': 'Laissez un murmure sur le mur. Anonyme. Permanent.',
    'whisper_placeholder': 'Que portez-vous en silence ?',
    'whisper_send': 'Murmurer',
    'whisper_added': 'Votre murmure rejoint le mur.',
    'whisper_another': 'Murmurer encore',
    'shared_moments_title': 'En ce moment même...',
    'shared_moments_subtitle': 'Comment allez-vous, honnêtement ?',
    'feeling_grateful': 'Reconnaissant',
    'feeling_lost': 'Perdu',
    'feeling_hopeful': 'Plein d\'espoir',
    'feeling_heavy': 'Lourd',
    'feeling_peaceful': 'En paix',
    'feeling_restless': 'Agité',
    'room_whisper-wall': 'Le Mur des Murmures',
    'room_collective-memory': 'Mémoire Collective',
    'room_shared-moments': 'Moments Partagés',
    'sub_regret-machine': 'Explorez ce qui aurait pu être',
    'sub_memory-reconstruction': 'Reconstruisez un souvenir qui s\'estompe',
    'sub_whisper-wall': 'Des vérités anonymes flottent dans l\'obscurité',
    'sub_collective-memory': 'Fragments partagés de ce dont nous nous souvenons',
    'sub_shared-moments': 'Comment allez-vous, honnêtement?',
    'sub_the-archive': 'Votre historique complet de voyage',
    'sub_mirror-of-all-selves': 'L\'IA révèle votre portrait comportemental',
    'sub_the-ending': 'Un adieu que vous avez mérité',

    // v3.21.0 - New Rooms
    'room_mask-you-wear': 'Le Masque que Vous Portez',
    'sub_mask-you-wear': 'Quel visage montrez-vous ?',
    'room_recurring-dream': 'Votre Rêve Récurrent',
    'sub_recurring-dream': 'Le rêve qui revient sans cesse',
    'room_promise-you-broke': 'La Promesse que Vous Avez Brisée',
    'sub_promise-you-broke': 'Honnêteté sur les promesses brisées',
    'room_words-you-swallowed': 'Les Mots Avalés',
    'sub_words-you-swallowed': 'Ce que vous n\'avez jamais dit',
    'room_age-you-feel': 'L\'Âge que Vous Ressentez',
    'sub_age-you-feel': 'Quel âge avez-vous vraiment ?',
    'room_biggest-lie': 'Votre Plus Grand Mensonge',
    'sub_biggest-lie': 'Le mensonge que vous dites le plus',
    'room_person-you-pretend-to-be': 'La Personne que Vous Prétendez Être',
    'sub_person-you-pretend-to-be': 'Votre performance',
    'room_person-you-pretend': 'La Personne que Vous Prétendez Être',
    'sub_person-you-pretend': 'Votre performance',
    'room_what-you-collect': 'Ce que Vous Collectionnez',
    'sub_what-you-collect': 'Les objets révèlent l\'identité',
    'room_apology-you-owe': 'Les Excuses que Vous Devez',
    'sub_apology-you-owe': 'Écrivez ce que vous auriez dû dire',
    'room_unfinished-story': 'Votre Histoire Inachevée',
    'sub_unfinished-story': 'Ce que vous n\'avez jamais terminé',
    'room_version-others-see': 'La Version que les Autres Voient',
    'sub_version-others-see': 'Comment les autres vous perçoivent',
    'room_secret-talent': 'Votre Talent Secret',
    'sub_secret-talent': 'Ce que personne ne sait que vous faites',
    'room_fear-you-feed': 'La Peur que Vous Nourrissez',
    'sub_fear-you-feed': 'Quelle peur vous contrôle ?',
    'room_guilty-pleasure': 'Votre Plaisir Coupable',
    'sub_guilty-pleasure': 'Ce que vous cachez apprécier',
    'room_rule-you-break': 'La Règle que Vous Brisez Toujours',
    'sub_rule-you-break': 'Votre schéma de rébellion',
    'room_childhood-promise': 'Votre Promesse d\'Enfance',
    'sub_childhood-promise': 'Ce que vous vous êtes promis',
    'room_compliment-you-reject': 'Le Compliment que Vous Rejetez',
    'sub_compliment-you-reject': 'Pourquoi vous ne pouvez pas accepter les éloges',
    'room_autopilot-mode': 'Votre Mode Pilote Automatique',
    'sub_autopilot-mode': 'Ce que vous faites sans réfléchir',
    'room_boundary-you-need': 'La Limite dont Vous Avez Besoin',
    'sub_boundary-you-need': 'Ce que vous devez protéger',
    'room_inner-critic': 'Votre Critique Intérieur',
    'sub_inner-critic': 'La voix qui juge',
    'room_last-sunday': 'Dimanche Dernier',
    'sub_last-sunday': 'Qu\'avez-vous vraiment fait ?',
    'room_wasted-hours': 'Vos Heures Perdues',
    'sub_wasted-hours': 'Où le temps disparaît',
    'room_year-that-changed': 'L\'Année qui Vous a Changé',
    'sub_year-that-changed': 'Le tournant',
    'room_tomorrows-regret': 'Le Regret de Demain',
    'sub_tomorrows-regret': 'Que souhaiteriez-vous avoir fait ?',
    'room_morning-autopilot': 'Votre Pilote Automatique du Matin',
    'sub_morning-autopilot': 'Le rituel de la première heure',
    'room_season-you-dread': 'La Saison que Vous Redoutez',
    'sub_season-you-dread': 'Quelle saison pèse le plus ?',
    'room_ten-years-ago': 'Il y a Dix Ans',
    'sub_ten-years-ago': 'Qui étiez-vous alors ?',
    'room_last-normal-day': 'Votre Dernier Jour Normal',
    'sub_last-normal-day': 'Avant que tout change',
    'room_clock-you-ignore': 'L\'Horloge que Vous Ignorez',
    'sub_clock-you-ignore': 'Le temps que vous prétendez ne pas passer',
    'room_your-deadline': 'Votre Date Limite',
    'sub_your-deadline': 'Que reportez-vous ?',
    'room_the-pause': 'La Pause',
    'sub_the-pause': 'Respirez un instant',
    'room_static-room': 'Statique',
    'sub_static-room': 'Bruit blanc et immobilité',
    'room_gap-between-thoughts': 'L\'Espace Entre les Pensées',
    'sub_gap-between-thoughts': 'Trouvez l\'espace',
    'room_sit-with-discomfort': 'Asseyez-vous avec l\'Inconfort',
    'sub_sit-with-discomfort': 'Ne réparez rien',
    'room_boring-room': 'La Chambre Ennuyeuse',
    'sub_boring-room': 'Rien d\'intéressant du tout',
    'room_unplug-room': 'Débrancher',
    'sub_unplug-room': 'Imaginez sans appareils',
    'room_unplug': 'Débrancher',
    'sub_unplug': 'Imaginez sans appareils',
    'room_last-page': 'La Dernière Page',
    'sub_last-page': 'L\'histoire se termine ici',
    'room_scrolling-pattern': 'Votre Défilement',
    'sub_scrolling-pattern': 'Comment vous consommez le contenu',
    'room_tab-you-keep-open': 'L\'Onglet Toujours Ouvert',
    'sub_tab-you-keep-open': 'Ce qui reste dans votre navigateur',
    'room_digital-shadow': 'Votre Ombre Numérique',
    'sub_digital-shadow': 'Votre empreinte numérique',
    'room_notification-you-ignore': 'La Notification Ignorée',
    'sub_notification-you-ignore': 'Ce que vous rejetez',
    'room_typing-speed': 'Votre Vitesse de Frappe',
    'sub_typing-speed': 'À quelle vitesse vous exprimez-vous ?',
    'room_link-you-almost-clicked': 'Le Lien Presque Cliqué',
    'sub_link-you-almost-clicked': 'Schémas d\'hésitation',
    'room_bookmark-graveyard': 'Votre Cimetière de Favoris',
    'sub_bookmark-graveyard': 'Sauvegardés mais jamais revisités',
    'room_search-you-deleted': 'La Recherche Supprimée',
    'sub_search-you-deleted': 'Ce que vous avez cherché et effacé',
    'room_gravity-room': 'Salle de Gravité',
    'sub_gravity-room': 'Tout tombe lentement',
    'room_ink-blot': 'Tache d\'Encre',
    'sub_ink-blot': 'Que voyez-vous ?',
    'room_hallway-of-mirrors': 'Le Couloir des Miroirs',
    'sub_hallway-of-mirrors': 'Reflets infinis',
    'room_dissolving-text': 'Texte qui Se Dissout',
    'sub_dissolving-text': 'Les mots fondent pendant la lecture',
    'room_the-pendulum': 'Le Pendule',
    'sub_the-pendulum': 'Mouvement hypnotique',
    'room_pixel-rain': 'Pluie de Pixels',
    'sub_pixel-rain': 'Précipitation numérique',
    'room_the-threshold': 'Le Seuil',
    'sub_the-threshold': 'Entre ici et là-bas',
    'most_loved_rooms': 'Chambres les Plus Aimées',
    'rate_room': 'Évaluez cette chambre',
    'update_52_new_rooms': '52 nouvelles chambres dans les catégories Identité, Temps, Contemplation, Méta et Expérimental',
    'update_room_rating': 'Système de notation - pouce haut/bas avec section Chambres les Plus Aimées',
    'update_bug_fix_reflections': 'Correction de l\'affichage et du stockage des réflexions anonymes',
    'update_100_rooms': 'Plus de 100 chambres disponibles dans le Couloir',
    // v3.22.0
    'room_the-friend-you-lost': "L'Ami Que Vous Avez Perdu",
    'sub_the-friend-you-lost': "Écrivez à quelqu'un dont vous vous êtes éloigné.",
    'room_conversation-you-replay': 'La Conversation Que Vous Rejouez',
    'sub_conversation-you-replay': 'Recréez-la de mémoire.',
    'room_the-thank-you': 'Le Merci',
    'sub_the-thank-you': "Gratitude que vous n'avez jamais exprimée.",
    'room_people-you-forgot': 'Les Gens Que Vous Avez Oubliés',
    'sub_people-you-forgot': 'Essayez de vous rappeler des noms oubliés.',
    'room_the-advice-you-give': 'Le Conseil Que Vous Donnez',
    'sub_the-advice-you-give': 'Un conseil que vous donnez mais ne suivez jamais.',
    'room_what-others-dont-know': 'Ce Que Les Autres Ne Savent Pas',
    'sub_what-others-dont-know': 'Vos couches cachées.',
    'room_the-lie-you-believe': 'Le Mensonge Que Vous Croyez',
    'sub_the-lie-you-believe': 'Une contre-vérité réconfortante.',
    'room_your-shadow-self': "Votre Moi de l'Ombre",
    'sub_your-shadow-self': 'La partie que vous réprimez.',
    'room_the-story-you-tell': "L'Histoire Que Vous Racontez",
    'sub_the-story-you-tell': 'Comment vous narrez votre vie.',
    'room_your-safe-place': 'Votre Lieu Sûr',
    'sub_your-safe-place': 'Décrivez où vous vous sentez en sécurité.',
    'room_the-risk-you-didnt-take': "Le Risque Que Vous N'Avez Pas Pris",
    'sub_the-risk-you-didnt-take': 'Chemins non empruntés.',
    'room_your-last-excuse': 'Votre Dernière Excuse',
    'sub_your-last-excuse': "L'excuse que vous utilisez le plus.",
    'room_the-habit-you-hide': "L'Habitude Que Vous Cachez",
    'sub_the-habit-you-hide': 'Routines secrètes.',
    'room_what-you-postpone': 'Ce Que Vous Reportez',
    'sub_what-you-postpone': 'La chose toujours "demain".',
    'room_the-door-you-closed': 'La Porte Que Vous Avez Fermée',
    'sub_the-door-you-closed': 'Opportunités que vous avez rejetées.',
    'journal_title': 'Journal de Réflexion',
    'journal_subtitle': 'Vos pensées à travers les chambres',
    'journal_empty': "Pas encore de réflexions. Visitez des chambres et écrivez vos pensées.",
    'journal': 'Journal',
    'room_chains': 'Chaînes de Chambres',
    'chain_identity_journey': "Voyage d'Identité",
    'chain_time_reflection': 'Réflexion du Temps',
    'chain_truth_path': 'Chemin de la Vérité',
    'recently_visited': 'Récemment Visités',
    'update_reflection_journal': 'Journal de Réflexion - sauvegardez vos pensées entre les chambres',
    'update_room_chains': 'Chaînes de Chambres - complétez des chambres en séquence',
    'update_15_new_rooms': '15 nouvelles chambres interactives dans 3 catégories',
    'update_recently_visited': 'Section récemment visités pour un retour rapide',
    'ai_companion': 'Compagnon',
    'companion_create': 'Créez Votre Compagnon',
    'companion_setup_desc': 'Un reflet qui marche à vos côtés.',
    'companion_name': 'Nommez votre compagnon',
    'companion_name_placeholder': 'ex., Écho, Miroir, Ombre...',
    'companion_lens': 'Lentille de personnalité',
    'companion_mood': 'Ton de communication',
    'companion_fears': "De quoi avez-vous peur ? (optionnel)",
    'companion_struggles': 'Avec quoi luttez-vous ? (optionnel)',
    'create_companion': 'Créer',
    'companion_input': 'Dites quelque chose...',
    'companion_greeting': 'Votre compagnon est là. Dites quelque chose.',
    'companion_starter_1': "Qu'est-ce qui occupe mon esprit aujourd'hui ?",
    'companion_starter_2': 'Je me sens bloqué.',
    'companion_starter_3': "Dis-moi quelque chose que j'évite.",
    'fears_placeholder': "ex., l'échec, le rejet, être oublié...",
    'struggles_placeholder': 'ex., trop réfléchir, procrastiner, solitude...',
    'back_step': 'Retour',
    'lens_reflective': 'Réflectif',
    'lens_reflective_desc': 'Questions profondes, reconnaissance de schémas',
    'lens_supportive': 'Bienveillant',
    'lens_supportive_desc': 'Chaleureux, encourageant, validant',
    'lens_challenging': 'Stimulant',
    'lens_challenging_desc': 'Direct, provocateur',
    'lens_curious': 'Curieux',
    'lens_curious_desc': 'Questions inattendues, émerveillement',
    'lens_shadow': 'Ombre',
    'lens_shadow_desc': 'Le côté caché, inconfort honnête',
    'companion_mood_calm': 'Calme',
    'companion_mood_calm_desc': 'Mesuré et paisible',
    'companion_mood_intense': 'Intense',
    'companion_mood_intense_desc': 'Urgent et profond',
    'companion_mood_playful': 'Espiègle',
    'companion_mood_playful_desc': 'Spirituel et léger',
    'companion_mood_melancholic': 'Mélancolique',
    'companion_mood_melancholic_desc': 'Belle tristesse',
    'update_ai_companion': "Compagnon IA - une IA personnelle qui se souvient de votre voyage",
  },
};
